import { clearSwitchStates } from "$lib/components/providers-usage/provider-usage-state.svelte.js";
import { persistDesktopCredential } from "../core/desktop-credentials.js";
import { workSessionTypingState } from "./work-session-typing.svelte.js";
import { noteSessionEnded } from "$lib/login-redirect.js";
import {
  agentCommandText,
  rememberTypedCommand,
  sendRetryingWhileBusy,
  type AgentCommandInput,
} from "$lib/plugins/agents/agent-command-input.js";
import {
  failedMoveMessage,
  type HandoffAgent,
  type WorkspaceHandoffs,
} from "$lib/components/agents/handoff/types.js";
import { agentDisplayName, cyboNameOf } from "$lib/agent-display.js";
import { goto } from "$app/navigation";
import { SvelteSet } from "svelte/reactivity";
import { toast } from "svelte-sonner";
import { showNoticeToast, type NoticeForToast } from "./agent-notice-toast.js";
import { isUnsupportedByRelay } from "./thread-sync.js";
import { applyMessageChanges } from "./message-changes-patch.js";
import { signOutOrdered } from "./sign-out.js";
import { errorCodeOf, isRefusalCode, reportUiFailure, shouldReport } from "../report-failure.js";
import { isTransportFailure } from "../core/transport-failure.js";
import { tombstoneDeleted } from "../core/message-delete.js";
import {
  alertSplitChanged,
  hiddenAlertsChanged,
  otherWorkspaceCountSet,
} from "./alerts-split-sync.js";
import { alertsCounts, checkBadgeMismatch, reportAlertsSeed } from "./activity-telemetry.js";
import { reportServerErrorFrame } from "./server-refusal.js";
import {
  type SendSurface,
  contentRefusalReason,
  dmRefusalReason,
  forgetOutboxBubble,
  keepBubble,
  keepParkedBubble,
  openSendRow,
  pendingOutboxBubbles,
  reapplyKeptBubbles,
  setBubbleStatus,
  surfaceFor,
} from "./send-bubbles.js";
import {
  SEND_WATCHDOG_MS,
  armSendWatchdog,
  clearSendWatchdog,
  markSendWatchdogsDropped,
  sweepReconciledWatchdogs,
  watchOf,
} from "./send-watchdog.js";
import { reportForcedLogout } from "./forced-logout.js";
import { ageBucket } from "../transcript-invariant.js";
import { patchWorkSummary } from "../message-work-patch.js";
import { rowWork } from "../row-work.svelte.js";
import { client } from "./client.js";
import {
  beginCyboWait,
  cyboRosterMirror,
  endCyboWait,
  noteCyboAnswer,
} from "../core/cybo-roster-provisional.svelte.js";
import { mirrorFirst } from "../core/roster-mirror-first.js";
import { dmHiddenState, lastChatHidden, showDm } from "./dm-hidden.svelte.js";
import { relayCapabilities } from "../relay-capabilities.svelte.js";
import {
  mapDaemonConnStatus,
  nextAgentSnapshot,
  reconcileDaemonStatuses,
  type DaemonConnStatus,
} from "../core/daemon-status.js";
import { mergeDaemonLiveness } from "../core/daemon-liveness-merge.js";
import {
  loadRosterCache,
  pruneRosterForDaemons,
  saveRosterCache,
  type RosterStore,
} from "../core/agent-roster-cache.js";
import { loadSessionListCache, saveSessionListCache } from "../core/session-list-cache.js";
import {
  refreshAccessToken,
  wsUrlToHttpBase,
  saveRefreshToken,
  loadRefreshToken,
} from "../core/auth-refresh.js";
import type {
  CloneAgentResult,
  ProviderCredentialPayload,
  ProviderAuthMeta,
} from "../ws-client.js";
import type { ActivityReadChangedPayload } from "../ws-client-types.js";
import { refreshChannelRosterOnMembershipChange } from "../channel-roster-refresh.js";
import { connectState } from "./connect.svelte.js";
// isMe, NOT `x === authState.user.id`: a human holds TWO ids and which one a server
// payload carries is decided by the transport that produced the row. See its doc in
// session-scope.ts.
import {
  answersSessionPermissions,
  defaultDmSessionForCybo,
  dmAddress,
  filterMine,
  isMe,
  isMine,
  notRunSessionId,
  rosterMissesAgentSession,
  type ViewerIdentity,
} from "../session-scope.js";
import { bannerToolLabel } from "../permission-banner.js";
import { isWorkspaceRole } from "../workspace-role.js";
import { sessionAlias, sessionDisplayName, sharedSessionNames } from "./session-alias.svelte.js";
import { terminalAlias } from "./terminal-alias.svelte.js";
import { favoritesState } from "./favorites.svelte.js";
import {
  applyWorkSession,
  hydrateWorkSessions,
  beginWorkSessionsSnapshot,
  workSessionsState,
} from "./work-sessions.svelte.js";
import { openedDmsState } from "./opened-dms.svelte.js";
import { mentionQueueState } from "./mention-queue.svelte.js";
import { heldDmReload } from "./held-dm-reload.svelte.js";
import { busyRefusalCopy } from "./busy-refusal-copy.js";
import { parseHoldReason } from "./mention-hold-copy.js";
import { TypingLines } from "../core/typing-lines.svelte.js";
import {
  routeTypingEvent,
  clearTypingOnSessionTurnEnd,
  type TypingSurfaces,
} from "./typing-route.js";
import { migrateRekeyedIdentity } from "./identity-rekey.js";
import { scheduledMessagesState } from "./scheduled-messages.svelte.js";
import { schedulesState } from "./schedules.svelte.js";
import { recipesState } from "./recipes.svelte.js";
import { promptTemplatesState } from "./prompt-templates.svelte.js";
import { draftsState } from "../drafts.svelte.js";
// Right-hand panels are mutually exclusive (Thread XOR Profile): opening a thread
// closes any open profile and vice-versa. Coordinated through the NEUTRAL
// rhs-panel-exclusive module (registration) instead of importing profile-panel
// directly — a direct cross-import formed a circular dependency that broke the
// open handlers at runtime.
import { closeOpenProfile, registerThreadCloser } from "../rhs-panel-exclusive.js";
import { ensureMediaToken } from "../media/asset-url.svelte.js";
import { rememberLastChat, getLastChat } from "../last-chat.js";
// An OS banner renders no markup, so it must be handed the unwrapped form of a
// mention token rather than the stored one — see mentionPlainText's own doc.
import { mentionPlainText } from "../resolve-mentions.js";
import { liveHumanName } from "../components/message/message-author.js";
import { followRetriedSend } from "../components/message/message-arrival.js";
import {
  applyReactionChange,
  canAddReaction,
  normalizeReactionEvent,
  normalizeReactions,
  reactedByMe,
  setMyReaction,
} from "../components/message/reactions/reactions.js";
import { rowReactions } from "../components/message/reactions/row-reactions.svelte.js";
import { singleEmoji } from "../emoji.js";
import { providerFlow } from "../components/providers/provider-flow.svelte.js";
import {
  AuthError,
  NetworkError,
  isPromptNotSent,
  normalizeWorkerInvite,
  PromptNotSentError,
  TERMINAL_CLOSE_REASONS,
  type CyboTransferDetail,
  type NoticeDetail,
} from "../core/client.js";
import { licenseState } from "./license.svelte.js";
import { handleLicenseError, handleUpgradeError } from "./license-errors.js";
// Billing/license RPC facades extracted to ./billing.ts (knowledge/87 Phase 2);
// re-exported below. Imported here for the one internal caller (selectWorkspace
// seeds the license via refreshLicense).
import { refreshLicense, startLicenseRead } from "./billing.js";
// Workspace/projects/channels CRUD facades extracted to ./workspace-crud.svelte.ts
// (knowledge/87 Phase 3); re-exported below. projectsCache is imported here for the
// logout/account-switch warm-cache wipe, renameDefaultProjectInCache for updateWorkspace.
import {
  fetchProjects,
  projectsCache,
  renameDefaultProjectInCache,
} from "./workspace-crud.svelte.js";
import type {
  Attachment,
  Channel,
  ExternalParticipant,
  PageCard,
  WorkSession,
  Workspace,
  WorkspaceMember,
} from "../core/types.js";
import {
  type AuthWorkspacesPayload,
  isOutsiderSeat,
  memberWorkspaces,
  mergeMembershipRefresh,
  reconcileWorkspaceList,
  upsertExternalWorkspace,
  workspaceListFromAuth,
} from "../core/workspace-list.js";
import {
  shouldNotifyFor,
  hasMention,
  threadReplyReachesUser,
  type NotifyContext,
  type NotificationKind,
  type NotificationPref,
} from "../notify-policy.js";
import { playNotificationSound, unlockNotificationAudio } from "../notify-sound.js";
import { notifyClientPrefsState } from "../notify-client-prefs.svelte.js";
import { registerMobilePush, unregisterMobilePush, isTauriIOS } from "../mobile/push.js";
import { isTauriAndroid } from "../mobile/platform.js";
import { ForegroundAway } from "./foreground-away.js";
import * as cache from "../cache/index.js";
import { ERASED_PEER } from "../cache/scope.js";
import {
  claimLocalStorage,
  releaseLocalStorage,
  viewerCacheKeySegment,
} from "../cache/local-owner.js";
import { cacheEnabled } from "../cache/enabled.js";
// CYBORG-989: the client half of the relay-held workspace snapshot — the stored
// version the bootstrap RPC is answered against, plus the workspace drain cursor.
import * as cacheSnapshot from "../cache/workspace-snapshot.js";
import type {
  Agent,
  AgentPermissionRequest,
  AgentPermissionResponse,
  AgentSlashCommand,
  CyboReadiness,
} from "../plugins/agents/types.js";
// The backfill gate, defined next to the openable gate it mirrors and counted with
// the SAME countVisibleStreamEntries the drill-down's own empty state uses — so the
// fetch below cannot disagree with either about whether a transcript is empty.
import { shouldBackfillSubagentTranscript } from "../session-subagents.js";
import { fetchWholeSubagentTranscript } from "../plugins/agents/provider-subagents.js";
import { permissionRequestTitle } from "../plugins/agents/tool-action-phrase.js";
import {
  applyCyboUpdate,
  applyCyboDenormToSessions,
  applyCeoSeat,
} from "../plugins/agents/apply-cybo-update.js";
import { appendCybo, removeCybo, removeCyboSessions } from "../plugins/agents/cybo-roster-ops.js";
import { noteA2aRefusal } from "../plugins/agents/a2a-refusals.svelte.js";
import { joinsWaitingMessageCard } from "../plugins/agents/message-approval.js";
import {
  PEER_HOLD_EXPIRED_TEXT,
  peerDisplayName,
  peerHoldTitle,
} from "../plugins/agents/peer-message.js";
import { pollForConnectedDaemonId } from "../plugins/agents/daemon-select.js";
import { AgentFrameBatch } from "../plugins/agents/agent-frame-batch.js";
import {
  applyAgentStreamFrame,
  throttledGapRecovery,
  turnEndOf,
} from "../plugins/agents/committed-seq.js";
import { newTextSince, TurnDeltaTracker } from "../plugins/agents/transcript-checks.js";
import { createSeqGapState, recoverSeqGap } from "./seq-gap-recovery.js";
import { dropExternalChannel } from "./external-channels.svelte.js";
import { drainWorkspaceSync } from "./workspace-sync-drain.js";
import { WorkspaceSyncCursor } from "./workspace-sync-cursor.js";
import { foregroundOnce } from "./foreground-once.js";
import { spacedCatchUp } from "./spaced-catch-up.js";
import { daemonNoticeAction, mergeDaemonRows } from "./daemon-status-notice.js";
import { coldStartTrace, errorSlug } from "$lib/desktop-cold-start-trace.js";
import {
  claimThisComputer,
  rememberDesktopClaim,
  reportDesktopClaimError,
} from "./desktop-claim.js";
import { createUnreadReconcileState, reconcileUnread } from "./unread-reconcile.js";
import { serverReadAhead } from "./unread-divider-refreeze.js";
import { coalesceBurst } from "./coalesce-burst.js";
import { channelMessageIsActive } from "./channel-message-active.js";
import {
  conversationRendered,
  routeConversationKind,
  type RenderedKind,
} from "./conversation-rendered.js";
import { alertsPaneState } from "./alerts-pane.svelte.js";
import { canonicalAgentDmPeerId as resolveCanonicalAgentDmPeerId } from "../cybo-dm-peer-id.js";
import {
  agentDmHasReachableSurface,
  agentDmPeerName,
  heldPromptNoticePeer,
  DmSurfaceHealGate,
  DmSurfaceHealPump,
  orphanAgentDmPeerIds,
} from "../agent-dm-surface.js";
import { serverCountableMention } from "../mention-badge-door.js";
import { retiredBadgeKeysForArchivedAgent } from "../archived-peer-badge.js";
import { workspaceBadgeTotal, badgeCount, doorlessKeys } from "./unread-selectors.js";
import {
  UnreadIncrementTelemetry,
  UnreadRowInvariants,
  dmSurfaceKind,
  reseedRaises,
  unreadKeyKind,
  type UnreadKeyKind,
  type UnreadRosters,
  type UnreadSource,
  type UnreadSurfaceKind,
} from "./unread-telemetry.js";
import { daemonGraceRecheckMs } from "../core/daemon-grace-recheck.js";
import {
  agentIdsForDaemonCatchUp,
  runCatchUpsPerDaemon,
  timelineIdsToCatchUp,
} from "./agent-catchup-scope.js";
import { shellConfig } from "../core/plugin.svelte.js";
import { createActiveViewSender } from "./active-view.js";
import { notificationsToClose } from "../push-sw.js";

// Re-export core state (only things NOT using coreClient)
export {
  authState,
  connectionState,
  daemonHealthState,
  presenceState,
  workspaceUserStatusesState,
  workspaceState,
  channelState,
  dmState,
  dmTypingState,
  userStatusState,
  notificationState,
  unreadFlagState,
  dmActivityState,
  sortMembersByDmRecency,
  notifPrefsState,
  archivedChannelsState,
  isArchivedForMe,
  threadsState,
  savedState,
  messageFocusState,
  foregroundSignal,
  getSavedSession,
  clearSavedSession,
} from "../core/state.svelte.js";
export type { SavedSession, UserStatus, DaemonHealthData } from "../core/state.svelte.js";
// Terminal foreground fast-path signal (knowledge/86 Task 1) — bumped in onForeground().
import { terminalForegroundSignal } from "./terminal-foreground.svelte.js";
export { terminalForegroundSignal } from "./terminal-foreground.svelte.js";
// Shared unread-badge selectors (B1/B2/B3) — the ONE place that combines the
// count/flag/mute stores. Re-exported so every surface imports them from the
// same barrel as the rest of the app state.
export {
  badgeCount,
  hasBadge,
  boldChannel,
  workspaceBadgeTotal,
  globalBadgeTotal,
  anyUnread,
  dmRecency,
  unreadRostersSettled,
} from "./unread-selectors.js";
// Client-only sidebar favorites (channels/DMs/agents). Re-exported here so UI
// components import it alongside the rest of the workspace state.
export { favoritesState } from "./favorites.svelte.js";
// Client-only "opened DMs" set — the DM sidebar section shows only opened
// conversations. Re-exported so components import it alongside app state.
export { openedDmsState } from "./opened-dms.svelte.js";
// Recurring cybo schedules (#611/#613/#619 — the Tasks-tab "Scheduled" board).
// Re-exported so the Tasks route + create dialog import it alongside app state.
export { schedulesState } from "./schedules.svelte.js";
// Built-in integrations (recipes — the integrations "Built-in" section). Re-exported
// so the integrations routes import it alongside app state.
export { recipesState } from "./recipes.svelte.js";
export type { ThreadSummary } from "../core/client.js";
import type { TaskAlertCard, ThreadStateEvent } from "../core/client.js";
import { createThreadFeedSync } from "./thread-feed-sync.js";
export { AuthError, NetworkError } from "../core/client.js";
// Client-only notification pref (DND). Re-exported here so settings/profile UIs
// import all state from one place, matching the rest of the codebase.
export { notifyClientPrefsState } from "../notify-client-prefs.svelte.js";

import {
  authState,
  connectionState,
  daemonHealthState,
  presenceState,
  workspaceUserStatusesState,
  workspaceState,
  channelState,
  dmState,
  agentDmReadState,
  dmUnreadCursorAtOpen,
  dmTypingState,
  userStatusState,
  notificationState,
  unreadFlagState,
  dmActivityState,
  readCursorState,
  notifPrefsState,
  archivedChannelsState,
  isArchivedForMe,
  threadsState,
  savedState,
  messageFocusState,
  foregroundSignal,
  clearSavedSession,
} from "../core/state.svelte.js";
import {
  bubbleTime,
  findOptimisticIndex,
  insertMessageSorted,
  newerPageCursor,
  rowsTheTailMissed,
} from "../core/message-reconcile.js";

// Re-export agent state
export {
  agentStreamState,
  attentionState,
  providerState,
  cyboState,
  daemonState,
  activityState,
  sessionState,
  daemonAccessRequestsState,
  pageAccessRequestsState,
  workerInvitesState,
} from "../plugins/agents/state.svelte.js";
import { forwardedTurnCards } from "../plugins/agents/forwarded-turn-cards.svelte.js";

import {
  agentStreamState,
  attentionState,
  providerState,
  cyboState,
  daemonState,
  activityState,
  sessionState,
  daemonAccessRequestsState,
  pageAccessRequestsState,
  workerInvitesState,
  ARCHIVED_SESSIONS_PAGE_SIZE,
  isWorkSessionActivityEventType,
  activityKindOf,
  reconcileAgentTranscript,
  catchUpAgent,
  rewindConversation,
  recoverTimelineRange,
  adoptNewLife,
  adoptRetiredLife,
  deferWhileAdopting,
  noteSessionCreatedHere,
  RECONNECTING_STATUS,
} from "../plugins/agents/state.svelte.js";
import { MirrorRevisions, mirrorResetOutcome } from "../plugins/agents/mirror-reset.js";
import { RedeliveryHolds, redeliveryKey } from "../plugins/agents/agent-unreachable.js";
import { notificationEntryUrl } from "../plugins/agents/route-entry.js";
import type { ActivityEventType, ArchivedSession } from "../plugins/agents/state.svelte.js";
import { verdictSettles } from "../plugins/agents/history/outbox.svelte.js";

// Live pending join-request store (corporate-email onboarding) — mutated by the
// join_request_changed push handler below.
import { joinRequestsState } from "./join-requests.svelte.js";
// Live pending invite-request store (CYBORG-1087) — mutated by the
// invite_request_changed push handler below.
import { inviteRequestsState } from "./invite-requests.svelte.js";
import { TaskBoardSync } from "./task-board-sync.js";

export type {
  StreamEntry,
  TurnStatus,
  ActivityItem,
  ActivityEventType,
  DaemonAccessRequest,
  PageAccessRequest,
  WorkerInvite,
  RecentSession,
  ArchivedSession,
  ResumeOverrides,
} from "../plugins/agents/state.svelte.js";
import { followRosterTurnStatus, turnStatusBook } from "../plugins/agents/turn-status.svelte.js";
import { followLegacyQueues } from "../plugins/agents/legacy-queue-migration.svelte.js";
import { wireTurnRecord } from "../plugins/agents/turn-status-schema.js";
import { accountSync, conversationSync, workspaceSync } from "../plugins/agents/sync-session.js";
import { cyboTransferRequestsState } from "./cybo-transfer-requests.svelte.js";
import { DmVisitCounter } from "./dm-visit.js";
import { humanDmCandidates, LatestReadGate, MemberRereadRunner } from "./dm-member-heal.js";
import {
  bootOnlineDaemons,
  coveredSeq,
  daemonEdgeWorkspaces,
  daemonOnlineEdge,
  daemonOutsideWorkspace,
  failedRereads,
  nextRosterEdge,
  rosterEdgeSeq,
  rosterReadSeq,
} from "./roster-edges.js";

import type {
  ServerActivityItem,
  ResumeOverrides,
  DaemonAccessRequest,
  PageAccessRequest,
  WorkerInvite,
  RecentSession,
} from "../plugins/agents/state.svelte.js";
export type { Daemon, DaemonMeta, DaemonAccessEntry } from "../plugins/agents/types.js";
export type { ProviderCredentialPayload, ProviderAuthMeta } from "../ws-client.js";

// ─── Provider subagents (Codex/OpenCode/Claude) ──────────────────
// The reactive descriptor store for provider-spawned subagents. Its live
// conversations are fed into agentStreamState (keyed by subagent id) so the
// existing AgentStreamView renders them with no new timeline plumbing.
import { ProviderSubagentStore } from "../plugins/agents/provider-subagent-store.svelte.js";
import { isSessionStreamKey } from "../session-progress.js";

export const providerSubagentStore = new ProviderSubagentStore({
  handleEvent: (agentId, event) => agentStreamState.handleEvent(agentId, event),
  clear: (agentId) => agentStreamState.clear(agentId),
  // A rewind page swaps the entries wholesale; pending permissions live outside them.
  replace: (agentId, items) =>
    agentStreamState.hydrateFromTimeline(agentId, items, { mode: "rewind" }),
});
// A logout drops the previous account's subagent rows (the store's own clearAll).
accountSync.adopt("providerSubagents", providerSubagentStore, (s) => s.clearAll());

// CYBORG-1416: each agent's goal, background tasks and retry, for the header pills, the tray's
// Background group and the typing indicator. Pushed by the daemon, read back on a reload.
import { agentActivityStore } from "../plugins/agents/agent-activity-store.svelte.js";
import { usageLimitState } from "../plugins/agents/usage-limit-state.svelte.js";
import { reportNoticeToastShown } from "../plugins/agents/agent-activity-telemetry.js";
import { mentionLine } from "../activity-preview.js";
import { collapseMessageMarkdown } from "../message-markdown.js";
import { rosterSettled, UnknownAgentRelist } from "./unknown-agent-relist.js";

export { agentActivityStore };
accountSync.adopt("agentActivity", agentActivityStore, (s) => s.clearAll());

// The process modal's logs (backlog + live frames), for the session owner.
import {
  agentProcessOutputStore,
  type AgentProcessTail,
} from "../plugins/agents/agent-process-output.svelte.js";
export { agentProcessOutputStore };
accountSync.adopt("agentProcessOutput", agentProcessOutputStore, (s) => s.clearAll());

// ─── Composed client (agents + core) ─────────────────────────────

// Every fetch path's reactions → one chip per emoji (the relay may still send raw rows).
function hydrateMessages(
  messages: import("../core/types.js").Message[],
): import("../core/types.js").Message[] {
  return messages.map((m) =>
    m.reactions?.length ? { ...m, reactions: normalizeReactions(m.reactions) } : m,
  );
}

// ─── Local message cache (every surface — Caveats #28/#29) ───────────────────
//
// Strictly additive. Everything below no-ops unless cacheOn() is true — which is
// everywhere now (iOS native, Electron desktop, Tauri Android AND plain web),
// unless the localStorage kill switch turns it off. On a cache MISS or ANY error
// the existing network-fetch path runs exactly as before. All writes are
// fire-and-forget. The L1 Map lives in a plain `.ts` singleton (Caveat #28);
// cursors are stamped only on fetch SUCCESS (Caveat #29).

// The gate's BODY now lives in cache/enabled.ts (CYBORG-987) — unchanged, just
// reachable from the agents plugin state module, which cannot import THIS module
// (this module imports IT) and needs the same answer for the agent-timeline /
// session-work read seams. Kept as a local function so every call site below reads
// exactly as it did.
function cacheOn(): boolean {
  return cacheEnabled();
}

// L1 memory key (per active conversation) + L2 scope key share the same string.
// Per viewer (CYBO-1846): one person's cached rows are never another's on a shared browser.
function cacheViewer(): string {
  return viewerCacheKeySegment(authState.user?.email);
}
function channelKey(wsId: string, channelId: string): string {
  return cache.channelScope(wsId, channelId, cacheViewer());
}
function dmKey(wsId: string, peerId: string): string {
  return cache.dmScope(wsId, peerId, cacheViewer());
}

/**
 * Synchronous cold-render peek for selectChannel/selectDm. Returns the cached
 * Message[] if L1 has them (instant paint, no skeleton); null otherwise — the
 * caller then keeps its current `messages=[]; loading=true` behavior. The async
 * L2 read is kicked off in the background and, if it lands while we're still on
 * the same conversation and the network hasn't answered yet, paints then.
 *
 * NOTE: this whole module is gated on `cacheOn()`, which is now true on EVERY
 * surface (plain web included) unless the localStorage kill switch turns it off.
 * The actual reported prod incident (pane rendered a DIFFERENT channel's history
 * while header/composer stayed correct, reproduced in a plain Chrome tab) can't
 * come from here — it was `selectWorkspace`'s default-channel bootstrap fetch
 * racing a direct-URL `selectChannel`, unguarded by channel id (fixed there; see
 * its comment).
 * `ChannelState.setMessages`/`DmState.mergeServer` now carry the real
 * belt-and-braces content guard for BOTH paths (network and this on-device
 * cache). Both the L1 (sync) and L2 (async) hits here are ALSO filtered
 * through `messageBelongsToChannel`/`Dm` before ever reaching `setMessages` —
 * redundant with the state-class guard, but kept as defense-in-depth for the
 * on-device cache against the same bug class (a legacy/mismatched IDB row
 * surfacing under the wrong scope key). `getRecent` (the L2 source)
 * already filters at the IDB layer; this repeats the check at the render site
 * itself so nothing between here and there can slip an unrelated batch through
 * un-noticed.
 */
function coldPeekChannel(channelId: string): import("../core/types.js").Message[] | null {
  const wsId = workspaceState.channelWorkspaceId(channelId);
  if (!cacheOn() || !wsId) return null;
  const key = channelKey(wsId, channelId);
  const hit = cache.memGet(key);
  const hitMessages = hit?.messages.filter((m) =>
    cache.messageBelongsToChannel(m, wsId, channelId),
  );
  if (hitMessages && hitMessages.length > 0) return hitMessages;
  // L1 miss: try L2 in the background. If it resolves before the network and
  // we're still on this channel still loading, paint the cached window.
  void (async () => {
    const entry = await cache.readCache(key, key);
    if (!entry || entry.messages.length === 0) return;
    const messages = entry.messages.filter((m) =>
      cache.messageBelongsToChannel(m, wsId, channelId),
    );
    if (messages.length === 0) return;
    if (channelState.activeId === channelId && channelState.loading) {
      channelState.setMessages(messages);
      channelState.loading = false;
    }
  })();
  return null;
}

const viewerIsMe = (id: string | null | undefined): boolean => isMe(id, authState.user);

function coldPeekDm(peerId: string): import("../core/types.js").Message[] | null {
  if (!cacheOn() || !workspaceState.current) return null;
  const wsId = workspaceState.current.id;
  const key = dmKey(wsId, peerId);
  const hit = cache.memGet(key);
  const hitMessages = hit?.messages.filter((m) =>
    cache.messageBelongsToDm(m, wsId, peerId, viewerIsMe),
  );
  if (hitMessages && hitMessages.length > 0) return hitMessages;
  void (async () => {
    const entry = await cache.readCache(key, key);
    if (!entry || entry.messages.length === 0) return;
    const messages = entry.messages.filter((m) =>
      cache.messageBelongsToDm(m, wsId, peerId, viewerIsMe),
    );
    if (messages.length === 0) return;
    if (dmState.activePeerId === peerId && dmState.loading) {
      dmState.mergeServer(messages);
      dmState.loading = false;
    }
  })();
  return null;
}

// ─── Delta open (CYBORG-988) ────────────────────────────────────────────────
//
// The cache painted instantly but the open still refetched the whole tail, so
// the skeleton was saved and the bandwidth was not. These two helpers read the
// LIVE inputs (the L1 window we just painted, the scope's sync mark, the unread
// signals) and hand them to the pure decision in cache/delta.ts — the same
// split as cacheOn()/decideCacheEnabled. `tail` means "behave exactly as before".
//
// Both unread signals are consulted for a channel and both must be quiet: the RED
// badge (notificationState — MENTIONS only for a channel) and the BOLD flag
// (unreadFlagState — ANY unread message). A channel with an unread block belongs
// to CYBORG-767's around-window read, which loads context ABOVE the divider; a
// delta would splice the unread head onto the cached window with no such context.
// Call these BEFORE the open path clears those signals.
function planChannelOpen(
  channelId: string,
  cached: import("../core/types.js").Message[] | null,
): cache.OpenFetchPlan {
  const wsId = workspaceState.channelWorkspaceId(channelId);
  if (!cacheOn() || !wsId || !cached) return { kind: "tail" };
  const hasUnread = channelHasUnread(wsId, channelId);
  return cache.planOpen({
    cached,
    syncedAt: cache.scopeSyncedAt(channelKey(wsId, channelId)),
    hasUnread,
    now: Date.now(),
  });
}

// The RED badge (mentions) or the BOLD flag (any unread); the open clears both.
function channelHasUnread(wsId: string, channelId: string): boolean {
  return (
    notificationState.getCount(wsId, channelId) > 0 || unreadFlagState.isUnread(wsId, channelId)
  );
}

// Cold paint for a channel open. With unreads the cached window lacks the very rows
// that made it unread and the read replaces it, so painting it would hand the open
// (and its scroll) to a window about to vanish: that open waits for the read.
function coldPaintChannel(
  channelId: string,
  hasUnread: boolean,
): import("../core/types.js").Message[] | null {
  const cached = hasUnread ? null : coldPeekChannel(channelId); // null: coldPeekChannel's "no window"
  if (cached) {
    channelState.setMessages(cached);
    channelState.loading = false;
  } else {
    channelState.loading = true;
    channelState.messages = [];
  }
  // My unsent rows show at once unless the open waits for the unread read, which reapplies them.
  if (!hasUnread) reapplyOutboxBubbles("channel", channelId);
  return cached;
}

// DM twin. DMs are not tracked in the bold-flag store (their badge bolds), so the
// per-DM message count IS the unread signal — the same one selectDm already uses
// to decide between the around-window and the tail read.
function planDmOpen(
  peerId: string,
  cached: import("../core/types.js").Message[] | null,
): cache.OpenFetchPlan {
  const wsId = workspaceState.current?.id;
  if (!cacheOn() || !wsId || !cached) return { kind: "tail" };
  return cache.planOpen({
    cached,
    syncedAt: cache.scopeSyncedAt(dmKey(wsId, peerId)),
    hasUnread: notificationState.getCount(wsId, peerId) > 0,
    now: Date.now(),
  });
}

// Run a planned CHANNEL read through the one open-read policy (cache.fetchPlannedPage:
// around-target, then delta-unless-gap, then around-the-frozen-cursor or the tail).
//
// Deliberately ONE function for both entry seams (selectWorkspace's default-channel
// bootstrap and selectChannel): CYBORG-755 exists because those two drifted apart
// once already, and the read coalescer collapses them to a single wire request only
// while their params match exactly.
async function fetchChannelForPlan(
  wsId: string,
  channelId: string,
  plan: cache.OpenFetchPlan,
  frozen: number | null,
  aroundId?: string,
): Promise<{ page: Awaited<ReturnType<typeof client.fetchMessages>>; usedDelta: boolean }> {
  return cache.fetchPlannedPage(plan, (o) => client.fetchMessages(wsId, channelId, o), {
    frozen,
    aroundId,
  });
}

/** DM twin of fetchChannelForPlan. Same plan, same gap fallback, same around-target read. */
async function fetchDmForPlan(
  wsId: string,
  peerId: string,
  plan: cache.OpenFetchPlan,
  frozen: number | null,
  aroundId?: string,
): Promise<{ page: Awaited<ReturnType<typeof client.fetchDmMessages>>; usedDelta: boolean }> {
  return cache.fetchPlannedPage(plan, (o) => client.fetchDmMessages(wsId, peerId, o), {
    frozen,
    aroundId,
  });
}

/**
 * Where a conversation open should land (knowledge/117). `focusMessageId` is the
 * alert's / permalink's TIMELINE target: the open reads the window around it and
 * says so when it is gone. A reply targeted inside a thread is not passed here —
 * the thread pane owns that jump.
 */
export interface SelectConversationOptions {
  focusMessageId?: string;
}
// The default for a plain open. A named constant, not `= {}` in the signature: the
// source-scan tests read a function's body by brace matching from its signature.
const NO_FOCUS: SelectConversationOptions = {};

// The around read came back without the target: it is deleted or unreadable.
// Say it (settle → toast) instead of leaving the focus loop to walk the tail.
function reportMissingFocusTarget(
  focusMessageId: string | undefined,
  messages: readonly import("../core/types.js").Message[],
): void {
  if (!focusMessageId) return;
  if (messages.some((m) => m.id === focusMessageId)) return;
  messageFocusState.settleTimeline(false);
}

// Splice a delta page into the open channel pane. A UNION by id, never a replace:
// the pane already holds the cached window and the delta is only what came after
// it, so `setMessages` here would throw the cold paint away. `addMessage` has no
// channel guard of its own (unlike `setMessages`), so the belt-and-braces scope
// filter both cold-paint sites use lives here too.
function applyChannelDelta(
  wsId: string,
  channelId: string,
  messages: import("../core/types.js").Message[],
): void {
  for (const m of hydrateMessages(messages)) {
    if (cache.messageBelongsToChannel(m, wsId, channelId)) channelState.addMessage(m);
  }
}

// Older-history flag for a delta open — shared with the agent-DM surface, which reads
// back the same `getRecent(scope, 50)` window. See cache/delta.ts deltaHasMore.
// The scope's recorded older-edge answer rides along: a short window from an
// open-at-unread is NOT the start of the conversation (CYBORG-1194).
function deltaHasMore(
  scopeKey: string,
  cached: import("../core/types.js").Message[] | null,
): boolean {
  return cache.deltaHasMore(cached, cache.scopeOlderHistory(scopeKey));
}

// Writeback after a successful fetch — persists the authoritative window +
// stamps the read cursor (Caveat #29). Fire-and-forget.
//
// `fromFullRead` (CYBORG-992) is `!usedDelta` at every seam: it decides whether
// this write may re-stamp the scope's sync mark. Only a full read re-reads the
// last ~50 rows, so only a full read may refresh the clock the delta backstop
// measures — see cache/delta.ts markScopeSynced.
//
// `aroundTarget` (knowledge/117): a window read around an alert's message is not
// the tail — it must never become the next cold paint, so it is not written.
function cacheWriteChannel(
  wsId: string,
  channelId: string,
  fromFullRead: boolean,
  aroundTarget?: string,
): void {
  if (!cacheOn() || aroundTarget) return;
  const key = channelKey(wsId, channelId);
  void cache.writeScopeMessages(
    key,
    key,
    channelState.messages,
    readCursorState.getChannel(channelId),
    fromFullRead,
    channelState.hasMore,
  );
}
function cacheWriteDm(
  wsId: string,
  peerId: string,
  fromFullRead: boolean,
  aroundTarget?: string,
): void {
  if (!cacheOn() || aroundTarget) return;
  const key = dmKey(wsId, peerId);
  void cache.writeScopeMessages(
    key,
    key,
    dmState.messages,
    readCursorState.getDm(peerId),
    fromFullRead,
    dmState.hasMore,
  );
}

// Resolve the scope key for a live message (used by WS-mutation writeback). A
// channel message → channel scope; a DM → dm scope keyed by the peer.
function scopeForMessage(msg: import("../core/types.js").Message): string | null {
  const wsId = msg.workspaceId ?? workspaceState.current?.id;
  if (!wsId) return null;
  if (msg.channelId) return channelKey(wsId, msg.channelId);
  const me = authState.user;
  if (isMe(msg.fromId, me) || isMe(msg.toId, me)) {
    const peerId = isMe(msg.fromId, me) ? msg.toId : msg.fromId;
    // The erased mask would pool every erased person under one dm:__erased__ scope: never cache them there.
    if (peerId && peerId !== "__erased__") return dmKey(wsId, peerId);
  }
  return null;
}

// Upsert a live channel/DM message into the cache so cold revisits are fresh.
// Skips optimistic local rows (the real echo is cached when it arrives) and any
// message we can't scope. Fire-and-forget; no-op when the cache is off.
function cacheSyncIncoming(msg: import("../core/types.js").Message): void {
  if (!cacheOn()) return;
  if (msg.id.startsWith("local-")) return;
  const scopeKey = scopeForMessage(msg);
  if (!scopeKey) return;
  void cache.cacheUpsertMessage(scopeKey, msg);
}

// Re-exported from ./client.js (the singleton lives there to break the
// app.svelte.ts and web-push import cycle). Consumers that import `client` from
// this module are unaffected.
export { client };

// Wire core events
let hasConnectedBefore = false;

// Outbox: messages whose send threw because the socket was down (e.g. mid
// deploy) or open-but-unauthenticated. We keep the optimistic bubble on screen
// and replay the send once the socket is back+authed, instead of silently
// rolling it back. The broadcast echo reconciles the optimistic message (by
// clientMsgId, #501), so a single replay doesn't duplicate.
//
// Each entry is keyed by its clientMsgId AND mirrored to IndexedDB (#502) so an
// undelivered send survives a reload: on startup the queue is rebuilt from the
// persisted records (rehydrateOutbox), and each record is deleted once its echo
// reconciles the bubble (dequeueOutbox). The closure can't be persisted, so it's
// always derived from the serializable record via recordToCommand.
interface SendQueueEntry {
  clientMsgId: string;
  createdAt: number;
  resend: () => void;
  // Where the bubble lives: its surface and channel or peer (a thread reply has none and no watchdog).
  surface?: SendSurface;
  targetId?: string;
}
const sendOutbox: SendQueueEntry[] = [];

function queueEntryOf(rec: cache.OutboxRecord): SendQueueEntry {
  return {
    clientMsgId: rec.clientMsgId,
    createdAt: rec.createdAt,
    resend: outboxResend(rec),
    ...(rec.parentId ? {} : { surface: rec.kind, targetId: rec.targetId }),
  };
}

// The id this server issued me on the PREVIOUS login, when it just re-keyed me
// onto a different one (#2301, migrateRekeyedIdentity). Set on connect, consumed
// once by rehydrateOutbox to adopt the sends queued under it. Null the rest of
// the time — which is every login while the mint flag is off.
let rekeyedFromUserId: string | null = null;

// Build the replay closure for a record (pure mapping → the right client call).
function outboxResend(rec: cache.OutboxRecord): () => void {
  const cmd = cache.recordToCommand(rec);
  return cmd.kind === "dm"
    ? () => client.sendDm(...cmd.args)
    : () => client.sendMessage(...cmd.args);
}

// Every send is recorded until its echo, so closing the app mid-send replays it on reopen
// (same clientMsgId: the relay answers a copy it already stored instead of posting it twice).
function sendThroughOutbox(rec: cache.OutboxRecord, send: () => void): void {
  void cache.putOutbox(rec);
  try {
    // An open-but-unauthenticated socket would accept the raw send and have the
    // relay silently drop it — treat it as a failure so it's queued, not lost.
    if (!client.authenticated) throw new Error("not authenticated");
    send();
  } catch {
    // intentional: the send is queued for replay on reconnect (the record is already stored).
    enqueueOutbox(rec);
  }
}

// Queue a send for replay: persist it (survives reload) + push its closure.
// Skips a duplicate clientMsgId already queued (a retry re-uses the same id).
function enqueueOutbox(rec: cache.OutboxRecord): void {
  void cache.putOutbox(rec);
  if (sendOutbox.some((e) => e.clientMsgId === rec.clientMsgId)) return;
  sendOutbox.push(queueEntryOf(rec));
}

// The send was delivered (its echo reconciled the optimistic row) — drop the
// persisted record so a future reload doesn't re-send it, and clear any
// still-pending in-memory replay for the same id.
function dequeueOutbox(clientMsgId: string | null | undefined): void {
  if (!clientMsgId) return;
  dropOutboxReplay(clientMsgId);
  forgetOutboxBubble(clientMsgId);
}

function dropOutboxReplay(clientMsgId: string): void {
  void cache.deleteOutbox(clientMsgId);
  const idx = sendOutbox.findIndex((e) => e.clientMsgId === clientMsgId);
  if (idx >= 0) sendOutbox.splice(idx, 1);
}

// A replay is watched afresh, on the queue entry's surface: no echo on a live socket is a fault.
function rearmReplayedSend(entry: SendQueueEntry): void {
  const localId = cache.localIdFor(entry.clientMsgId);
  const watch = watchOf(localId);
  const surface = watch?.surface ?? entry.surface;
  const targetId = watch?.targetId ?? entry.targetId;
  if (!surface || !targetId) return;
  setBubbleStatus(surface, localId, "pending");
  armSendWatchdog(localId, surface, targetId);
}

function flushSendOutbox(): void {
  if (sendOutbox.length === 0) return;
  const pending = sendOutbox.splice(0, sendOutbox.length);
  const now = Date.now();
  for (const entry of pending) {
    // Hours old is not a pending send (iOS replayed a 3-day-old message, 2026-09-07):
    // show it failed with Retry instead of posting it as new.
    if (cache.isOutboxRecordStale(entry, now)) {
      reportUiFailure("chat.send_aged_out", new Error("queued send aged out before replay"), {
        clientMsgId: entry.clientMsgId,
      });
      failSendByClientMsgId(entry.clientMsgId);
      continue;
    }
    try {
      // The record stays until the echo (settleOwnEcho) or a refusal drops it: a socket
      // that took the frame is not a server that stored it. A replay is deduped by id.
      entry.resend();
      rearmReplayedSend(entry);
    } catch {
      // Still down — re-queue (keep the persisted record) so the next connect
      // (or reload) retries; the record stays in IDB until it's handed off.
      sendOutbox.push(entry);
    }
  }
}

function reapplyOutboxBubbles(kind: SendSurface, targetId: string): void {
  reapplyKeptBubbles(kind, targetId, (clientMsgId) => settleOwnEcho(true, clientMsgId));
}

// The name to stamp on MY OWN optimistic rows (message bubbles, thread replies,
// reactions) and to match @mentions of me against.
//
// Resolves through my PER-WORKSPACE display name (0121) first, exactly like the
// server does when it stamps `fromName` on the persisted row (pg.getWorkspaceUser →
// COALESCE(memberships.display_name, users.name)). Reading the ACCOUNT name here
// instead meant my optimistic bubble showed one name and the server echo that
// replaced it showed another — the same global-vs-workspace divergence as the
// avatar. Mention matching uses it too: in THIS workspace, people type the name I
// chose HERE.
function myDisplayName(fallback = "You"): string {
  return authState.selfName ?? authState.user?.email ?? fallback;
}

// Cold-start rehydration (#502): rebuild the optimistic bubbles + replay queue
// from persisted outbox records for THIS user. Called right after auth, before
// flushSendOutbox runs on the live socket. Re-adds each queued send's bubble (as
// "pending") to its channel/DM so a reload re-shows it — both immediately if that
// surface is already open AND via reapplyOutboxBubbles after its history fetch.
// The same clientMsgId is preserved so the echo reconciles the exact row.
async function rehydrateOutbox(userId: string): Promise<void> {
  // #2301: this server re-keyed me onto a new id, so sends queued under my old
  // one match nothing below and would silently vanish — never replayed, never
  // shown as failed, never deleted. Re-stamp them onto the new id first. The
  // record key is the clientMsgId, so `put` overwrites IN PLACE (no duplicate,
  // no orphan) and the eventual echo still reconciles the original bubble.
  const adoptFrom = rekeyedFromUserId;
  rekeyedFromUserId = null;
  if (adoptFrom && adoptFrom !== userId) {
    for (const rec of await cache.getOutboxForUser(adoptFrom)) {
      await cache.putOutbox({ ...rec, userId });
    }
  }
  const records = await cache.getOutboxForUser(userId);
  if (records.length === 0) return;
  const myName = myDisplayName();
  for (const rec of records) {
    // Thread reply (#502): it must NOT re-appear in the channel/DM timeline —
    // thread replies are never in the main list, and the thread panel is closed
    // on a cold reload, so there is no surface to re-show an optimistic bubble in.
    // Rebuild ONLY the replay queue so it still delivers after reconnect; on the
    // next open the thread re-fetches it from the server. No bubble, no watchdog.
    if (rec.parentId) {
      if (!sendOutbox.some((e) => e.clientMsgId === rec.clientMsgId)) {
        sendOutbox.push(queueEntryOf(rec));
      }
      continue;
    }
    const msg = cache.recordToOptimisticMessage(rec, myName);
    // Remember it so the owning surface re-shows it after its fetch (setMessages
    // replaces the list, so a one-shot add now can be clobbered by a later load).
    // A second run in one session must not add a stale "pending" copy beside the kept one.
    keepBubble(rec.kind, rec.targetId, msg);
    // If that surface is already open, show it immediately too.
    if (rec.kind === "channel" && rec.targetId === channelState.activeId) {
      channelState.addMessage(msg);
    } else if (rec.kind === "dm" && rec.targetId === dmState.activePeerId) {
      dmState.addMessage(msg);
    }
    // Always rebuild the replay queue (dedup by clientMsgId) so the pending send
    // is retried regardless of which surface is currently open.
    let entry = sendOutbox.find((e) => e.clientMsgId === rec.clientMsgId);
    if (!entry) {
      entry = queueEntryOf(rec);
      sendOutbox.push(entry);
    }
    // Replayed, so pending and watched: a kept "failed" copy must not offer Retry meanwhile.
    rearmReplayedSend(entry);
  }
}

// The sentence to show beside Retry. PromptNotSentError's constructor already
// lifts its cause's message, and an error REPLY is the relay's own error, so the
// message is the reason in both cases. Undefined when there is nothing legible to
// say, which renders exactly as this indicator did before.
function sendFailureReason(err: unknown): string | undefined {
  if (err instanceof Error) return err.message.trim() || undefined;
  if (typeof err === "string") return err.trim() || undefined;
  return undefined;
}

async function persistAgentPrompt(
  workspaceId: string,
  agentId: string,
  clientMessageId: string,
  text: string,
  attachments?: Attachment[],
  pageCard?: PageCard,
  workSessionId?: string,
): Promise<void> {
  const owner = authState.user?.email.trim().toLowerCase();
  const sessionRevision = authState.sessionRevision;
  try {
    if (!owner) throw new Error("Sign in before sending this message");
    // SNAPSHOT, not the live objects. `attachments` and `pageCard` come straight
    // off the composer, which holds them in `$state` — so they arrive here as
    // reactive PROXIES, and a proxy has internal slots that the structured clone
    // algorithm refuses. IndexedDB runs exactly that algorithm inside
    // `store.put()`, synchronously, so an agent prompt carrying an attachment
    // died right here with DataCloneError — before it was ever sent. Measured in
    // a real browser, not reasoned: node's structuredClone accepts these proxies
    // and Chromium's does not, which is why the unit suite could not see it.
    // $state.snapshot leaves anything it cannot clone as-is, so a genuinely
    // unstorable record still fails the write, exactly as it should.
    await cache.putAgentPromptOutbox(
      $state.snapshot({
        clientMessageId,
        owner,
        workspaceId,
        agentId,
        text,
        attachments: attachments ?? null,
        pageCard: pageCard ?? null,
        workSessionId: workSessionId ?? null,
        createdAt: Date.now(),
      }) as cache.AgentPromptOutboxRecord,
    );
    if (authState.sessionRevision !== sessionRevision) {
      throw new Error("Account changed before the message was sent");
    }
  } catch (cause) {
    if (authState.sessionRevision !== sessionRevision) throw new PromptNotSentError(cause);
    const entry = agentStreamState
      .getEntries(agentId)
      .find((row) => row.kind === "user_message" && row.clientMessageId === clientMessageId);
    if (entry)
      agentStreamState.setSendStatus(agentId, entry.id, "failed", sendFailureReason(cause));
    throw new PromptNotSentError(cause);
  }
}

// A send older than a minute whose receipt says it never landed (most old rows did land).
const PROMPT_UNCONFIRMED_AFTER_MS = 60_000;

function reportPromptUnconfirmed(workspaceId: string, record: cache.AgentPromptOutboxRecord): void {
  const age = Date.now() - record.createdAt;
  if (age <= PROMPT_UNCONFIRMED_AFTER_MS) return;
  agentStreamState.invariants.report("prompt_unconfirmed", {
    agentId: record.agentId,
    workspaceId,
    clientMessageId: record.clientMessageId,
    ageBucket: ageBucket(age),
    hasAttempt: Boolean(record.promptAttemptId),
  });
}

// P03: the owning daemon's admission ledger answers "did it land?" for a prompt this
// reload never saw echoed. Without it the bubble waits out the watchdog and then
// offers Retry on a message the agent may already have answered — the Floema report.
async function settleAgentPromptFromReceipt(
  workspaceId: string,
  record: cache.AgentPromptOutboxRecord,
  entryId: string,
  sessionRevision: number,
): Promise<void> {
  try {
    const receipt = await client.fetchAgentPromptReceipt(
      workspaceId,
      record.agentId,
      record.clientMessageId,
      record.promptAttemptId,
      record.workSessionId ?? undefined,
    );
    if (authState.sessionRevision !== sessionRevision) return;
    // No row: the daemon never admitted it (closed mid-send). Resend it under the same
    // id; the relay's DM row and the daemon's ledger both dedupe a copy that did land.
    if (!receipt) {
      reportPromptUnconfirmed(workspaceId, record);
      agentStreamState.setSendStatus(record.agentId, entryId, "unknown");
      retryAgentPrompt(record.agentId, entryId);
      return;
    }
    // Rejected is the one affirmative "it did not reach the model". An attempt still
    // `reserved` when the daemon recorded it abandoned never got there either: the
    // process that held its claim is gone.
    const undelivered =
      receipt.admission === "rejected" ||
      (receipt.admission === "reserved" && receipt.abandonedAt !== null);
    // A held one too: the receipt cannot tell a removal from an expiry or a full lane, so it is
    // never dropped; failed leaves the bar and shows the bubble with Retry (setSendStatus).
    if (undelivered) {
      reportPromptUnconfirmed(workspaceId, record);
      agentStreamState.setSendStatus(record.agentId, entryId, "failed");
      return;
    }
    agentStreamState.markSent(record.agentId, entryId);
    void cache.deleteAgentPromptOutbox(record.clientMessageId);
  } catch (err) {
    // The prompt is unaffected; the bubble keeps the pending state the reload gave
    // it. Beaconed because a receipt read that always fails is invisible otherwise.
    reportUiFailure("settleAgentPromptFromReceipt", err, {
      workspaceId,
      agentId: record.agentId,
      clientMessageId: record.clientMessageId,
    });
  }
}

async function rehydrateAgentPrompts(workspaceId: string): Promise<void> {
  const owner = authState.user?.email.trim().toLowerCase();
  const sessionRevision = authState.sessionRevision;
  if (!owner) return;
  const records = await cache.getAgentPromptOutbox(owner, workspaceId);
  if (activeWorkspaceSelection !== workspaceId || authState.sessionRevision !== sessionRevision)
    return;
  for (const record of records) {
    agentStreamState.bindAgentWorkspace(record.agentId, workspaceId);
    if (record.promptAttemptId)
      latestPromptAttempt.set(record.clientMessageId, record.promptAttemptId);
    if (
      agentStreamState
        .getEntries(record.agentId)
        .some(
          (entry) =>
            entry.kind === "user_message" && entry.clientMessageId === record.clientMessageId,
        )
    ) {
      continue;
    }
    // A confirmed row drops its clientMessageId, so the check above misses it.
    if (agentStreamState.isPromptServerConfirmed(record.clientMessageId)) {
      void cache.deleteAgentPromptOutbox(record.clientMessageId);
      continue;
    }
    // Delivery unknown, not failed: the owning daemon's page settles it (Floema,
    // 2026-09-08 — a reload showed Retry on a prompt the agent had already answered).
    const entryId = agentStreamState.addUserMessage(
      record.agentId,
      record.text,
      (record.pageCard as PageCard | null) ?? undefined,
      "pending",
      (record.attachments as Attachment[] | null) ?? undefined,
      record.clientMessageId,
      record.workSessionId ?? undefined,
      true,
      record.createdAt,
      record.queued,
    );
    // With or without an attempt: the daemon then answers by the prompt's own id from its
    // durable timeline, so an old row settles by identity instead of reading "unconfirmed".
    void settleAgentPromptFromReceipt(workspaceId, record, entryId, sessionRevision);
  }
}

// CYBORG-87: silent token self-heal. Given the guest WS url the client connected
// to, trade the stored refresh token for a fresh access token at
// POST {origin}/api/auth/refresh. Self-hosted-safe — the origin is derived from
// the url we actually connected to, never a hardcoded prod relay. The ws client's
// close-4003 handler drives the exactly-once retry + transparent reconnect.
client.reauth = (wsUrl: string) => refreshAccessToken(wsUrlToHttpBase(wsUrl));

// The silent refresh could not heal a rejected token (no stored refresh token,
// or the refresh token itself is dead) → surface an actionable re-login instead
// of an infinite reconnect loop / spinner. Mirror the handleReconnect
// unauthorized path: disconnectFromServer() clears the saved session (+ refresh
// token); only navigate when not already on /login (avoid focus-stealing
// remounts mid-keystroke).
client.on("auth_expired", ({ reason, cause }) => {
  // NO clearQueuedSends: a dead refresh token is not a sign-out request. The
  // persisted outbox stays so anything composed offline still sends after the
  // user re-authenticates (rehydrateOutbox → flushSendOutbox on the next
  // authenticate()); getOutboxForUser's userId filter keeps it invisible to a
  // different account that signs in first.
  // Carry WHY the session ended through to the login screen. Anything that is not
  // a known terminal-identity close is an expiry as far as the user is concerned
  // (a 4003 reports the literal "unauthorized", which explains nothing), so it
  // collapses to "expired" — the login page owns the wording.
  const ended = reason && TERMINAL_CLOSE_REASONS.has(reason) ? reason : "expired";
  noteSessionEnded(ended); // before the disconnect: the workspace guard's redirect reads it
  disconnectFromServer();
  reportForcedLogout(cause ?? "rejected", ended);
  if (typeof window === "undefined" || window.location.pathname !== "/login") {
    void goto(`/login?ended=${encodeURIComponent(ended)}`);
  }
});

// Desktop cold-start trace t0: "navigate" on a launch, "reload" on Cmd+R.
coldStartTrace("boot", {
  nav: (
    globalThis.performance?.getEntriesByType?.("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined
  )?.type,
});

client.on("auth_state", ({ authenticated }) => {
  connectionState.authenticated = authenticated;
  if (!authenticated) markSendWatchdogsDropped();
  if (authenticated) client.reportMyTimeZone?.(); // W1-G4: my agents' clock follows me; ?.: test fakes
});

client.on("connection", ({ status, attempt, delayMs }) => {
  coldStartTrace("relay-ws", { status, attempt });
  connectionState.status = status;
  if (status !== "connected") {
    turnDeltas.taintAll();
    markSendWatchdogsDropped();
  }
  // Surface reconnect progress in the banner ("attempt N · retrying in Ns").
  // Present only on a "reconnecting" emit; reset on a successful connection so a
  // future drop starts clean.
  if (status === "reconnecting") {
    if (typeof attempt === "number") connectionState.reconnectAttempt = attempt;
    if (typeof delayMs === "number") connectionState.reconnectDelayMs = delayMs;
  } else if (status === "connected") {
    connectionState.reconnectAttempt = 0;
    connectionState.reconnectDelayMs = 0;
    retryFailedRereads();
  }
  // Mark the first-ever successful connect so the ConnectionStatus banner only
  // appears for REAL drops afterward — never during the cold-start restore
  // (which the splash already covers).
  if (status === "connected") connectionState.hasConnectedOnce = true;

  // CYBORG-988: the delta open is only sound while the socket has been unbroken
  // since a scope's last successful fetch — that is what makes every edit,
  // delete, pin and reaction on the cached rows provably already applied. The
  // moment the socket is not connected that premise is gone for EVERY scope (the
  // reconnect burst only heals the ACTIVE conversation), so drop all the marks
  // and let each next open read the tail exactly as it did before this change.
  //
  // CYBORG-991: on EVERY connection event, `connected` included — not only the
  // ones that announce a loss. Two paths replace the socket without ever emitting
  // a non-connected status: a `4003` silent re-auth (the close handler returns
  // before scheduleReconnect and runReauth calls doConnect directly) and
  // connect() (endpoint switch / re-login, which nulls the socket synchronously
  // so the old one's close early-returns). Both still emit `connected` from the
  // new socket's open handler, so gating on `status !== "connected"` kept every
  // mark alive across exactly the boundary they assert cannot exist — and a delta
  // recovers rows AFTER the anchor, never a mutation to a row already cached. The
  // endpoint switch is worse still: cacheInit is keyed on email and deliberately
  // survives an endpoint change, so a cloud-relay anchor id can be handed to a
  // backend that has never seen it, and an unknown `after` id answers [] ("nothing
  // new"). Unconditional is also the cheap direction: a mark dropped needlessly on
  // the connect that follows a real disconnect costs one tail fetch, and this runs
  // BEFORE handleReconnect below, so the burst re-stamps what it actually refetches.
  // CYBORG-1210: suspended, not dropped: the reconnect change feed resumes or invalidates them.
  // A first connect has no burst and no marks, so it just clears.
  if (status === "connected" && !hasConnectedBefore) cache.invalidateSyncedScopes();
  else cache.suspendSyncedScopes();
  // Same reason for the subagent transcript backfill (Codex S1): a connection change makes
  // "already attempted" stale, so a backfill that failed while disconnected can retry.
  attemptedSubagentTranscriptFetches.clear();
  // Relay capabilities are per-connection: reset fail-closed and re-derive (Codex S3), so a
  // reconnect to a different relay never keeps a stale answer.
  resetRelayCapabilitiesForConnection();

  if (status === "connected") {
    // The relay is back — clear the deploy banner. The outbox is NOT flushed
    // here: a freshly-opened socket is not yet authenticated (cyborg:auth runs
    // afterward in handleReconnect/connectToServer), and unauthenticated sends
    // are silently dropped by the relay. Flushing happens right after auth
    // succeeds, so replayed messages aren't lost.
    connectionState.deploying = false;
  }
  if (status === "connected" && hasConnectedBefore) {
    handleReconnect();
  } else if (status === "connected") {
    // FIRST connect of this page load. handleReconnect (and with it the DM
    // refill) is gated on hasConnectedBefore to avoid double-running the burst
    // against connectToServer's own cold-start init — so on a reload the DM pane
    // had NO recovery path at all: selectDm latches on activePeerId, so once its
    // 3 attempts failed the pane stayed empty until an UNRELATED later socket
    // drop happened to fire a reconnect. That wait — not any query — is the
    // "messages come after a few minutes". #2452 made that recovery path faster
    // (one shared readiness deadline, DM hoisted ahead of drainSync) but left it
    // unreachable here, which is why the report survived the fix.
    //
    // Only the DM refill runs, not the whole burst, so nothing double-inits.
    // Awaiting auth is handled downstream: catchUpDm's RPCs park in
    // client.waitForAuth until cyborg:auth completes on this socket. Idempotent
    // with a concurrent selectDm — request() coalesces identical in-flight reads
    // and mergeServer dedups by id.
    const wsId = workspaceState.current?.id;
    if (wsId) void refillActiveDm(wsId);
  }
  if (status === "connected") hasConnectedBefore = true;
});

// A hole in the per-workspace broadcast sequence was detected on an OPEN socket
// (#499) — events lost between relay instances / on a flaky link without the
// socket dropping. Catch up immediately (onSeqGap is coalesced + rate-limited).
// CYBORG-1205: the sync cursor is the workspace's, moved by drains AND by every live
// row that is the very next seq, so a reconnect never re-reads what the socket delivered.
const workspaceSyncCursor = new WorkspaceSyncCursor();
const syncCursorSaveTimers = new Map<string, ReturnType<typeof setTimeout>>();
const SYNC_CURSOR_SAVE_DELAY_MS = 5_000;
function scheduleSyncCursorSave(wsId: string): void {
  if (syncCursorSaveTimers.has(wsId)) return;
  syncCursorSaveTimers.set(
    wsId,
    setTimeout(() => {
      syncCursorSaveTimers.delete(wsId);
      void cacheSnapshot.saveDrainSeq(wsId, workspaceSyncCursor.get(wsId), cacheViewer());
    }, SYNC_CURSOR_SAVE_DELAY_MS),
  );
}
client.on("broadcast_seq", ({ workspaceId, seq }) => {
  if (workspaceSyncCursor.observeLive(workspaceId, seq)) scheduleSyncCursorSave(workspaceId);
});

client.on("seq_gap", ({ workspaceId, fromSeq, toSeq }) => {
  // CYBORG-988/1205: the marks are suspended until a drain reads across the hole, then
  // only the scopes of rows found in it lose theirs (drainSync -> settleSeqHoles).
  cache.noteSeqHole(workspaceId, fromSeq, toSeq);
  void onSeqGap(workspaceId);
});

// The relay announced a planned restart (deploy). Show a friendly "Updating…"
// state so the imminent socket drop reads as maintenance, not an error, and the
// reconnect that follows is expected.
client.on("server_shutdown", () => {
  connectionState.deploying = true;
  connectionState.status = "reconnecting";
});

// Apply one sync page's messages to the active channel. UNION ONLY — never a
// replace, whatever `mode` the relay labels the page with.
//
// `cyborg:sync` answers `lastSeq === 0` with mode "snapshot", and this used to
// take that as licence to `setMessages()` — a FULL REPLACE of the pane. But a
// sync page is not a window of this channel: it is `getMessagesSince(workspace,
// seq > cursor)` ordered by seq ASC and capped at 500, i.e. the workspace's
// OLDEST 500 rows across every channel and DM. Intersected with the open
// channel that is an arbitrary OLDEST PREFIX — usually none at all for a
// channel created after the workspace's first 500 messages. So the pane was
// replaced with a truncated prefix, or emptied outright, while the full history
// sat on the server untouched: you restart the app and a months-long thread
// renders as the two messages that arrived since.
//
// Nothing recovers it reliably. The later pages that would re-add the rest can
// throw (drainSync has no per-page catch — runReconnectBurst's outer catch
// swallows it and skips everything after, including the self-heal), the drain is
// capped at 50 pages, and `getMessagesSince` filters `seq > sinceSeq` so the
// large share of rows persisted with seq 0 can never come back through sync at
// all. The one repair — runReconnectBurst's re-fetch — is guarded on
// `messages.length === 0`, so a SHORT window is not repaired, only an empty one
// (the same "empty was handled, short was not" shape as #2371 on the agent
// timeline). Fix it at the seam instead: this is the only place a sync page
// touches the pane, and a union can never lose a row.
//
// The DM twin already worked this way (catchUpDm → DmState.mergeServer, "a union
// by id, never a replace" — see the note on DmState.lastSeq); the channel path
// was the odd one out. #844's maxAppliedSeq high-water only stopped the CURSOR
// regressing, which is why a fresh session (lastSeq legitimately 0) still hit it.
function applyChannelSyncPage(messages: import("../core/types.js").Message[]): void {
  const hydrated = hydrateMessages(messages);
  const activeChannel = channelState.activeId;
  // Collapsed threads: replies belong to the thread panel, never the channel
  // timeline — exclude them from the sync delta too (not just the initial fetch),
  // so a reconnect doesn't splice replies back into the channel.
  const channelMessages = activeChannel
    ? hydrated.filter(
        (m: { channelId?: string | null; parentId?: string | null }) =>
          m.channelId === activeChannel && !m.parentId,
      )
    : [];
  for (const msg of channelMessages) channelState.addMessage(msg);
}

// Drain the workspace delta in pages — a long disconnect can exceed one page
// (500); the old single call silently dropped the rest. Bounded against an
// infinite loop if the cursor ever fails to advance.
async function drainSync(wsId: string, fromSeq: number): Promise<void> {
  // CYBORG-989: start from the highest cursor we know, not from 0. `cyborg:sync` is
  // a WORKSPACE-wide read (`getMessagesSince(workspace, seq > lastSeq)`), so the
  // workspace-wide `seq` counter is the right cursor for it — unlike the per-scope
  // `lastSyncedSeq`, which delta.ts documents as the wrong one for a per-conversation
  // open. A fresh session had `channelState.lastSeq === 0` and drained the workspace
  // from the beginning: up to 50 pages of 500 rows on every reload.
  // Gated on the SAME capability as the rest of CYBORG-989, even though the drain
  // has nothing to do with the snapshot: the flag lives on the relay, the UI ships
  // in its own bundle, and a rollback that unset the flag would otherwise leave this
  // half of the change live with no way to turn it off. One switch, whole diff.
  const persisted = client.bootstrapSupported
    ? await cacheSnapshot.loadDrainSeq(wsId, cacheViewer())
    : 0;
  workspaceSyncCursor.advanceTo(wsId, persisted);
  // CYBORG-1203: the loop lives in workspace-sync-drain.ts. It advances the cursor
  // on EVERY applied page, the last one included; the old inline loop broke on
  // `!hasMore` before taking `nextSeq`, so a one-page drain never moved the cursor
  // and every reconnect re-downloaded the same rows. A failed page still stops the
  // drain without unwinding runReconnectBurst (per-page catch inside the helper).
  // CYBORG-1205: start from the workspace cursor (drains + contiguous live rows), not
  // only the open channel's seq, which a channel switch resets to 0.
  // A host channel opened in place counts in the HOST's sequence, never in this one.
  const channelSeq =
    workspaceState.channelWorkspaceId(channelState.activeId) === wsId ? fromSeq : 0;
  const start = Math.max(channelSeq, workspaceSyncCursor.get(wsId));
  const drained: Array<{ seq?: number; scopeKey: string | null }> = [];
  const end = await drainWorkspaceSync(start, {
    fetchPage: (cursor) => client.sync(wsId, cursor),
    isCurrent: () => workspaceState.current?.id === wsId,
    applyPage: (messages) => {
      for (const m of messages) drained.push({ seq: m.seq, scopeKey: scopeForMessage(m) });
      applyChannelSyncPage(messages);
    },
    saveCursor: (cursor) => {
      workspaceSyncCursor.advanceTo(wsId, cursor);
      void cacheSnapshot.saveDrainSeq(wsId, cursor, cacheViewer());
    },
  });
  workspaceSyncCursor.advanceTo(wsId, end);
  cache.settleSeqHoles(wsId, start, end, drained);
}

// Catch the open DM up after a disconnect (#500). A populated DM forward-drains
// from its newest known seq via fetchDmSince (paginated until caught up), so a
// disconnect with >50 new DM messages reconstructs the FULL history — the old
// blind "latest 50" refetch silently lost everything older than the 50 newest.
// A cold DM (no seq cursor) or an older relay without fetch_dm_since falls back
// to the latest-page fetch (prior behavior). mergeServer dedups + reconciles
// optimistic rows, so re-running on a populated DM only splices in what we missed.
export async function catchUpDm(wsId: string, peerId: string): Promise<void> {
  const stillCurrent = () => workspaceState.current?.id === wsId && dmState.activePeerId === peerId;
  const fromSeq = dmState.lastSeq;
  if (fromSeq > 0) {
    // Drained from the last PAGE row (no live row above it): the drain reaches the tail, so it
    // moves the newer-edge cursor and closes that edge. Otherwise it skipped a gap: leave both.
    const tailRow = dmState.messages.find((m) => m.id === dmState.fetchedTailId);
    const fromPageTail = tailRow?.seq === fromSeq;
    try {
      let cursor = fromSeq;
      for (let page = 0; page < 50; page++) {
        const { messages, hasMore, nextSeq } = await client.fetchDmSince(wsId, peerId, cursor);
        if (!stillCurrent()) return;
        if (fromPageTail) dmState.mergePage(hydrateMessages(messages), peerId);
        else dmState.mergeServer(hydrateMessages(messages), peerId);
        if (fromPageTail && !hasMore) dmState.hasMoreNewer = false;
        if (!hasMore || nextSeq === undefined || nextSeq <= cursor) break;
        cursor = nextSeq;
      }
      return;
    } catch {
      // Older relay without fetch_dm_since — fall through to the latest window.
    }
  }
  try {
    const { messages, hasMore } = await client.fetchDmMessages(wsId, peerId, { limit: 50 });
    if (!stillCurrent()) return;
    dmState.mergeServer(hydrateMessages(messages), peerId);
    // Only set the older-history paging flag on the cold path — a populated DM may
    // have already paged older history that this fresh window must not discard.
    if (fromSeq === 0) dmState.hasMore = hasMore;
  } catch {
    // server may not support DM fetch yet
  }
}

// Refill the OPEN DM pane from server truth. Extracted from runReconnectBurst so
// the FIRST connect can reach it too — see the call in the `connection` handler.
//
// Runs for BOTH the empty self-heal case AND the already-populated case (a DM
// message could have arrived while the socket was a backgrounded zombie):
// mergeServer() dedups by message id and reconciles optimistic local- rows, so
// re-running it on a populated DM only splices in what we missed.
async function refillActiveDm(wsId: string): Promise<void> {
  if (!dmState.activePeerId) return;
  const peerId = dmState.activePeerId;
  const wasEmpty = dmState.messages.length === 0;
  // Show the spinner only when the DM was empty (the self-heal case); a silent
  // background reconcile must not flash a loader over messages already on
  // screen. catchUpDm seq-drains a populated DM (#500) or fetches the latest
  // window when cold.
  if (wasEmpty) dmState.loading = true;
  try {
    await catchUpDm(wsId, peerId);
    // A heal that actually filled the pane counts as loaded, so a later route
    // $effect re-run doesn't clear it (dmState.messages = []) and refetch.
    if (dmState.activePeerId === peerId && dmState.messages.length > 0) {
      loadedDmPeers.add(peerId);
    }
  } finally {
    if (wasEmpty && dmState.activePeerId === peerId) dmState.loading = false;
  }
}

// Seq-gap recovery (#499 + #639). A forward jump in the per-workspace broadcast
// seq means a message row may have been lost on an OPEN socket: drain the active
// channel + DM messages now. No panel reconcile (CYBORG-1203, see the module).
const SEQ_GAP_MIN_INTERVAL_MS = 10_000;
const seqGapState = createSeqGapState();
async function onSeqGap(wsId: string): Promise<void> {
  // A host's channel open in place (knowledge/142) is outside this workspace's drain: re-read it.
  const hostOpen = (id: string) => workspaceState.openHostChannel?.workspaceId === id;
  await recoverSeqGap(
    wsId,
    {
      isActiveWorkspace: (id) => workspaceState.current?.id === id || hostOpen(id),
      now: () => Date.now(),
      drainMessages: (id) =>
        hostOpen(id) ? loadLatestMessages() : drainSync(id, channelState.lastSeq),
      catchUpDm: async (id) => {
        if (dmState.activePeerId) await catchUpDm(id, dmState.activePeerId);
      },
    },
    seqGapState,
    SEQ_GAP_MIN_INTERVAL_MS,
  );
}

// Authoritative unread reconcile on foreground (#672). The red unread badge
// lives in PERSISTED localStorage and only self-corrects on a fetch_unread
// reseed; onForeground() previously rode that reseed on handleReconnect(), gated
// behind away ≥ 30s + a needed reconnect — so a brief background → read
// elsewhere → foreground left the stale badge. This re-fetches the authoritative
// snapshot and reseeds on EVERY foreground (the orchestration — in-flight guard,
// min-interval coalescing, no-swallow retry — lives in the testable
// unread-reconcile module; here we just wire the live deps). Scoped to
// unread/read-state only — it does NOT touch the zombie-socket reconnect (still
// away-gated below) nor the status path (#671).
const UNREAD_RECONCILE_MIN_INTERVAL_MS = 10_000;
const unreadReconcileState = createUnreadReconcileState();
// Reconnect seed for the ACTIVE workspace. The open conversation's frozen divider
// moves only when the server is ahead of the cursor this device already knew: its
// own reads are already in the local map (every markRead carries the same stamp
// it advanced locally), so that gap is a read on another device (#514).
function reseedReadCursors(u: {
  reads?: Record<string, number> | null;
  dmReads?: Record<string, number> | null;
}): void {
  const channelId = channelState.activeId;
  const peerId = dmState.activePeerId;
  const knownChannel = channelId ? readCursorState.getChannel(channelId) : null;
  const knownDm = peerId ? readCursorState.getDm(peerId) : null;
  const agentPeerId = agentDmReadState.peerId;
  const knownAgentDm = agentPeerId ? readCursorState.getDm(agentPeerId) : null;
  readCursorState.seed({ reads: u.reads, dmReads: u.dmReads });
  const channelAhead = channelId ? serverReadAhead(knownChannel, u.reads?.[channelId]) : undefined;
  if (channelAhead !== undefined) channelState.advanceUnreadCursor(channelAhead);
  const dmAhead = peerId ? serverReadAhead(knownDm, u.dmReads?.[peerId]) : undefined;
  if (dmAhead !== undefined) dmState.advanceUnreadCursor(dmAhead);
  const agentAhead = agentPeerId
    ? serverReadAhead(knownAgentDm, u.dmReads?.[agentPeerId])
    : undefined;
  if (agentPeerId && agentAhead !== undefined)
    agentDmReadState.advanceUnreadCursor(agentPeerId, agentAhead);
}

// One workspace's authoritative reseed: same body as subscribeAndSeedAllWorkspaces'
// per-workspace seed, but AWAITED and allowed to THROW so the orchestrator can
// retry on the next foreground instead of stranding the persisted-stale cache.
async function reconcileWorkspaceUnread(wsId: string): Promise<void> {
  // Captured BEFORE the request goes out — any local increment stamped after
  // this moment might not be reflected in `u` yet (see seedCounts).
  const seededAt = Date.now();
  const u = await client.fetchUnread(wsId);
  seedUnreadSignals(wsId, u, seededAt);
  // Re-freeze the divider cursors from server truth — but only for the active
  // workspace, since readCursorState is a single global map and a background
  // workspace must not clobber the open conversation's cursor.
  if (workspaceState.current?.id === wsId) reseedReadCursors(u);
  const orphans = orphanDmPeers(wsId);
  dmHealPump.heal(wsId, orphans);
  healUnknownDmMembers(
    wsId,
    humanDmCandidates(orphans, (k) => unreadIncrements.surfaceOf(wsId, k), humanDmActors()),
  );
  // NOTE: the dock/title push is intentionally NOT here. When the orchestrator
  // reconciles every workspace concurrently, a per-workspace push would emit one
  // intermediate (and possibly inconsistent) badge total per workspace. The
  // caller pushes globalTotal ONCE after all workspaces have settled.
}
async function reconcileUnreadOnForeground(): Promise<void> {
  await reconcileUnread(
    {
      listWorkspaceIds: () => memberWorkspaces(workspaceState.list).map((ws) => ws.id),
      reconcileWorkspaceUnread,
      now: () => Date.now(),
    },
    unreadReconcileState,
    UNREAD_RECONCILE_MIN_INTERVAL_MS,
  );
  // Push the freshly-reconciled total to the dock/title ONCE, after every
  // workspace has been reseeded — so the badge updates on foreground even before
  // the reactive effect re-runs, without flickering through intermediate
  // per-workspace totals.
  pushUnreadIndicators(activityState.badgeCount);
}

// Reconcile sidebar state (channels/members/agents/daemons) whose broadcasts
// were missed while disconnected. Active-channel messages are handled by sync.
// `seeded`: the bootstrap this reconnect already asked for this workspace (CYBORG-1193).
async function reconcileWorkspacePanels(
  wsId: string,
  seeded?: Promise<WorkspaceBootstrap | null>,
): Promise<void> {
  try {
    // Each pane reconcile is independent; null means "keep the current pane"
    // (guarded by the `if (channels)`… checks below), so one fetch failing leaves
    // that pane stale rather than blanking it — not a lost user action.
    // CYBORG-989: one bootstrap for the two relay-answered panes. On an unchanged
    // workspace this returns the stored snapshot and the relay runs no
    // workspace-wide query — the reconnect that used to refetch every channel and
    // every member of every workspace now confirms a version number.
    const boot = await (seeded ?? bootstrapWorkspace(wsId));
    const [channels, members, agentsRes, daemons] = await Promise.all([
      boot ? Promise.resolve(boot.channels) : client.fetchChannels(wsId).catch(() => null), // intentional: stale pane, see above
      boot
        ? Promise.resolve({ members: boot.members, external: boot.external })
        : client.listMembers(wsId).catch(() => null), // intentional: stale pane, see above
      // WithMeta: this call REPLACES the roster, so it needs the relay's
      // `pendingDaemons` partial-answer signal — see nextAgentSnapshot below.
      listRoster(wsId)
        .then((res) => rereadLateDaemons(wsId, res))
        .catch(() => null), // intentional: stale pane, see above
      client.listDaemons(wsId).catch(() => null), // intentional: stale pane, see above
    ]);
    if (workspaceState.current?.id !== wsId) return;
    if (channels) workspaceState.channels = channels;
    if (members) {
      workspaceState.members = members.members;
      workspaceState.external = members.external;
      authState.setMemberImagesFromMembers([...members.members, ...members.external]);
    }
    if (agentsRes) {
      const agents = agentsRes.agents;
      // A reconnect can land before the daemon re-attaches, so this proxied
      // list can be empty; nextAgentSnapshot keeps the last-known roster (greyed
      // rows) unless a daemon is actually reachable — see daemon-status.ts.
      // It can also come back SHORT (a daemon too slow for the relay's fan-out
      // window): `pendingDaemons` names those, and the snapshot carries their
      // rows over instead of letting a partial list wipe them.
      const daemonConnected = daemonConnStatus() === "connected";
      workspaceState.agents = nextAgentSnapshot(
        workspaceState.agents,
        agents,
        daemonConnected,
        agentsRes.pendingDaemons,
      );
      // Refresh the cold-start cache so the next launch hydrates from the freshest
      // roster (and a removed agent drops once the daemon returns the real list).
      saveRosterCache(
        rosterStore(),
        wsId,
        authState.user?.email,
        workspaceState.agents,
        daemonConnected,
      );
      // #591: a reconnect snapshot is the authoritative "what happened while I
      // was away" — seed the derived attention badges from it.
      attentionState.seedFromAgents(agents);
      // A turn whose terminal event fired while the socket was down is otherwise
      // stuck client-side forever: the resumed live stream starts at "now" and the
      // timeline catch-up rebuilds entries only — nothing clears turnStatus. Use
      // this same fresh roster (the raw fetch result, not the merged snapshot —
      // its rows are guaranteed server truth) to settle any turn the roster says
      // is over, un-sticking the composer's Queue mode and flushing the queued
      // prompt the now-idle agent can take. See reconcileTurnWithRoster.
      for (const a of agents) {
        agentStreamState.reconcileTurnWithRoster(a.agentId, a.lifecycle);
      }
    }
    if (daemons) {
      daemonStatusState.load(daemons.daemons);
      // The rows too: their lastSeenAt aged through the outage (a status would say "Stopped").
      daemonState.refreshList(daemons.daemons);
    }
    // Cron jobs and their held run histories missed their broadcasts too.
    void schedulesState.catchUp(wsId).catch((err: unknown) => {
      reportUiFailure("reconnect.schedules", err, { workspaceId: wsId });
    });
    // Archived sessions + cybos aren't reconciled by the fetches above and their
    // sidebar fetches are one-shot — refetch here so a reconnect (fresh socket)
    // refills them instead of leaving "No archived sessions" / blank cybo avatars
    // until reload.
    void fetchSessions();
    void fetchCybos();
    void seedThreadCounts(wsId);
    void seedUserStatuses(wsId);
  } catch (err) {
    // Background reconnect reconcile — a failure leaves a stale pane until the
    // next sync, not a lost user action. Reported, no toast (low-noise policy).
    reportUiFailure("reconnect.reconcile_workspace_panels", err, { workspaceId: wsId });
  }
}

// Subscribe to EVERY workspace the user belongs to (not just the active one)
// and seed each one's unread counts. Without this the socket only hears the
// current workspace, so a DM / channel message in another workspace doesn't
// badge until you switch there. Called on connect and on reconnect (a fresh
// socket starts with no subscriptions). Fire-and-forget.
// BUG #2: seed the TWO independent unread signals from server truth.
//   • RED badge (notificationState): per-channel MENTION count + per-DM message
//     count (DMs always count, so their message count IS their badge).
//   • BOLD flag (unreadFlagState): channels that have ANY unread message
//     (counts > 0). DMs are not tracked in the flag store (their badge bolds).
// Backward-compatible: older relays omit `mentionCounts`, so channel badges fall
// back to {} (no red number from a plain unread) — still correct for BUG #2.
// `mentionCounts`/`dmCounts` UNDEFINED vs `{}` carry different meanings, and a
// blind `?? {}` collapses them incorrectly. The cloud relay path
// (relay-standalone.ts handleFetchUnread) always returns BOTH fields, even when
// genuinely empty — `{}` there means "server just confirmed zero, right now".
// The daemon-direct dispatcher path (message-router.ts handleFetchUnread)
// returns NEITHER field — `{counts, reads}` only — so `undefined` there means
// "this response shape doesn't report mention/DM counts at all", not "they're
// zero". Seeding `{}` in that case would blank every pre-existing DM/mention
// badge (rail, dock, every DM row) on every daemon-direct reconcile (round 2
// finding #3). notificationState.seedMentionAndDmCounts skips the seed entirely
// when BOTH fields are undefined, instead of defaulting them to `{}`.
// `seededAt`: the caller's clock reading taken BEFORE it issued the fetchUnread
// request that produced `u` — forwarded to notificationState.seedCounts AND
// unreadFlagState.seed so a live increment/markUnread that races ahead of this
// snapshot survives the reseed instead of being wiped (see seedCounts /
// UnreadFlagState.seed for the full race explanation; round 2 finding #1 closed
// the gap where only the count path had this guard).
// `dmLastMessageAt` is the THIRD signal, and the one that makes DM ordering
// server-truth instead of device-local: peer id (or group_dm channel id) → epoch
// ms of that conversation's newest message. Without it the order is whatever
// this device happened to observe — correct on a device you have used, blank on
// a new one, and blind to everything that arrived while the app was closed.
// Same UNDEFINED vs `{}` rule as the counts above, for the same reason: the
// daemon-direct dispatcher answers `{counts, reads}` only, and a solo daemon has
// no relay to ask at all, so `undefined` means "this response shape does not
// report recency" — leave the local map alone and keep today's behaviour. A
// `?? {}` here would turn every one of those responses into a seed of nothing.
function seedUnreadSignals(
  workspaceId: string,
  u: {
    counts: Record<string, number>;
    mentionCounts?: Record<string, number>;
    dmCounts?: Record<string, number>;
    dmLastMessageAt?: Record<string, number>;
    dmHiddenAt?: Record<string, number>;
    archivedChannelIds?: string[];
  },
  seededAt: number,
): void {
  // Hidden DMs land in the same pass as the recency they are compared with. Absent = older relay.
  if (u.dmHiddenAt) dmHiddenState.seed(workspaceId, u.dmHiddenAt);
  // An unopened workspace learns its hidden channels here, so the dock total skips them.
  if (u.archivedChannelIds) archivedChannelsState.seed(u.archivedChannelIds, workspaceId);
  const prior = { ...notificationState.countsForWorkspace(workspaceId) };
  notificationState.seedMentionAndDmCounts(workspaceId, u.mentionCounts, u.dmCounts, seededAt);
  const after = notificationState.countsForWorkspace(workspaceId);
  for (const r of reseedRaises(prior, after, u, unreadRosters())) {
    recordUnreadIncrement(workspaceId, r.key, r.surfaceKind, "reseed", r.count, r.keyKind);
  }
  const unreadChannelIds = Object.entries(u.counts)
    .filter(([, n]) => n > 0)
    .map(([id]) => id);
  unreadFlagState.seed(workspaceId, unreadChannelIds, seededAt, u.counts);
  if (u.dmLastMessageAt) {
    // BEFORE the seed, and the reason is the DM list's visible membership, not
    // its order: openedDmsState.seedFromActivity (connectToServer) promotes every
    // peer with recorded activity into the visible list the first time it sees a
    // workspace, and it never sees one that has no stored entry — so on the next
    // boot it would read this server-seeded recency as opened history and add a
    // row for everyone who has messaged the user in 90 days. markSeeded stores the
    // (empty) entry that retires the migration for this workspace.
    openedDmsState.markSeeded(workspaceId);
    dmActivityState.seedActivity(workspaceId, u.dmLastMessageAt, seededAt);
  }
}

// CYBORG-989 — ONE round trip for a workspace open, when the relay serves it.
//
// The shape this replaces is the expensive part, not just the count: opening a
// workspace fired ~20 RPCs, and three of them (subscribe, notification prefs,
// unread) repeated PER WORKSPACE the viewer belongs to on EVERY reconnect. This
// collapses fetch_channels + list_members + fetch_unread + fetch_notification_prefs
// + fetch_drafts into `cyborg:bootstrap`, and — the point — a workspace whose
// version has not moved comes back with NO channels, members or sessions at all
// and costs the relay no workspace-wide query.
//
// Returns null when the relay does not serve bootstrap (older relay, the flag off,
// or the viewer is an EXTERNAL in this workspace, whose scopes are answered by the
// individual gated RPCs). Every caller keeps its existing per-RPC path for that
// case, so an old relay and a new client still work.
interface WorkspaceBootstrap {
  version: number;
  channels: Channel[];
  members: WorkspaceMember[];
  external: ExternalParticipant[];
  workSessions: WorkSession[];
  unread: {
    counts: Record<string, number>;
    mentionCounts?: Record<string, number>;
    reads: Record<string, number>;
    dmCounts: Record<string, number>;
    dmReads: Record<string, number>;
    // Mirrors BootstrapUserState.unread (client.ts) — optional because an older
    // relay omits it entirely. See seedUnreadSignals for what that absence means.
    dmLastMessageAt?: Record<string, number>;
  };
  prefs: Record<string, string>;
  drafts: { scope: string; text: string; updatedAt: number }[];
  draftTombstones?: { scope: string; updatedAt: number }[];
  archivedChannelIds?: string[];
  dmHiddenAt?: Record<string, number>;
}

type NotifPrefsRead = Awaited<ReturnType<typeof client.fetchNotificationPrefs>>;

// `issued` fires once the request is on the wire, so the caller can send the independent reads behind it.
async function bootstrapWorkspace(
  wsId: string,
  issued: () => void = () => {},
): Promise<WorkspaceBootstrap | null> {
  if (!client.bootstrapSupported) return null;
  // EVERYTHING below is inside the try, and that is the point: this is the FIRST
  // await of selectWorkspace, so anything that escapes it strands
  // workspaceState.switching at true — an infinite "Switching to …" spinner with
  // no error and no recovery (the invariant workspace-switch-spinner.test.ts
  // locks). A bootstrap that fails for ANY reason — the cache read throwing, the
  // RPC rejecting, or a relay/mock that answers something other than the
  // documented shape — is a transient failure, never a reason to hang the open:
  // fall back to the per-RPC path, which is the same path an older relay takes.
  try {
    // The version we hold, from the SAME IndexedDB the message scopes live in. No
    // stored snapshot ⇒ version 0, which the relay can never match, so the answer
    // always carries a full one.
    // The viewer is taken once, so the save below lands under the key this read used.
    const viewer = cacheViewer();
    const held = await cacheSnapshot.loadWorkspaceSnapshot(wsId, viewer);
    const pending = client.bootstrapWorkspace(wsId, held?.version ?? 0);
    issued();
    const resp = await pending;
    return applyBootstrapAnswer(wsId, held, resp, viewer);
  } catch {
    return null;
  }
}

// The answer half, split out only so the try above can wrap the whole call
// without swallowing a throw from the state writes further down selectWorkspace.
// `resp` is typed but arrives off the wire, so every field is read optionally —
// an answer missing `supported` falls back rather than throwing.
function applyBootstrapAnswer(
  wsId: string,
  held: cacheSnapshot.StoredWorkspaceSnapshot | null,
  resp: Awaited<ReturnType<typeof client.bootstrapWorkspace>> | undefined,
  viewer: string,
): WorkspaceBootstrap | null {
  if (!resp?.supported) return null;
  if (resp.unchanged) {
    // Nothing workspace-wide moved. Serve channels/members from the stored
    // snapshot; if we somehow hold none (a wiped cache between the read above and
    // now), fall back rather than paint an empty sidebar.
    if (!held) return null;
    return {
      version: resp.version,
      channels: held.channels as Channel[],
      members: held.members as WorkspaceMember[],
      external: held.external as ExternalParticipant[],
      workSessions: held.workSessions as WorkSession[],
      unread: resp.unread,
      prefs: resp.prefs,
      drafts: resp.drafts,
      draftTombstones: resp.draftTombstones,
      archivedChannelIds: resp.archivedChannelIds,
      dmHiddenAt: resp.dmHiddenAt,
    };
  }
  // A fresh snapshot — persist it so the NEXT open/reconnect can be answered
  // `unchanged`. Caveat #29: written only from a successful answer that actually
  // carried one, never optimistically.
  void cacheSnapshot.saveWorkspaceSnapshot(
    wsId,
    {
      version: resp.version,
      channels: resp.channels,
      members: resp.members,
      external: resp.external,
      workSessions: resp.workSessions,
    },
    viewer,
  );
  return {
    version: resp.version,
    channels: resp.channels,
    members: resp.members,
    external: resp.external,
    workSessions: resp.workSessions,
    unread: resp.unread,
    prefs: resp.prefs,
    drafts: resp.drafts,
    draftTombstones: resp.draftTombstones,
    archivedChannelIds: resp.archivedChannelIds,
    dmHiddenAt: resp.dmHiddenAt,
  };
}

// Apply the per-user half of a bootstrap answer — identical work to what the
// individual fetchUnread / fetchNotificationPrefs handlers did, in one place so
// the bootstrap path and the legacy path cannot seed differently.
function applyBootstrapUserState(wsId: string, boot: WorkspaceBootstrap, seededAt: number): void {
  seedUnreadSignals(wsId, { ...boot.unread, dmHiddenAt: boot.dmHiddenAt }, seededAt);
  if (workspaceState.current?.id === wsId) {
    readCursorState.seed({ reads: boot.unread.reads, dmReads: boot.unread.dmReads });
  }
  notifPrefsState.merge(boot.prefs);
  draftsState.seedFromBootstrap(wsId, boot);
}

// Returns each workspace's bootstrap so the panel reconcile reuses it, not a second one.
function subscribeAndSeedAllWorkspaces(): Map<string, Promise<WorkspaceBootstrap | null>> {
  const boots = new Map<string, Promise<WorkspaceBootstrap | null>>();
  for (const ws of workspaceState.list) {
    // intentional: fire-and-forget background subscribe; selectWorkspace surfaces a real subscribe failure for the active one.
    client.subscribeWorkspace(ws.id).catch(() => {});
    // A host reached as an external: no bootstrap, no fetch_unread (both refused by the floor).
    if (ws.external) {
      void legacySeedWorkspace(ws.id, true);
      continue;
    }
    // CYBORG-989: the two RPCs below (prefs + unread) are the per-workspace half of
    // the reconnect burst — with N workspaces they were 2N calls on every single
    // reconnect. One bootstrap answers both, and a workspace whose version has not
    // moved costs the relay no workspace-wide query at all. Falls through to the
    // pair below only when the relay does not serve it.
    if (client.bootstrapSupported) {
      const seededAt = Date.now();
      const bootP = bootstrapWorkspace(ws.id);
      boots.set(ws.id, bootP);
      void (async () => {
        const boot = await bootP;
        if (!boot) {
          // Unsupported for THIS workspace (an external seat) — pay the old pair.
          void legacySeedWorkspace(ws.id);
          return;
        }
        applyBootstrapUserState(ws.id, boot, seededAt);
        // Belt-and-braces dock/title push, exactly as the legacy unread seed does.
        pushUnreadIndicators(activityState.badgeCount);
      })().catch((err) => {
        // The legacy path this replaces wraps its equivalent in try/catch. Without
        // this the seed becomes a silent unhandled rejection and the workspace is
        // left with no unread signals at all, which reads as "everything is read".
        reportUiFailure("bootstrap.workspace_seed", err, { workspaceId: ws.id });
        void legacySeedWorkspace(ws.id);
      });
      continue;
    }
    void legacySeedWorkspace(ws.id);
  }
  return boots;
}

// The pre-CYBORG-989 per-workspace seed: notification prefs + unread, both
// background and best-effort. Kept verbatim as the fallback for an older relay,
// the flag being off, and an external seat.
function legacySeedWorkspace(wsId: string, external = false): void {
  {
    const ws = { id: wsId };
    // Cross-workspace notification prefs: load + MERGE each workspace's per-scope
    // prefs up front. notifPrefsState was previously seeded ONLY for the active
    // workspace, so a plain (non-mention) message arriving in a DIFFERENT
    // workspace's muted / mentions-only channel had an UNKNOWN pref and the
    // policy fell through to notify — firing an OS banner the user had muted.
    // Loading every workspace's prefs (keyed by globally-unique scope id) lets
    // decideChannel apply the REAL pref to cross-workspace messages. Background +
    // best-effort: selectWorkspace re-fetches and merges the active workspace's
    // prefs authoritatively, so a miss here just delays a background pref.
    void (async () => {
      try {
        const r = await client.fetchNotificationPrefs(ws.id);
        notifPrefsState.merge(r.prefs);
        // The only archive seed an external seat gets: fetch_unread (which carries it for a
        // member) is not outsider-reachable, so without this an archived row returns on reload.
        if (r.archivedChannelIds) archivedChannelsState.seed(r.archivedChannelIds, ws.id);
      } catch {
        // intentional: background cross-workspace pref seed; a miss is recovered on workspace switch (selectWorkspace merges that workspace's prefs).
      }
    })();
    // The prefs read above is outsider-reachable; fetch_unread is not.
    if (external) return;
    // Captured BEFORE the request goes out — see reconcileWorkspaceUnread.
    const seededAt = Date.now();
    client
      .fetchUnread(ws.id)
      .then((u) => {
        seedUnreadSignals(ws.id, u, seededAt);
        // Re-freeze the divider cursors from server truth — but only for the
        // active workspace, since readCursorState is a single global map and a
        // background workspace must not clobber the open conversation's cursor.
        if (workspaceState.current?.id === ws.id) reseedReadCursors(u);
        // Belt-and-braces: push the freshly-seeded total to the dock/title
        // imperatively, so the badge appears on connect/reconnect even if the
        // reactive effect hasn't (re)run yet.
        pushUnreadIndicators(activityState.badgeCount);
        return;
      })
      // intentional: background per-workspace unread seed for badges; a miss just delays a badge until the next sync.
      .catch(() => {});
  }
}

// Re-seed every open agent session's timeline from the server on reconnect, so
// items streamed while the socket was down are recovered: the rows after the newest
// held seq, or the latest page when the stream holds none (hydrateFromTimeline).
// `daemonId` scopes the catch-up to one daemon's open sessions (used by the
// owner-daemon reconnect self-heal, #518) — omit it (client reconnect) to
// re-hydrate every open session.
async function catchUpAgentTimelines(wsId: string, daemonId?: string): Promise<void> {
  const openAgentIds = Array.from(agentStreamState.streams.keys()).filter(
    (id) =>
      agentStreamState.workspaceForAgent(id) === wsId && agentStreamState.getEntries(id).length > 0,
  );
  // A REFUSAL IS AN ANSWER: the same mark fetchAgentTimeline's guard obeys gates this
  // read too, or a denied session is re-asked once per reconnect (81 times per id).
  const agentIds = timelineIdsToCatchUp(
    agentIdsForDaemonCatchUp(openAgentIds, workspaceState.agents, daemonId, (id) =>
      agentStreamState.isTimelineGone(id),
    ),
    {
      isTimelineReadDenied: (id) => agentStreamState.isTimelineReadDenied(id),
      isTimelineGone: (id) => agentStreamState.isTimelineGone(id),
      isProviderSubagent: (id) => providerSubagentStore.lastUpdateFor(id) !== null,
    },
  );
  // One catch-up per conversation at a time; a trigger meanwhile runs it once more after.
  const owners = new Map(workspaceState.agents.map((a) => [a.agentId, a.daemonId]));
  await runCatchUpsPerDaemon(
    agentIds,
    (id) => owners.get(id),
    (agentId) => catchUpAgent(client, wsId, agentId, daemonId),
    () => workspaceState.current?.id === wsId,
  );
}

// Re-assert foreground/background on a freshly-(re)connected socket. The relay's
// `backgrounded` flag is CONNECTION-scoped, so a new GuestConnection defaults to
// active — without this, a reconnect while backgrounded would make the relay think
// we're active and suppress push. Called right after auth on every connect path.
// Push decisions E-H: the open conversation (route + thread pane), set by the workspace layout.
export const activeViewSender = createActiveViewSender({ send: (f) => client.sendActiveView(f) });

function syncAppStateToRelay(): void {
  activeViewSender.resend();
  // CYBORG-990: re-assert the agent live-stream watches on the same beat. The relay's
  // watch set is CONNECTION-scoped, so a reconnect starts it empty and every agent
  // currently on screen would stop streaming tokens until the user navigated away and
  // back. Deliberately NOT routed through catchUpAgentTimelines: that one re-fetches
  // history for streams that already have entries, which is a different question from
  // "what is visible", and it is the reason a freshly-opened agent could be missed.
  // Placed before the app-state send so the watches are re-armed even if that throws.
  client.resendAgentWatches();
  // An open process modal: the daemon forgot the subscription, and lines may have passed.
  // After auth, like the watches: before it the relay drops the frame (client.canSend).
  for (const [k, f] of followedProcesses) {
    const [agentId, processId] = k.split("\t");
    client.subscribeAgentProcess(f.workspaceId, agentId, processId, f.attachId);
    void readProcessBacklog(f.workspaceId, agentId, processId);
  }
  if (typeof document === "undefined") return;
  client.setAppState(document.visibilityState === "hidden" ? "background" : "active");
}

// CYBORG-990 — take a live-stream watch on an agent for as long as the caller is
// mounted. Designed to be the WHOLE body of a Svelte effect:
//
//   $effect(() => watchAgentStream(agent?.agentId));
//
// The effect re-runs when the id (or the workspace) changes, and Svelte calls the
// returned release before each re-run and at unmount — so the watched set tracks what
// is on screen with no bookkeeping at the call site. Returns undefined when there is
// nothing to watch, which an effect accepts.
//
// EVERY surface that renders live agent tokens must call this. The relay stops fanning
// per-token deltas to the whole workspace, so a surface that reads `getLastActivity`,
// `getEntries` or listens for `timeline` frames and does NOT hold a watch shows dead
// air. That is the bug this replaced.
//
// SELECTED workspace, and the relay matches on the BROADCAST's. They agree only
// because every caller reads its agentId from the selected workspace's own data and a
// daemon broadcasts an agent's deltas solely under its binding's workspace. A caller
// that can name an agent from ANOTHER workspace must call `client.watchAgent` with
// that workspace instead, as `lib/tasks/board-query-last.svelte.ts` does.
export function watchAgentStream(agentId: string | null | undefined): (() => void) | undefined {
  const wsId = workspaceState.current?.id;
  if (!agentId || !wsId) return undefined;
  return client.watchAgent(wsId, agentId);
}

// The list form, for a surface that renders many agents' live steps at once (the cybo
// activity feed, the sessions list, the board-query dialog). Releases all of them
// together.
//
// DEDUPED, and that matters: these lists re-derive on any roster touch — a status flip,
// a usage tick — and a naive effect would release and re-take the WHOLE set each time,
// emitting 2N frames against the relay's 2,400-per-60s guest budget. A 40-row sessions
// pane would spend that budget in half a minute of ordinary agent activity. The Set
// collapses duplicate ids within one call; the refcount in client.watchAgent already
// makes a re-take of the same id free ACROSS calls (0→1 is the only send), so the
// remaining churn is one release+retake pair per row per recompute, which is why the
// caller should pass a stable id list rather than objects.
export function watchAgentStreams(
  agentIds: readonly (string | null | undefined)[],
): (() => void) | undefined {
  const wsId = workspaceState.current?.id;
  if (!wsId) return undefined;
  const unique = new Set(agentIds.filter((id): id is string => !!id));
  if (unique.size === 0) return undefined;
  const releases = Array.from(unique, (id) => client.watchAgent(wsId, id));
  return () => {
    for (const release of releases) release();
  };
}

// Start minting the app-wide media pass the instant we are allowed to, on every
// connect path — right here, beside the other post-auth fire-and-forgets.
//
// WHY. An UPLOADED avatar paints from /api/assets/, and with asset reads requiring a credential
// in production a passless request is REFUSED (thumb 401s; /o/ bounces to the SPA).
// mediaToken() is null until the first mint lands, so a shell that renders before it
// shows initials and pops to the photo a moment later — most visibly on your own
// avatar in the profile menu, the one most likely to be a real photo. The mint is
// ~120ms and per-USER, so starting it here usually beats the workspace seed and the
// first paint already carries the pass.
//
// NOT awaited, deliberately: ensureMediaToken never rejects (it resolves null and
// beacons), and nothing may block the shell on a network call — an await here would
// trade a brief initials flash for a slower app, which is the worse deal.
//
// ponytail: a race, not a guarantee. This only makes the miss RARER; Avatar's reset
// effect is still what recovers when the seed wins, and remains the actual fix.
function warmMediaPass(): void {
  void ensureMediaToken();
}

// CYBORG-1210: the relay-clock cursor of the reconnect change feed, bound to the relay and
// the user it was read from so an endpoint switch or re-login never reuses it.
let changesCursor: { owner: string; cursor: string } | null = null;

function changesOwner(): string {
  return `${client.httpBase}|${authState.user?.id ?? ""}`;
}

// An answer without a cursor is a relay that does not speak the feed: full reads, no alarm.
function hasChangesCursor(res: unknown): res is { cursor: string } {
  return (
    typeof res === "object" && res !== null && "cursor" in res && typeof res.cursor === "string"
  );
}

async function armMessageChanges(wsId: string): Promise<void> {
  if (changesCursor?.owner === changesOwner() || cache.syncedScopesSuspended() !== null) return;
  if (isOutsiderSeat(workspaceState.list, wsId)) return; // the change feed is member-only
  try {
    const res: unknown = await client.messageChanges(wsId);
    if (!hasChangesCursor(res)) return;
    changesCursor = { owner: changesOwner(), cursor: res.cursor };
  } catch (err) {
    // An older relay has no change feed: expected during rollout, and the full reads run.
    if (isUnsupportedByRelay(err)) console.debug("[messageChanges] relay without feed", err);
    else reportUiFailure("messageChanges.arm", err, { workspaceId: wsId });
  }
}

function patchChangedRows(res: import("../core/client.js").MessageChanges): void {
  const gone = res.tombstones.map((t) => t.id);
  if (res.upserts.length === 0 && gone.length === 0) return;
  const apply = (messages: import("../core/types.js").Message[]) =>
    applyMessageChanges(messages, res);
  channelState.messages = apply(channelState.messages);
  dmState.messages = apply(dmState.messages);
  threadState.replies = apply(threadState.replies);
  if (threadState.parent) threadState.parent = apply([threadState.parent])[0] ?? threadState.parent;
  if (cacheOn()) for (const id of gone) void cache.cacheDeleteMessage(id);
}

/**
 * Keep the message caches across a reconnect: ask what changed since the cursor, patch the
 * open panes, drop only the marks of scopes that changed. Anything else reads again.
 */
export async function catchUpMessageChanges(wsId: string | undefined): Promise<void> {
  const epoch = cache.syncedScopesSuspended();
  if (epoch === null) return;
  const held = changesCursor?.owner === changesOwner() ? changesCursor : undefined;
  if (!wsId || !held) {
    cache.invalidateSyncedScopes();
    if (wsId) await armMessageChanges(wsId);
    return;
  }
  try {
    const res = await client.messageChanges(wsId, held.cursor);
    if (!hasChangesCursor(res)) {
      cache.invalidateSyncedScopes();
      return;
    }
    changesCursor = { owner: changesOwner(), cursor: res.cursor };
    if (res.reset === true) {
      cache.invalidateSyncedScopes();
      return;
    }
    patchChangedRows(res);
    const myIds = new Set(authState.user ? [authState.user.id] : []);
    const rows = [...res.upserts, ...res.tombstones];
    const viewer = cacheViewer();
    const touched = cache.scopesTouchedByChanges(wsId, myIds, rows, viewer);
    cache.resumeSyncedScopes(wsId, touched, epoch);
  } catch (err) {
    // An older relay has no change feed: expected during rollout, and the full reads run.
    if (isUnsupportedByRelay(err)) console.debug("[messageChanges] relay without feed", err);
    else reportUiFailure("messageChanges.catchUp", err, { workspaceId: wsId });
    cache.invalidateSyncedScopes();
  }
}

function applyReauthWorkspaceList(authResp: AuthWorkspacesPayload): void {
  // Off the wire: an answer without the list keeps the one held rather than emptying it.
  if (!Array.isArray(authResp.workspaces)) return;
  workspaceState.list = reconcileWorkspaceList(workspaceState.list, authResp);
  const current = workspaceState.current;
  const now = current && workspaceState.list.find((w) => w.id === current.id);
  // Only a changed KIND swaps the open workspace's object; same kind keeps its identity.
  if (current && now && !now.external !== !current.external) workspaceState.current = now;
}

async function runReconnectBurst(): Promise<void> {
  daemonsNoticedThisSocket.clear();
  try {
    const authResp = await client.authenticate();
    // FIRST: which workspaces are memberships vs external seats may have changed while offline,
    // and the outsider gate, the queued sends and every reconcile below read the list.
    applyReauthWorkspaceList(authResp);
    // Socket is authenticated again — now it's safe to replay queued sends
    // (the relay drops sends that arrive before cyborg:auth).
    flushSendOutbox();
    void registerMobilePush(client);
    syncAppStateToRelay();
    warmMediaPass();
    authState.user = authResp.user;
    const boots = subscribeAndSeedAllWorkspaces();
    // CYBORG-1210: before any open reads the marks, so a delta open only trusts a caught-up cache.
    await catchUpMessageChanges(workspaceState.current?.id);

    if (!workspaceState.current) return;
    const wsId = workspaceState.current.id;

    // DM resync FIRST (Caveat #24 + the "messages take minutes to render"
    // report). This block used to sit at the very END of this function, behind
    // `await drainSync` + `await reconcileWorkspacePanels` + a possible
    // `await selectChannel` — the ONLY catch-up here that was awaited behind
    // anything (every other one is `void`-ed precisely so a slow lane can't
    // block the rest). drainSync loops up to 50 pages, fully serialized, each a
    // cyborg:sync RPC on the plain 15s client budget, so the open DM — the one
    // surface the user is actually staring at — could wait minutes after the
    // socket was already back and healthy before it refilled. A reconnect IS
    // the DM pane's only recovery path (selectDm latches on activePeerId and
    // handleReconnect is gated on hasConnectedBefore, so the first connect after
    // a page reload never self-heals the DM), which is what turned "the initial
    // fetch failed" into "they come after a few minutes".
    //
    // Safe to move: catchUpDm is guarded on wsId + peerId internally and shares
    // no state with the channel drain (drainSync only reconciles the active
    // CHANNEL — the reason this DM block has to exist at all).
    //
    // Runs for BOTH the empty self-heal case AND the already-populated case (a
    // DM message could have arrived while the socket was a backgrounded zombie):
    // mergeServer() dedups by message id and reconciles optimistic local- rows,
    // so re-running it on a populated DM only splices in what we missed.
    await refillActiveDm(wsId);

    // Catch up the active channel's delta. Drain even when lastSeq===0: a fresh
    // session (or one whose active view never loaded) still needs the snapshot,
    // and applyChannelSyncPage no-ops when there's no active channel — so the old
    // `if (seq > 0)` guard only ever skipped legitimate catch-up.
    await drainSync(wsId, channelState.lastSeq);
    // A host's channel open in place (knowledge/142) is outside that drain: re-read its tail.
    if (workspaceState.openHostChannel) await loadLatestMessages();
    await reconnectPanelCatchUp(wsId, boots.get(wsId));

    // Self-heal: if the active channel never loaded (e.g. the initial fetch failed
    // during a not-OPEN window before auth completed), re-fetch it now that we're
    // authenticated.
    if (channelState.activeId && channelState.messages.length === 0) {
      await selectChannel(channelState.activeId);
    }
    // (DM resync ran FIRST, above — see the comment there for why it moved.)
  } catch (e) {
    // Mid-session token-expiry → logout (Caveat #25). The re-auth above
    // (client.authenticate()) is the only step that can fail with the relay's
    // "unauthorized" response; everything after it is a best-effort resync that
    // must NOT trigger a logout. Classify exactly like connectToServer: a bad/
    // expired token (message matches /unauthor/i) clears the session and routes
    // to /login; anything else is a transient relay/network failure that we
    // swallow so the existing backoff loop keeps retrying (prior behavior).
    const msg = e instanceof Error ? e.message : String(e);
    if (/unauthor/i.test(msg)) {
      // The stored token can no longer be verified (session expired, or the relay's
      // JWT secret was rotated so tokens signed with the old one are rejected).
      //
      // CYBORG-87: the relay ALSO closes the socket with `4003` right after this
      // `cyborg:error` (see relay-standalone.ts), which drives the ws client's
      // silent-refresh self-heal — either a transparent reconnect with a fresh
      // token, or an `auth_expired` event (handled at module top) that routes to
      // re-login. So do NOT log out here when a refresh token is available: doing
      // so (disconnect → intentionalClose) would preempt that refresh. Fall back
      // to the old immediate logout ONLY when there is no stored refresh token to
      // heal with (e.g. a passkey-only session), so we never hang connected-but-
      // unauthenticated. Only navigate if not already on /login — a redundant goto
      // remounts the route and steals focus mid-keystroke (the "it kept dropping my
      // focus" bug after a forced re-login).
      if (!loadRefreshToken()) {
        // NO clearQueuedSends — same reason as the auth_expired handler above:
        // an unhealable token is not the user asking to sign out.
        reportForcedLogout("reconnect_unauthorized", "expired");
        disconnectFromServer();
        if (typeof window === "undefined" || window.location.pathname !== "/login") {
          void goto("/login");
        }
      }
      // else: the 4003 close handler's refresh path owns recovery.
    } else {
      // Transient: the next reconnect retries. A burst that keeps failing is reported
      // once per 10 minutes, so the storm is visible without one report per attempt.
      if (shouldReport("reconnect.burst", 600_000)) {
        reportUiFailure("reconnect.burst", e, {
          workspaceId: workspaceState.current?.id,
        });
      }
    }
  }
}

// Guarded reconnect entry point. `handleReconnect` runs a heavy catch-up burst
// (per-workspace subscribe + unread, panel refetch, activity, agent timelines,
// tasks, DM resync) on EVERY reconnect, so a reconnect storm (the heartbeat-
// terminate loop) would otherwise stack N of them concurrently and amplify load.
// coalesceBurst serializes overlapping/rapid reconnects to one burst at a time
// with a single trailing re-run, so a storm settles in ~1-2 bursts without ever
// skipping catch-up. Kept as a function declaration so the connection-status
// listener above (registered before this line) can call it by hoisted name.
const reconnectBurst = coalesceBurst(runReconnectBurst);
// CYBORG-1205: auth, subscribe, the DM refill and the message drain run on every
// reconnect; the panel reads (~200 KB) are shared by reconnects within 5 s.
const RECONNECT_PANEL_SPACING_MS = 5_000;
const reconnectPanelCatchUp = spacedCatchUp(
  async (wsId: string, boot: Parameters<typeof reconcileWorkspacePanels>[1]) => {
    if (workspaceState.current?.id !== wsId) return;
    await reconcileWorkspacePanels(wsId, boot);

    // Catch up the Activity feed (mentions / DM-received items) from server truth:
    // its broadcasts were missed while the socket was down.
    void seedActivityFromServer(wsId, workspaceState.channels, [], authState.user?.id);

    // Catch up every open agent session's timeline — agent stream events that
    // arrived while disconnected are otherwise lost (the live stream only resumes
    // from "now" after re-subscribe). Fire-and-forget: agent timelines are
    // independent and can be slow, so don't block the active channel/DM restore
    // behind them (each fetch is workspace-guarded internally).
    void catchUpAgentTimelines(wsId);

    // Catch up the task board: task mutations aren't replayed by drainSync, so ask
    // for what changed while the socket was down (a delta, not the whole board).
    // Fire-and-forget + workspace-guarded inside reloadTasks.
    void reloadTasks(wsId);
  },
  RECONNECT_PANEL_SPACING_MS,
);
function handleReconnect(): Promise<void> {
  return reconnectBurst();
}

// Banner for an Alerts row that has no message path (machine-access request, assigned task):
// no notify-policy kind exists for them, so only this device's DND applies.
function fireAlertBanner(title: string, body: string, url: string): void {
  if (notifyClientPrefsState.dnd) return;
  playNotificationSound();
  renderOSNotification(title, body, url);
}

// Render an OS banner. No focus/suppression logic here — the notify policy
// (shouldNotifyFor) already decided this should fire. Inside Electron we route
// to the main process for a native notification (richer + deep-link click,
// like the original app); in a plain browser we fall back to the in-tab
// Notification API.
function renderOSNotification(
  title: string,
  body: string,
  url?: string,
  // CYBORG-1404: a pending permission the macOS banner offers Allow/Deny buttons for.
  // The shell echoes the click back as the "notification-action" event (below).
  answer?: PermissionBannerAnswer,
): void {
  if (typeof window === "undefined") return;
  // Caveat #30: on the iOS shell, FCM-over-APNs delivers the banner server-side.
  // Firing a local notification here too would double-banner — explicitly no-op.
  if (isTauriIOS()) return;
  const bridge = (
    window as Window & {
      cyborg7Desktop?: {
        notify?: (o: {
          title: string;
          body: string;
          url?: string;
          answer?: PermissionBannerAnswer;
        }) => void;
      };
    }
  ).cyborg7Desktop;
  if (bridge?.notify) {
    bridge.notify({ title, body, url, answer });
    return;
  }
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, { body, icon: "/icon-192.png", tag: url ?? title });
    if (url) {
      n.addEventListener("click", () => {
        window.focus();
        void goto(url);
        n.close();
      });
    }
  } catch {
    // notification construction can throw on some platforms — best-effort
  }
}

interface PermissionBannerAnswer {
  agentId: string;
  requestId: string;
}

// CYBORG-1404: the workspace an agent's requests belong to (its own, not the one on screen).
function workspaceOfAgent(agentId: string): string | undefined {
  return agentStreamState.workspaceForAgent(agentId) ?? workspaceState.current?.id;
}

// Is THIS viewer one of the session's people (answersSessionPermissions)? An unknown agent row or
// a foreign workspace fails closed.
function controlsSessionOf(agentId: string): boolean {
  // A provider subagent's requests are filed under ITS id: the session it belongs to (the
  // parent) is what the viewer controls.
  const owner = permissionOwnerOf(agentId);
  const agent = workspaceState.agents.find((a) => a.agentId === owner);
  const ws = workspaceState.current;
  if (!agent || !ws || workspaceOfAgent(owner) !== ws.id) return false;
  return answersSessionPermissions(agent, authState.user, ws.role);
}

// CYBORG-1434: the machine the agent runs on, when the viewer's access to it is known.
function agentDaemonOf(agentId: string): { daemonId: string; label: string } | undefined {
  const daemonId = workspaceState.agents.find(
    (a) => a.agentId === permissionOwnerOf(agentId),
  )?.daemonId;
  const daemon = daemonId ? daemonState.byId(daemonId) : undefined;
  return daemonId && daemon ? { daemonId, label: daemon.label } : undefined;
}

// CYBORG-1434: answering runs code on the agent's machine, so it needs DRIVE authority there:
// owner, or a spawn / terminal / admin scope (daemonState.canDrive, the twin of the relay's
// scopesRefuseApproval). Workspace admins get no pass. An agent with no known machine is
// not refused here (the relay's degraded path): the relay and daemon stay the real gate.
function drivesAgentMachine(agentId: string): boolean {
  const machine = agentDaemonOf(agentId);
  return (
    !machine ||
    !daemonState.accessKnown ||
    daemonState.canDrive(machine.daemonId, authState.user?.id)
  );
}

// CYBORG-1516: the machine hosting the agent is known to be offline, so nothing can be asked of it.
export function permissionHostOffline(agentId: string): boolean {
  const machine = agentDaemonOf(agentId);
  return !!machine && daemonState.isKnownOffline(machine.daemonId);
}

// May THIS viewer answer the agent's permission prompts: controls the session AND drives its machine.
export function canAnswerPermissionsFor(agentId: string): boolean {
  return controlsSessionOf(agentId) && drivesAgentMachine(agentId);
}

// CYBORG-1434: pending requests of a session the viewer controls but whose machine they do not
// drive. The cards render these as a notice + "Request access" instead of Allow/Deny.
export function drivelessPermissions(
  agentId: string,
  opts: { session?: boolean } = {},
): AgentPermissionRequest[] {
  if (!controlsSessionOf(agentId) || drivesAgentMachine(agentId)) return [];
  return agentStreamState
    .getPendingPermissions(agentId)
    .filter((r) => opts.session || !answeredPermissions.has(answeredKey(agentId, r.id)));
}

// What the notice's "Request access" button needs: the machine, and the workspace it lives in.
export function driveNoticeTarget(
  agentId: string,
): { workspaceId: string | undefined; daemonId: string; daemonLabel: string } | undefined {
  const machine = agentDaemonOf(agentId);
  if (!machine) return undefined;
  return {
    workspaceId: workspaceOfAgent(agentId),
    daemonId: machine.daemonId,
    daemonLabel: machine.label,
  };
}

function permissionOwnerOf(agentId: string): string {
  for (const route of subagentPermissionRoutes.values()) {
    if (route.subagentId === agentId) return route.parentAgentId;
  }
  return agentId;
}

// Requests answered from some surface whose permission_resolved echo has not landed yet.
// A send is fire-and-forget (no ack), so the mark expires if no permission_resolved follows.
const ANSWERED_MARK_MS = 15_000;
const answeredPermissions = conversationSync.collection(
  "answeredPermissions",
  new SvelteSet<string>(),
);
const answeredKey = (agentId: string, requestId: string): string => `${agentId}\t${requestId}`;
function markAnswered(agentId: string, requestId: string): void {
  const key = answeredKey(agentId, requestId);
  answeredPermissions.add(key);
  setTimeout(() => answeredPermissions.delete(key), ANSWERED_MARK_MS);
}
export function isPermissionAnswered(agentId: string, requestId: string): boolean {
  return answeredPermissions.has(answeredKey(agentId, requestId));
}

// The pending requests this viewer can still act on: controllable session, not yet answered.
// The session page passes `session` and keeps showing an answered-but-unresolved request:
// it is the one surface where a lost send must stay retryable.
export function answerablePermissions(
  agentId: string,
  opts: { session?: boolean } = {},
): AgentPermissionRequest[] {
  if (!canAnswerPermissionsFor(agentId)) return [];
  return agentStreamState
    .getPendingPermissions(agentId)
    .filter((r) => opts.session || !answeredPermissions.has(answeredKey(agentId, r.id)));
}

// CYBORG-1404: OS banner for a permission request the viewer can answer. Goes through the
// notify policy (DND, muted channel, focus). The body is a generic tool label, never the
// request's own text, and the only button is Deny: Allow stays in the app, where the full
// request is visible.
export function maybeFirePermissionBanner(
  agentId: string,
  agentName: string,
  request: AgentPermissionRequest,
): void {
  if (!canAnswerPermissionsFor(agentId)) return;
  const agent = workspaceState.agents.find((a) => a.agentId === agentId);
  const decision = shouldNotifyFor(
    { kind: "agent_permission", remoteAgentId: agentId, channelId: agent?.channelId },
    notifyContext(),
  );
  if (!decision.notify) return;
  const wsId = workspaceOfAgent(agentId);
  const plain = request.kind !== "question" && !(request.actions && request.actions.length > 0);
  playNotificationSound();
  renderOSNotification(
    `${agentName} needs your permission`,
    bannerToolLabel(request.name),
    wsId ? `/workspace/${wsId}/agent/${agentId}` : undefined,
    plain ? { agentId, requestId: request.id } : undefined,
  );
}

// A banner button's answer. Deny only; ignored (and told) once the request is no longer
// answerable here.
export function answerFromBanner(p: unknown): void {
  const o = p as { agentId?: unknown; requestId?: unknown; behavior?: unknown } | null;
  if (typeof o?.agentId !== "string" || typeof o.requestId !== "string" || o.behavior !== "deny") {
    return;
  }
  const { agentId, requestId } = o;
  if (!answerablePermissions(agentId).some((r) => r.id === requestId)) {
    toast.info("That permission request was already resolved.");
    return;
  }
  try {
    respondToPermission(agentId, requestId, { behavior: "deny" });
  } catch (err) {
    reportUiFailure("agents.banner_permission_response", err, { agentId, requestId });
    toast.error("Couldn't send your answer — check the connection and try again.");
  }
}

// Android notification Deny (deep link ?action=deny&requestId=): the request is
// found among the pending permissions of the agents this viewer can answer for.
// "unknown" = not (yet) among them; the caller waits for the workspace's agents and
// pending permissions to load before giving up (the answer is never toasted here).
export function answerFromDeepLink(behavior: "deny", requestId: string): "answered" | "unknown" {
  const agentId = workspaceState.agents
    .map((a) => a.agentId)
    .find((id) => answerablePermissions(id).some((r) => r.id === requestId));
  if (!agentId) return "unknown";
  try {
    respondToPermission(agentId, requestId, { behavior });
  } catch (err) {
    reportUiFailure("agents.deeplink_permission_response", err, { agentId, requestId });
    toast.error("Couldn't send your answer — check the connection and try again.");
  }
  return "answered";
}

// Client-side activity-feed entry for an @mention (superseded by the server
// activity feed in P7b; kept until the ActivityPane is rewired). The OS
// notification decision is handled separately by the notify policy (P9).
function handleIncomingMention(msg: import("../core/types.js").Message, preview: string): void {
  const channel = workspaceState.channels.find((c) => c.id === msg.channelId);
  // Structured activity-feed entry (cyborg7's addMention API). The OS
  // notification decision is handled separately by the P9 policy below
  // (maybeFireChannelBanner), so this stays feed-only — no direct fire.
  activityState.addMention({
    sourceId: msg.id,
    actorName: msg.fromName ?? msg.fromId,
    actorId: msg.fromId,
    actorType: msg.fromType,
    preview,
    channelId: msg.channelId,
    channelName: channel?.name ?? null,
    createdAt: new Date(msg.createdAt).toISOString(),
  });
}

// P2 Item 9 (server-authoritative): seed the Activity feed + rail unread badge
// from the relay's cyborg:fetch_activity response — the authoritative
// activity_events the relay persists (channel mentions + thread replies) plus a
// server-counted unread total. This is the canonical seed; the old ~200-message
// reconstruction under-counted (it could only see mentions in that window). If
// the relay doesn't support the activity feed yet, fall back to backfilling
// recent @mentions from the workspace-activity messages so the feed isn't empty
// (legacy behavior — unread then derives from the seeded items, not the server).
const ACTIVITY_PAGE_SIZE = 100;

// D9: the next page of older alerts (scroll to the bottom of the list). One page in flight; a
// workspace switch mid-fetch drops the answer (the seed replaced the feed).
// `unreadOnly` ("Unread only" view) pages the server's own unread-only set, so an unread row older
// than the first page is reachable without pulling the whole history.
export async function loadMoreActivity(workspaceId: string, unreadOnly = false): Promise<void> {
  const selection = workspaceSelections;
  const before = unreadOnly ? activityState.oldestUnreadCursorMs : activityState.oldestCreatedAtMs;
  const more = unreadOnly ? activityState.hasMoreUnread : activityState.hasMore;
  if (!more || activityState.loadingMore || before === undefined) return;
  if (!client.connected) return;
  activityState.loadingMore = true;
  try {
    const { items } = await client.fetchActivity(workspaceId, {
      before: before + 1,
      limit: ACTIVITY_PAGE_SIZE,
      ...(unreadOnly ? { unreadOnly: true } : {}),
    });
    if (!selectionIsCurrent(workspaceId, selection)) return;
    // cast: the wire items are untyped records; same shape the seed above casts.
    activityState.appendOlder(
      items as unknown as ServerActivityItem[],
      ACTIVITY_PAGE_SIZE,
      unreadOnly,
    );
  } catch (err) {
    reportUiFailure("activity.loadMore", err, { workspaceId });
    // Stop here: a failing page must not become a request loop. Pull-to-refresh reseeds.
    if (unreadOnly) activityState.setHasMoreUnread(false);
    else activityState.setHasMore(false);
  } finally {
    activityState.loadingMore = false;
  }
}

async function seedActivityFromServer(
  workspaceId: string,
  channels: Channel[],
  fallbackMessages: import("../core/types.js").Message[],
  myId: string | undefined,
): Promise<void> {
  const selection = workspaceSelections;
  const started = Date.now();
  try {
    const { items, unread, totalUnread, unreadByWorkspace, hiddenUnreadByWorkspace } =
      await client.fetchActivity(workspaceId, { limit: ACTIVITY_PAGE_SIZE });
    if (!selectionIsCurrent(workspaceId, selection)) return;
    // cast: the wire items are untyped records (ServerActivityItem is their mapped shape).
    const rows = items as unknown as ServerActivityItem[];
    activityState.seedFromServer(
      rows,
      unread,
      totalUnread,
      unreadByWorkspace,
      hiddenUnreadByWorkspace,
    );
    activityState.setHasMore(items.length >= ACTIVITY_PAGE_SIZE);
    activityState.feed = { workspaceId, ok: true };
    reportAlertsSeed(workspaceId, items.length, unread, totalUnread, Date.now() - started);
    checkBadgeMismatch(workspaceId, () =>
      selectionIsCurrent(workspaceId, selection) ? alertsCounts(activityState) : undefined,
    );
  } catch (err) {
    reportUiFailure("activity.seed", err, { workspaceId });
    // A failed refresh keeps a feed that already read; only a first read is "unknown".
    if (
      selectionIsCurrent(workspaceId, selection) &&
      activityState.feed?.workspaceId !== workspaceId
    )
      activityState.feed = { workspaceId, ok: false };
    // Older relay without the activity feed — fall back to the recent-message
    // @mention backfill (idempotent, keyed by message id). Leaves _serverUnread
    // null so the badge falls back to counting unread items.
    if (!selectionIsCurrent(workspaceId, selection) || !myId || fallbackMessages.length === 0)
      return;
    const channelName = new Map(channels.map((c) => [c.id, c.name]));
    const backfill = fallbackMessages
      .filter((m) => m.fromId !== myId && m.fromType !== "system" && m.mentions?.includes(myId))
      .map((m) => ({
        sourceId: m.id,
        actorName: m.fromName ?? m.fromId,
        actorId: m.fromId,
        actorType: m.fromType,
        preview: typeof m.text === "string" ? m.text.slice(0, 120) : "",
        channelId: m.channelId,
        channelName: m.channelId ? (channelName.get(m.channelId) ?? null) : null,
        createdAt: new Date(m.createdAt).toISOString(),
      }));
    if (backfill.length > 0) activityState.addMentions(backfill);
  }
  // LOCAL FAILURE. Two of the three callers are fire-and-forget, so a rejection
  // escaping here is an unhandled rejection rather than the no-op it was while the
  // backfill was synchronous. Beaconed, never swallowed (observability rule).
  try {
    await backfillOrphanAgentDms(workspaceId, fallbackMessages);
  } catch (err) {
    reportUiFailure("seedActivityFromServer.backfillOrphanAgentDms", err, { workspaceId });
  }
}

// Rebuild the orphan-agent-DM inbox rows (see handleIncomingAgentDm) for the
// workspace that just finished opening. The live push there is client-only, so
// without this a reload strands exactly the badges this fix exists to make
// clearable: `fetch_unread` hands the count straight back, but the Activity row
// it was paired with is gone, and the red number is a door-less badge again.
//
// DRIVEN BY THE BADGE, not by a message scan: it walks the DM counts the server
// just seeded, so a row is written only for a peer that genuinely has unread —
// a conversation already read has a zeroed count (dm_reads) and produces
// nothing, and the feed can never disagree with the number it is explaining.
// `recent` — the workspace-activity window the caller already fetched, which
// includes the viewer's own DMs (pg-sync getRecentActivity) — supplies only the
// row's cosmetics (name, preview, timestamp); no extra round trip.
//
// One row per orphan conversation, keyed on that conversation's newest known
// message id so a live push of the SAME message collapses onto it (pushAgentDm's
// (peer, message) id). A peer whose unread DM is OLDER than that window costs one
// targeted read (the owner's case: a 2026-08-25 DM, 200 messages ago, badged
// forever with nothing to click); a peer with no message at all is skipped rather
// than given a blank row — its next incoming DM pushes a real one.
//
// The cap bounds ONLY the genuine surface-less agent peers orphanAgentDmPeerIds
// returns — never a badged channel or human DM — which is the rare set it was always
// meant to bound.
const ORPHAN_DM_BACKFILL_FETCH_MAX = 8;

// ONE conversation's newest message, for a badged peer that has none in the
// window. Local failure by design: a rejection here must never break the
// workspace open, so it just skips that peer.
async function newestAgentDmForPeer(
  workspaceId: string,
  peerId: string,
): Promise<import("../core/types.js").Message | null> {
  try {
    const { messages } = await client.fetchDmMessages(workspaceId, peerId, { limit: 1 });
    // The RPC returns oldest→newest, and the newest row in the pair can be the
    // viewer's own reply or a thread reply — mirror the in-window filter rather
    // than guessing.
    const m = messages.at(-1);
    if (!m || m.channelId != null || m.parentId || m.fromType !== "agent") return null;
    return m;
  } catch {
    // intentional: a failed targeted read skips this peer; the next reseed retries it.
    return null;
  }
}

// The live twin of the relay's own Activity row for a held-prompt notice from an agent the
// viewer has no DM surface for. pushAgentDm keys on (peer, message), so a seed that already
// carries the server row (hasDmRow) or this push arriving twice stays one row.
function pushHeldPromptNotice(msg: import("../core/types.js").Message): boolean {
  const peerId = heldPromptNoticePeer(msg, authState.user);
  if (
    !peerId ||
    agentDmHasReachableSurface(
      peerId,
      cyboState.list.map((c) => c.id),
      workspaceState.agents,
      authState.user,
    )
  )
    return false;
  pushAgentDmRow(peerId, msg);
  return true;
}

// One Activity row for an agent DM: the backfill and the held-prompt notice share it.
function pushAgentDmRow(peerId: string, m: import("../core/types.js").Message): void {
  activityState.pushAgentDm(
    peerId,
    m.id,
    agentDmPeerName(m.fromName, peerId, workspaceState.agents, cyboState.list),
    messagePreviewText(m, "Message"),
    new Date(m.createdAt).toISOString(),
  );
}

async function backfillOrphanAgentDms(
  workspaceId: string,
  recent: readonly import("../core/types.js").Message[],
): Promise<void> {
  const selection = workspaceSelections;
  if (!selectionIsCurrent(workspaceId, selection)) return;
  // countsForWorkspace is the MERGED map (channel ids AND DM peer ids), so the peers
  // worth a round trip are the ones the helper classifies — not every badged key.
  const orphanPeers = orphanDmPeers(workspaceId);
  if (orphanPeers.length === 0) return;
  dmHealPump.heal(workspaceId, orphanPeers);
  // Newest agent DM per peer, from the window the caller already holds.
  const newest = new Map<string, import("../core/types.js").Message>();
  for (const m of recent) {
    if (m.channelId != null || m.parentId || m.fromType !== "agent") continue;
    if (!isMe(m.toId, authState.user)) continue;
    const peer = canonicalAgentDmPeerId(m.fromId);
    const prev = newest.get(peer);
    if (!prev || m.createdAt >= prev.createdAt) newest.set(peer, m);
  }
  let fetched = 0;
  for (const peerId of orphanPeers) {
    let m = newest.get(peerId);
    if (!m) {
      if (fetched >= ORPHAN_DM_BACKFILL_FETCH_MAX) continue;
      fetched += 1;
      m = (await newestAgentDmForPeer(workspaceId, peerId)) ?? undefined;
      if (!selectionIsCurrent(workspaceId, selection)) return;
      if (!m) continue;
    }
    // Re-read the count: the awaits above give another device time to mark this DM
    // read, and a row explaining a badge that is already gone is a stale row.
    if (notificationState.getCount(workspaceId, peerId) <= 0) continue;
    pushAgentDmRow(peerId, m);
  }
}

// Build the policy context from current state (P9).
function notifyContext(): NotifyContext {
  return {
    // The user OBJECT, not `.id`: the policy's own-message skips go through `isMe`,
    // and only the object carries the viewer's second id (ViewerIdentity).
    currentUserId: authState.user,
    // Per-workspace display name (0121): mention matching must use the name people
    // type in THIS workspace, not the account one.
    currentUserName: authState.selfName ?? authState.user?.email?.split("@")[0],
    activeChannelId: channelState.activeId,
    viewingDmHumanUserId: dmState.activePeerId,
    // Canonicalize the raw URL agentId to the cybo id when cybo-bound (#1185
    // follow-up, same bug as the badge): decideDmAgent compares this against
    // the CANONICAL kind.remoteAgentId (maybeFireAgentDmBanner passes the
    // already-canonical peer id), so leaving this raw meant a cybo DM's OS
    // banner/sound never suppressed while that conversation was on screen.
    viewingDmAgentId: (() => {
      const id = activeAgentIdFromUrl();
      return id ? canonicalAgentDmPeerId(id) : null;
    })(),
    channelNotifPrefs: notifPrefsState.prefs as Record<string, NotificationPref>,
    // Client-only gate (this device).
    dndActive: notifyClientPrefsState.dnd,
  };
}

// DM pin (no channel): patch the open DM by id; an id not on screen is a no-op.
function setDmPinned(messageId: string, pinnedAt: number | null, pinnedBy: string | null): void {
  dmState.messages = dmState.messages.map((m) =>
    m.id === messageId ? { ...m, pinnedAt, pinnedBy } : m,
  );
}

// Per-message count of pin_message broadcasts; a failed pinMessage rolls back only if none arrived.
const pinBroadcasts = accountSync.collection("pinBroadcasts", new Map<string, number>());

client.on("pin_message", (p) => {
  pinBroadcasts.set(p.messageId, (pinBroadcasts.get(p.messageId) ?? 0) + 1);
  if (p.channelId === null) {
    setDmPinned(p.messageId, p.pinnedAt, p.pinnedBy);
  } else if (p.channelId === channelState.activeId) {
    channelState.setPinned(p.messageId, p.pinnedAt, p.pinnedBy);
  }
  // Mirror the pin state into the cache (patch-by-id, surface-agnostic). No-op
  // when the cache is off.
  if (cacheOn()) void cache.cachePinMessage(p.messageId, p.pinnedAt, p.pinnedBy);
});

// A personal save toggled on another of my devices (#609): reconcile the Saved
// view + the per-message bookmark glyph. Per-user broadcast, so this only ever
// carries my own saves. We don't have the full message row here on a remote save,
// so a remote ADD just flips the id (the Saved view refetches on next open); a
// remote REMOVE drops the row immediately.
client.on("save_message", (p) => {
  savedState.apply(p.messageId, p.saved);
});

// Cross-device read sync: another device of mine marked a channel or DM read.
// Clear the badge and MONOTONICALLY advance the read-cursor (Item 12). The frozen
// `unreadCursor` of the OPEN conversation follows too, forward only (#514) — unless
// the stamp is one this client sent: the relay never echoes a socket's own read,
// the daemon broadcasts to the sender as well.
client.on("read", (p) => {
  if (!p.workspaceId) return;
  const isActiveWorkspace = workspaceState.current?.id === p.workspaceId;
  if (p.dmPeerId) {
    notificationState.clear(p.workspaceId, p.dmPeerId);
    readCursorState.advanceDm(p.dmPeerId, p.lastReadAt);
    if (
      isActiveWorkspace &&
      p.dmPeerId === dmState.activePeerId &&
      !client.isOwnDmRead(p.workspaceId, p.dmPeerId, p.lastReadAt)
    )
      dmState.advanceUnreadCursor(p.lastReadAt);
    if (isActiveWorkspace && !client.isOwnDmRead(p.workspaceId, p.dmPeerId, p.lastReadAt))
      agentDmReadState.advanceUnreadCursor(p.dmPeerId, p.lastReadAt);
  } else if (p.channelId) {
    notificationState.clear(p.workspaceId, p.channelId);
    // BUG #2: clear BOTH signals — the red mention count AND the bold unread flag.
    unreadFlagState.clearUnread(p.workspaceId, p.channelId);
    readCursorState.advanceChannel(p.channelId, p.lastReadAt);
    if (
      isActiveWorkspace &&
      p.channelId === channelState.activeId &&
      !client.isOwnChannelRead(p.workspaceId, p.channelId, p.lastReadAt)
    )
      channelState.advanceUnreadCursor(p.lastReadAt);
  }
});

client.on("notification_pref_changed", (p) => {
  notifPrefsState.set(p.scopeId, p.preference);
});

// Archived/unarchived for me on my OTHER device: mirror it here. Ids are globally unique, so no
// workspace guard is needed.
// Hidden/shown on my OTHER device.
client.on("dm_hidden_changed", (p) => dmHiddenState.set(p.workspaceId, p.peerId, p.hiddenAt));

client.on("channel_archived_for_me_changed", (p) => {
  archivedChannelsState.set(p.channelId, p.archived);
  activityState.setHiddenSplit(p.hiddenUnreadByWorkspace);
});

// Server-side draft sync (#610): wire the WS transport into the drafts store so
// edits debounce-write to the server and clears delete server-side. Best-effort:
// on a socket that cannot send, client.canSend() skips the frame; a missed draft
// write is recovered on the next edit / workspace-load reconcile (drafts are lossy
// by nature; localStorage remains the instant cache regardless).
draftsState.bindSync({
  draftSet: (workspaceId, scope, text, updatedAt) =>
    client.draftSet(workspaceId, scope, text, updatedAt),
  draftClear: (workspaceId, scope, updatedAt) => client.draftClear(workspaceId, scope, updatedAt),
});

// A draft created/updated/cleared on the user's OTHER device → live-apply it here
// so an unfinished message follows them across devices in real time. Scoped to
// the active workspace (the store is keyed per active workspace's scope ids).
client.on("draft_changed", (p) => {
  if (workspaceState.current?.id !== p.workspaceId) return;
  draftsState.applyRemoteChange(p.scope, p.text, p.updatedAt);
});

// ─── Threads (CRT): list + badge refreshed from the canonical snapshot ─────────
// The client never MERGES a thread event. Every event — a reply, a read on another
// device, its own read's answer, a reconnect — only asks the relay for the snapshot
// again (the feed's window of followed threads + the viewer's totals, from ONE server
// transaction); requests are serialized, so answers cannot race (#3139 U1–U4). Scoped
// to the OPEN workspace (U3): an event for workspace B while A is open is ignored; a
// relay older than the contract sends no workspaceId. Wiring: thread-feed-sync.ts.
const threadFeed = createThreadFeedSync({
  state: threadsState,
  transport: client,
  currentWorkspaceId: () => workspaceState.current?.id ?? null,
  onLoadMoreError: () => toast.error("Couldn't load older threads"),
});

export function refreshThreadState(): void {
  threadFeed.refresh();
}

function onThreadEvent(e: ThreadStateEvent): void {
  if (e.workspaceId && e.workspaceId !== workspaceState.current?.id) return;
  refreshThreadState();
}
client.on("thread_updated", (e) => {
  const raised = e.unread_replies - (e.previous_unread_replies ?? 0);
  if (e.workspaceId && raised > 0 && isMe(e.toUserId, authState.user)) {
    recordUnreadIncrement(e.workspaceId, e.rootId, "thread", "live", raised, "thread_root");
  }
  onThreadEvent(e);
});
// A read on another tab/device patches its one row instead of re-asking all 50 (CYBORG-1201).
client.on("thread_read_changed", (e) => {
  if (e.workspaceId && e.workspaceId !== workspaceState.current?.id) return;
  threadFeed.readChanged(e);
});
// Follow / unfollow, on any device: the snapshot is re-asked like every other
// thread event (a local splice left the totals stale and a new follow invisible).
client.on("thread_follow_changed", () => {
  refreshThreadState();
});

// #607: a scheduled send FAILED at fire time — flip its row to failed live so the
// open Scheduled pane updates without a reload. (A successful send arrives as a
// normal channel/dm broadcast; a re-list picks up its processedAt.)
client.on("schedule_message_failed", (row) => {
  scheduledMessagesState.applyFailed(row);
});

// #619: a recurring schedule was mutated (by another client, or the runner
// deactivating a one-shot). Patch the Tasks-tab board live. The requesting
// client's own ack is resolved by requestId and patched there too; this broadcast
// keeps OTHER clients in sync. Cast the loose broadcast record to the typed
// payload — the structural fields (ok/op/scheduleId/schedule) line up.
client.on("schedule_mutated", (p) => {
  schedulesState.applyMutation(p as Parameters<typeof schedulesState.applyMutation>[0]);
});

// A run fired / changed status on the daemon — merge it so an open schedule detail
// shows "Running" → "Succeeded" live (the "Run now" button reflects the real run,
// not just the RPC round-trip).
client.on("schedule_run_recorded", (p) => {
  if (p.ok) schedulesState.applyRun(p.run);
});

// A built-in recipe install changed (enabled/disabled by another client). Re-list
// for the affected workspace so the integrations "Built-in" section stays live.
// The mutating client also patches its store from the ack; this keeps OTHERS synced.
client.on("recipes_changed", (p) => {
  if (p.workspaceId) void recipesState.load(p.workspaceId);
});

// A machine's install stages, for every surface showing that provider there.
client.on("provider_install_progress", (p) => {
  providerFlow.noteProgress(p.daemonId, p.provider, p.stage, p.state);
});

// The Threads pane opening: one canonical snapshot, through the same sequencer as
// every live refresh, so a pane open cannot overwrite a newer answer.
export async function fetchThreads(): Promise<void> {
  if (!workspaceState.current) return;
  threadsState.loading = true;
  try {
    await threadFeed.run();
  } finally {
    threadsState.loading = false;
  }
}

// "Load older threads": the window grows by one page and the snapshot is asked for
// again — pagination is a refresh, so it cannot race one.
export function loadMoreThreads(): Promise<void> {
  return threadFeed.loadMore();
}

async function seedThreadCounts(
  wsId: string,
  read: ReturnType<typeof client.threadCounts> = client.threadCounts(wsId),
  isCurrent: () => boolean = () => workspaceState.current?.id === wsId,
): Promise<void> {
  try {
    const c = await read;
    if (!isCurrent()) return;
    const raisedThreads = c.totalUnreadThreads - threadsState.counts.totalUnreadThreads;
    if (raisedThreads > 0) {
      recordUnreadIncrement(wsId, "threads", "thread", "reseed", raisedThreads, "thread_root");
    }
    // The badge itself comes from the canonical snapshot (same sequencer as every live
    // refresh — this answer must not overwrite a newer one). This seed runs on workspace
    // open and on every reconnect burst, so a read that failed while offline is retried
    // here too; recovery needs no reload.
    refreshThreadState();
    flushPendingThreadReads();
  } catch {
    // intentional: background thread-count seed; a miss just leaves counts to
    // arrive via live events. No user action, no surface.
  }
}

// P2 Item 6: fetch all co-members' custom statuses for a workspace and seed the
// shared map. Called on workspace open + reconnect. Fire-and-forget.
async function seedUserStatuses(
  wsId: string,
  read: ReturnType<typeof client.fetchUserStatuses> = client.fetchUserStatuses(wsId),
  isCurrent: () => boolean = () => workspaceState.current?.id === wsId,
): Promise<void> {
  try {
    const statuses = await read;
    if (isCurrent()) workspaceUserStatusesState.seed(statuses);
  } catch {
    // server may not support user statuses yet
  }
}

// Every followed thread of the viewer, server-side, read up to the click — not the page
// the feed holds (#3131 R2). The relay's answer is not merged: the canonical snapshot is
// refreshed through the sequencer, like after any other read.
export async function markAllThreadsRead(): Promise<void> {
  if (!workspaceState.current || threadsState.bulkPending) return;
  const wsId = workspaceState.current.id;
  threadsState.bulkPending = true;
  try {
    await client.markAllThreadsRead(wsId, Date.now());
    if (workspaceState.current?.id === wsId) await threadFeed.run();
  } catch (err) {
    reportUiFailure("app.mark_all_threads_read", err, { workspaceId: wsId });
    toast.error("Couldn't mark threads read");
  } finally {
    threadsState.bulkPending = false;
  }
}

// Reads are operations with a generation (ThreadReadController): the row reads as read
// at once (overlay), the relay's answer or refusal settles ONLY the entry it created,
// and either way the canonical snapshot is refreshed — a failed write is never shown as
// read past that refresh. A refused read stays pending with its captured cursor and is
// re-sent by flushPendingThreadReads on the next thread_counts seed (workspace open,
// every reconnect burst). The relay's cursor is forward-only, so a re-send is harmless.
export function flushPendingThreadReads(): void {
  threadFeed.flushReads();
}

// `viewedAt`: the last reply the viewer actually rendered (the feed's mark-on-view).
// Omitted (thread panel open, reply sent), the cursor is captured NOW — a retry re-sends
// this value, never a later clock, so a reply received while offline stays unread.
export function markThreadRead(rootId: string, viewedAt?: number): void {
  threadFeed.read(rootId, viewedAt ?? Date.now());
}

export function followThread(rootId: string, following: boolean): void {
  if (!workspaceState.current) return;
  client.followThread(workspaceState.current.id, rootId, following);
}

// Personal "Archive channel": optimistic; a refused write rolls back. Returns whether it stuck.
// `wsId` names the channel's workspace when it is not the one on screen (the External section).
export async function setChannelArchivedForMe(
  channelId: string,
  archived: boolean,
  wsId = workspaceState.current?.id,
): Promise<boolean> {
  if (!wsId) return false;
  archivedChannelsState.set(channelId, archived); // optimistic
  try {
    await client.setChannelArchivedForMe(wsId, channelId, archived);
    return true;
  } catch (err) {
    archivedChannelsState.set(channelId, !archived);
    reportUiFailure("channel.archive_for_me", err, { workspaceId: wsId, channelId });
    return false;
  }
}

// `wsId` names the channel's workspace when it is not the one on screen (the External section).
export function setChannelNotificationPref(
  channelId: string,
  preference: "all" | "mentions_only" | "muted",
  wsId = workspaceState.current?.id,
): void {
  if (!wsId) return;
  notifPrefsState.set(channelId, preference); // optimistic
  void client.setNotificationPref(wsId, channelId, preference).catch(() => {}); // intentional: optimistic pref persist; the next prefs fetch reconciles a failed write
}

// Reflect a per-channel slash model override in the local store right after the
// set-RPC resolves. Without this the channel in workspaceState keeps its stale
// (cached) slashCommandModel, so reopening the channel's AI tab reseeds from null
// and the override looks like it "didn't save" — even though PG persisted it.
export function applyChannelSlashModel(
  channelId: string,
  model: { provider: string; model: string } | null,
): void {
  workspaceState.channels = workspaceState.channels.map((c) =>
    c.id === channelId ? { ...c, slashCommandModel: model } : c,
  );
}

// P2 Item 6: set MY custom status. Updates local state optimistically and syncs
// to the relay so co-members see the emoji (broadcast) and it persists cross-
// device. expiresAt is an ISO string locally; the relay wants epoch ms.
export function setMyStatus(
  emoji: string | null,
  text: string | null,
  expiresAt: string | null,
): void {
  const ws = workspaceState.current;
  const previous = {
    emoji: userStatusState.emoji,
    text: userStatusState.text,
    expiresAt: userStatusState.expiresAt,
  };
  userStatusState.setStatus(emoji, text, expiresAt);
  if (!ws) return;
  const expiresMs = expiresAt ? new Date(expiresAt).getTime() : null;
  sendSelfPrefOrRollBack(
    "profile.set_status",
    { workspaceId: ws.id },
    () => client.setUserStatus(ws.id, emoji, text, expiresMs),
    () => userStatusState.setStatus(previous.emoji, previous.text, previous.expiresAt),
  );
}

// A fire-and-forget self preference throws "Not connected" synchronously on a closed
// socket; from a click handler that escaped to window.onerror. Report it, undo the
// optimistic state and say so: the change never reached the relay.
function sendSelfPrefOrRollBack(
  source: string,
  ids: Record<string, string>,
  send: () => void,
  rollBack: () => void,
): void {
  try {
    send();
  } catch (err) {
    rollBack();
    reportUiFailure(source, err, ids);
    toast.error(
      isTransportFailure(err)
        ? "You're offline, so that change wasn't saved. Try again once you're reconnected."
        : "That change wasn't saved. Try again.",
    );
  }
}

// The caller's OWN manual-away flag. The relay re-broadcasts presence (incl.
// awayUserIds) to co-members but NOT back to the sender, so there's no server
// reader for self-away on the client — we track it here so every surface (the
// Settings "Away" toggle, etc.) shows a consistent state across navigations
// within the session, instead of resetting to "Off" on each mount.
export const selfPresenceState = $state({ away: false });

// P2 Item 6: toggle the caller's manual "away" presence so co-members see it.
export function setMyPresence(away: boolean): void {
  selfPresenceState.away = away;
  sendSelfPrefOrRollBack(
    "profile.set_presence",
    {},
    () => client.setPresence(away),
    () => (selfPresenceState.away = !away),
  );
}

// Do Not Disturb: this device's banners and sound, and (through the relay) push on every device.
export function setMyDnd(active: boolean): void {
  notifyClientPrefsState.setDnd(active);
  sendSelfPrefOrRollBack(
    "profile.set_dnd",
    {},
    () => client.setDnd(active),
    () => notifyClientPrefsState.setDnd(!active),
  );
}

// Last-viewed chat persistence (v1 "activitySession", Sidebar.tsx:859). Restores
// the exact chat — channel, DM, OR agent session — the user last had open per
// workspace on return/reload. Storage + validation live in $lib/last-chat.ts.
export function rememberLastChannel(workspaceId: string, channelId: string): void {
  rememberLastChat(workspaceId, { kind: "channel", id: channelId });
}

// Back-compat shim: callers here (closePreview, channel page) only ever want a
// channel, so return the remembered id only when the last chat WAS a channel.
export function getLastChannel(workspaceId: string): string | null {
  const ref = getLastChat(workspaceId);
  // A hidden group DM is a channel too: never hand it back as somewhere to return to (it would re-show).
  // Same rule as the landing: one not yet known to be visible (hidden set unloaded) is not handed back.
  return ref?.kind === "channel" &&
    !lastChatHidden(workspaceId, workspaceState.channels)("channel", ref.id)
    ? ref.id
    : null;
}

// Notification-preview text for an incoming message. Text wins when present;
// an image/video/file-only send (composer sends `text.trim()`, so `text` is
// `""` for an attachment-only message — NOT the union-typed non-string that
// the old `typeof msg.text === "string"` check was actually guarding against)
// falls back to a labeled first attachment instead of rendering an empty OS
// banner body. Glyphs mirror SharedFilesPanel's fileGlyph mapping (video 🎬,
// generic 📎) for consistency with the rest of the app's attachment
// iconography. Shared by all three notify call sites below so the preview
// logic can't drift between channel/DM/agent-DM.
export function messagePreviewText(
  msg: import("../core/types.js").Message,
  fallback: string,
): string {
  // CYBORG-1315: a card's text column is not safe to preview. An approval card's fallback text
  // carries the tool input (the draft), so the preview is its fixed title, the same body the
  // server's notifyApprovalCardRequested push uses. A memory receipt's text IS the saved
  // memory/order/promise, so it never previews at all.
  if (msg.card?.kind === "approval") return msg.card.title.slice(0, 120);
  if (msg.card?.kind === "memory_receipt") return fallback;
  if (typeof msg.text === "string" && msg.text.trim().length > 0) {
    // Unwrap BEFORE the slice, so the 120 chars counted are the 120 the reader
    // sees — otherwise two bracket characters per mention eat into the budget
    // and a long message truncates earlier in the banner than in the app. The
    // markdown collapse sits between the two for the same reason: the budget
    // has to be spent on words, never on syntax no banner can render.
    // Bounded slice first: cost independent of length, same as the server's messagePushPreview.
    const collapsed = collapseMessageMarkdown(mentionPlainText(msg.text.slice(0, 2000)));
    if (collapsed.text.length > 0) return collapsed.text.slice(0, 120);
    // An image-only message has no words left to show. `🖼️ Image` /
    // `🖼️ Image: <alt>` is not new copy: it is the exact register the
    // attachment branch below already produces, glyph included, because a
    // markdown image and an uploaded image are the same thing to the reader.
    // The meaningful word leads, so an OS truncating at a width we do not
    // control still leaves a readable body; the reported defect was a body that
    // opened with a URL, which is broken even when the URL is valid. An alt
    // that just says "image" adds nothing, so it is dropped rather than
    // repeated.
    if (collapsed.hadImage) {
      const alt = collapsed.imageAlt;
      // Capped like every other branch: a 400-character alt is still a body
      // from a function whose whole contract is 120.
      return alt && alt.toLowerCase() !== "image" ? `🖼️ Image: ${alt}`.slice(0, 120) : "🖼️ Image";
    }
    // Text that collapsed to nothing at all (`[](url)`, a bare rule) is an
    // empty message; fall through and let it be labeled like one.
  }
  const att = msg.attachments?.[0];
  if (att) {
    let glyph = "📎";
    let kind = "File";
    const type = att.type ?? "";
    if (type.startsWith("image/")) {
      glyph = "🖼️";
      kind = "Image";
    } else if (type.startsWith("video/")) {
      glyph = "🎬";
      kind = "Video";
    }
    return att.name ? `${glyph} ${kind}: ${att.name}` : `${glyph} ${kind}`;
  }
  // A forward sent with no note has no words of its own; the copy's text stays in the app.
  if (msg.card?.kind === "forwarded") return "Forwarded a message";
  return fallback;
}

// Resolve the workspace a message belongs to, for notification-URL purposes.
// CYBORG-480 investigation: `msg.workspaceId` was suspected to be "routinely
// absent on the wire," which would make this fall back to the WRONG (merely
// currently-open) workspace. A full-repo audit refuted that: `workspaceId` is
// `NOT NULL` on the messages table in both Postgres (db/schema.ts) and SQLite
// (storage.ts), REQUIRED (not optional) in the wire schema
// (CyborgChannelMessageBroadcastSchema/CyborgDmBroadcastSchema in
// cyborg-messages.ts), and stamped by literally every one of the 28
// channel_message_broadcast/dm_broadcast producer call sites across
// message-router.ts, relay-standalone.ts, workspace-relay.ts, bootstrap.ts,
// approval-card-daemon.ts, cybo-mention-invoke.ts, mcp/server.ts,
// routes/slack.ts, and routes/webhooks.ts — confirmed by reading every one.
// relay-redis.ts's cross-instance pub/sub is a transparent JSON passthrough
// (nothing stripped). Commit 7ef3d9858 (the one that made this field optional
// on the client Message type) confirms in its own message: "the relay already
// sends it" — the `?? currentWsId` fallback exists only as defense for
// theoretical/legacy payloads, not a live gap. `currentWsId` is dead weight in
// practice but kept as a harmless last resort. This helper centralizes the
// fallback so all three call sites agree; it does NOT indicate a real client
// -side resolution gap, and no server-side stamping follow-up is needed.
export function resolveMessageWorkspaceId(
  msg: import("../core/types.js").Message,
  currentWsId: string | undefined,
): string | undefined {
  return msg.workspaceId ?? currentWsId;
}

// The notification-click deep-link URL for an incoming channel message.
// CYBORG-480 (issue 2 investigation): pulled out of maybeFireChannelBanner so
// it's directly testable that this is well-formed and non-undefined for a real
// channel message — the audit in resolveMessageWorkspaceId's own comment
// established `msgWsId` is populated on every current live broadcast, so this
// should never actually return undefined for a genuine channel_message event;
// the `undefined` branch is defense only for the historical/malformed-payload
// edge case that resolveMessageWorkspaceId itself documents.
// knowledge/117: `messageId` appends `?m=` so the banner lands ON the message
// (the mention), not at the channel tail — the same deep link an alert row uses.
export function channelNotificationUrl(
  msgWsId: string | undefined,
  channelId: string | null,
  messageId?: string,
): string | undefined {
  if (!msgWsId || !channelId) return undefined;
  // knowledge/142: a host I reach as an external opens its channel in place, never switching.
  const onScreen = workspaceState.current?.id;
  const inPlace =
    !!onScreen &&
    onScreen !== msgWsId &&
    workspaceState.list.some((w) => w.id === msgWsId && !!w.external);
  const query = new URLSearchParams();
  if (messageId) query.set("m", messageId);
  if (inPlace) query.set("host", msgWsId);
  const qs = query.toString();
  const base = `/workspace/${(inPlace && onScreen) || msgWsId}/channel/${channelId}`;
  return qs ? `${base}?${qs}` : base;
}

// Does this client POSITIVELY know the user is in this thread? Two sources, both
// server-derived and both safe to trust in the affirmative:
//  • threadsState.list — cyborg:fetch_threads is
//    `JOIN thread_memberships ON user_id = me WHERE following = true`, i.e. the
//    relay's own followed-threads set for this user;
//  • the thread open in the panel — you are literally in it.
// Absence proves NOTHING: the list is only fetched when the Threads pane has
// been opened. So this is a one-way signal — it can say "yes, in the thread",
// never "no". A follower this client can't see still gets the authoritative
// per-user signals the relay fans out for them: `thread_updated` (Threads badge)
// and `activity_new` eventType "thread_reply" (Activity bell). Under-notifying
// here matches what the server persists; over-notifying is the reported bug.
function knownThreadParticipant(rootId: string): boolean {
  return threadState.parentId === rootId || threadsState.list.some((t) => t.root?.id === rootId);
}

// The policy input for an incoming channel message, built once so the thread
// gate below and the banner decision cannot drift apart.
function channelNotifyKind(
  msg: import("../core/types.js").Message,
  me: ViewerIdentity,
): Extract<NotificationKind, { kind: "channel_message" }> {
  return {
    kind: "channel_message",
    channelId: msg.channelId!,
    // A webhook/automation card carries the creator's user id, but it isn't the
    // user's own send — drop it from the policy's self-check so it still notifies.
    senderId: msg.source === "webhook" ? undefined : msg.fromId,
    text: typeof msg.text === "string" ? msg.text : "",
    parentId: msg.parentId ?? null,
    // Proven membership only: following the thread / having it open, or being in
    // the reply's server-parsed mentions[] (an id list the client cannot fake).
    // The text-derived personal @mention leg is the policy's own job
    // (isPersonalHighlight).
    threadParticipant:
      !!msg.parentId &&
      (knownThreadParticipant(msg.parentId) || (msg.mentions?.some((m) => isMe(m, me)) ?? false)),
  };
}

// Fire the OS banner for an incoming channel message if the policy allows.
function maybeFireChannelBanner(
  msg: import("../core/types.js").Message,
  o: {
    mentioned: boolean;
    preview: string;
    msgWsId?: string;
    kind: Extract<NotificationKind, { kind: "channel_message" }>;
  },
): void {
  const decision = shouldNotifyFor(o.kind, notifyContext());
  if (!decision.notify) return;
  const channel = workspaceState.channels.find((c) => c.id === msg.channelId);
  // Same author identity as the channel row (liveHumanName): a webhook post
  // resolves to the card's GitHub actor, not the token owner — a banner that
  // said "acme/repo" for a message the channel attributes to "octocat" reads
  // as a different event when the user clicks through.
  // "Someone" idFallback: a standalone banner must never render the bare
  // fromId fragment (nullable `users.name`, no default — see authorName()).
  const who = liveHumanName(msg, workspaceState.members, "Someone");
  const title = o.mentioned
    ? `${who} mentioned you${channel?.name ? ` in #${channel.name}` : ""}`
    : `#${channel?.name ?? "channel"}`;
  const url = channelNotificationUrl(o.msgWsId, msg.channelId, msg.id);
  playNotificationSound();
  renderOSNotification(title, o.mentioned ? o.preview : `${who}: ${o.preview}`, url);
}

// Fire the OS banner + sound for an incoming human DM if the policy allows.
// DMs notify by default (Slack/Mattermost parity) — suppressed only when muted
// or when focused on that exact conversation (both handled by the policy).
function maybeFireDmBanner(
  msg: import("../core/types.js").Message,
  peerId: string,
  preview: string,
  wsId: string | undefined,
): void {
  const decision = shouldNotifyFor(
    {
      kind: "dm_human",
      fromUserId: msg.fromId,
      text: typeof msg.text === "string" ? msg.text : "",
    },
    notifyContext(),
  );
  if (!decision.notify) return;
  // knowledge/117: land on the DM itself (`?m=`), not at the conversation's tail.
  const url = wsId ? `/workspace/${wsId}/dm/${peerId}?m=${encodeURIComponent(msg.id)}` : undefined;
  playNotificationSound();
  renderOSNotification(msg.fromName ?? "Direct message", preview, url);
}

// Badge increment + activity + OS-notification for an incoming channel message
// True only when the app window actually has OS focus. `visibilityState` alone
// is not enough: a window can be "visible" yet unfocused (another app on top),
// in which case incoming messages to the open conversation SHOULD still badge —
// the user isn't looking. Mirrors Mattermost's window-focus check.
function windowFocused(): boolean {
  if (typeof document === "undefined") return false;
  if (document.visibilityState !== "visible") return false;
  // iOS WKWebView: document.hasFocus() is unreliable — it frequently returns
  // false even when the app is foreground and the chat is on-screen. That made
  // a message arriving in the chat you're VIEWING still get marked unread, so
  // opening it then leaving left it bold ("unread won't clear"). On the iOS
  // shell there is no separate window-focus concept: visible == focused. Mirrors
  // v1's mobile foreground detection (document.visibilityState). Web/desktop keep
  // the hasFocus() check — there you can see a tab without it being focused.
  return isTauriIOS() ? true : document.hasFocus();
}

// (own messages excluded). Extracted from the handler to keep it simple.
// oxlint-disable-next-line eslint/complexity -- mention/badge/banner gating is one decision tree
function notifyForIncoming(msg: import("../core/types.js").Message): void {
  const me = authState.user;
  const myId = me?.id;
  // Webhook/CI cards are injected under the creator's user id, but they're an
  // automation — NOT the user's own typing — so they must not be echo-suppressed
  // like a self-send (otherwise whoever created the webhook never gets a
  // sound/unread for its cards, even with "all messages" notifications on).
  const isAutomation = msg.source === "webhook";
  if (msg.imported) return; // CYBORG-1265: backfilled history is not news
  // System messages ("X joined the channel") render inline but never badge/notify.
  if (
    !myId ||
    (isMe(msg.fromId, me) && !isAutomation) ||
    !msg.channelId ||
    msg.fromType === "system"
  )
    return;
  // A THREAD REPLY is not channel traffic (Slack parity): it reaches thread
  // participants only. Everything below this line — the bold, the red badge, the
  // Activity row, the sound and the OS banner — is channel-scoped, so a reply in
  // a thread this user isn't in stops here rather than notifying every member of
  // #general. That was the reported bug, and it hit EVERYONE: the bold below is
  // gated on `pref !== "muted"`, which is the default. The server already agreed
  // with the rule — its unread/mention reseed (getUnreadCounts/getMentionCounts)
  // filters `isNull(parentId)` — so the badge this raised was live-only and
  // vanished on the next refresh. Followers keep their signal via the relay's
  // per-user thread events (see knownThreadParticipant).
  const kind = channelNotifyKind(msg, me);
  if (!threadReplyReachesUser(kind, notifyContext())) return;
  const preview = messagePreviewText(msg, "New message");
  const currentWsId = workspaceState.current?.id;
  const msgWsId = resolveMessageWorkspaceId(msg, currentWsId);
  // Track channel recency in the same activity store DMs use (keyed by id), so
  // the unified mobile inbox can sort channels + DMs together by last activity.
  if (msgWsId && msg.channelId) dmActivityState.bump(msgWsId, msg.channelId, msg.createdAt);
  // Active = viewing THIS channel with the window focused — full stop. It does
  // NOT matter who authored the message or whose /agent page you're on: viewing
  // a cybo's /agent page (a terminal/session host) never shows the channel it
  // posted to, so a post there is genuinely unread and must bold the channel.
  // The reverted "viewingThisAgent" branch (0dcc18306) suppressed exactly that,
  // so a cybo posting to #X on your behalf left #X un-bolded even though you'd
  // never opened it. See channel-message-active.ts for the full rationale. (An
  // agent's DM reply keeps its own focus-suppression in handleIncomingAgentDm,
  // which is correct — the DM route does render that conversation.)
  const isActive = channelMessageIsActive({
    windowFocused: windowFocused(),
    conversationRendered: isRendered("channel", msg.channelId, channelState.activeId),
    messageWorkspaceId: msgWsId,
    currentWorkspaceId: workspaceState.channelWorkspaceId(msg.channelId),
  });

  // BUG #2: keep the BOLD signal (unread flag) and the RED NUMBER (mention count)
  // independent, matching Mattermost + v1 (Sidebar.tsx unreadChannels vs
  // mentionCounts). A plain message bolds the channel but must NOT bump the red
  // badge — only a real mention does. Compute `mentioned` FIRST so it gates both
  // the badge and the OS banner. The mention test covers @everyone/@here/@channel
  // and a `@name` word-boundary match (hasMention), plus the explicit mentions[].
  // Per-workspace display name (0121) — see myDisplayName / notifyContext.
  const myName = authState.selfName ?? authState.user?.email?.split("@")[0];
  const text = typeof msg.text === "string" ? msg.text : "";
  const mentioned = (msg.mentions?.some((m) => isMe(m, me)) ?? false) || hasMention(text, myName);
  const pref = notifPrefsState.get(msg.channelId) as NotificationPref;

  // Resurface an archived-for-me channel: human root message (not muted) or a mention of me.
  // Same rule as pg-sync getArchivedChannelIdsForUser.
  // A mention resurfaces from a thread reply too (it is written TO me); a plain human message only
  // when it is a root.
  if (
    isArchivedForMe(msg.channelId) &&
    ((!msg.parentId && msg.fromType === "human" && pref !== "muted") || mentioned)
  ) {
    archivedChannelsState.resurface(msg.channelId);
    // Its unread alerts count again; for a workspace that is not open only the relay knows them.
    if (msgWsId !== currentWsId) hiddenAlertsChanged();
  }

  // ROOTS ONLY. The channel's unread state (bold + red number) is reseeded from
  // getUnreadCounts/getMentionCounts, both of which filter `isNull(parentId)` —
  // so a reply that bolded or badged the channel here produced a signal the very
  // next refresh erased. A followed thread's unread lives in the Threads badge
  // instead, maintained by the per-user `thread_updated` deltas.
  if (msgWsId && !isActive && pref !== "muted" && !msg.parentId) {
    const isCurrentWs = msgWsId === currentWsId;
    if (!isCurrentWs || workspaceState.channels.some((c) => c.id === msg.channelId)) {
      // Any non-muted unread → BOLD the channel name.
      unreadFlagState.markUnread(msgWsId, msg.channelId);
      // Only a mention the SERVER can count → bump the RED NUMBER. getMentionCounts
      // matches a structured id or the three group tokens, nothing else, so a
      // plain-text `@name` would raise a number no reseed can ever confirm. It keeps
      // its OS banner and its Activity row below — only the red count is narrowed.
      if (serverCountableMention(msg.mentions, text, me)) {
        notificationState.increment(msgWsId, msg.channelId);
        recordUnreadIncrement(msgWsId, msg.channelId, "channel");
      }
    }
  }

  // Muted channels stay out of the Activity feed too (B1) — the badge already
  // zeroes for a muted scope, so a mention landing in the inbox would be the one
  // surface still leaking it. `pref` is the read from a few lines up.
  // The reader's own line when the message names several people (activity-preview.ts).
  const myLine = mentioned ? mentionLine(text, me) : undefined;
  const mentionPreview = myLine ? messagePreviewText({ ...msg, text: myLine }, "New message") : preview; // prettier-ignore
  if (mentioned && pref !== "muted") handleIncomingMention(msg, mentionPreview);
  maybeFireChannelBanner(msg, { mentioned, preview, msgWsId, kind });
}

// Author-only ephemeral notice about a cybo @-mention that couldn't invoke
// (not a channel member / no runnable daemon / spawn failed). Rendered as a
// LOCAL system note pinned at the bottom of the channel (the #366/#210
// pattern: seq:0, ~30s TTL, never persisted). Other members never see it.
client.on("cybo_mention_notice", (p) => {
  if (!isMe(p.toUserId, authState.user)) return;
  if (p.channelId !== channelState.activeId) return;
  const noteId = `cybo-mention-note-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  channelState.addMessage({
    id: noteId,
    channelId: p.channelId,
    fromId: "system",
    fromType: "system",
    text: p.text,
    seq: 0,
    createdAt: Date.now(),
  });
  setTimeout(() => channelState.removeMessage(noteId), 30_000);
});

// Author-only signal that a channel @-mention was HELD behind that cybo's running
// turn instead of being answered now. Unlike the notice above this is not a decline
// and posts nothing to the channel — it decorates the sender's OWN message with a
// "Queued for @x" pill (ChatMessage.svelte). `queued: false` is the clear, sent when
// the daemon releases the mention to the real session or expires it, so the badge
// always comes down on its own.
client.on("cybo_mention_queued", (p) => {
  if (!isMe(p.toUserId, authState.user)) return;
  // A share-hold reason keeps the pill up even with queued:false (share_unavailable is terminal copy).
  const reason = parseHoldReason(p.reason);
  // the asker's own card buttons ride the pill only while it waits on them
  const ask = reason === "awaiting_owner_share" && p.holdId && p.actions?.length ? { holdId: p.holdId, actions: p.actions } : undefined; // prettier-ignore
  if (reason)
    mentionQueueState.hold(
      p.messageId,
      p.cyboId,
      reason,
      ask ? "you" : p.ownerName,
      p.write === true,
      ask,
    );
  else if (p.queued) mentionQueueState.hold(p.messageId, p.cyboId);
  else mentionQueueState.release(p.messageId);
});

// /catchup digest arrives ephemerally (scoped to this user by the relay). Fill
// the sheet — open it if the digest came from the composer path (where the sheet
// wasn't pre-opened). Never touches the channel: a personal digest, rendered here.
client.on("catchup_result", (p) => {
  if (!isMe(p.toUserId, authState.user)) return;
  catchupState.resolve(p);
});

client.on("channel_message", (msg) => {
  noteA2aRefusal(msg);
  // Before anything renders: an external Slack guest is NOT in the roster this client
  // loaded, and their first message is the only place their name + avatar exist yet
  // (CYBORG-956). No-op for members and agents.
  authState.rememberExternalIdentity(msg.fromId, msg.fromName, msg.fromImage);
  // isMe, not `=== authState.user.id`: `fromId` on a channel echo is stamped by the
  // relay (cloud id) OR by the daemon's message-router (the daemon-LOCAL id bootstrap
  // remaps this human onto), so the raw compare called my own echo someone else's and
  // the outbox record below was never dropped. Mirrors the `dm` handler's own fromMe.
  const fromMe = isMe(msg.fromId, authState.user);
  if (msg.parentId) {
    // Thread reply: never in the main list. Bump the parent indicator + feed
    // the thread panel if it's open.
    if (msg.channelId === channelState.activeId) {
      channelState.bumpReplyCount(msg.parentId, msg, workspaceState.agents, cyboState.list);
    }
    threadState.receiveReply(msg);
    // #515: the sender's message landed → drop their "is typing…" in the thread
    // immediately instead of waiting out the 5s expiry (no-op if not the open
    // thread / not typing).
    threadState.clearTypingFor(msg.fromId);
    // My own thread reply just echoed back — drop its persisted outbox record
    // (#502) so a reload doesn't re-send it. No-op unless the reply was queued
    // (socket was down / unauthenticated at send time). Mirrors the main-list
    // branch below; without this a queued reply would replay forever.
    if (fromMe) dequeueOutbox(msg.clientMsgId);
  } else if (msg.channelId === channelState.activeId) {
    channelState.addMessage(msg);
    // #515: clear the sender's channel typing indicator now their message is on
    // screen (no ≤5s overlap). Own messages never have a typing entry → no-op.
    channelState.clearTypingFor(msg.fromId);
    // My own message just echoed back and collapsed its optimistic row — cancel
    // its failure watchdog so a slow-but-successful send never shows as failed,
    // and drop its persisted outbox record (#502) so a reload never re-sends it.
    if (fromMe) {
      sweepReconciledWatchdogs("channel", msg.channelId, msg.clientMsgId);
      dequeueOutbox(msg.clientMsgId);
    }
    // #516: an incoming message in the focused active channel advances the read
    // cursor live (own echoes already imply read). Throttled + focus-gated.
    else if (msg.fromType !== "system") scheduleLiveReadAdvance();
  }
  settleOwnEcho(fromMe, msg.clientMsgId);
  // Keep the local cache in sync. Upsert the live message into its channel scope
  // so a cold revisit is fresh; fire-and-forget, no-op when the cache is off.
  cacheSyncIncoming(msg);
  // Channel recency for your OWN post. notifyForIncoming owns the recency bump
  // for everyone ELSE's messages, but it early-returns on a self-echo (it is a
  // notification path — you never notify yourself), so posting in a group DM
  // never moved that conversation in a recency-ordered list. Bumped HERE rather
  // than by relaxing notifyForIncoming's isMe guard: that guard sits ABOVE the
  // thread-reachability check, so hoisting the bump into it would also start
  // re-ranking channels on thread replies that never reach this user. Roots
  // only, and never a system card, for the same reason.
  if (fromMe && !msg.parentId && msg.channelId && msg.fromType !== "system") {
    const selfWsId = resolveMessageWorkspaceId(msg, workspaceState.current?.id);
    if (selfWsId) dmActivityState.bump(selfWsId, msg.channelId, msg.createdAt);
  }
  // Badge + activity-feed + policy-based OS banner (P9). System messages
  // ("X joined the channel") show inline but never badge/notify — that guard
  // lives inside notifyForIncoming.
  notifyForIncoming(msg);
});

// A DM from the peer (not my own echo): badge it unless I'm focused on that exact
// conversation, and surface it in the activity feed. Extracted to keep the `dm`
// handler under the complexity budget.
function handleIncomingDmFromPeer(msg: import("../core/types.js").Message, peerId: string): void {
  // A DM thread reply is not a DM: the relay counts it under the thread (never
  // getDmUnreadCounts) and alerts it as thread_reply over activity_new — badging and
  // feeding it here as well made one message two alerts (#3131 R4).
  if (msg.parentId) return;
  const wsId = resolveMessageWorkspaceId(msg, workspaceState.current?.id);
  const isActiveDm =
    windowFocused() &&
    isRendered("dm", peerId, dmState.activePeerId) &&
    wsId === workspaceState.current?.id;
  // NEVER badge a "system" sender. The relay delivers its own notices (e.g. an
  // outgoing webhook deactivated after repeated delivery failures) as a DM
  // stamped `fromId: "system"` / `fromType: "system"` addressed to the owner, so
  // it clears this handler's same-human gate like any peer DM. The server's own
  // DM unread query excludes it on purpose (`m.from_type <> 'system'` in
  // getDmUnreadCounts), and no Direct Messages row exists for a peer called
  // "system" — so incrementing here raised a count on a key the workspace/dock
  // totals sum but nothing in the DM list can clear or reconcile. The notice
  // itself still reaches the viewer: the Activity row and the OS banner below
  // are deliberately left intact.
  //
  // ROOTS ONLY, exactly like the channel twin in notifyForIncoming. getDmUnreadCounts
  // filters `m.parent_id IS NULL`, so a DM THREAD reply raises a count no reseed can
  // ever return — the reply's own signal is the Threads badge + Activity row instead.
  if (wsId && !isActiveDm && !msg.parentId && msg.fromType !== "system") {
    notificationState.increment(wsId, peerId);
    recordUnreadIncrement(wsId, peerId, "dm_human");
    healUnknownDmMembers(wsId, [peerId]);
  }
  const preview = messagePreviewText(msg, "Direct message");
  // Only surface in the Activity feed when the DM belongs to the workspace
  // currently open. The feed is per-workspace (ActivityItem carries no
  // workspaceId and seedFromServer replaces it per workspace), so an unscoped
  // push leaks another workspace's DM into the current feed (the "Activity shows
  // a message from another workspace" bug). The cross-workspace unread badge
  // above stays unconditional so other workspaces still light up in the rail.
  // Rendered DM => the server wrote the row read and sends no activity_badge; a local unread row
  // would bump the badge with nothing to correct it.
  if (wsId === workspaceState.current?.id && !isActiveDm && !pushHeldPromptNotice(msg)) {
    activityState.push(
      "dm_received",
      msg.fromName ?? msg.fromId,
      msg.fromId,
      msg.fromType,
      preview,
      null,
      null,
      msg.id,
    );
  }
  // Fire an OS banner + sound for the DM (channels only did this for @mentions).
  maybeFireDmBanner(msg, peerId, preview, wsId);
}

// The agent currently open in the UI (route .../agent/:agentId). Read from the
// URL at event time — no reactive state needed for an event-handler decision.
function activeAgentIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/\/agent\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// Cybo DM identity canonicalization (#1185 follow-up) — wires the live
// workspaceState.agents deps into the testable pure mapping in
// cybo-dm-peer-id.ts (see there for the full "why"; app.svelte.ts can't be
// imported under plain-node vitest, same reason unread-reconcile.ts is split
// out).
function canonicalAgentDmPeerId(agentId: string): string {
  return resolveCanonicalAgentDmPeerId(workspaceState.agents, agentId);
}

// Fire the OS banner + sound for an agent's DM reply (dm_agent policy: notify by
// default unless muted or focused on that agent's conversation).
function maybeFireAgentDmBanner(
  msg: import("../core/types.js").Message,
  agentId: string,
  preview: string,
  wsId: string | undefined,
): void {
  // CYBORG-1315: no banner for a memory receipt, same as the server push (#4047). It confirms
  // the viewer's own action under a reply that already notified, and its text is private.
  if (msg.card?.kind === "memory_receipt") return;
  const decision = shouldNotifyFor(
    {
      kind: "dm_agent",
      // Canonicalize agent→cybo for the mute/dedup policy so it agrees with the
      // badge key; the URL below keeps the RAW session id (navigation resolves a
      // live session on /agent/:id, not the stable cybo id).
      remoteAgentId: canonicalAgentDmPeerId(agentId),
      text: typeof msg.text === "string" ? msg.text : "",
    },
    notifyContext(),
  );
  if (!decision.notify) return;
  // The notification names itself and its message, so the route re-reads it on entry (#3244).
  const url = wsId
    ? notificationEntryUrl(`/workspace/${wsId}/agent/${agentId}`, msg.id)
    : undefined;
  playNotificationSound();
  // CYBORG-1315: same body as the server push for a reply whose turn read private material.
  renderOSNotification(
    sessionDisplayName(
      { agentId: msg.sessionAgentId ?? agentId },
      undefined,
      msg.fromName ?? "Agent",
    ),
    msg.previewPrivate ? "New message" : preview,
    url,
  );
}

// ─── Badge telemetry (knowledge/123 § Unread badges) ───────────────────────
const unreadIncrements = new UnreadIncrementTelemetry();
const unreadRowInvariants = new UnreadRowInvariants();

function unreadRosters(): UnreadRosters {
  return {
    channelIds: new Set(workspaceState.channels.map((c) => c.id)),
    memberIds: new Set(workspaceState.members.map((m) => m.userId)),
    cyboIds: new Set(cyboState.list.map((c) => c.id)),
    sessionIds: new Set(workspaceState.agents.map((a) => a.agentId)),
  };
}

function recordUnreadIncrement(
  workspaceId: string,
  key: string,
  surfaceKind: UnreadSurfaceKind,
  source: UnreadSource = "live",
  count = 1,
  keyKind: UnreadKeyKind = unreadKeyKind(key, unreadRosters()),
): void {
  unreadIncrements.record({ workspaceId, surfaceKind, source, keyKind }, key, count);
}

// Checks the badge invariants against the rows a sidebar renders (`rowIds`: raw row ids,
// as its templates pass them to badgeCount). Returns when a pending state is due again.
export function checkUnreadRows(workspaceId: string, rowIds: Iterable<string>): number | undefined {
  const rowCounts = new Map<string, number>();
  for (const id of rowIds) rowCounts.set(canonicalAgentDmPeerId(id), badgeCount(workspaceId, id));
  const rosters = unreadRosters();
  const keyKind = (key: string) => unreadKeyKind(key, rosters);
  const doorless = doorlessKeys(workspaceId);
  return unreadRowInvariants.check({
    workspaceId,
    counts: notificationState.countsForWorkspace(workspaceId),
    // Archived-for-me channels are hidden on purpose and read 0 in every selector: not a missing door.
    isMuted: (key) => notifPrefsState.get(key) === "muted" || isArchivedForMe(key),
    hasOtherDoor: (key) =>
      activityState.items.some((i) => i.actorType === "agent" && i.sourceId === `dm:${key}`),
    isDoorless: (key) => doorless.has(key),
    rowCounts,
    keyKind,
    surfaceKind: (key) =>
      unreadIncrements.surfaceOf(workspaceId, key) ??
      (rosters.channelIds.has(key) ? "channel" : dmSurfaceKind(keyKind(key))),
  });
}

// A badged DM peer the rosters don't hold re-reads them (see DmSurfaceHealGate).
const dmSurfaceHeal = accountSync.own("dmSurfaceHeal", new DmSurfaceHealGate(60_000));
const DM_SURFACE_HEAL_RETRY_MS = 3_000;
const dmHealPending = accountSync.collection("dmHealPending", new Map<string, Set<string>>());
const dmHealRunning = accountSync.collection("dmHealRunning", new Set<string>());

const dmHealPump = new DmSurfaceHealPump(dmSurfaceHeal, dmHealPending, dmHealRunning, {
  isCurrent: (wsId) => workspaceState.current?.id === wsId,
  readOut: (wsId) => workspaceState.agentsLoading || cyboState.loading || cybosLoad?.wsId === wsId,
  reachable: (peerId) =>
    agentDmHasReachableSurface(
      peerId,
      cyboState.list.map((c) => c.id),
      workspaceState.agents,
      authState.user,
    ),
  reread: (wsId, peerIds) => rereadRostersFor(wsId, peerIds),
});

async function rereadRostersFor(wsId: string, peerIds: readonly string[]): Promise<void> {
  const [cybos, agents] = await Promise.allSettled([loadCybosOnce("heal"), relistAgents(wsId)]);
  const failures: { roster: string; err: unknown }[] = [];
  if (cybos.status === "rejected") failures.push({ roster: "cybos", err: cybos.reason });
  else if (cybos.value.status === "failed")
    failures.push({ roster: "cybos", err: cybos.value.error });
  if (agents.status === "rejected") failures.push({ roster: "agents", err: agents.reason });
  if (failures.length === 0) {
    for (const peerId of peerIds) dmSurfaceHeal.succeeded(wsId, peerId);
    return;
  }
  for (const { roster, err } of failures) {
    reportUiFailure("dm.surface_heal", err, { workspaceId: wsId, peerId: peerIds[0], roster });
  }
  const retry = peerIds.filter((peerId) => dmSurfaceHeal.retryAfterFailure(wsId, peerId));
  if (retry.length > 0) setTimeout(() => dmHealPump.heal(wsId, retry), DM_SURFACE_HEAL_RETRY_MS);
}

// A human DM from someone the loaded member list lacks (joined after it loaded) has no row
// to open: the members are re-read, at most once a minute per peer (#3812).
const dmMemberHeal = accountSync.own("dmMemberHeal", new DmSurfaceHealGate(60_000));
const memberReread = accountSync.own("memberReread", new MemberRereadRunner(refreshMembers));
const membersReadOrder = accountSync.own("membersReadOrder", new LatestReadGate());
function humanDmActors(): Set<string> {
  const ids = activityState.items.flatMap((i) =>
    i.eventType === "dm_received" && i.actorType === "human" && i.actorId ? [i.actorId] : [],
  );
  return new Set(ids);
}
function healUnknownDmMembers(wsId: string, peerIds: readonly string[]): void {
  if (wsId !== workspaceState.current?.id || workspaceState.membersLoading) return;
  const known = new Set(
    [...workspaceState.members, ...workspaceState.external].map((m) => m.userId),
  );
  const now = Date.now();
  let reread = false;
  for (const id of peerIds) {
    if (known.has(id) || isMe(id, authState.user)) continue;
    if (dmMemberHeal.shouldHeal(wsId, id, now)) reread = true;
  }
  if (reread) memberReread.request(wsId);
}

function orphanDmPeers(wsId: string): string[] {
  return orphanAgentDmPeerIds(
    notificationState.countsForWorkspace(wsId),
    workspaceState.channels.map((c) => c.id),
    workspaceState.members.map((m) => m.userId),
    cyboState.list.map((c) => c.id),
    workspaceState.agents,
    authState.user,
  );
}

// A root DM raises the red count; an approval card does not (its Alerts row and the hand say it).
const raisesDmCount = (msg: import("../core/types.js").Message): boolean =>
  !msg.parentId && msg.card?.kind !== "approval";

// An agent reply arrives as a dm_broadcast with fromType "agent". Badge it keyed
// by agentId (agents live on /agent/:id, not the DM route) unless focused on that
// agent's page, and fire a notification — Paseo-style red-badge parity.
function handleIncomingAgentDm(msg: import("../core/types.js").Message, agentId: string): void {
  const wsId = resolveMessageWorkspaceId(msg, workspaceState.current?.id);
  // Canonicalize the live-session id agent→cybo BEFORE it becomes a badge key,
  // so the id we increment is the SAME id clearActiveConversationBadge clears
  // and markDmRead heals (both go through canonicalAgentDmPeerId). Post-#1185
  // msg.fromId is already the cybo id, so this is idempotent in the common case
  // (canonicalAgentDmPeerId returns a non-session id unchanged); it hardens the
  // path against any dm_broadcast that still carries a raw per-summon session id
  // — the wave-badge bug, where increment(agentId) + clear(cyboId) keyed
  // different buckets and orphaned the rail/dock badge until the next reseed.
  const peerId = canonicalAgentDmPeerId(agentId);
  // The URL still carries the live session's raw id — canonicalize it before
  // comparing, or an actively-open cybo conversation never matches "active" and
  // badges itself while on screen.
  const activeUrlAgentId = activeAgentIdFromUrl();
  const isActive =
    windowFocused() &&
    agentDmReadState.canRead(peerId) &&
    isRendered(
      "agent",
      peerId,
      activeUrlAgentId,
      (a, b) => canonicalAgentDmPeerId(a) === canonicalAgentDmPeerId(b),
    ) &&
    wsId === workspaceState.current?.id;
  // ROOTS ONLY, same rule as the human twin: getDmUnreadCounts filters
  // `m.parent_id IS NULL`, so badging a DM thread reply raises a count the server
  // can never confirm or clear. The reply still reaches the Threads badge. An approval card is not
  // a message: its Alerts row and the row's raised hand say it (getDmUnreadCounts skips it too).
  if (wsId && !isActive && raisesDmCount(msg)) {
    notificationState.increment(wsId, peerId);
    recordUnreadIncrement(wsId, peerId, "dm_agent");
  }
  // Seen as it arrived: move the server cursor too, or the next unread reseed badges messages
  // the viewer watched land (every setup card). Never before the relay's own timestamp.
  if (wsId && isActive && !msg.parentId) {
    const readAt = Math.max(Date.now(), msg.createdAt);
    if (connectionState.status === "connected") client.markDmRead(wsId, peerId, readAt);
    readCursorState.advanceDm(peerId, readAt);
  }
  const preview = messagePreviewText(msg, "Message");
  // THE BADGE MUST HAVE A DOOR. Everything above this line raises a red count
  // keyed on `peerId`; the ONLY places that clear it are the Direct Messages row
  // (built from the cybo roster) and the viewer's own Sessions rows. A DM from a
  // plain provider session — a Claude/Codex harness run with no cybo identity,
  // which `cyborg7_send_message { to }` lets it send — matches neither, so the
  // count was raised on an id nothing renders and nothing could mark read: a red
  // number the recipient could only see in a push banner and never clear (the
  // next `fetch_unread` reseeds it for as long as the sender's session is
  // unarchived). Put those, and only those, in Activity — the app's inbox —
  // where the row's `dmPeer` read scope runs the SAME `client.markDmRead` /
  // `dm_reads` cursor every other DM uses. See agent-dm-surface.ts.
  //
  // Scoped to the OPEN workspace exactly like the human-DM twin
  // (handleIncomingDmFromPeer): ActivityItem carries no workspaceId and
  // seedFromServer replaces the feed per workspace, so an unscoped push leaks
  // another workspace's DM into the current feed. The cross-workspace badge
  // above stays unconditional, and a workspace the viewer later opens rebuilds
  // these rows from its own reseeded counts (backfillOrphanAgentDms).
  if (
    wsId === workspaceState.current?.id &&
    !isActive &&
    !agentDmHasReachableSurface(
      peerId,
      cyboState.list.map((c) => c.id),
      workspaceState.agents,
      authState.user,
    )
  ) {
    activityState.pushAgentDm(
      peerId,
      msg.id,
      agentDmPeerName(msg.fromName, peerId, workspaceState.agents, cyboState.list),
      preview,
      new Date(msg.createdAt).toISOString(),
      msg.sessionAgentId ?? agentId,
    );
    if (wsId) dmHealPump.heal(wsId, [peerId]);
  }
  // Badge/dm-list use the canonical peerId (above); the banner takes the RAW
  // agentId so clicking the notification navigates to the live session.
  maybeFireAgentDmBanner(msg, agentId, preview, wsId);
}

// #17 — THE one place DM recency is recorded, and the reason it is only one.
//
// Every real DM this viewer takes part in — one they sent, one they received,
// one from a person, one from an agent, one bridged in from an external chat
// surface — is persisted and re-broadcast by the relay and comes back through
// the single `dm` handler below. Recording recency there, rather than at each
// individual send/receive site, is what makes "sent" and "bridged" count at all:
// four separate writers used to cover only two of those cases.
//
// CALLED ABOVE that handler's `if (!fromMe && peerId)` gate, and it must stay
// above it. A message the viewer originates arrives back as their OWN echo
// (fromMe is true), and so does a send made from a bridged external surface and
// a prompt given to an agent — the gate drops all three, so a bump underneath it
// would silently never fire for the conversation the viewer just spoke in.
//
// `msg.createdAt` is the RELAY's clock and the only clock used here: bump is
// monotonic, so a client running fast would stamp a peer in the future and then
// swallow every genuinely newer inbound message until wall-clock caught up.
// Canonicalised because a cybo's live session id is a fresh UUID per summon
// while the list renders under the stable cybo id. A system notice has no DM row
// to re-rank (see handleIncomingDmFromPeer), so it is excluded.
function recordDmRecency(msg: import("../core/types.js").Message, peerId: string | null): void {
  if (!peerId || msg.fromType === "system") return;
  const wsId = resolveMessageWorkspaceId(msg, workspaceState.current?.id);
  if (wsId) dmActivityState.bump(wsId, canonicalAgentDmPeerId(peerId), msg.createdAt);
}

// An agent's reply arrives as a DM frame only AFTER its row is written (broadcastAgentReply):
// the moment the newest reply in an agent chat becomes reactable. Its own listener, so the
// DM handler below keeps its complexity budget.
client.on("dm", (reply) => {
  if (reply.fromType === "agent" && reply.id) rowReactions.track(reply.id);
});

client.on("dm", (msg) => {
  const me = authState.user;
  if (!me?.id) return;
  // The relay broadcasts DMs workspace-wide, so ignore any DM I'm not part of —
  // otherwise everyone gets a phantom badge (and a content leak) for other
  // people's conversations.
  //
  // isMe, not `=== myId`: this ONE gate sits in front of everything below it —
  // rendering, dmActivityState.bump, the badge, maybeFireAgentDmBanner — so when an
  // agent's reply arrived stamped with my OTHER id (a daemon-forwarded DM carries the
  // daemon-LOCAL recipient id; `authState.user.id` is the cloud one) the message was
  // dropped here and the human saw NOTHING: no banner, no badge, no message, ever.
  // Still strictly same-human, so someone else's conversation is refused exactly as
  // before.
  const fromMe = isMe(msg.fromId, me);
  if (!fromMe && !isMe(msg.toId, me)) return;

  const peerId = fromMe ? (msg.toId ?? null) : msg.fromId;
  // #17 DM recency, recorded ONCE for every DM this viewer is part of, and
  // DELIBERATELY ABOVE the `if (!fromMe && peerId)` gate further down — it must
  // stay there. See recordDmRecency for the whole reasoning.
  recordDmRecency(msg, peerId);
  // #515: an incoming DM from the peer means they stopped typing — clear their
  // SIDEBAR typing badge (keyed by the typing sender's id) now, instead of letting
  // it linger up to 5s. Own echoes are a no-op; covers both main-list and thread.
  if (!fromMe) dmTypingState.clearFor(msg.fromId);
  if (msg.parentId) {
    // DM thread reply: never in the main DM list. Bump the root's footer (if the
    // root is in view) and feed the open thread panel — mirrors channel_message.
    if (peerId && peerId === dmState.activePeerId) {
      dmState.bumpReplyCount(msg.parentId, msg, workspaceState.agents, cyboState.list);
    }
    threadState.receiveReply(msg);
    // #515: drop the sender's typing indicator in the open DM thread now their
    // reply is on screen (no-op if not the open thread / not typing).
    threadState.clearTypingFor(msg.fromId);
    // My own DM thread reply just echoed back — drop its persisted outbox record
    // (#502) so a reload doesn't re-send it (mirrors the channel_message branch).
    if (fromMe) dequeueOutbox(msg.clientMsgId);
  } else if (peerId && peerId === dmState.activePeerId) {
    dmState.addMessage(msg);
    // #515: clear the sender's DM typing indicator now their message rendered.
    dmState.clearTypingFor(msg.fromId);
    // My own DM just echoed back and collapsed its optimistic row — cancel its
    // failure watchdog so a slow-but-successful send never shows as failed, and
    // drop its persisted outbox record (#502) so a reload never re-sends it.
    if (fromMe) {
      sweepReconciledWatchdogs("dm", peerId, msg.clientMsgId);
      dequeueOutbox(msg.clientMsgId);
    }
    // #516: an incoming human DM in the focused active conversation advances the
    // read cursor live. Agent replies surface via /agent/:id (not the DM cursor),
    // so scope this to human DMs.
    else if (msg.fromType !== "agent") scheduleLiveReadAdvance();
  }
  settleOwnEcho(fromMe, msg.clientMsgId);
  // Keep the local cache in sync for HUMAN DMs. Agent replies arrive here with
  // fromType "agent" but are surfaced via agentStreamState, not selectDm — they
  // have no DM cache scope, so don't pollute it. Fire-and-forget.
  if (msg.fromType !== "agent") cacheSyncIncoming(msg);
  if (!fromMe && peerId) {
    if (msg.fromType === "agent") {
      handleIncomingAgentDm(msg, peerId);
    } else {
      handleIncomingDmFromPeer(msg, peerId);
    }
  }
});

// An on-screen agent DM with no real session in my roster: re-read it so the page becomes the
// agent chat. Off-screen costs nothing (startCyboChat re-reads on open); 30 s per agent.
const agentSessionRosterReads = new Map<string, number>();
client.on("dm", (msg) => {
  const me = authState.user;
  const wsId = workspaceState.current?.id;
  if (!me || !wsId || resolveMessageWorkspaceId(msg, wsId) !== wsId) return;
  if (!rosterMissesAgentSession(workspaceState.agents, me, msg)) return;
  const peer = canonicalAgentDmPeerId(msg.fromId);
  const same = (x: string, y: string) => canonicalAgentDmPeerId(x) === canonicalAgentDmPeerId(y);
  if (!isRendered("agent", peer, activeAgentIdFromUrl(), same)) return;
  const now = Date.now();
  if (now - (agentSessionRosterReads.get(msg.fromId) ?? 0) < 30_000) return;
  agentSessionRosterReads.set(msg.fromId, now);
  void refreshAgentRoster(wsId);
});

client.on("typing", (event) => {
  routeTypingEvent(event, typingSurfaces(), (id) => isMe(id, authState.user));
});

// When the window regains focus, the conversation the user is looking at counts
// as read — clear its badge (and sync read-state server-side for channels). This
// pairs with the focus-aware badge guards above: a message arriving while the
// window was unfocused badges even the open conversation, then clears here.
function clearActiveConversationBadge(): void {
  const ws = workspaceState.current;
  if (!ws) return;
  // On Alerts with the last-viewed conversation not rendered: it was not seen, don't mark it read.
  if (conversationHiddenBehindAlerts()) return;
  const readAt = Date.now();
  const activePeer = dmState.activePeerId;
  if (activePeer && isRendered("dm", activePeer, activePeer)) {
    notificationState.clear(ws.id, activePeer);
    // Item 12: re-mark-read on focus (Mattermost markAsReadOnFocus) so a read on
    // another device while this tab was blurred reconciles. Advance the LOCAL
    // cursor monotonically; the frozen divider snapshot is untouched.
    client.markDmRead(ws.id, activePeer, readAt);
    readCursorState.advanceDm(activePeer, readAt);
  }
  const activeChannel = channelState.activeId;
  if (activeChannel && isRendered("channel", activeChannel, activeChannel)) {
    const chWsId = workspaceState.channelWorkspaceId(activeChannel) ?? ws.id;
    notificationState.clear(chWsId, activeChannel);
    // BUG #2: clear BOTH the red mention count AND the bold unread flag.
    unreadFlagState.clearUnread(chWsId, activeChannel);
    client.markRead(chWsId, activeChannel, readAt);
    readCursorState.advanceChannel(activeChannel, readAt);
  }
  const agentId = activeAgentIdFromUrl();
  if (agentId) {
    // Canonicalize to the cybo id when this session is cybo-bound (#1185
    // follow-up) — the server increments/heals DM unread under `cyboId ??
    // agentId`, so clearing the raw session id here was a silent no-op that
    // left the rail/dock badge stuck until the next full reseed.
    const peerId = canonicalAgentDmPeerId(agentId);
    if (!agentDmReadState.canRead(peerId)) return;
    notificationState.clear(ws.id, peerId);
    // Bug A: agent replies badge like a human DM (keyed by agentId as the DM
    // peer), so it MUST persist read-state server-side too — a local clear alone
    // leaves the dm_reads cursor stale and every focus/reconnect reseeds the
    // badge. Mirror the DM branch's fire-and-forget markDmRead, but guard on the
    // socket: send() throws "Not connected" on a closed socket, and this focus
    // handler can fire mid-reconnect. Matches the agent-route mount's guard.
    if (connectionState.status === "connected") {
      client.markDmRead(ws.id, peerId, readAt);
    }
    readCursorState.advanceDm(peerId, readAt);
  }
}

// Clicking a sidebar item in an UNFOCUSED window fires the window `focus` event
// (on mousedown) BEFORE the click navigates (on mouseup → goto → selectChannel,
// which synchronously nulls dmState.activePeerId). Running clearActiveConversation
// Badge() synchronously on focus therefore marked the DM the user was LEAVING as
// read — users reported a DM's red badge vanishing just by clicking into the app
// or opening another channel without ever reading the DM. Defer the clear past the
// in-flight navigation and re-read the active conversation at fire time, so only
// the conversation the user actually lands on (and stays on) is marked read. A
// pure refocus (alt-tab back, no navigation) still clears the open conversation —
// just a frame later, which is imperceptible.
//
// The deferral is backed by cancellation, not just timing: selectChannel /
// selectDm / disconnectFromServer call cancelActiveConversationBadgeClear() up
// front, so a navigation away DEFUSES any pending clear regardless of how long
// the route takes to settle — the 250ms only ever fires for a pure refocus where
// the active conversation never changed.
let badgeClearTimer: ReturnType<typeof setTimeout> | null = null;
function cancelActiveConversationBadgeClear(): void {
  if (badgeClearTimer) {
    clearTimeout(badgeClearTimer);
    badgeClearTimer = null;
  }
}
function scheduleActiveConversationBadgeClear(): void {
  if (typeof window === "undefined") {
    clearActiveConversationBadge();
    return;
  }
  cancelActiveConversationBadgeClear();
  badgeClearTimer = setTimeout(() => {
    badgeClearTimer = null;
    clearActiveConversationBadge();
  }, 250);
}

// ─── Live read-cursor advance for the active+focused conversation (#516) ──────
// The read cursor previously only advanced on conversation OPEN and on
// focus/foreground — so sitting in a channel/DM reading a live stream left the
// server `lastReadAt` frozen until the next focus event. That caused (a) another
// device to keep showing the conversation unread while it's being read here (the
// read_broadcast cross-device sync never fired for live-arriving messages), and
// (b) a reconnect `fetchUnread` re-seed to resurrect a bold/unread state for a
// conversation the user was actively reading.
//
// This advances the cursor LIVE: when a message lands in the focused active
// conversation, (re-)mark it read — THROTTLED to once per window so a fast stream
// doesn't spam markRead. We reuse clearActiveConversationBadge(), which already
// does the full job for whatever conversation is active (markRead/markDmRead →
// relay read_broadcast → other devices, advance readCursorState, clear the local
// unread flag/count) and re-reads the active conversation at fire time, so a mid-
// debounce switch just marks the conversation the user actually landed on.
//
// This is the LOCAL live-advance only (#516). The other direction — a read on
// another device moving THIS device's frozen divider — is the `read` handler and
// reseedReadCursors (#514).
const LIVE_READ_ADVANCE_MS = 1_500;
let liveReadTimer: ReturnType<typeof setTimeout> | null = null;
// The conversation a pending advance was scheduled FOR. Captured so a timer that
// outlives a mid-window conversation switch doesn't mark the NEW conversation
// read (and so a switch reschedules cleanly for the conversation now in view).
let liveReadChannelId: string | null = null;
let liveReadPeerId: string | null = null;
// The Alerts route (/workspace/:id/activity) keeps channelState.activeId on the last channel
// but renders it only when its context pane shows it (alertsPaneState). conversationRendered()
// is the one "is it on screen" answer for the live read advance, the sidebar bold and the DM /
// agent-DM badges.
const onAlertsRoute = (): boolean =>
  typeof window !== "undefined" &&
  /\/workspace\/[^/]+\/activity(?:[/?#]|$)/.test(window.location.pathname);
const currentRouteKind = (): RenderedKind | undefined =>
  typeof window === "undefined" ? undefined : routeConversationKind(window.location.pathname);
function isRendered(
  kind: RenderedKind,
  id: string | null | undefined,
  routeId: string | null | undefined,
  eq?: (a: string, b: string) => boolean,
): boolean {
  return conversationRendered({
    onAlerts: onAlertsRoute(),
    routeKind: currentRouteKind(),
    pane: alertsPaneState.conversation,
    kind,
    id,
    routeId,
    eq,
  });
}
function conversationHiddenBehindAlerts(): boolean {
  // Every route keeps the last activeId (Tasks, or a channel id on /agent/<id>): the live read
  // advance would mark a channel read (and clear its fresh @mention alert) that is not on screen.
  const c = channelState.activeId;
  const p = dmState.activePeerId;
  if (!onAlertsRoute() && activeAgentIdFromUrl()) return false; // the agent block below is route-true
  return !(isRendered("channel", c, c) || isRendered("dm", p, p));
}
function scheduleLiveReadAdvance(): void {
  if (typeof window === "undefined") return;
  // Gate on focus up front (don't mark read while backgrounded).
  if (!windowFocused()) return;
  if (conversationHiddenBehindAlerts()) return;
  const channelId = channelState.activeId;
  const peerId = dmState.activePeerId;
  // A pending advance from a DIFFERENT conversation is stale — drop it so this
  // message reschedules for the conversation actually in view.
  if (liveReadTimer && (channelId !== liveReadChannelId || peerId !== liveReadPeerId)) {
    clearTimeout(liveReadTimer);
    liveReadTimer = null;
  }
  // Coalesce: a burst of messages in the same conversation schedules ONE trailing
  // advance, not one per message.
  if (liveReadTimer) return;
  liveReadChannelId = channelId;
  liveReadPeerId = peerId;
  liveReadTimer = setTimeout(() => {
    liveReadTimer = null;
    // Fire only if still focused AND on the SAME conversation we scheduled for —
    // a switch during the window must not mark the new conversation read here
    // (it was already marked read on open).
    if (
      windowFocused() &&
      !conversationHiddenBehindAlerts() &&
      channelState.activeId === liveReadChannelId &&
      dmState.activePeerId === liveReadPeerId
    ) {
      clearActiveConversationBadge();
    }
  }, LIVE_READ_ADVANCE_MS);
}

// Flush a PENDING live-read-advance for the conversation being LEFT (CYBORG-767).
// scheduleLiveReadAdvance() arms a 1.5s TRAILING timer when a message lands in the
// focused active conversation; leaving that conversation before it fires — a fast
// switch (selectDm/selectChannel), a tab-hide, or an app close — used to DROP the
// timer WITHOUT advancing the cursor (its fire-guard requires the SAME conversation
// still be active, which the switch just changed). So a message seen <1.5s before
// switching never advanced the read cursor, and re-opening froze a STALE cursor →
// a phantom "New messages" divider → the open pinned mid-list (onOpenAtUnread)
// instead of at the bottom. Flush advances the cursor for the conversation the
// timer was scheduled FOR (liveReadChannelId/liveReadPeerId — NOT the currently
// active one, which a switch may already have nulled), mirroring the mark-read work
// the timer's clearActiveConversationBadge() would have done.
//
// GUARD: only when a timer is actually pending. That pending timer is the exact,
// safe signal that a message was SEEN in that conversation (scheduleLiveReadAdvance
// only arms it while focused + on that conversation), so this never marks-read a
// conversation the user merely navigated PAST — which is the focus-during-navigation
// race the badge-clear code above (see cancelActiveConversationBadgeClear) is
// careful to avoid. Monotonic (advance only); the frozen divider snapshot of the
// CURRENT visit is untouched.
function flushLiveReadAdvance(): void {
  if (!liveReadTimer) return;
  clearTimeout(liveReadTimer);
  liveReadTimer = null;
  const channelId = liveReadChannelId;
  const peerId = liveReadPeerId;
  liveReadChannelId = null;
  liveReadPeerId = null;
  const ws = workspaceState.current;
  if (!ws) return;
  const now = Date.now();
  // The LOCAL cursor advance is the actual phantom-divider fix and always runs.
  // Guard only the server sync on a live socket: this can fire on blur/pagehide
  // while the socket is tearing down, and send() throws "Not connected" on a
  // closed socket (mirrors the connected-gate on the focus handler's markDmRead).
  const connected = connectionState.status === "connected";
  if (peerId) {
    notificationState.clear(ws.id, peerId);
    if (connected) client.markDmRead(ws.id, peerId, now);
    readCursorState.advanceDm(peerId, now);
  }
  if (channelId) {
    const chWsId = workspaceState.channelWorkspaceId(channelId) ?? ws.id;
    notificationState.clear(chWsId, channelId);
    unreadFlagState.clearUnread(chWsId, channelId);
    if (connected) client.markRead(chWsId, channelId, now);
    readCursorState.advanceChannel(channelId, now);
  }
}

// ─── Mobile/background reconnect + liveness (foreground hook) ─────────────────
// A mobile OS freezes the WebSocket while the app is backgrounded; on resume the
// socket can still read OPEN (a "zombie") so neither onclose nor the auto-
// reconnect fires and the UI silently stops receiving messages. Two defenses:
//   1. Foreground hook (here) — on visibility/online, probe liveness and force a
//      reconnect (or, if alive but away a while, resync the missed delta).
//   2. Periodic heartbeat (transport-level, inside SlackClient, #503) — pings
//      every 30s for the LIFE OF THE CONNECTION and tears down a half-open
//      socket so the backoff loop reconnects. It is NOT foreground-gated, so a
//      backgrounded-but-open desktop/web tab recovers too (previously the app
//      heartbeat below was paused on background and missed exactly that case).
//      On mobile the OS suspends the timer while backgrounded, so a frozen
//      socket is never pinged; this foreground hook covers resume.
//
// GATING: a half-open / zombie socket (proxy idle-timeout, VPN/NAT drop, sleep-
// wake, relay redeploy whose FIN never arrives) can read OPEN on EVERY platform,
// not just iOS — so neither `close` nor the auto-reconnect fires and the client
// sits "online to itself, offline to everyone else". The liveness PROBE + forced
// TEARDOWN therefore run on iOS + desktop + web (any platform with a `window`).
// The probe is a no-DB `cyborg:ping` round-trip (see probeConnection). The old
// iOS-only `iosLiveness()` gate was replaced by `livenessEnabled()` below;
// callers that still need the iOS-only branch (e.g. the local message cache,
// push) call `isTauriIOS()` directly.

// Whether the foreground reconnect probe should run on this surface. True for
// iOS + desktop + web — i.e. anywhere there's a `window`. The zombie-socket
// failure is not iOS-specific (see above), so the cheap no-DB ping guards every
// platform. SSR (no `window`) stays off.
function livenessEnabled(): boolean {
  return typeof window !== "undefined";
}
const FOREGROUND_RESYNC_AWAY_MS = 30_000;
// Timeout for the on-foreground liveness probe below. The PERIODIC heartbeat now
// lives transport-level inside SlackClient (#503), so the app layer keeps only
// the foreground resync probe — no app-side interval, no double-pinging.
const FOREGROUND_PROBE_TIMEOUT_MS = 10_000;
// CYBORG-1198: away = time the page's JS was stopped while hidden, not time since the
// last focus (a window focus on a visible page used to trigger the full resync).
const foregroundAway = new ForegroundAway();
let hiddenTickTimer: ReturnType<typeof setInterval> | null = null;
function noteBackgrounded(): void {
  foregroundAway.hidden(Date.now());
  hiddenTickTimer ??= setInterval(() => foregroundAway.tick(Date.now()), 10_000);
}
function noteForegrounded(): number {
  if (hiddenTickTimer !== null) clearInterval(hiddenTickTimer);
  hiddenTickTimer = null;
  return foregroundAway.visible(Date.now(), isTauriIOS() || isTauriAndroid());
}

// Liveness probe: resolves true only if the socket round-trips within timeoutMs.
// Uses the lightweight `cyborg:ping` → `cyborg:pong` RPC (client.ping), which
// touches NO Postgres and needs NO workspace subscription. An error round-trip
// (e.g. an older relay that doesn't know cyborg:ping) still counts as alive
// (client.ping resolves true on any matching-requestId response), which keeps a
// half-open socket from being torn down just because the relay is old.
async function probeConnection(timeoutMs: number): Promise<boolean> {
  if (!client.connected) return false;
  return client.ping(timeoutMs);
}

async function onForeground(): Promise<void> {
  const awayMs = noteForegrounded();
  // The conversation on screen: deltas missed while away are on the daemon's timeline;
  // settle against them (#3193 F10).
  const openAgentId = activeAgentIdFromUrl();
  if (openAgentId) void reconcileAgentTranscript(client, openAgentId);
  // Signal the active message surface FIRST (sync, before any await): its
  // ScrollAnchorController preserves a detached read and re-pins a genuine follower,
  // so the resync below (which mutates the message array) can't jump a history reader
  // to the latest message. Reuses the existing focus/visibility/online handlers — no
  // new listeners.
  foregroundSignal.bump();
  // A tab parked for hours comes back with a pass that expired long ago (30 min TTL
  // since CYBORG-929). Nothing renders while backgrounded, and mediaToken() is what
  // triggers a refresh — so without this the FIRST paint after returning goes out
  // with a dead `?t=`, every image 401s, and the page only heals once the mint that
  // paint kicked off lands. Minting here is free when the pass is fresh
  // (ensureMediaToken short-circuits) and deduped when it is not, so it costs one
  // request at most and only when one was needed anyway.
  warmMediaPass();
  // Terminal fast-path (knowledge/86 Task 1): bump the terminal signal in the SAME
  // sync-first window (before any await) so every mounted TerminalView reattaches
  // immediately instead of waiting out the ping-probe blind window. Within the relay
  // 90s grace (#1377) the re-subscribe returns live → instant repaint, no replay;
  // after grace it resolves to the existing ended/[Restart] path. reattachLive() is
  // #797-latched, so firing on every foreground is safe. The probe/reconnect below
  // then heals a genuinely dead socket in parallel.
  terminalForegroundSignal.bump();
  // Sync the open conversation's read-state. This is the ONLY thing the old
  // visibilitychange/focus handler did, and it must keep running on every
  // platform (web/desktop/Android/iOS) — it's not a reconnect behavior change.
  // Deferred so a click that BOTH refocuses the window and navigates away can't
  // mark the conversation being left as read (see scheduleActiveConversationBadgeClear).
  scheduleActiveConversationBadgeClear();

  // Everything below is the zombie-socket recovery (liveness probe + forced
  // reconnect). It now runs on iOS + desktop + web — a half-open socket can read
  // OPEN on any platform without firing `close`, so the probe guards them all.
  // Only SSR (no `window`) is skipped.
  if (!livenessEnabled()) return;

  if (!client.connected) {
    client.forceReconnect();
    return;
  }
  const alive = await probeConnection(FOREGROUND_PROBE_TIMEOUT_MS);
  if (!alive) {
    client.forceReconnect();
    return;
  }
  // #671: user statuses expire server-side via a periodic sweep whose
  // `user_status_changed` clear a frozen WebView misses entirely (the OS suspends
  // the socket while backgrounded). Unlike the channel/DM delta below, a status
  // clear is silently-drifting state that the < 30s gate would leave stale on a
  // quick lock/unlock or app-switch. So re-fetch statuses on EVERY foreground (a
  // single cheap RPC; seed() already drops absentees), independent of awayMs.
  // Scoped to statuses only — the read-state/channel/DM resync stays gated below.
  const fgWsId = workspaceState.current?.id;
  if (fgWsId) void seedUserStatuses(fgWsId);
  // Socket is genuinely alive but we were away long enough that a broadcast
  // could have been missed mid-freeze — resync explicitly (handleReconnect
  // drains the workspace channel delta from channelState.lastSeq AND reconciles
  // the open DM by re-fetching + dedup-merging its recent window).
  // handleReconnect ALSO reseeds unread (subscribeAndSeedAllWorkspaces), so it
  // covers the badge reconcile on the ≥30s path.
  if (awayMs >= FOREGROUND_RESYNC_AWAY_MS) {
    await handleReconnect();
    return;
  }

  // #672: THE GAP — socket alive, away < 30s. Nothing above reseeded unread, so
  // a read elsewhere during a BRIEF background (or a read of a DIFFERENT
  // conversation than the one badged) leaves the stale PERSISTED badge. Reconcile
  // the authoritative unread snapshot now (seedCounts replaces the whole map, so
  // now-read entries drop; a genuine server unread SURVIVES). Coalesced +
  // no-swallow inside reconcileUnread. Scoped to unread/read-state only.
  await reconcileUnreadOnForeground();

  // THE AGENT-STREAM GAP (sibling of #672): on a BRIEF background (away < 30s)
  // the OS freezes the WebSocket, so `agent_stream` events the daemon emitted
  // mid-freeze are DROPPED — they're not replayed on an already-open socket, and
  // the ≥30s `handleReconnect` catch-up (which calls catchUpAgentTimelines) never
  // ran. For an INTERACTIVE Claude session that's mid-turn, the lost events
  // include the agent's reply and its `turn_completed`, so the stream freezes on
  // the old turn and the chat reads as "closed"/dead after a quick tab-switch or
  // a few seconds of inactivity. Re-hydrate the open agent timelines from server
  // truth now — the same recovery the reconnect path uses, extended to the
  // brief-foreground path the user actually hits. Fire-and-forget +
  // workspace-guarded internally; only re-hydrates streams that are already open
  // (so it never resurrects a closed/archived session). Mirrors the terminal
  // re-attach-on-tab-switch fix (#738).
  if (fgWsId) void catchUpAgentTimelines(fgWsId);
}

// CYBORG-1205: `visibilitychange` and `focus` both fire on one return to the window;
// they share ONE run. `online` (the network came back) always runs.
const FOREGROUND_COALESCE_MS = 2_000;
const runForeground = foregroundOnce(onForeground, FOREGROUND_COALESCE_MS);

if (typeof window !== "undefined") {
  // `focus` runs the same foreground path as visibility/online. onForeground()
  // calls clearActiveConversationBadge() first (the old `focus` handler's only
  // job) and then — iOS-only — does the liveness probe + reconnect kick. Off-iOS
  // it is therefore behaviorally identical to the previous focus handler.
  window.addEventListener("focus", () => void runForeground());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      // Tell the relay we're active again so it resumes suppressing push to this
      // (now-foreground) device and delivers messages over the live socket.
      try {
        client.setAppState("active");
      } catch {
        // intentional: fire-and-forget foreground signal to the relay; reconnect re-asserts.
      }
      void runForeground();
    } else {
      // Going to the background — record when (so the next foreground can tell
      // how long we were away). CRUCIAL: tell the relay we're backgrounded. The
      // OS keeps this socket nominally OPEN, so without this the relay counts us
      // "active" and NEVER sends the push — the root cause of "no notifications
      // when the app is backgrounded/closed". Now the relay treats us as a push
      // target. (The transport heartbeat is connection-tied, not foreground-
      // gated, and the OS suspends its timer on mobile background anyway, so
      // there's nothing to pause here.)
      noteBackgrounded();
      // A hidden tab's socket can freeze: no turn in flight is judged by what it saw.
      turnDeltas.taintAll();
      // CYBORG-767: leaving the foreground counts as leaving the open conversation
      // — flush any pending live-read-advance so a message seen <1.5s ago is marked
      // read before the tab hides (guarded to only fire when one is actually
      // pending; no-op otherwise).
      flushLiveReadAdvance();
      try {
        client.setAppState("background");
      } catch {
        // intentional: fire-and-forget background signal to the relay; reconnect re-asserts.
      }
    }
  });
  window.addEventListener("online", () => void runForeground({ force: true }));
  // CYBORG-767: window blur (alt-tab / click another app) and pagehide (tab close /
  // reload) are best-effort flush points for a message seen in the open conversation
  // just before leaving — same guarded no-op-when-nothing-pending as above.
  window.addEventListener("blur", () => flushLiveReadAdvance());
  window.addEventListener("pagehide", () => flushLiveReadAdvance());

  // Unlock the notification sound on the first user gesture (browsers/Chromium
  // block audio.play() until then).
  const unlock = () => unlockNotificationAudio();
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });

  // Deep-link from a clicked native (Electron main-process) notification.
  const bridge = (
    window as Window & {
      cyborg7Desktop?: { events?: { on?: (e: string, h: (p: unknown) => void) => () => void } };
    }
  ).cyborg7Desktop;
  bridge?.events?.on?.("navigate", (url) => {
    if (typeof url === "string") void goto(url);
  });
  bridge?.events?.on?.("notification-action", answerFromBanner);
  // Web Push click (service-worker.ts) while the tab is open: route in place.
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (e: MessageEvent) => {
      const d = e.data as { type?: unknown; url?: unknown } | null;
      if (
        d?.type === "navigate" &&
        typeof d.url === "string" &&
        d.url.startsWith("/") &&
        !d.url.startsWith("//")
      ) {
        void goto(d.url);
      }
    });
  }
}

// Committed rows the live stream skipped are read back from the durable timeline.
const GAP_RECOVERY_WINDOW_MS = 5_000;
const recoverTimelineGap = throttledGapRecovery(
  (agentId, gap) => {
    // A hole the server cut on purpose is read back like any other, but is no failure.
    if (gap.cut !== true)
      reportUiFailure(
        "agents.timelineGap",
        "the live stream skipped committed rows; reading them back",
        {
          workspaceId: agentStreamState.workspaceForAgent(agentId),
          agentId,
          gapAfter: gap.after,
          gapBefore: gap.before,
        },
        { failureKind: "recovered", code: "timeline_gap" },
      );
    void recoverTimelineRange(client, agentId, gap.after, gap.before);
  },
  GAP_RECOVERY_WINDOW_MS,
  Date.now,
  undefined,
  conversationSync,
);

// A hydrate kept the conversation on screen under a page that left a hole: read it back.
// A session body's synthetic key is not an agent timeline; its next page fills it.
agentStreamState.onTurnEndUnseen = (agentId) => void reconcileAgentTranscript(client, agentId);
agentStreamState.onTimelineHole = (agentId, hole) => {
  if (isSessionStreamKey(agentId)) return;
  const wsId = workspaceState.current?.id;
  // A hole in a conversation of another workspace is not read from this one.
  if (!wsId || agentStreamState.workspaceForAgent(agentId) !== wsId) return;
  const gen = agentStreamState.generationOf(agentId);
  recoverTimelineGap(
    agentId,
    hole,
    () => workspaceState.current?.id === wsId && agentStreamState.generationOf(agentId) === gen,
  );
};

// Re-sends a prompt the restarting computer refused (agent-unreachable.ts), with backoff,
// until it lands or the window is spent. Account-scoped: never re-sent under another one.
const promptRedeliveries = accountSync.collection("promptRedeliveries", new RedeliveryHolds());
function bookPromptRedelivery(agentId: string, entryId: string): boolean {
  return promptRedeliveries.book(redeliveryKey(agentId, entryId), Date.now(), () => {
    // retryPrompt re-sends only a FAILED bubble.
    agentStreamState.setSendStatus(agentId, entryId, "failed");
    retryAgentPrompt(agentId, entryId);
  });
}
agentStreamState.onPromptUndelivered = bookPromptRedelivery;
// A held re-send refused while the computer is still away: book the next one. False
// when not held or the window is spent — the caller fails it as usual.
function rebookHeldPrompt(agentId: string, entryId: string): boolean {
  if (!promptRedeliveries.isHeld(redeliveryKey(agentId, entryId), Date.now())) return false;
  if (!bookPromptRedelivery(agentId, entryId)) return false;
  agentStreamState.setSendStatus(agentId, entryId, "pending", RECONNECTING_STATUS);
  return true;
}

// Proven undelivered → failed (Retry) unless a held re-send rebooks; lost answer → unknown.
function settleRefusedSend(agentId: string, entryId: string, notSent: boolean, err: unknown): void {
  if (notSent && rebookHeldPrompt(agentId, entryId)) return;
  agentStreamState.setSendStatus(
    agentId,
    entryId,
    notSent ? "failed" : "unknown",
    sendFailureReason(err),
    errorCodeOf(err),
  );
}

// The workspace a frame's agent belongs to: the frame's own, the stream's, or the roster's.
function bindStreamWorkspace(payload: { agentId: string; workspaceId?: string }): void {
  const eventWorkspaceId =
    payload.workspaceId ??
    agentStreamState.workspaceForAgent(payload.agentId) ??
    (workspaceState.agents.some((agent) => agent.agentId === payload.agentId)
      ? workspaceState.current?.id
      : undefined);
  if (eventWorkspaceId) agentStreamState.bindAgentWorkspace(payload.agentId, eventWorkspaceId);
}

// A frame that must not be applied now: one arriving while its agent adopts another life
// (held, replayed after), or the first row of another life — a rewind made elsewhere —
// which starts that adoption and is itself replayed once it lands.
function heldForAnotherLife(
  payload: {
    agentId: string;
    timelineLife?: string;
    timelineSeq?: number;
    event?: { type?: string };
  },
  replay: () => void,
): boolean {
  const { agentId, timelineLife, timelineSeq } = payload;
  const delta = payload.event?.type === "timeline";
  if (deferWhileAdopting(agentId, replay, timelineLife, timelineSeq, delta)) return true;
  if (!agentStreamState.lifeDiffers(agentId, timelineLife)) return false;
  void adoptNewLife(client, agentId, undefined, timelineLife);
  deferWhileAdopting(agentId, replay, timelineLife, timelineSeq, delta);
  return true;
}

// Live assistant text per watched turn: a turn the open conversation watched whole that
// got none, while the durable reply has some, is a stream that never reached the screen.
const turnDeltas = conversationSync.own("turnDeltas", new TurnDeltaTracker());
const DURABLE_TS_SKEW_MS = 30_000;

function checkSilentTurn(agentId: string, type: string): () => void {
  const startedAt = turnDeltas.finishSilent(agentId);
  if (type !== "turn_completed" || startedAt === undefined || agentId !== activeAgentIdFromUrl()) {
    return () => {};
  }
  const before = new Set(agentStreamState.getEntries(agentId).map((entry) => entry.id));
  return () => {
    const entries = agentStreamState.getEntries(agentId);
    const rows = newTextSince(entries, before, startedAt - DURABLE_TS_SKEW_MS);
    if (rows > 0) {
      agentStreamState.invariants.report("turn_completed_without_deltas", {
        agentId,
        workspaceId: agentStreamState.workspaceForAgent(agentId),
        rows,
      });
    }
  };
}

// The mirror adopted another life (knowledge/125): taken like a rewind made elsewhere.
const mirrorRevisions = conversationSync.own("mirrorRevisions", new MirrorRevisions());
client.on("agent_timeline_reset", (payload) => {
  agentFrames.flushAgent(payload.agentId);
  const lifeReplaces = (agentId: string, life: string) =>
    agentStreamState.lifeReplaces(agentId, life);
  if (mirrorResetOutcome(payload, mirrorRevisions, lifeReplaces) !== "adopt") return;
  if (payload.life) void adoptRetiredLife(client, payload.agentId, payload.life);
});

// The roster's list_agents rows are the turn-status snapshot (knowledge/126 step 2b).
const stopFollowingRosterTurnStatus = followRosterTurnStatus(
  turnStatusBook,
  () => workspaceState.agents,
);
// The follower lives as long as this module: a hot reload replaces the module, so it ends there.
import.meta.hot?.dispose(stopFollowingRosterTurnStatus);

// CYBORG-1560: messages an older app queued in localStorage go to the daemon once, in order.
const stopMigratingLegacyQueues = followLegacyQueues(
  () => workspaceState.agents,
  () => !!authState.user && !!workspaceState.current,
  () => (typeof localStorage === "undefined" ? undefined : localStorage),
  {
    newPromptId: () => globalThis.crypto.randomUUID(),
    sendPrompt: (agentId, row, promptId) =>
      sendAgentPrompt(
        agentId,
        row.text,
        row.attachments,
        row.pageRef,
        row.pageCard,
        undefined,
        row.workSessionId,
        promptId,
      ),
    sendCommand: (agentId, command, promptId, workSessionId) =>
      sendAgentCommand(agentId, command, { promptId, ...(workSessionId ? { workSessionId } : {}) }),
    drawFailedCommand: (agentId, row, command, promptId) => {
      rememberTypedCommand(promptId, command);
      agentStreamState.addUserMessage(
        agentId,
        agentCommandText(command),
        undefined,
        "failed",
        undefined,
        promptId,
        row.workSessionId,
      );
    },
    drawUnconfirmed: (agentId, row, promptId) => {
      const drawn = agentStreamState
        .getEntries(agentId)
        .some((e) => e.kind === "user_message" && e.clientMessageId === promptId);
      if (drawn || agentStreamState.serverEchoedPromptIds.has(promptId)) return;
      agentStreamState.addUserMessage(
        agentId,
        row.text,
        row.pageCard,
        "unknown",
        row.attachments,
        promptId,
        row.workSessionId,
        true,
      );
    },
  },
);
import.meta.hot?.dispose(stopMigratingLegacyQueues);

const TERMINAL_TURN_EVENTS: ReadonlySet<string> = new Set([
  "turn_completed",
  "turn_failed",
  "turn_canceled",
]);

// The turn is over: settle what the screen shows against what the daemon holds. A
// browser that stopped applying deltas (a suspended tab, a phone in the background) would
// otherwise keep a cut reply, or none (#115, #3193 F10). Any hydrated agent, any terminal
// event, a cancel included; an end past the screen holds the recovery (knowledge/124 T1).
function settleTurnEnd(payload: import("../plugins/agents/types.js").AgentStreamPayload): void {
  const type = payload.event.type;
  if (!TERMINAL_TURN_EVENTS.has(type)) return;
  agentStreamState.noteTurnEnd(payload.agentId, turnEndOf(payload.turnEnd));
  if (type === "turn_canceled") {
    turnDeltas.finishSilent(payload.agentId);
    void reconcileAgentTranscript(client, payload.agentId);
    return;
  }
  const afterReconcile = checkSilentTurn(payload.agentId, type);
  void reconcileAgentTranscript(client, payload.agentId).then(afterReconcile);
}

const agentFrames = conversationSync.own(
  "agentFrames",
  new AgentFrameBatch<import("../plugins/agents/types.js").AgentStreamPayload>({
    apply: applyAgentStream,
    batch: (run) => agentStreamState.batchTouches(run),
  }),
);

agentStreamState.beforeTranscriptWipe = (agentId) => agentFrames.flushAgent(agentId);

// Test seam: a frame's handler runs synchronously in the suites that read state right after it.
export function flushAgentStreamFrames(): void {
  agentFrames.flush();
}

// Wire agent events
client.on("agent_stream", function onAgentStream(payload) {
  // Every status frame of the agent, before any body or life filter (knowledge/126 step 2b).
  turnStatusBook.apply(payload.agentId, wireTurnRecord(payload));
  if (payload.event.type === "timeline") {
    agentFrames.enqueue(payload.agentId, payload);
    return;
  }
  // Terminal, permission and turn frames never wait, and never overtake the agent's rows.
  agentFrames.flushAgent(payload.agentId);
  if (payload.event.type === "permission_resolved") {
    // It clears the linked subagent's card too, which must not pass that subagent's rows.
    const subagentId = agentStreamState.linkedSubagent(payload.agentId, payload.event.requestId);
    if (subagentId) agentFrames.flushAgent(subagentId);
  }
  applyAgentStream(payload);
});

function applyAgentStream(payload: import("../plugins/agents/types.js").AgentStreamPayload): void {
  if (heldForAnotherLife(payload, () => applyAgentStream(payload))) return;
  bindStreamWorkspace(payload);
  // Read BEFORE the frame deletes it: the request a resolution closes names what kind of card it was.
  if (payload.event.type === "permission_resolved")
    settlePermissionAlert(payload.agentId, payload.event);
  turnDeltas.observe(
    payload.agentId,
    payload.event as { type: string; item?: { type?: string } },
    Date.now(),
    typeof document !== "undefined" && document.visibilityState === "hidden",
  );
  const gap = applyAgentStreamFrame(agentStreamState, payload.agentId, payload.event, payload);
  const wsId = workspaceState.current?.id;
  // A gap in a conversation of another workspace is not read from this one.
  if (gap && wsId && agentStreamState.workspaceForAgent(payload.agentId) === wsId) {
    const gen = agentStreamState.generationOf(payload.agentId);
    recoverTimelineGap(
      payload.agentId,
      gap,
      () =>
        workspaceState.current?.id === wsId &&
        agentStreamState.generationOf(payload.agentId) === gen,
    );
  }

  const turnEvents = ["turn_started", "turn_completed", "turn_failed", "turn_canceled"];
  settleTurnEnd(payload);
  if (turnEvents.includes(payload.event.type)) {
    const agent = workspaceState.agents.find((a) => a.agentId === payload.agentId);
    if (agent) {
      const status = agentStreamState.getTurnStatus(payload.agentId);
      // Through noteAgentLifecycle, not a raw row mutation + respread: this is a
      // LIVE edge, so it must also be recorded as such or the very next
      // listAgents snapshot (fetched before this turn ended) overwrites it with
      // a stale "running" — see WorkspaceState's stale-snapshot lifecycle guard.
      workspaceState.noteAgentLifecycle(payload.agentId, status === "canceled" ? "idle" : status);
    }
    // #591: derive the list-level "needs attention" badge from the live turn
    // edge (completed → "finished", failed → "error"), mirroring the daemon's
    // edge rule for agents already streaming here — so a background agent that
    // finishes/errors badges without waiting for the next listAgents snapshot.
    // Skips the agent currently in view (it's being read → no "review me").
    if (payload.agentId !== activeAgentIdFromUrl()) {
      attentionState.noteTurnEvent(
        payload.agentId,
        payload.event.type as "turn_started" | "turn_completed" | "turn_failed" | "turn_canceled",
      );
    }
  }

  const agentName = () =>
    sessionDisplayName(
      { agentId: payload.agentId },
      undefined,
      agentDisplayName(
        workspaceState.agents.find((a) => a.agentId === payload.agentId),
        cyboState.list,
      ),
    );
  if (
    payload.event.type === "permission_requested" &&
    canAnswerPermissionsFor(payload.agentId) &&
    // A message that joins its session's waiting card alerted with the card (message-approval.ts).
    !joinsWaitingMessageCard(
      payload.event.request,
      agentStreamState.getPendingPermissions(payload.agentId),
    )
  ) {
    const name = agentName();
    activityState.push(
      "permission_request",
      name,
      payload.agentId,
      "agent",
      `Requesting permission: ${permissionAlertTitle(payload.event.request)}`,
      null,
      null,
      null,
      payload.event.request.id,
    );
    maybeFirePermissionBanner(payload.agentId, name, payload.event.request);
  } else if (payload.event.type === "turn_failed") {
    activityState.push(
      "agent_error",
      agentName(),
      payload.agentId,
      "agent",
      payload.event.error ?? "Agent turn failed",
    );
  }
}

// The daemon's authoritative per-agent lifecycle push. This frame has ALWAYS been
// broadcast (message-router.emitAgentStatus) and fanned out to guests — the UI
// simply never consumed it, so the roster's `lifecycle` had no live source at all:
// only listAgents snapshots and the turn-event edge above. A snapshot fetched
// before a turn ended, applied after, therefore pinned an agent at "running"
// forever, leaving the composer in Queue mode with a message that never sends and
// no path back short of restarting the app.
//
// Two writes, both cheap and both needed — they clear the TWO independent inputs
// to isTurnActive (see its own doc):
//  1. the roster lifecycle, via the guarded funnel (recorded as a live push so
//     the next stale snapshot can't undo it), and
//  2. a client turnStatus still stuck at "running" whose terminal event never
//     arrived — reconcileTurnWithRoster settles it silently and flushes the
//     queued prompt the now-idle agent can genuinely take. Same server-truth
//     contract as the reconnect roster refetch that already calls it, just
//     delivered live and per-agent instead of once per reconnect.
// THE ARCHIVE IS THE DOOR. Archiving (or clearing) a session takes its sender out
// of getDmUnreadCounts' `live_senders`, so from that moment on the server can
// never return that peer's unread count again and the DM list has no row for it
// either — whatever red number the client is holding becomes unreconcilable and
// unclickable. This frame is broadcast to EVERY guest in the workspace (the
// relay's owner-archive and offline-clear paths), so every client retires the key
// at the same moment, not just the one that pressed archive.
//
// Canonicalized BEFORE the roster can drop the row — canonicalAgentDmPeerId reads
// workspaceState.agents, and the id mapping is gone once the session leaves it.
function retireArchivedAgentBadge(workspaceId: string, agentId: string): void {
  if (!workspaceId) return;
  const keys = retiredBadgeKeysForArchivedAgent(
    agentId,
    canonicalAgentDmPeerId(agentId),
    cyboState.list.map((c) => c.id),
  );
  for (const key of keys) notificationState.retire(workspaceId, key);
}

const unknownAgentRelist = accountSync.own("unknownAgentRelist", new UnknownAgentRelist());
client.on("agent_status", (payload) => {
  if (!payload?.agentId) return;
  agentFrames.flushAgent(payload.agentId); // a status never overtakes the rows sent before it
  turnStatusBook.apply(payload.agentId, wireTurnRecord(payload));
  // The removal frame carries `lifecycle` and NO `status`, so it fell through the
  // guard below and every client ignored it.
  if (payload.lifecycle === "removed") {
    retireArchivedAgentBadge(payload.workspaceId, payload.agentId);
    // The session was archived or cleared: drop its conversation, as archiveAgent does
    // for the client that pressed it, or every catch-up keeps re-reading it (CYBORG-1182).
    agentStreamState.clear(payload.agentId);
    // And its roster row, as archiveAgent does: a device that did not press End otherwise kept
    // the dead session as the DM's default (header "Active", next message sent to the ended id).
    dropEndedAgentRow(payload.workspaceId, payload.agentId);
    return;
  }
  if (!payload.status) return;
  const wsId = payload.workspaceId;
  // Only the open cybo DM needs it: a re-read elsewhere relabels other cybos' sidebar rows early.
  const openDm = !!payload.cyboId && payload.cyboId === activeAgentIdFromUrl();
  if (openDm && wsId === workspaceState.current?.id && !workspaceState.agentsLoading) {
    unknownAgentRelist.note(
      payload.agentId,
      rosterSettled(workspaceState.agents.find((a) => a.agentId === payload.agentId)),
      () => relistAgents(wsId),
    );
  }
  workspaceState.noteAgentLifecycle(payload.agentId, payload.status);
  const ended = agentStreamState.reconcileTurnWithRoster(payload.agentId, payload.status);
  // A lost terminal frame, or an end past the screen: read back to it (knowledge/124 T1).
  const owed =
    payload.status !== "running" &&
    agentStreamState.noteTurnEnd(payload.agentId, turnEndOf(payload.turnEnd)) === "owed";
  if (ended || owed) void reconcileAgentTranscript(client, payload.agentId);
  // A roster "running" push means the daemon accepted the run — the bounded
  // pre-turn wait is satisfied even before the first stream event (a cold
  // provider spawn can take tens of seconds to emit one).
  agentStreamState.noteRosterRunning(payload.agentId, payload.status);
});

// Provider-subagent updates (Codex/OpenCode/Claude). The daemon forwards its
// AgentManager `provider_subagent` events as the protocol-native
// `agent.provider_subagents.update` frame, whose payload IS the discriminated
// update (descriptor upsert, a single timeline row, or a removal) — fed straight
// into the reactive store, which forwards timeline rows into agentStreamState
// (keyed by subagent id) so the live conversation renders through the existing
// AgentStreamView.
// CYBORG-884: the owning daemon's answer for one of THIS client's prompts: a queued prompt is
// an ordinary bubble once it runs, or comes back failed with Retry when it never did. No toast:
// one fired per answer, from this global listener, on whatever screen was open.
// promptId -> the latest delivery attempt the relay minted for it: a verdict for an older attempt
// (a replay, or the bounce of a send that was retried since) must not settle the retry.
// Owned by the account (#3629): a sign-out drops it, so no attempt id outlives the session.
const latestPromptAttempt = accountSync.collection(
  "latestPromptAttempt",
  new Map<string, string>(),
);
client.on("agent_prompt_verdict", ({ agentId, promptId, verdict, reason, promptAttemptId }) => {
  agentFrames.flushAgent(agentId);
  const latest = latestPromptAttempt.get(promptId);
  if (promptAttemptId && latest && promptAttemptId !== latest) return;
  const pendingHere = agentStreamState
    .getEntries(agentId)
    .some((row) => row.kind === "user_message" && verdictSettles(row, promptId, verdict));
  agentStreamState.settlePromptVerdict(agentId, promptId, verdict, reason);
  // A confirmed refusal is a failed bubble with Retry now: it must not also come back as an
  // uncertain send from the outbox after a reload.
  if (pendingHere && verdict === "unavailable") void cache.deleteAgentPromptOutbox(promptId);
  // A typed DM command is acked "routed" before the daemon's busy queue may still turn it away
  // (full, or the wait bound). Its row must not keep saying sent: it is marked failed, with why.
  mentionQueueState.release(`c:${promptId}`);
  heldDmReload.forget(promptId);
  // Only the queue's own explicit refusal means "it never ran": a bare "unavailable" can be a
  // bounced send-now, whose row is still live.
  // The daemon also writes a durable line into the DM saying it did not run; the failed mark is the
  // live twin of it. No toast: this listener never toasts (send-now-refusal.test.ts).
  const refusal = verdict === "unavailable" ? busyRefusalCopy(reason) : undefined;
  if (!refusal) return;
  setBubbleStatus("dm", cache.localIdFor(promptId), "failed", refusal);
  // A command already acked carries the server's id: the same mark, found by its client id.
  dmState.failSentByClientMsgId(promptId, refusal);
});

// The one place a not-run verdict reaches the stream: a DM names the cybo, a session prompt the agent.
client.on("agent_not_run", (verdict) => {
  if (!verdict.agentId && workspaceState.current?.id !== verdict.workspaceId) return;
  const mine = workspaceState.agents.filter((a) => isMine(a, authState.user));
  const agentId = notRunSessionId(verdict, mine);
  if (!agentId) return;
  agentFrames.flushAgent(agentId);
  agentStreamState.noteNotRun(agentId);
});

// PR #3402: the daemon holds a prompt behind the running turn. The DM row shows the existing
// queued pill (held by client id; the verdict above releases it). The agent id stands in for the
// cybo id: it never resolves to a handle, so the pill reads plain "Queued". The stream's bubble
// stays, marked queued, below the running reply (markQueued).
client.on("agent_prompt_queue", ({ agentId, held }) => {
  const listed = new Set(held.map((item) => item.promptId));
  heldDmReload.frame(agentId, listed);
  mentionQueueState.releaseUnlisted(agentId, listed);
  agentStreamState.setSharedQueue(agentId, held);
});

client.on("agent_usage_limit", (frame) => usageLimitState.apply(frame));

client.on("agent_prompt_queued", ({ agentId, promptId }) => {
  agentFrames.flushAgent(agentId);
  mentionQueueState.hold(`c:${promptId}`, agentId);
  heldDmReload.remember(promptId, agentId);
  agentStreamState.markQueued(agentId, promptId, true); // newer than any run of it (a requeued steer)
});

client.on("agent_activity", (payload) => {
  if (!payload?.agentId) return;
  agentActivityStore.replace(payload.agentId, {
    goal: payload.goal,
    tasks: payload.tasks,
    processes: payload.processes,
    retry: payload.retry,
    rateLimit: payload.rateLimit,
    mcp: payload.mcp,
  });
});

// The processes a modal follows (agentId\tprocessId → workspace + attachId), so a frame that skips
// lines re-reads the backlog from where the log stops, and a reconnect re-subscribes.
const followedProcesses = accountSync.collection(
  "followedProcesses",
  new Map<string, { workspaceId: string; attachId: string }>(),
);
// One backlog read per process at a time: a burst of gap frames must not start parallel reads.
const readingProcesses = accountSync.collection("readingProcesses", new Set<string>());
const rereadPending = accountSync.collection("rereadPending", new Set<string>());
client.on("agent_process_output", (frame) => {
  if (!frame?.agentId || !frame.processId || !Array.isArray(frame.lines)) return;
  if (agentProcessOutputStore.applyFrame(frame)) return;
  const followed = followedProcesses.get(`${frame.agentId}\t${frame.processId}`);
  if (followed) void readProcessBacklog(followed.workspaceId, frame.agentId, frame.processId);
});

// A turn was refused for its login: that computer's provider rows are re-read now, so the
// Providers panel says "Sign in expired" at once. A read, not a re-check: the refusal already
// dropped the daemon's cached rows, and a re-check per refusal was an RPC storm in an outage.
async function recheckProvidersAfterLoginNotice(notice: NoticeForToast): Promise<void> {
  const agent = workspaceState.agents.find((a) => a.agentId === notice.agentId);
  if (!agent?.daemonId) return;
  await fetchProviders(agent.daemonId, notice.workspaceId, { fresh: true });
}

// CYBORG-1416: a provider notice is also the existing toast (the in-thread line stays). A notice
// id is toasted once, however many times the push or a reconnect replays it.
const toastedNoticeIds = accountSync.collection("toastedNoticeIds", new Set<string>());
const TOASTED_NOTICES_MAX = 200;
client.on("agent_notice", (payload) => {
  if (payload?.agentId) agentFrames.flushAgent(payload.agentId);
  if (!payload?.noticeId || !payload.text || toastedNoticeIds.has(payload.noticeId)) return;
  if (toastedNoticeIds.size >= TOASTED_NOTICES_MAX) {
    toastedNoticeIds.delete(toastedNoticeIds.values().next().value as string);
  }
  toastedNoticeIds.add(payload.noticeId);
  if (payload.kind === "login_required") void recheckProvidersAfterLoginNotice(payload);
  // The person is looking at that conversation: the in-thread notice line is enough, no toast.
  if (isRendered("agent", payload.agentId, activeAgentIdFromUrl())) return;
  const agent = workspaceState.agents.find((a) => a.agentId === payload.agentId);
  showNoticeToast(payload, agent && agentDisplayName(agent, cyboState.list), agent?.daemonId);
  reportNoticeToastShown(payload.agentId, payload.kind);
});

client.on("provider_subagent", (update) => {
  const parentAgentId =
    update.kind === "upsert" ? update.subagent.parentAgentId : update.parentAgentId;
  const subagentId = update.kind === "upsert" ? update.subagent.id : update.subagentId;
  agentFrames.flushAgent(parentAgentId);
  agentFrames.flushAgent(subagentId);
  const workspaceId = agentStreamState.workspaceForAgent(parentAgentId);
  if (workspaceId) {
    providerSubagentStore.bindParentWorkspace(parentAgentId, workspaceId);
    agentStreamState.bindAgentWorkspace(subagentId, workspaceId);
  }
  providerSubagentStore.applyUpdate(update);
});

// A provider subagent asked for a tool permission (#1612). The daemon attributes
// the parent-channel permission to the child and broadcasts it here. Feed the
// request into agentStreamState UNDER THE SUBAGENT ID so the shared permission
// card + track badge (getPendingPermissions(subagentId)) light up in the
// subagent inspector, and remember the parent route so the answer can go back on
// cyborg:subagent_permission_response (the parent owns the permission channel).
client.on("subagent_permission_request", (payload) => {
  const { parentAgentId, subagentId, request } = payload;
  agentFrames.flushAgent(subagentId);
  const workspaceId = agentStreamState.workspaceForAgent(parentAgentId);
  if (workspaceId) agentStreamState.bindAgentWorkspace(subagentId, workspaceId);
  subagentPermissionRoutes.set(subagentPermissionRouteKey(subagentId, request.id), {
    parentAgentId,
    subagentId,
  });
  // Link parent+requestId -> subagentId so the daemon's real permission_resolved (on the
  // parent) clears THIS subagent card, not a weak relay ack (Codex S2).
  agentStreamState.linkSubagentPermission(parentAgentId, request.id, subagentId);
  agentStreamState.handleEvent(subagentId, {
    type: "permission_requested",
    provider: request.provider,
    request,
  });
});

// When the real resolution clears a subagent card, drop its answer-routing entry (Codex S2).
agentStreamState.onSubagentPermissionResolved = (subagentId, requestId) => {
  subagentPermissionRoutes.delete(subagentPermissionRouteKey(subagentId, requestId));
};

client.on("error", (err) => {
  connectionState.error = err.message;
  // A permission response is fire-and-forget and its refusal names neither agent nor request,
  // so a refusal cannot be matched: drop every optimistic mark and let the cards come back.
  answeredPermissions.clear();
  // The only sink for fire-and-forget refusals (channel/DM send, permission response):
  // without this report a refused send is invisible to telemetry.
  reportServerErrorFrame(err, workspaceState.current?.id);
  // CYBORG-987: a channel/DM send is FIRE-AND-FORGET (sendMessage/sendDm carry no
  // requestId), so the relay's attachment refusal has no pending promise to reject
  // and lands here instead — and `connectionState.error` is read by nothing. It has
  // to reach the person who just pressed Send, or their message silently never
  // appears. Same treatment as license_required/upgrade_required, the other two
  // gates that fire on this path.
  //
  // This covers the two FIRE-AND-FORGET acceptors ONLY. `cyborg:send_agent_prompt`
  // is an RPC (ws-client.ts's `request`), so its refusal carries the requestId,
  // rejects the caller's promise, and surfaces as that composer's own failed-send
  // state — it never reaches this handler and must not be toasted twice.
  // A refused send names its bubble (CYBORG-818, as a refused replay or a parked send finally lost,
  // CYBORG-1459, always did): it fails in place (X + Retry) at once, and nothing re-queues it.
  if (err.clientMsgId) {
    const onScreen = sendBubbleOnScreen(err.clientMsgId);
    const localId = cache.localIdFor(err.clientMsgId);
    const watch = watchOf(localId);
    const personDm = dmRefusalReason(watch?.surface, err.code, err.gate);
    failSendByClientMsgId(err.clientMsgId, personDm ?? contentRefusalReason(err.code, err.message));
    // A lost send in the open conversation already shows X + Retry on its row; toast only off-screen.
    if (err.code !== "send_lost" || !onScreen) toast.error(personDm ?? err.message);
    if (personDm) void recheckPeerErased(watch?.targetId); // she may be erased: the read says so, and the composer becomes the banner
  } else if (err.code?.startsWith("attachment_")) toast.error(err.message);
});

// The person is looking at the conversation that holds this send's bubble (focused window).
function sendBubbleOnScreen(clientMsgId: string): boolean {
  const localId = cache.localIdFor(clientMsgId);
  const c = channelState.activeId;
  const p = dmState.activePeerId;
  const inChannel =
    channelState.messages.some((m) => m.id === localId) && isRendered("channel", c, c);
  const inDm = dmState.messages.some((m) => m.id === localId) && isRendered("dm", p, p);
  return windowFocused() && (inChannel || inDm);
}

// CYBORG-1459: a parked send stays pending for the relay's replay budget; the echo settles it.
client.on("message_queued", (p) => {
  if (p.clientMsgId) holdParkedSend(p.clientMsgId, p.budgetMs ?? SEND_WATCHDOG_MS);
});

// HARD-PAUSE gate on a fire-and-forget path (guest agent-prompt send): the
// relay can't reject a pending RPC, so it emits a top-level guest_error with
// error:'license_required'. Re-seed the license + open the activate modal.
client.on("license_required", (p) => {
  if (p.license) licenseState.set(p.license);
  licenseState.openModal();
  toast.error(p.message);
});

// Pricing v2 upgrade gate on the fire-and-forget path (mirror of license_required).
// A Free workspace hit a Pro feature / seat cap: preserve the server copy + open
// the upgrade modal. Distinct from the paused-trial paywall above.
client.on("upgrade_required", (p) => {
  licenseState.setUpgradeMessage(p.message);
  licenseState.openModal();
});

client.on("presence", (payload) => {
  presenceState.update(payload.onlineUserIds);
  // P2 Item 6: away is a subset of online, broadcast alongside it.
  presenceState.updateAway(payload.awayUserIds ?? []);
});

// P2 Item 6: a co-member's custom status changed live. If it's MY own status
// (echoed on another device), also reconcile my self-status state.
client.on("user_status_changed", (p) => {
  workspaceUserStatusesState.set(p.userId, {
    emoji: p.emoji,
    text: p.text,
    expiresAt: p.expiresAt,
  });
  if (p.userId === authState.user?.id) {
    userStatusState.applyRemote(
      p.emoji,
      p.text,
      p.expiresAt != null ? new Date(p.expiresAt).toISOString() : null,
    );
  }
});

// A conversation read carries no activity ids, so "read" is judged from the feed itself: every
// notification tagged with the serverId (= activityId) of a row that is now read.
async function clearReadWebNotifications(channelIds?: readonly string[]): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  // Desktop (cyborg://app) has native notifications and no service worker to ask.
  if (!/^https?:$/.test(globalThis.location?.protocol ?? "")) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    const shown = await reg.getNotifications();
    const readIds = activityState.items.flatMap((i) =>
      i.isRead && i.serverId ? [i.serverId] : [],
    );
    for (const n of notificationsToClose(shown, readIds, channelIds)) n.close();
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    const unread = activityState.badgeCount;
    if (unread > 0) await nav.setAppBadge?.(unread);
    else await nav.clearAppBadge?.();
  } catch (err) {
    reportUiFailure("webpush.clearRead", err, {}, { failureKind: "transient" });
  }
}

// The row-level shapes of activity_read_changed (mark-all, one row, a daemon request, a thread,
// deleted messages). True when one applied, so the server's unread count is taken as the truth.
function applyRowReadShapes(p: ActivityReadChangedPayload): boolean {
  let rowShape = false;
  if (p.all) {
    // The ids the server flipped, when it names them (an older relay does not: clear everything).
    if (p.eventIds?.length)
      activityState.markRowsRead(p.eventIds, activityState.handUnreadServerIds());
    else activityState.markAllRead();
    rowShape = true;
  }
  if (p.eventId) {
    activityState.markReadByServerId(p.eventId);
    rowShape = true;
  }
  if (p.sourceType === "daemon" && p.sourceId) {
    activityState.markReadByDaemonRequest(p.sourceId);
    rowShape = true;
  }
  // An invite/join request decided, or a live row marked by (type, source) on another tab.
  if (p.sourceType !== "daemon" && p.eventType && p.sourceId) {
    activityState.markReadBySource(p.eventType, p.sourceId);
    rowShape = true;
  }
  if (!p.all && (p.threadRootId || p.eventIds?.length)) {
    activityState.markReadByEventIds(p.eventIds ?? [], p.sourceIds ?? []);
    rowShape = true;
  }
  // The messages these rows quoted were deleted (the root and its cascaded replies).
  if (p.deletedSourceIds) {
    activityState.tombstoneMessages(p.deletedSourceIds);
    rowShape = true;
  }
  return rowShape;
}

// P2 Item 9: a conversation read on one of my devices also cleared its Activity
// feed items. Reconcile the local ActivityState so the badge matches everywhere.
client.on("activity_read_changed", (p) => {
  // The total spans every workspace, so a read elsewhere (another device or workspace) must lower
  // it even when the rows below are not ours. Y's per-workspace badge is still left alone.
  if (p.workspaceId !== workspaceState.current?.id) {
    activityState.setTotalUnread(p.totalUnread, p.hiddenUnreadByWorkspace);
    if (typeof p.unread === "number") activityState.applyWorkspaceUnread(p.workspaceId, p.unread);
    otherWorkspaceCountSet();
    return;
  }
  if (p.channelId) activityState.markReadByChannel(p.channelId);
  if (p.dmPeerId) activityState.markReadByDmPeer(p.dmPeerId);
  if (p.taskId) activityState.markReadByTask(p.taskId);
  const rowShape = applyRowReadShapes(p);
  // The server's count is the truth after any read it applied: row shapes AND the scoped reads
  // (a channel/DM/task read clears rows this client may not hold, e.g. past its loaded page).
  const scoped = !!(p.channelId || p.dmPeerId || p.taskId);
  if ((rowShape || scoped) && typeof p.unread === "number") activityState.setServerUnread(p.unread);
  activityState.setTotalUnread(p.totalUnread, p.hiddenUnreadByWorkspace);
  // Web push: close the OS notifications for what was just read (the open page does it, since a
  // silent clear push would make Chrome show "updated in the background"), then sync the badge.
  void clearReadWebNotifications([...(p.channelIds ?? []), ...(p.channelId ? [p.channelId] : [])]);
});

client.on("delete_message", ({ messageId, workspaceId }) => {
  // Its alert row stops quoting it (also covers a delete path the relay does not fan out for).
  activityState.tombstoneMessages([messageId]);
  // …and the agent stream view of a real session (a rewind cut on another device).
  agentStreamState.dropDeletedMessage(messageId, workspaceId);
  const apply = (messages: import("../core/types.js").Message[]) =>
    tombstoneDeleted(messages, messageId);
  channelState.messages = apply(channelState.messages);
  dmState.messages = apply(dmState.messages);
  if (threadState.parentId === messageId) {
    threadState.replies = [];
  } else {
    threadState.replies = apply(threadState.replies);
  }
  // Mirror the tombstone (+ reply cascade) into the cache. No-op when the cache is off.
  if (cacheOn()) void cache.cacheDeleteMessage(messageId);
});

client.on("edit_message", ({ messageId, text, source }) => {
  // A system line changing state (source) is not an author edit: no "edited" stamp.
  const updateMessages = (messages: import("../core/types.js").Message[]) =>
    messages.map((m) =>
      m.id === messageId ? { ...m, text, ...(source ? { source } : { updatedAt: Date.now() }) } : m,
    );
  channelState.messages = updateMessages(channelState.messages);
  dmState.messages = updateMessages(dmState.messages);
  // …and the thread pane (the last holdout among these in-place patchers).
  threadState.replies = updateMessages(threadState.replies);
  // Mirror the edit into the cache. No-op when the cache is off.
  if (cacheOn()) void cache.cacheEditMessage(messageId, text, source);
});

// URL unfurls finished fetching server-side (Tier 2): patch the message in place
// wherever it's rendered. The relay scopes the broadcast (channelId for channels,
// toId/fromId for DMs), but a message can be on screen in the channel timeline,
// the open DM, or the thread panel — so we patch all three by messageId. Older
// messages without an `unfurls` field are unaffected (this only assigns when the
// id matches, mirroring the edit/reaction in-place patch pattern).
client.on("message_unfurled", ({ messageId, unfurls }) => {
  const patch = (messages: import("../core/types.js").Message[]) =>
    messages.map((m) => (m.id === messageId ? { ...m, unfurls } : m));
  channelState.messages = patch(channelState.messages);
  dmState.messages = patch(dmState.messages);
  threadState.replies = patch(threadState.replies);
  // Mirror the unfurls into the cache. No-op when the cache is off.
  if (cacheOn()) void cache.cacheUnfurlMessage(messageId, (unfurls as unknown[]) ?? []);
});

// A turn ended: its summary (steps, outcome, missing permissions) lands on its posts, on screen and in
// both cache tiers, which paint a conversation that was not on screen when this arrived.
client.on("message_work_updated", ({ messageIds, work }) => {
  rowWork.setFromFrame(messageIds, work);
  channelState.messages = patchWorkSummary(channelState.messages, messageIds, work);
  dmState.messages = patchWorkSummary(dmState.messages, messageIds, work);
  threadState.replies = patchWorkSummary(threadState.replies, messageIds, work);
  cache.memSetWork(messageIds, work);
  if (cacheOn()) void cache.cacheWorkMessages(messageIds, work);
});

// A signed card action settled server-side (#600): the server re-rendered the
// card and broadcast the result. Patch the message's `card` in place wherever
// it's rendered (channel timeline, open DM, thread panel) — same in-place
// single-message pattern as the unfurl handler above, and mirrored into both
// cache tiers, which paint a conversation that was not on screen when this arrived.
client.on("message_card_updated", ({ messageId, card, posted }) => {
  // an answered approval card's Alerts row resolves (a share card also leaves the DM)
  if (card?.kind === "approval" && card.resolution)
    activityState.settleApprovalCard(messageId, posted);
  const patch = (messages: import("../core/types.js").Message[]) =>
    messages.map((m) => (m.id === messageId ? { ...m, card } : m));
  channelState.messages = patch(channelState.messages);
  dmState.messages = patch(dmState.messages);
  threadState.replies = patch(threadState.replies);
  // …and the agent transcript, where a cybo's DM card lives as a stream ENTRY
  // rather than a Message (mergeCrossSessionReply). Without this the promoted-
  // default DM surface got only the card's OPTIMISTIC settle and reverted to
  // live buttons whenever the authoritative broadcast re-rendered it.
  agentStreamState.patchCardByMessageId(messageId, card);
  // …and a human turn's forwarded card, which the transcript reads by row id.
  forwardedTurnCards.patch(messageId, card);
  cache.memSetCard(messageId, card);
  if (cacheOn()) void cache.cacheCardMessage(messageId, card);
});

// A DM that forwards a message: its card is what the agent transcript draws for that turn.
client.on("dm", (msg) => forwardedTurnCards.note(msg));

// A cybo work_session was created / status-changed / agent-changed (CYBORG-791).
// The relay already DM-scopes delivery, so upsert whatever arrives into the thin
// live store (the sidebar/chips UI reads it — CYBORG-794).
client.on("work_session_changed", ({ session }) => {
  const prev = workSessionsState.byId[session.id];
  applyWorkSession(session);
  clearTypingOnSessionTurnEnd(prev, session, typingSurfaces(), agentStreamState.invariants);
});

// A legacy frame names its reactor by bare id, and a cybo's may be its session id.
function rosterReactor(id: string): Partial<import("../core/types.js").Reactor> | undefined {
  const cyboId = cyboState.list.some((c) => c.id === id)
    ? id
    : workspaceState.agents?.find((a) => a.agentId === id)?.cyboId;
  const cybo = cyboId ? cyboState.list.find((c) => c.id === cyboId) : undefined;
  if (cybo) return { id: cybo.id, kind: "cybo", name: cybo.name };
  const name = authState.getMemberName(id);
  return name ? { name } : undefined;
}

// Patch one message's reactions in every store that renders it, the thread root included.
function patchReactions(
  messageId: string,
  next: (
    reactions: import("../core/types.js").Message["reactions"],
  ) => import("../core/types.js").Reaction[],
): void {
  const update = (messages: import("../core/types.js").Message[]) =>
    messages.map((m) => (m.id === messageId ? { ...m, reactions: next(m.reactions) } : m));
  channelState.messages = update(channelState.messages);
  dmState.messages = update(dmState.messages);
  threadState.replies = update(threadState.replies);
  if (threadState.parent?.id === messageId) threadState.parent = update([threadState.parent])[0];
  // …and the agent chat's rows, which live in no message store (row-reactions.svelte.ts).
  rowReactions.patch(messageId, next);
}

// My own echo is applied (cross-device sync); setMyReaction makes it land once.
client.on("reaction", (wire) => {
  const change = normalizeReactionEvent(wire, rosterReactor);
  if (!change) return;
  const viewer = authState.user;
  patchReactions(change.messageId, (reactions) => applyReactionChange(reactions, change, viewer));
  if (cacheOn()) void cache.cacheApplyReaction(change, viewer);
});

client.on("members_updated", ({ workspaceId, members }) => {
  // Self-removal: an admin removed ME. The broadcast still reaches this socket
  // (removal doesn't unsubscribe it), so when my own row is gone from a
  // workspace I have loaded, drop it like deleteWorkspace does — filter the
  // list and switch away if it was active — instead of showing a stale
  // workspace until reconnect.
  // isMe, NOT `m.userId === authState.user?.id` (CYBORG-1060): a roster row names a
  // human by whichever of their two ids the TRANSPORT that wrote it held, so my own row
  // can arrive under my daemon-LOCAL id while `authState.user.id` is the cloud one. The
  // raw compare then found no row for me in a roster I am very much in, and this
  // DESTRUCTIVE branch fired — the workspace vanished from my list and I was switched
  // away with nobody having removed me. Widening any further would be a security bug,
  // so the VIEWER is on one side and the row on the other: isMe only ever adds MY OWN
  // second id (both from my own auth response), never another human's, so a roster that
  // genuinely no longer contains me still evicts exactly as before.
  const me = authState.user;
  if (
    me?.id &&
    workspaceState.list.some((w) => w.id === workspaceId) &&
    !members.some((m) => isMe(m.userId, me))
  ) {
    workspaceState.list = workspaceState.list.filter((w) => w.id !== workspaceId);
    if (workspaceState.current?.id === workspaceId) {
      const next = workspaceState.list[0];
      if (next) {
        void selectWorkspace(next);
      } else {
        workspaceState.current = null;
      }
    }
    return;
  }
  if (workspaceState.current?.id === workspaceId) {
    workspaceState.members = members;
    authState.setMemberImagesFromMembers(members);
  }
  // Promoted or demoted elsewhere: my role (every admin gate) follows the roster.
  const myRole = members.find((m) => isMe(m.userId, me))?.role;
  if (isWorkspaceRole(myRole)) {
    const rows = [workspaceState.current, ...workspaceState.list].filter(
      (w) => w?.id === workspaceId,
    );
    for (const w of rows) if (w && w.role !== myRole) w.role = myRole;
  }
});

// A task was created/updated/deleted anywhere (this client, another member, a
// cybo) → patch the board live so it reflects without a manual refetch. Guarded
// on the active workspace so a stale broadcast can't leak into a since-switched
// one. created/updated share one path (patch by id, else prepend) — a "created"
// row is new (prepend), an "updated" row already exists (merge onto it). A
// "deleted" op drops the row by id (an open detail peek for it falls back to
// "not found").
//
// The incoming `task` is already the camelCase `Task` model (client.ts ran the
// broadcast through `mapRawTask`), so every Tasks-Redesign field
// (projectId/parentId/stateId/sequenceId/cycleId/priority/startDate/dueAt/
// labelIds/moduleIds) rides along and is carried onto the board row instead of
// being dropped on reconcile. We MERGE (spread the existing row first) rather
// than replace so a partial broadcast — a recurrence-spawned `{ id }` or a
// future field-scoped `updated` op where `mapRawTask` defaulted the absent
// Plane columns to null/[] — can't wipe fields the board already knows.
// CYBORG-1176: the only path that fetches the board. One call at workspace open; later
// callers join it or ask for a delta. The tasks_changed reducer below keeps it live.
const taskBoard = new TaskBoardSync({
  fetchBoard: (wsId, onPartial) => client.fetchTaskBoard(wsId, undefined, onPartial),
  fetchChanges: (wsId, since, heldIds) => client.fetchTaskBoardChanges(wsId, since, heldIds),
  currentWorkspaceId: () => workspaceState.current?.id,
  getTasks: () => workspaceState.tasks ?? [],
  setTasks: (tasks) => {
    workspaceState.tasks = tasks;
  },
});

client.on("tasks_changed", ({ workspaceId, op, task }) => {
  if (workspaceState.current?.id !== workspaceId) return;
  // Guard the rune-backed list: it can be transiently undefined mid workspace
  // switch (the broadcast can land between clearing and re-seeding tasks).
  const tasks = workspaceState.tasks ?? [];
  if (op === "deleted") {
    workspaceState.tasks = tasks.filter((t) => t.id !== task.id);
    activityState.removeTask(task.id);
    return;
  }
  const existing = tasks.find((t) => t.id === task.id);
  if (!existing) {
    workspaceState.tasks = [task, ...tasks];
  } else {
    // MUTATE the existing $state task in place rather than replacing the object/
    // array reference: Svelte 5 deep-reactivity tracks per-property writes, so
    // surgical assignments re-render only what changed and keep object identity
    // stable for keyed list items. Copy each incoming field onto the live row.
    for (const key of Object.keys(task) as (keyof typeof task)[]) {
      // Per-task scheduling — never let a partial broadcast that OMITTED `schedule`
      // (`undefined`) blank an existing cadence chip. The server denormalizes the
      // schedule onto every edit broadcast, but this guard is defense in depth:
      // preserve the prior schedule unless the broadcast actually carried the field.
      // An explicit `null` (the schedule was detached via its own *_schedule RPC)
      // DOES fall through and clears the chip.
      if (key === "schedule" && task.schedule === undefined) continue;
      (existing as unknown as Record<string, unknown>)[key] = task[key];
    }
  }
});

// A page was created/updated/archived/deleted server-side. The pages list view
// owns its own state + refetch, so this global handler is an intentional no-op
// stub (registered for consistency with tasks_changed); the list view subscribes
// to `pages_changed` itself and refetches off this same broadcast. Kept here so
// the event mapping is wired end-to-end and a future global pages cache has an
// obvious hook point.
client.on("pages_changed", () => {
  // intentional: pages live-update is handled by the pages list view's own
  // `client.on("pages_changed", …)` subscription + refetch, not global state.
});

async function listRoster(wsId: string, daemonId?: string) {
  const issuedAt = rosterEdgeSeq;
  rosterReadSeq.set(daemonId ?? "*", issuedAt);
  const scope = daemonId ?? "all";
  coldStartTrace("roster-req", {
    ws: wsId,
    scope,
    online: Object.keys(daemonStatusState.all).filter(
      (id) => daemonStatusState.all[id] === "online",
    ),
  });
  let res: Awaited<ReturnType<typeof client.listAgentsWithMeta>>;
  try {
    res = await (daemonId
      ? client.listAgentsWithMeta(wsId, daemonId)
      : client.listAgentsWithMeta(wsId));
  } catch (err) {
    coldStartTrace("roster-err", { ws: wsId, scope, err: errorSlug(err) });
    throw err;
  }
  const late = [...daemonOnlineEdge]
    .filter(([id, seq]) => seq > issuedAt && (!daemonId || id === daemonId))
    .map(([id]) => id);
  const perDaemon: Record<string, number> = {};
  for (const a of res.agents) {
    const key = a.daemonId ?? "none";
    perDaemon[key] = (perDaemon[key] ?? 0) + 1;
  }
  coldStartTrace("roster-res", {
    ws: wsId,
    scope,
    perDaemon,
    pending: res.pendingDaemons,
    noDaemonAnswered: res.noDaemonAnswered,
    late,
  });
  const pendingDaemons = late.length === 0 ? res.pendingDaemons : [...res.pendingDaemons, ...late];
  return { ...res, pendingDaemons, late };
}

// The caller of a roster read schedules the late daemons' own reads (#3629): listRoster
// only reads, so it never calls back into refetchDaemonRoster.
function rereadLateDaemons<T extends { late: readonly string[] }>(wsId: string, res: T): T {
  for (const id of res.late) {
    const seq = daemonOnlineEdge.get(id) ?? 0;
    if (seq > coveredSeq(id)) void refetchDaemonRoster(wsId, id, "late-edge");
  }
  return res;
}

// Read ONE daemon's rows and lay them over the held roster: the online edge of a daemon
// whose rows this tab does not hold, and a roster answer that was issued before that
// daemon connected (see listRoster).
async function refetchDaemonRoster(
  wsId: string,
  daemonId: string,
  reason: "online-edge" | "late-edge" | "boot-queued" | "retry" = "online-edge",
): Promise<void> {
  if (daemonOutsideWorkspace(daemonId, wsId)) return;
  coldStartTrace("roster-reread", { ws: wsId, daemon: daemonId, reason });
  // Capture per-field clocks BEFORE the roster request. A model/mode/thinking
  // event or local selection that lands while the request is in flight must
  // outrank that older snapshot when it returns.
  const controlTokens = new Map(
    workspaceState.agents
      .filter((agent) => agent.daemonId === daemonId)
      .map((agent) => [agent.agentId, agentStreamState.controlSnapshotToken(agent.agentId)]),
  );
  try {
    // WithMeta + nextAgentSnapshot (Part C), aligned with selectWorkspace /
    // reconcileWorkspacePanels: a plain listAgents + direct assignment wiped a
    // SLOW daemon's rows on a multi-daemon workspace (the relay finalizes on a
    // fixed window and answers short). The snapshot carries reached-but-silent
    // daemons' rows over (pendingDaemons) instead of blanking them.
    // CYBORG-1210: a tab that holds a roster reads only this daemon's rows.
    // A boot-queued read is always scoped: the unscoped answer it would race is still landing.
    const scoped = workspaceState.agents.length > 0 || reason === "boot-queued";
    const res = rereadLateDaemons(wsId, await listRoster(wsId, scoped ? daemonId : undefined));
    if (workspaceState.current?.id === wsId) {
      for (const agent of res.agents) {
        if (agent.daemonId !== daemonId) continue;
        const token =
          controlTokens.get(agent.agentId) ?? agentStreamState.controlSnapshotToken(agent.agentId);
        agentStreamState.reconcileControlsFromSnapshot(
          agent.agentId,
          {
            model: agent.model,
            currentModeId: agent.modeId,
            availableModes: agent.availableModes,
            thinkingOptionId: agent.thinkingOptionId,
          },
          token,
        );
      }
      const daemonConnected = daemonConnStatus() === "connected";
      workspaceState.agents = scoped
        ? mergeDaemonRows(workspaceState.agents, daemonId, res.agents, res.pendingDaemons)
        : nextAgentSnapshot(workspaceState.agents, res.agents, daemonConnected, res.pendingDaemons);
      // The daemon just came online, so this is the
      // authoritative real roster — refresh the cold-start cache, which also
      // drops any agent removed while it was offline (no ghost rows).
      saveRosterCache(
        rosterStore(),
        wsId,
        authState.user?.email,
        workspaceState.agents,
        daemonConnected,
      );
      attentionState.seedFromAgents(res.agents); // #591: seed derived attention
    }
  } catch (err) {
    coldStartTrace("roster-reread-fail", {
      ws: wsId,
      daemon: daemonId,
      reason,
      err: errorSlug(err),
    });
    reportUiFailure("roster.daemon_reread", err, { workspaceId: wsId, daemonId });
    if (reason !== "retry") failedRereads.set(daemonId, wsId);
  }
  // Re-hydrate the open sessions THIS daemon owns (#518): the agent_stream
  // only resumes "from now" after the daemon re-attaches, so output streamed
  // (and turns that progressed) while it was away are lost to a client that
  // stayed connected. The list refetch above is registry-only — without this
  // the open session shows a gap until a manual reopen. Fire-and-forget +
  // workspace-guarded internally; scoped to this daemon to skip redundant
  // fetches of sessions whose daemon never dropped. Runs after the list
  // refetch so workspaceState.agents has the fresh daemon ownership.
  if (workspaceState.current?.id === wsId) {
    void catchUpAgentTimelines(wsId, daemonId);
  }
}

function retryFailedRereads(): void {
  for (const [daemonId, wsId] of failedRereads) {
    failedRereads.delete(daemonId);
    if (workspaceState.current?.id === wsId) void refetchDaemonRoster(wsId, daemonId, "retry");
  }
}

// Runs where a selection settles: the edges parked during boot are applied now, and a
// daemon whose edge no read has covered yet gets its own read.
function drainBootOnlineDaemons(): void {
  const wsId = workspaceState.current?.id;
  for (const daemonId of bootOnlineDaemons) {
    bootOnlineDaemons.delete(daemonId);
    daemonStatusState.set(daemonId, "online");
    if (!wsId) continue;
    if ((daemonOnlineEdge.get(daemonId) ?? 0) > coveredSeq(daemonId)) {
      void refetchDaemonRoster(wsId, daemonId, "boot-queued");
    } else {
      void catchUpAgentTimelines(wsId, daemonId);
    }
  }
}

// Daemons that sent a daemon_status since the relay socket last (re)connected; cleared at
// the start of every reconnect burst.
const daemonsNoticedThisSocket = accountSync.collection(
  "daemonsNoticedThisSocket",
  new Set<string>(),
);

// Which workspace's full daemon rows the liveness poll holds, and as of which daemon
// edge. A daemon_status edge bumps the epoch: a reconnecting daemon may come back with
// a new version/host, which only the full rows carry.
let daemonRowsEpoch = 0;
let daemonRowsFull: { wsId: string; epoch: number } | null = null; // null: no rows held yet
accountSync.own("daemonRowsFull", {
  reset: () => {
    daemonRowsFull = null; // null: no rows held
  },
  size: () => (daemonRowsFull ? 1 : 0),
});

// Another workspace's copy, dropped before the dedup so it cannot swallow ours.
function foreignDaemonCopy(daemonId: string, workspaceId: string | undefined): boolean {
  if (!workspaceId) return false;
  const named = daemonEdgeWorkspaces.get(daemonId) ?? new Set<string>();
  named.add(workspaceId);
  daemonEdgeWorkspaces.set(daemonId, named);
  const current = workspaceState.current;
  return !!current && !workspaceState.switching && workspaceId !== current.id;
}

function noteDaemonOffline(daemonId: string): void {
  // A Codex plan held past its turn lives only in that daemon's memory: it died with it.
  for (const a of workspaceState.agents) {
    if (a.daemonId === daemonId) agentStreamState.dropCarriedPlans(a.agentId);
  }
}

client.on("daemon_status", ({ daemonId, status, workspaceId }) => {
  if (foreignDaemonCopy(daemonId, workspaceId)) return;
  daemonRowsEpoch += 1;
  // CYBORG-1205: one copy per shared workspace; only the first is a change. Deduped on the
  // last PUSH: list_daemons says "online" for 60s after a heartbeat, which swallowed the edge.
  // The first notice since the relay socket (re)connected is an edge, never a copy: the
  // reconnect's roster read may have answered this daemon from the offline mirror.
  const firstThisSocket = !daemonsNoticedThisSocket.has(daemonId);
  daemonsNoticedThisSocket.add(daemonId);
  const prev = firstThisSocket ? undefined : daemonStatusState.pushed(daemonId);
  const action = daemonNoticeAction(
    prev,
    status,
    daemonId,
    workspaceState.agents,
    !firstThisSocket && daemonOnlineEdge.has(daemonId),
  );
  coldStartTrace("daemon-status", {
    daemon: daemonId,
    status,
    prev,
    action,
    current: workspaceState.current?.id,
    held: workspaceState.agents.filter((a) => a.daemonId === daemonId).length,
  });
  if (action !== "ignore") retryFailedRereads();
  if (action === "ignore") return;
  if (status === "online") daemonOnlineEdge.set(daemonId, nextRosterEdge());
  // No committed workspace (a cold boot) or a switch in flight: nothing can read the roster
  // yet, so park the edge unmarked instead of spending it; the commit re-reads it.
  if (status === "online" && (!workspaceState.current || workspaceState.switching)) {
    bootOnlineDaemons.add(daemonId);
    daemonState.setStatus(daemonId, status);
    return;
  }
  bootOnlineDaemons.delete(daemonId);
  daemonStatusState.set(daemonId, status);
  // Mirror into the global daemon state so sidebar/detail dots stay realtime.
  daemonState.setStatus(daemonId, status);
  // The daemon dropped offline — forget its workspace's roster answer so the Home
  // card re-arms "Connecting.." until the roster genuinely re-answers on the next
  // online self-heal (Part A). Without this a reconnect would keep the stale
  // "answered" and paint a false "Healthy · 0 agents" during the reconnect window.
  if (status === "offline") noteDaemonOffline(daemonId);
  // list_agents/fetch_cybos are forwarded to the daemon, so they return empty
  // while the daemon is down — e.g. after an account switch re-claims and
  // restarts the embedded daemon, or right after a hard reload while the daemon
  // re-attaches. selectWorkspace already ran against the offline daemon and
  // nothing else refreshes the lists, so the agents panel / cybo templates stay
  // empty until a manual reload. Refetch on the offline→online transition.
  if (action === "apply" && status === "online" && workspaceState.current) {
    // The tab already holds this daemon's rows: the notice is the change. No roster
    // re-read; the open sessions it owns still catch up (#518).
    const wsId = workspaceState.current.id;
    void catchUpAgentTimelines(wsId, daemonId);
    if (cyboRosterMirror.mirrorOnly) healCyboRoster(wsId);
    return;
  }
  if (action === "refetch-roster" && workspaceState.current) {
    const wsId = workspaceState.current.id;
    void refetchDaemonRoster(wsId, daemonId);
    healCyboRoster(wsId);
  }
});

// #705 (REQUEST → NOTIFY → APPROVE): a daemon-access request was created (→ the
// daemon OWNER) or resolved (→ the REQUESTER). Pushed per-user, so the payload is
// always relevant to the current user. Upsert the row (the inbox/affordances read
// from here), then — when I'm the owner of a freshly PENDING request — bump the
// Activity feed so the "Permissions" tab surfaces an approvable row immediately
// (the activity_new push below handles the persisted badge; this is the live one).
// Agent transfers: both people's open apps follow a request (the Alerts card, the sender's pill).
client.on("cybo_transfer_request_changed", ({ request }) => {
  if (workspaceState.current?.id !== request.workspaceId) return;
  cyboTransferRequestsState.upsert(request);
});

client.on("daemon_access_request_changed", ({ request }) => {
  if (workspaceState.current?.id !== request.workspaceId) return;
  const existing = daemonAccessRequestsState.byId(request.id);
  daemonAccessRequestsState.upsert(request as DaemonAccessRequest);
  // Only bump activity for a NEWLY pending request I own (not on resolve, and not
  // for my own outgoing request). The owner is the daemon's ownerId.
  const me = authState.user;
  const daemon = daemonState.byId(request.daemonId);
  const iOwn = isMe(daemon?.ownerId, me);
  const isNewPending = request.status === "pending" && !existing;
  if (iOwn && isNewPending && !isMe(request.requesterId, me)) {
    const daemonLabel = daemon?.label ?? "your daemon";
    activityState.pushDaemonAccessRequest(
      request.id,
      request.requesterName ?? "Someone",
      request.requesterId,
      `requested access to ${daemonLabel}`,
    );
    fireAlertBanner(
      request.requesterName ?? "Someone",
      `requested access to ${daemonLabel}`,
      `/workspace/${request.workspaceId}/daemons/${request.daemonId}`,
    );
  }
  // When MY pending request is approved, the grant landed server-side — refetch
  // daemon access so daemonState reflects it live (canAccess flips without a full
  // workspace reload).
  if (request.status === "approved" && isMe(request.requesterId, me)) {
    client
      .fetchDaemonAccess(request.workspaceId)
      .then(({ access }) => {
        daemonState.setAccess(access);
        return;
      })
      // intentional: best-effort live access refresh; the next workspace load reconciles otherwise.
      .catch(() => {});
  }
});

// Re-read the workspace's machine rows (and, with `withAccess`, its access rows) after
// a live push, so the Machines page, a machine's page and New Session match without a
// reload. Rows + access only: a live refresh must not move the user's machine pick.
// A failure keeps the stale view until the next workspace load, and is reported.
export function refreshWorkspaceMachines(
  workspaceId: string,
  opts: { withAccess: boolean; withInvites?: boolean },
): Promise<void> {
  return Promise.all([
    client.listDaemons(workspaceId),
    opts.withAccess ? client.fetchDaemonAccess(workspaceId) : undefined,
  ])
    .then(([{ daemons, workerBillingLive, pendingWorkerInvites }, accessRes]) => {
      if (workspaceState.current?.id !== workspaceId) return;
      daemonStatusState.load(daemons);
      daemonState.setWorkerBillingLive(workerBillingLive);
      daemonState.refreshList(daemons);
      if (accessRes) daemonState.setAccess(accessRes.access);
      if (opts.withInvites && pendingWorkerInvites) {
        workerInvitesState.load(pendingWorkerInvites.map(normalizeWorkerInvite));
      }
      return;
    })
    .catch((err: unknown) => {
      reportUiFailure("machines.liveRefresh", err, { workspaceId, withAccess: opts.withAccess });
    });
}

// Worker tier: an invite was minted/canceled elsewhere, or a machine redeemed
// one (status "connected"). Upsert the row (the Pending Workers rows + the
// AddWorkerDialog waiting step read from here); on connect, the workspace just
// gained a daemon, so refresh the daemon list too.
client.on("worker_invite_changed", ({ invite }) => {
  if (workspaceState.current?.id !== invite.workspaceId) return;
  const view = normalizeWorkerInvite(invite);
  workerInvitesState.upsert(view as WorkerInvite);
  if (view.status === "connected") {
    void refreshWorkspaceMachines(invite.workspaceId, { withAccess: false });
  }
});

// Who can use a machine changed elsewhere (an admin set the default, or granted /
// revoked a person): re-read the machine rows and access rows.
client.on("workspace_machines_changed", ({ workspaceId }) => {
  if (workspaceState.current?.id !== workspaceId) return;
  void refreshWorkspaceMachines(workspaceId, { withAccess: true });
});

// Page-access-request live push, structural clone of daemon_access_request_changed
// above (minus scopes — a page grant is per-user, all-or-nothing). Upsert the
// row (PageCard's "Requested" pending-collapse reads from here), then bump the
// Activity feed for
// a NEWLY PENDING request. Unlike the daemon case there's no client-side
// page-ownership index to double-check "iOwn" against — but the server only ever
// pushes a "pending" row to the page's OWNER (relay-standalone.ts's
// cyborg:request_page_access targets pageOwnerId, never the requester), so
// isNewPending alone is a safe signal that this client IS the owner.
client.on("page_access_request_changed", ({ request }) => {
  if (workspaceState.current?.id !== request.workspaceId) return;
  const existing = pageAccessRequestsState.byId(request.id);
  pageAccessRequestsState.upsert(request as PageAccessRequest);
  const isNewPending = request.status === "pending" && !existing;
  if (isNewPending) {
    activityState.pushPageAccessRequest(
      request.id,
      null,
      request.requesterName ?? "Someone",
      request.requesterId,
      `${request.requesterName ?? "Someone"} requested access to a page`,
    );
  }
});

// Join-request live push (corporate-email onboarding), structural sibling of
// daemon_access_request_changed above. The server pushes it to the workspace
// owner+admins on CREATE (a new pending request to approve) and to the requester
// on DECIDE (approved/rejected). Keep the shared store fresh so the Members
// approvals list + the Settings-tab pending badge update live, and — on a NEWLY
// pending request an admin can act on — bump the Activity feed. When MY OWN
// request is approved, my membership landed server-side; refresh the workspace
// list so the newly-joined workspace appears without a full reload.
client.on("join_request_changed", ({ request }) => {
  const me = authState.user;
  const forCurrentWorkspace = workspaceState.current?.id === request.workspaceId;
  // Only mirror into the (workspace-scoped) store for the active workspace, so a
  // push for another workspace can't inflate this workspace's badge.
  if (forCurrentWorkspace) {
    // Adopt the workspace if the store hasn't been seeded yet (badge before the
    // Members panel is ever opened), so the count is live from the first push.
    if (joinRequestsState.workspaceId === null) {
      joinRequestsState.load(request.workspaceId, []);
    }
    joinRequestsState.upsert(request);
  }
  // My own request was approved → the membership exists server-side now. Refresh
  // the workspace list so the joined workspace is navigable immediately.
  if (request.status === "approved" && isMe(request.userId, me)) {
    refreshWorkspaceList().catch((e) =>
      reportUiFailure("workspace.join_approved_refresh", e, { workspaceId: request.workspaceId }),
    );
  }
});

// My membership in a workspace became active (an invite accepted in another tab, a join approved):
// re-read the list so it shows up in this already-open tab without a reload. A host that seated me
// as an EXTERNAL is no membership, so the re-read would never list it: subscribe first (the relay's
// external arm widens this socket to my seats there), then fold in the entry the push carries.
client.on("workspace_membership_changed", ({ workspaceId, externalWorkspace }) => {
  if (externalWorkspace) {
    void (async () => {
      await client.subscribeWorkspace(externalWorkspace.id);
      workspaceState.list = upsertExternalWorkspace(workspaceState.list, externalWorkspace);
    })().catch((e) => reportUiFailure("workspace.external_seated", e, { workspaceId }));
    return;
  }
  refreshWorkspaceList().catch((e) =>
    reportUiFailure("workspace.membership_changed_refresh", e, { workspaceId }),
  );
});

/** Re-read my memberships from the server into `workspaceState.list`. */
export async function refreshWorkspaceList(): Promise<void> {
  const workspaces = await client.fetchWorkspaces();
  // fetch_workspaces answers memberships only — merge, never assign, or the
  // refresh evicts the host workspaces this viewer reaches as an external.
  workspaceState.list = mergeMembershipRefresh(workspaceState.list, workspaces);
}

// Invite-request live push (CYBORG-1087), sibling of join_request_changed above:
// pushed to the owners/admins when a member asks for an invite, and to the
// requester plus the admins when it is decided. Mirroring it into the
// workspace-scoped store keeps the Members approvals list and the Settings badge
// live; a push for another workspace is dropped so no badge can bleed across.
client.on("invite_request_changed", ({ request }) => {
  if (workspaceState.current?.id !== request.workspaceId) return;
  if (inviteRequestsState.workspaceId === null) {
    inviteRequestsState.load(request.workspaceId, []);
  }
  inviteRequestsState.upsert(request);
});

// Page-access REVOKE live push (review finding #1 — an approve used to be
// irrevocable): the owner just removed this user's standing grant. A neutral
// toast only — the read gate is authoritative on the next fetch_page
// regardless of whether this push lands, and PageEditor's notFound wall is
// deliberately existence-blind (do not add page-specific reactive logic
// there, see its own header comment), so this stays a plain notification
// with no page-state side effect.
client.on("page_access_revoked", () => {
  toast("Your access to a page was removed by its owner.");
});

// Page-mention live bell (CYBORG-304): route into the feed keyed by
// page:<pageId> (idempotent: pushPageMention ignores a duplicate id). projectId
// is required to deep-link — a malformed push without one is dropped rather
// than producing a dead-end row. Extracted from the activity_new handler below
// (already at the complexity cap) so this branch's own conditions don't count
// against it. Returns true iff this event was a page_mention (and was routed).
function routePageMentionActivity(e: {
  id: string;
  sourceType?: string;
  eventType: string;
  sourceId: string;
  projectId?: string | null;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
}): boolean {
  if (e.sourceType !== "page" || e.eventType !== "page_mention" || !e.projectId) return false;
  activityState.pushPageMention(
    e.sourceId,
    e.projectId,
    e.actorName ?? "Someone",
    e.actorId,
    e.previewText ?? "",
    e.id,
  );
  return true;
}

// Page-access-request live bell, structural clone of routePageMentionActivity
// above: routes into the feed keyed by page_access_request:<requestId>
// (idempotent: pushPageAccessRequest ignores a duplicate id, so this is safe to
// race against the page_access_request_changed handler below — whichever
// arrives first wins, the other is a no-op). Unlike page_mention, a missing
// projectId does NOT drop the push — ActivityPane falls back to a fetchPage
// lookup on click (same fallback page_mention itself uses for a reload seed).
// Returns true iff this event was a page_access_request (and was routed).
function routePageAccessRequestActivity(e: {
  sourceType?: string;
  eventType: string;
  sourceId: string;
  projectId?: string | null;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
}): boolean {
  if (e.sourceType !== "page" || e.eventType !== "page_access_request") return false;
  activityState.pushPageAccessRequest(
    e.sourceId,
    e.projectId ?? null,
    e.actorName ?? "Someone",
    e.actorId,
    e.previewText ?? "requested access to a page",
  );
  return true;
}

// Page-access-request RESOLVED live bell — sibling of
// routePageAccessRequestActivity above. Without this the requester's "approved"
// / "denied" notification was dropped entirely live: this eventType fell
// through every branch in the activity_new handler below and died on the
// `e.eventType !== "daemon_access_request"` guard at the end — no bell, no
// badge, no row, until the next reload happened to re-seed it. e.sourceId here
// IS the pageId (the server stamps it that way for this event type — see
// relay-standalone.ts / dispatcher.ts's resolve handlers — unlike the pending
// event above, where sourceId is the request id), which pushPageAccessResolved
// keys on directly. Returns true iff this event was a page_access_request_resolved
// (and was routed).
function routePageAccessResolvedActivity(e: {
  id: string;
  sourceType?: string;
  eventType: string;
  sourceId: string;
  projectId?: string | null;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
}): boolean {
  if (e.sourceType !== "page" || e.eventType !== "page_access_request_resolved") return false;
  activityState.pushPageAccessResolved(
    e.sourceId,
    e.projectId ?? null,
    e.actorName ?? "Someone",
    e.actorId,
    e.previewText ?? "Your page access request was resolved",
    e.id,
  );
  return true;
}

// Thread-reply live bell (Mattermost CRT parity, knowledge/17): the relay now
// also fans this out live (was persist-only, so the root author's badge only
// updated on next reload). pushThreadReply keys by the same `mention:<sourceId>`
// scheme the server seed uses for thread_reply rows, so this dedupes against
// seedFromServer instead of double-counting. Extracted from the activity_new
// handler below (adding the page-access-request routing pushed it over the
// complexity cap) so this branch's own conditions don't count against it.
// Returns true iff this event was a thread_reply (and was routed).
function routeThreadReplyActivity(e: {
  id?: string;
  eventType: string;
  sourceId: string;
  channelId: string | null;
  dmPeerId?: string | null;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
}): boolean {
  if (e.eventType !== "thread_reply" || !e.sourceId) return false;
  const channel = e.channelId
    ? workspaceState.channels.find((c) => c.id === e.channelId)
    : undefined;
  activityState.pushThreadReply(
    e.sourceId,
    e.actorName ?? "Someone",
    e.actorId,
    e.previewText ?? "replied to a thread you're following",
    e.channelId,
    channel?.name ?? null,
    e.dmPeerId ?? null, // null: wire null
    e.id,
  );
  return true;
}

// Work-session status live bell (CYBORG-839), structural sibling of the route*
// helpers above (extracted for the same reason: the activity_new handler is at
// its complexity cap). The relay emits this ONLY to the session's human
// initiator, so anything that arrives here is for us. The cybo's display name is
// resolved from the live roster first — the relay's own actorName is a
// best-effort PG lookup that can miss — so the row reads "Sprite finished the
// work", never "Someone". Returns true iff this event was a work_session_* one.
function routeWorkSessionActivity(e: {
  id: string;
  sourceType?: string;
  eventType: string;
  sourceId: string;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
  actorSessionId?: string | null;
}): boolean {
  // A DM-anchored session's row is sourceType "dm" with sourceId = the cybo (the DM peer).
  if (e.sourceType !== "work_session" && e.sourceType !== "dm") return false;
  if (!isWorkSessionActivityEventType(e.eventType)) return false;
  const cyboName = e.actorId ? cyboNameOf({ cyboId: e.actorId }, cyboState.list) : undefined;
  activityState.pushWorkSessionStatus(
    e.id,
    e.sourceId,
    e.eventType,
    cyboName ?? e.actorName ?? "Your agent",
    e.actorId,
    e.previewText ?? "",
    e.sourceType === "dm" ? "dm" : undefined,
    e.actorSessionId ?? undefined,
  );
  return true;
}

// Connect live bell, structural sibling of the route* helpers above (extracted for
// the same reason: the activity_new handler is at its complexity cap). BOTH ends of a
// connection's life ride this one branch — the outsider accepted, or their seat was
// revoked by something inside their own company — because the difference between them
// is copy, which lives in the registry, not routing. The relay emits either ONLY to
// the inviter — owners+admins when that account is gone, see connect-external.ts's
// externalAcceptRecipients — so anything that arrives here is addressed to us.
// Without this branch the event fell through every helper above and died on the
// `e.eventType !== "daemon_access_request"` guard below: no badge, no row, until a
// reload happened to re-seed it (the same drop routePageAccessResolvedActivity was
// written to fix). The channel name comes off the live roster (the lookup
// routeThreadReplyActivity uses) so the row names the channel; e.channelId is
// legitimately null when the seat channel was deleted, and the row is still worth
// showing then. Returns true iff this event was one of the two (and was routed).
// CYBORG-1087: the requester's own alert when an owner/admin approves or declines their
// invite request. Without this the event died on the `daemon_access_request` guard below —
// no badge, no row, until a reload re-seeded it. The relay addresses it to the requester
// alone, so anything arriving here is ours. Returns true iff it was routed.
function routeInviteRequestDecisionActivity(e: {
  id: string;
  sourceType?: string;
  eventType: string;
  sourceId: string;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
}): boolean {
  if (e.sourceType !== "invite_request") return false;
  if (e.eventType !== "invite_request_approved" && e.eventType !== "invite_request_declined") {
    return false;
  }
  activityState.pushInviteRequestDecision(
    e.eventType,
    e.id,
    e.sourceId,
    e.actorName ?? "An admin",
    e.actorId,
    e.previewText ?? "",
  );
  return true;
}

function routeExternalConnectionActivity(e: {
  id: string;
  sourceType?: string;
  eventType: string;
  sourceId: string;
  channelId: string | null;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
}): boolean {
  if (e.sourceType !== "connection") return false;
  if (e.eventType !== "external_invite_accepted" && e.eventType !== "external_connection_revoked") {
    return false;
  }
  const channel = e.channelId
    ? workspaceState.channels.find((c) => c.id === e.channelId)
    : undefined;
  activityState.pushExternalConnectionActivity(
    e.eventType,
    e.id,
    e.sourceId,
    e.actorName ?? "Someone",
    e.actorId,
    e.previewText ??
      (e.eventType === "external_invite_accepted"
        ? "accepted your invitation"
        : "no longer has access"),
    e.channelId,
    channel?.name ?? null,
  );
  return true;
}

// CYBORG-1074: "Slack stopped syncing" (relay slack-connection-health.ts), addressed only to
// owners, admins and whoever connected Slack. Same live-bell shape as the Connect rows above.
function routeSlackConnectionLostActivity(e: {
  id: string;
  sourceType?: string;
  eventType: string;
  sourceId: string;
  previewText: string | null;
}): boolean {
  if (e.sourceType !== "integration") return false;
  if (e.eventType !== "slack_connection_lost" && e.eventType !== "slack_post_as_me_stopped") {
    return false;
  }
  activityState.pushSlackConnectionLost(e.id, e.sourceId, e.previewText ?? "", e.eventType);
  return true;
}

// CYBORG-1231: agent transfer alerts (cybo_moved to the target machine's owner, cybo_handed_off
// to the recipient). sourceId is the cybo id.
function routeCyboTransferActivity(e: {
  id: string;
  sourceType?: string;
  eventType: string;
  sourceId: string;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
  detail?: CyboTransferDetail | NoticeDetail;
}): boolean {
  if (e.sourceType !== "cybo") return false;
  if (
    e.eventType !== "cybo_moved" &&
    e.eventType !== "cybo_handed_off" &&
    e.eventType !== "cybo_needs_home"
  ) {
    return false;
  }
  activityState.pushCyboTransferActivity(
    e.eventType,
    e.id,
    e.sourceId,
    e.actorName ?? "Someone",
    e.actorId,
    e.previewText ?? "",
    e.detail as CyboTransferDetail | undefined, // cybo_* kinds only carry the transfer shape
  );
  return true;
}

// Alerts catalog: the kinds with no bespoke route above all land here, so a kind the server
// emits is never dropped live (it used to die on the deny-by-default guard below and only
// appear after a reload). permission_request is also raised from the agent stream; the server
// now persists it too, and pushCatalogActivity merges the two by (agent, request id).
// Dedup is by activity id inside pushCatalogActivity.
const CATALOG_LIVE_KINDS: ReadonlySet<string> = new Set([
  "approval_requested",
  "schedule_needs_input",
  "daemon_access_request_resolved",
  "agent_error",
  "workspace_join_request",
  "workspace_join_request_decided",
  "workspace_invite_request",
  "worker_daemon_disabled",
  "worker_first_connect",
  "worker_daemon_transferred",
  "daemon_access_revoked",
  "workspace_machine_removed",
  "system_notice",
  "order_mirrored",
  "agent_dm_received",
  "permission_request",
  "plan_pending",
  "page_agent_done",
  "page_agent_failed",
  "goal_completed",
  "subagent_failed",
  "dm_reply_not_delivered",
  "session_resumed",
  "session_resume_skipped",
  "session_archived_tasks_stopped",
  "session_close_held",
  "agent_refused_contained",
  "agent_teammate_unanswered",
  "session_contained",
  // CYBORG-1408 W4 recordAction notify kinds (user-action-telemetry.ts / the daemon twin).
  "cockpit_access_granted",
  "cockpit_access_revoked",
  "daemon_access_granted",
  "daemon_access_changed",
  "daemon_used_by_other",
  "action.terminal_lost",
  "agent_deleted_by_other",
  "agent_edited_by_other",
  "webhook_fire_skipped",
  "daemon_offline",
  "cybo_transfer_request",
  "cybo_transfer_resolved",
]);

function routeCatalogActivity(e: {
  id: string;
  eventType: string;
  sourceId: string;
  messageId?: string | null;
  channelId: string | null;
  dmPeerId?: string | null;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
  detail?: CyboTransferDetail | NoticeDetail;
  actorSessionId?: string | null;
  isRead?: boolean;
}): boolean {
  // A daemon's row arrives namespaced (`action.goal_completed`); read it back as its kind.
  const kind = activityKindOf(e.eventType);
  if (!CATALOG_LIVE_KINDS.has(kind)) return false;
  const channel = e.channelId
    ? workspaceState.channels.find((c) => c.id === e.channelId)
    : undefined;
  activityState.pushCatalogActivity(kind as ActivityEventType, {
    ...e,
    channelName: channel?.name ?? null, // null: the channel is not on this roster
  });
  return true;
}

// Task notification (assignment / status change): route into the feed keyed by
// task:<taskId> (idempotent: pushTask ignores a duplicate id), mirroring the
// daemon-access-request live push. sourceType="task" disambiguates from a
// message-sourced event sharing the task_* prefix.
function routeTaskActivity(e: {
  id: string;
  sourceType?: string;
  eventType: string;
  sourceId: string;
  actorName: string | null;
  actorId: string | null;
  previewText: string | null;
  tasks?: TaskAlertCard[];
}): boolean {
  if (e.sourceType !== "task") return false;
  if (e.eventType !== "task_assigned" && e.eventType !== "task_status_changed") return false;
  activityState.pushTask(
    e.sourceId,
    e.eventType,
    e.actorName ?? "Someone",
    e.actorId,
    e.previewText ?? "",
    e.id,
    e.tasks,
  );
  if (e.eventType === "task_assigned" && workspaceState.current?.id) {
    fireAlertBanner(
      e.actorName ?? "Someone",
      e.previewText || "assigned you a task",
      `/workspace/${workspaceState.current.id}/tasks/item/${e.sourceId}`,
    );
  }
  return true;
}

// #705: the server also persists a generic activity_new badge for a daemon-access
// request. Mention/DM activity already flows through the message paths, so this
// handler only routes the daemon_access_request type into the feed — keyed by the
// request id (sourceId), so it dedupes against the live daemon_access_request_changed
// push (idempotent: pushDaemonAccessRequest ignores a duplicate id).
client.on("activity_new", (e) => {
  // AFTER routing: setTotalUnread stores total minus this workspace's unread, and the server
  // total already counts this row, so setting it first and then routing the row in added it
  // twice (dock showed total+1). finally: another workspace's row (early return) still counts.
  try {
    if (workspaceState.current?.id !== e.workspaceId) return;
    if (!e.sourceId) return;
    const sourceId = e.sourceId;
    // Every row pushed while routing takes the server's createdAt, not this browser's clock.
    activityState.stampLive(e.createdAt, () => {
      if (routeTaskActivity(e)) return;
      if (routePageMentionActivity(e)) return;
      if (routePageAccessRequestActivity(e)) return;
      if (routePageAccessResolvedActivity(e)) return;
      if (routeThreadReplyActivity(e)) return;
      if (routeWorkSessionActivity(e)) return;
      if (routeExternalConnectionActivity(e)) return;
      if (routeSlackConnectionLostActivity(e)) return;
      if (routeInviteRequestDecisionActivity(e)) return;
      if (routeCyboTransferActivity(e)) return;
      if (routeCatalogActivity(e)) return;
      if (e.eventType !== "daemon_access_request") return;
      activityState.pushDaemonAccessRequest(
        sourceId,
        e.actorName ?? "Someone",
        e.actorId,
        e.previewText ?? "requested access to your daemon",
      );
    });
  } finally {
    applyActivityNewTotals(e);
  }
});

function applyActivityNewTotals(e: {
  workspaceId: string;
  totalUnread?: number;
  hiddenUnreadByWorkspace?: unknown;
}): void {
  activityState.setTotalUnread(e.totalUnread, e.hiddenUnreadByWorkspace);
  if (workspaceState.current?.id !== e.workspaceId) alertSplitChanged();
}

// A human DM carries no activity_new (only dm_broadcast, which has no total): the server sends the
// fresh cross-workspace total AFTER writing the dm_received row. Registered after the "dm" handler
// above, so a current-workspace DM is already routed (and counted in the feed) when this applies,
// the same ordering activity_new relies on; a DM for another workspace just moves the total.
client.on("activity_badge", (e) => {
  activityState.setTotalUnread(e.totalUnread, e.hiddenUnreadByWorkspace);
  alertSplitChanged();
});

client.on("channel_created", ({ channel }) => {
  if (workspaceState.current?.id !== channel.workspaceId) return;
  if (workspaceState.channels.some((c) => c.id === channel.id)) return;
  // Only surface a channel we created (we're its first member). A public channel
  // someone ELSE creates is NOT auto-added to our sidebar — that diverged from the
  // server's member-only channel list (it showed up live but vanished on reload)
  // and let a non-member open a composer in a channel they never joined. Others'
  // public channels are found via "Browse channels" → read-only Preview → Join.
  // isMe: a channel created through a DAEMON-side path is stamped with the creator's
  // daemon-local id, so the raw compare failed for the person who JUST created it and
  // the channel didn't appear until a reload.
  if (isMe(channel.createdBy, authState.user)) {
    workspaceState.channels = [...workspaceState.channels, channel];
  }
});

// #608: a group DM was created. Unlike channel_created (which only self-adds
// the creator), a group DM is added to the sidebar for EVERY member — the
// broadcast carries the authoritative memberIds. A hidden channel must never
// appear for a non-member, so gate strictly on membership.
client.on("group_dm_created", ({ channel, memberIds }) => {
  if (workspaceState.current?.id !== channel.workspaceId) return;
  if (!authState.user?.id || !memberIds.includes(authState.user.id)) return;
  if (workspaceState.channels.some((c) => c.id === channel.id)) return;
  workspaceState.channels = [...workspaceState.channels, channel];
});

client.on("channel_updated", ({ channel }) => {
  // Another workspace's channel (a socket subscribed to several) is never this sidebar's.
  if (workspaceState.current?.id !== channel.workspaceId) return;
  // Archived channels are excluded from the active list — drop a newly-archived
  // channel here so every client (not just the actor) sees it disappear. The
  // archive broadcast reuses channel_updated rather than a bespoke event.
  if (channel.isArchived) {
    workspaceState.channels = workspaceState.channels.filter((c) => c.id !== channel.id);
    // retire, as channel_deleted does: getMentionCounts skips archived channels, so a kept
    // count was a badge with no row until the next reseed (unread.invariant.key_without_row).
    notificationState.retire(channel.workspaceId, channel.id);
    unreadFlagState.clearUnread(channel.workspaceId, channel.id);
    if (channelState.activeId === channel.id) {
      channelState.activeId = null;
    }
    return;
  }
  const exists = workspaceState.channels.some((c) => c.id === channel.id);
  if (exists) {
    workspaceState.channels = workspaceState.channels.map((c) =>
      c.id === channel.id ? { ...c, ...channel } : c,
    );
    return;
  }
  // Channel re-surfaced (e.g. unarchived) and not in our list. Only show it if
  // it's public or we created it — never silently re-add a private channel to a
  // non-member's sidebar (mirrors the channel_created visibility guard).
  if (!channel.isPrivate || isMe(channel.createdBy, authState.user)) {
    workspaceState.channels = [...workspaceState.channels, channel];
  }
});

// A channel moved department (anyone, any client): re-read the departments this person may
// see (also refills the sidebar's warm cache) and re-file the channel from that answer.
export async function refileChannelFromServer(
  workspaceId: string,
  channelId: string,
): Promise<void> {
  if (workspaceState.current?.id !== workspaceId) return;
  if (!workspaceState.channels.some((c) => c.id === channelId)) return;
  const { channelProjects } = await fetchProjects();
  if (workspaceState.current?.id !== workspaceId) return;
  // null: in no department this person can see
  const projectId = channelProjects.find((cp) => cp.channelId === channelId)?.projectId ?? null;
  workspaceState.channels = workspaceState.channels.map((c) =>
    c.id === channelId ? { ...c, projectId } : c,
  );
}

client.on("channel_project_changed", ({ workspaceId, channelId }) => {
  refileChannelFromServer(workspaceId, channelId).catch((err) => {
    reportUiFailure("sidebar.channel_project_changed", err, { workspaceId, channelId });
  });
});

// #630: per-channel mention-roster refresh signal. Bumped whenever a member or
// cybo is added/removed to a channel. An OPEN composer's roster effect
// (MessageInput) depends on versionOf(channelId), so a bump makes it re-fetch
// fetch_channel_members / fetch_channel_cybos → @-autocomplete updates LIVE
// without a reload. Per-channel so only the affected composer re-fetches.
class ChannelRosterRefreshState {
  versions = $state<Record<string, number>>({});
  versionOf(channelId: string | null | undefined): number {
    return channelId ? (this.versions[channelId] ?? 0) : 0;
  }
  bump(channelId: string): void {
    // Mutate the key directly (not reassign the object): Svelte 5's deep $state
    // proxy then re-runs ONLY effects observing THIS channel's version, instead
    // of every composer watching any channel (gemini review).
    this.versions[channelId] = (this.versions[channelId] ?? 0) + 1;
  }
}
export const channelRosterState = new ChannelRosterRefreshState();

client.on("channel_member_added", async ({ channelId, userId, workspaceId }) => {
  if (workspaceState.current?.id !== workspaceId) return;
  // Self-add: make sure the channel shows in my sidebar.
  if (userId === authState.user?.id && !workspaceState.channels.some((c) => c.id === channelId)) {
    try {
      const channels = await client.fetchChannels(workspaceId);
      const added = channels.find((c) => c.id === channelId);
      if (added && !workspaceState.channels.some((c) => c.id === added.id)) {
        workspaceState.channels = [...workspaceState.channels, added];
      }
    } catch {
      // ignore — channel will appear on next workspace refresh
    }
    // Re-joined a private channel: its alert rows were hidden server-side while out, so refetch
    // to bring them back live (rare event, one fetch).
    void refreshActivity();
    // Joining can reveal a department I could not see before (it is listed only for people in
    // one of its channels): re-read the list, or the channel files under the default one.
    fetchProjects().catch((err) => {
      reportUiFailure("sidebar.channel_member_added_departments", err, { workspaceId, channelId });
    });
  }
  // #630: ANY member added to a channel I'm in → refresh its mention roster so an
  // open composer's @-autocomplete picks up the new member live (previously this
  // handler returned early for non-self adds, so the roster went stale until a
  // reload).
  refreshChannelRosterOnMembershipChange(
    {
      eventWorkspaceId: workspaceId,
      channelId,
      currentWorkspaceId: workspaceState.current?.id,
      myChannelIds: workspaceState.channels.map((c) => c.id),
    },
    (id) => channelRosterState.bump(id),
  );
});

// #630: a cybo added/removed to a channel I'm in → refresh its mention roster so
// @-autocomplete includes/drops the cybo live. These broadcasts are SEPARATE
// from channel_member_added (cybos aren't user members) and were previously
// unhandled on the client — the exact repro (adding a cybo needed a reload).
function refreshRosterForChannelCybo({
  channelId,
  workspaceId,
}: {
  channelId: string;
  workspaceId: string;
}): void {
  refreshChannelRosterOnMembershipChange(
    {
      eventWorkspaceId: workspaceId,
      channelId,
      currentWorkspaceId: workspaceState.current?.id,
      myChannelIds: workspaceState.channels.map((c) => c.id),
    },
    (id) => channelRosterState.bump(id),
  );
}
client.on("channel_cybo_added", refreshRosterForChannelCybo);
client.on("channel_cybo_removed", refreshRosterForChannelCybo);

// #640: a cybo was edited (name/model/avatar/role/…) anywhere in the workspace →
// patch it in the shared roster so the @-mention autocomplete, the agents pane,
// and the in-channel cybo header reflect it LIVE instead of going stale until a
// reload. Sibling of channel_cybo_added (#633): UPDATE must broadcast too.
client.on("cybo_updated", ({ cybo }) => {
  // Cross-workspace isolation: one socket is subscribed to ALL workspaces I
  // belong to (subscribeAndSeedAllWorkspaces), so a cybo edited in workspace B
  // reaches this handler while viewing workspace A. Drop it — same guard as the
  // channel_* handlers.
  if (workspaceState.current?.id !== cybo.workspaceId) return;
  const match = cyboState.list.find((c) => c.id === cybo.id);
  if (!match) return; // not a cybo we track — nothing to do
  // Mutate the $state roster element IN PLACE so Svelte 5's deep-proxy
  // reactivity is surgical (only the changed keys re-trigger; the array ref and
  // sibling cybos stay untouched) — same pattern as #633's live refresh.
  applyCyboUpdate(match, cybo);
  applyCeoSeat(cyboState.list, cybo);
  // The roster refresh above isn't enough: live agent rows AND archived-session
  // rows carry their OWN denormalized copy of the cybo's name/avatar, and
  // resolveCyboChatIdentity prefers that denorm over the roster. Re-stamp it so
  // the sidebar / DM list / agents roster reflect the edit live (what a restart
  // did for free by re-fetching the rows).
  // The merged roster row, not the frame: a partial frame carries no name/avatar.
  applyCyboDenormToSessions(workspaceState.agents, cybo.id, {
    name: match.name,
    avatar: match.avatar ?? null, // null: the row has no photo
  });
  applyCyboDenormToSessions(sessionState.list, cybo.id, {
    name: match.name,
    avatar: match.avatar ?? null, // null: the row has no photo
  });
  // Bump the open channel's mention roster so an active composer's autocomplete
  // re-renders the cybo's new name immediately.
  if (channelState.activeId) channelRosterState.bump(channelState.activeId);
});

// #644: a cybo was created/deleted anywhere in the workspace → keep the shared
// roster (agents pane + @-mention autocomplete) in sync live instead of stale
// until reload. Completes the create/add/update/remove broadcast symmetry
// (#633 add, #641 update). Both ops mutate cyboState.list in place (push/splice)
// and are idempotent, so the actor (who already refetched/filtered locally) and
// every other member converge without duplicates.
client.on("cybo_created", ({ cybo }) => {
  // Cross-workspace isolation (load-bearing): one socket is subscribed to ALL
  // workspaces I belong to (subscribeAndSeedAllWorkspaces), so a cybo created in
  // workspace B reaches this handler while I'm viewing workspace A. Without this
  // guard appendCybo blindly appends it and it shows LIVE until reload. Same
  // guard as the channel_* handlers.
  if (workspaceState.current?.id !== cybo.workspaceId) return;
  appendCybo(cyboState.list, cybo);
  // The roster is non-empty now (append never removes) — loadError has no
  // valid surface anymore (it's exclusively an empty-roster state).
  cyboState.loadError = false;
});
client.on("cybo_deleted", ({ cyboId, workspaceId }) => {
  if (workspaceState.current?.id !== workspaceId) return;
  removeCybo(cyboState.list, cyboId);
  // The roster was only HALF the prune, and the missing half is the reported
  // bug: the cybo's agent BINDINGS live in workspaceState.agents, and the DM
  // sidebar row, /agent/<id> and ProfilePanel all resolve identity, the status
  // dot and the composer from a binding — never from the roster. So a deleted
  // agent kept a live-looking DM row, a green dot (honest about its daemon,
  // mute about the agent), a name invented from its provider, and a working
  // composer, in the same app where "View full profile" already 404'd.
  // The render path does NOT rely on this landing (a client that was offline,
  // restarted, or is another device never sees this broadcast) — see
  // isDeletedCyboBinding — but every client that DOES get it converges now
  // instead of at the next reload.
  // CYBORG-1182: its conversations go too, or every catch-up keeps re-reading a session
  // no daemon has. Collected BEFORE the prune below removes the bindings that name them.
  const goneAgentIds = workspaceState.agents
    .filter((a) => a.cyboId === cyboId)
    .map((a) => a.agentId);
  removeCyboSessions(workspaceState.agents, cyboId);
  for (const agentId of goneAgentIds) agentStreamState.clear(agentId);
  // A deleted cybo may have been a channel member → bump the open channel's
  // mention roster so it drops out of an active composer's autocomplete live.
  if (channelState.activeId) channelRosterState.bump(channelState.activeId);
});

client.on("channel_deleted", ({ channelId, workspaceId }) => {
  workspaceState.channels = workspaceState.channels.filter((c) => c.id !== channelId);
  activityState.dropChannel(channelId);
  // retire, not clear: the channel is gone, so getMentionCounts can never name it
  // again and no row renders it. A fetch_unread already IN FLIGHT when it was
  // deleted still counts it, and `clear` would let that stale answer put the
  // badge straight back with nothing left to click. Same reasoning as the
  // archived-session frame in retireArchivedAgentBadge.
  notificationState.retire(workspaceId, channelId);
  unreadFlagState.clearUnread(workspaceId, channelId);
  if (channelState.activeId === channelId) {
    channelState.activeId = null;
  }
  // Drop the cached scope so a deleted channel's history doesn't resurface on a
  // cold open. No-op when the cache is off.
  if (cacheOn()) {
    const key = channelKey(workspaceId, channelId);
    void cache.cacheDropScope(key, key);
  }
});

client.on("channel_member_removed", ({ channelId, userId, workspaceId }) => {
  if (userId === authState.user?.id) {
    const ch = workspaceState.channels.find((c) => c.id === channelId);
    if (ch?.isPrivate) {
      workspaceState.channels = workspaceState.channels.filter((c) => c.id !== channelId);
      activityState.dropChannel(channelId);
      // retire, not clear — same as channel_deleted above: off the member list of
      // a private channel, getMentionCounts stops returning it for this viewer.
      notificationState.retire(workspaceId, channelId);
      unreadFlagState.clearUnread(workspaceId, channelId);
      if (channelState.activeId === channelId) {
        channelState.activeId = null;
      }
      // Removed from a private channel — drop its cached history. No-op when the cache is off.
      if (cacheOn()) {
        const key = channelKey(workspaceId, channelId);
        void cache.cacheDropScope(key, key);
      }
    }
  }
  // #630/#635 symmetry: a member removed from a channel I'm still in (incl.
  // actor≠self — someone ELSE removed) → refresh the mention roster so they drop
  // out of @-autocomplete live. The self-from-private case above already dropped
  // the channel from my list, so this gate no-ops there (channel ∉ myChannelIds).
  refreshChannelRosterOnMembershipChange(
    {
      eventWorkspaceId: workspaceId,
      channelId,
      currentWorkspaceId: workspaceState.current?.id,
      myChannelIds: workspaceState.channels.map((c) => c.id),
    },
    (id) => channelRosterState.bump(id),
  );
});

// Cyborg7 Connect — who in this room is from another company, kept live.
//
// The directory is fetched ONCE, on workspace entry (routes/workspace/[id]/
// +layout.svelte), so a host page that was already open when a partner accepted an
// invitation held an empty orgByUser: the partner's first message arrived live and
// painted no organization mark until the page was reloaded.
//
// A channel membership change is exactly the event that moves this payload — who is
// in which channel IS orgsByChannel, and an external accepting now lands here as a
// channel_member_added (relay-standalone.ts, cyborg:accept_external_invite). Hooked
// to THAT and never to an incoming message: a membership change is rare, a message
// is not, and re-reading the directory per message would be a round trip per line of
// chat.
//
// A RE-READ, never a client-side patch of the map. The frame carries a userId and a
// channelId and no organization at all, so the only way to name the company from it
// would be to guess one — and a wrong company name on somebody's message is worse
// than no mark. The server stays the single source of the name.
//
// Guarded on the workspace the store is actually holding rather than the one on
// screen: load() is what sets connectState.workspaceId, so a frame for anywhere else
// (or one arriving before the first load) is dropped instead of re-pointing the
// store at a workspace this shell never entered.
function refreshConnectDirectory({ workspaceId }: { workspaceId: string }): void {
  if (connectState.workspaceId !== workspaceId) return;
  void connectState.load(workspaceId);
}
client.on("channel_member_added", refreshConnectDirectory);
client.on("channel_member_removed", refreshConnectDirectory);
// A partner's workspace renamed: home hears it only as its feedback channel's channel_updated
// (feedback-channel.ts followName), and the org name is the directory's. Shared channels only.
client.on("channel_updated", ({ channel }) => {
  if (connectState.orgsInChannel(channel.id).length > 0) refreshConnectDirectory(channel);
});

// ─── Daemon status state ────────────────────────────────────────

class DaemonStatusState {
  private graceRecheck: ReturnType<typeof setTimeout> | undefined;
  private statuses = $state<Record<string, "online" | "offline">>({});
  // Realtime overrides, keyed by daemon id, written ONLY by set() from the
  // `daemon_status` push. Mirrors DaemonState.liveStatus (plugins/agents/
  // state.svelte.ts:2207): a live push is authoritative "right now", so it wins
  // over the grace-based list value that load() brings in — and is cleared only
  // by a later contradicting push (a real reconnect), never on a timer, exactly
  // like DaemonState. Without this, the 30s liveness poll's blind full-replace
  // load() clobbers a just-pushed `offline` back to the relay's 60s-grace
  // "online", reading a dead daemon as healthy.
  private liveStatus = $state<Record<string, "online" | "offline">>({});
  // ms timestamp of the last LIVE offline→online transition per daemon, stamped
  // ONLY by set() (the `daemon_status` push) — never by load(). Drives the Home
  // card's brief "Connecting.." settle beat right after a reconnect/boot (see
  // SETTLE_MS in daemon-status.ts). load() is a snapshot (initial fetch / 30s
  // poll), not a live transition, so stamping there would give a plain page
  // refresh of an already-healthy daemon a false beat.
  private connectedAtMs = $state<Record<string, number>>({});
  // Relay-sourced `lastSeenAt` per daemon, from the most recent load(). Plain
  // fields, not $state — nothing renders them; they exist only to let load()
  // tell "the relay has heard from this daemon since we were told it died" from
  // "the relay's 60s grace window is still echoing a dead daemon".
  private lastSeen: Record<string, number> = {};
  // What `lastSeen` was for a daemon at the moment an "offline" override was
  // recorded. A later load reporting a STRICTLY GREATER lastSeenAt refutes that
  // override — this is what lets a missed one-shot `online` push self-heal
  // instead of pinning the pill offline until the app is quit. See
  // reconcileDaemonStatuses in core/daemon-status.ts.
  private offlineBaseline: Record<string, number> = {};

  set(daemonId: string, status: "online" | "offline"): void {
    // Read the CURRENT effective status BEFORE we overwrite it, so we can detect
    // a genuine offline→online transition (the only moment worth stamping).
    const prev = this.get(daemonId);
    if (status === "online") {
      // Stamp only on a real transition INTO online — not on online→online (else
      // it would re-stamp on every heartbeat push and never settle).
      if (prev !== "online") {
        this.connectedAtMs = { ...this.connectedAtMs, [daemonId]: Date.now() };
      }
      // Back online → the override is gone, so its refutation baseline is dead
      // weight; drop it rather than let it apply to some later offline push.
      delete this.offlineBaseline[daemonId];
    } else {
      // Dropped offline → drop the stamp so the next reconnect starts a fresh
      // settle window rather than reusing a stale one.
      if (daemonId in this.connectedAtMs) {
        const { [daemonId]: _drop, ...rest } = this.connectedAtMs;
        this.connectedAtMs = rest;
      }
      // Record the relay's last-known heartbeat for this daemon AS OF NOW. Any
      // later load reporting a newer one proves the daemon came back and this
      // override is stale. Only stamp when we actually have a relay value: with
      // no prior list row we cannot distinguish a fresh heartbeat from the dying
      // daemon's own final one, and load() adopts a baseline on the next pass.
      if (daemonId in this.lastSeen) {
        this.offlineBaseline[daemonId] = this.lastSeen[daemonId];
      }
    }
    this.liveStatus = { ...this.liveStatus, [daemonId]: status };
    this.statuses = { ...this.statuses, [daemonId]: status };
  }

  get(daemonId: string): "online" | "offline" {
    return this.statuses[daemonId] ?? "offline";
  }

  // The last status a live daemon_status push set; a polled list's "online" is not a push.
  pushed(daemonId: string): "online" | "offline" | undefined {
    return this.liveStatus[daemonId];
  }

  connectedAt(daemonId: string): number | null {
    return this.connectedAtMs[daemonId] ?? null;
  }

  get onlineCount(): number {
    return Object.values(this.statuses).filter((s) => s === "online").length;
  }

  get all(): Record<string, "online" | "offline"> {
    return this.statuses;
  }

  load(daemons: Array<{ id: string; status: string; lastSeenAt?: number | null }>): void {
    const loaded: Record<string, "online" | "offline"> = {};
    const lastSeen: Record<string, number> = {};
    for (const d of daemons) {
      loaded[d.id] = d.status === "online" ? "online" : "offline";
      if (typeof d.lastSeenAt === "number") lastSeen[d.id] = d.lastSeenAt;
    }
    this.lastSeen = lastSeen;
    // Reconcile against the live overrides so a grace-based "online" from the
    // relay's list can't overwrite an authoritative realtime "offline" push. The
    // reconcile also prunes overrides for daemons no longer in the list, so
    // load([]) at logout fully resets both maps.
    const { statuses, live } = reconcileDaemonStatuses(
      loaded,
      this.liveStatus,
      lastSeen,
      this.offlineBaseline,
    );
    this.statuses = statuses;
    this.liveStatus = live;
    // Keep a baseline only for an override that actually survived, and adopt one
    // for a surviving offline override that had none (set() couldn't stamp it
    // because we had no list row yet). Adopting here is safe — this load already
    // agreed the override stands — and gives that daemon a heal path on the next
    // load instead of pinning it offline forever.
    const prunedBaseline: Record<string, number> = {};
    for (const [id, status] of Object.entries(live)) {
      if (status !== "offline") continue;
      const baseline = this.offlineBaseline[id] ?? lastSeen[id];
      if (baseline !== undefined) prunedBaseline[id] = baseline;
    }
    this.offlineBaseline = prunedBaseline;
    // Prune the settle stamps the same way liveStatus is pruned: keep a stamp
    // ONLY for a daemon that is still in the list AND still reconciles to online.
    // This does NOT stamp (load is a snapshot, not a transition) — it only drops
    // stamps for daemons that vanished from the list or flipped to offline, so
    // load([]) at logout fully resets it and a daemon that dies mid-poll loses
    // its stale window.
    const prunedConnectedAt: Record<string, number> = {};
    for (const [id, ts] of Object.entries(this.connectedAtMs)) {
      if (statuses[id] === "online") prunedConnectedAt[id] = ts;
    }
    this.connectedAtMs = prunedConnectedAt;
    this.armGraceRecheck(daemons);
  }

  private armGraceRecheck(daemons: Array<{ status: string; lastSeenAt?: number | null }>): void {
    clearTimeout(this.graceRecheck);
    const due = daemonGraceRecheckMs(daemons, Date.now());
    if (due === undefined) return;
    this.graceRecheck = setTimeout(() => {
      const wsId = workspaceState.current?.id;
      if (wsId) void refreshDaemonLiveness(wsId);
    }, due);
  }

  clear(): void {
    clearTimeout(this.graceRecheck);
    this.statuses = {};
    this.liveStatus = {};
    this.connectedAtMs = {};
    this.lastSeen = {};
    this.offlineBaseline = {};
  }
}

export const daemonStatusState = new DaemonStatusState();

/**
 * Re-read the daemon rows so their persisted heartbeat (`lastSeenAt`) is current.
 *
 * `daemon_status` only fires on CONNECT and DISCONNECT — a daemon that stays up
 * emits nothing — so a page that judges liveness by heartbeat age has to refetch
 * or it will eventually declare a perfectly healthy daemon dead. Callers should
 * poll this while such a surface is mounted, and stop on unmount.
 *
 * Best-effort: a failed poll leaves the previous rows in place (a dropped request
 * is not evidence the daemon died) and the next tick corrects it.
 * CYBORG-1198: full rows on the first call and after any daemon_status edge; every
 * other tick reads id/status/lastSeenAt only (mergeDaemonLiveness).
 * Resolves true only when this workspace's rows landed; false is "unknown", not "none".
 */
export async function refreshDaemonLiveness(workspaceId: string): Promise<boolean> {
  try {
    let daemons: Awaited<ReturnType<typeof client.listDaemons>>["daemons"] | null = null;
    if (daemonRowsFull?.wsId === workspaceId && daemonRowsFull.epoch === daemonRowsEpoch) {
      const res = await client.listDaemonLiveness(workspaceId);
      if (workspaceState.current?.id !== workspaceId) return false;
      daemons =
        res.livenessOnly === true
          ? mergeDaemonLiveness([...daemonState.list, ...daemonState.masked.rows], res.daemons)
          : res.daemons;
    }
    if (!daemons) {
      // The epoch is read BEFORE the request: an edge that lands while it is in
      // flight bumps it, so the next tick reads full rows again.
      const epoch = daemonRowsEpoch;
      const full = await client.listDaemons(workspaceId);
      // Ignore a response that landed after the user moved to another workspace.
      if (workspaceState.current?.id !== workspaceId) return false;
      daemons = full.daemons;
      daemonState.setWorkerBillingLive(full.workerBillingLive);
      daemonRowsFull = { wsId: workspaceId, epoch };
    }
    daemonStatusState.load(daemons);
    daemonState.refreshList(daemons);
    return true;
  } catch (err) {
    // A polled tick: one report per workspace per 10 minutes.
    if (shouldReport(`daemons.liveness_refresh:${workspaceId}`, 600_000)) {
      reportUiFailure("daemons.liveness_refresh", err, { workspaceId });
    }
    return false;
  }
}

// Live 3-state daemon connection status. Reads the reactive stores and delegates the mapping
// to the pure mapDaemonConnStatus (unit-tested without importing this module).
function daemonConnStatus(): DaemonConnStatus {
  return mapDaemonConnStatus({
    relayStatus: connectionState.status,
    hasConnectedOnce: connectionState.hasConnectedOnce,
    healthLastChecked: daemonHealthState.lastChecked,
    healthOverall: daemonHealthState.overall,
    onlineCount: daemonStatusState.onlineCount,
  });
}

// Reconnect action for the offline indicator: drop the (possibly zombie) socket
// and reconnect immediately, bypassing the backoff. The daemon_status
// offline→online self-heal then refetches the roster. Same primitive the
// foreground/heartbeat paths use.
//
// On DESKTOP that socket recycle is not enough, and used to be the whole
// action: "Offline" there almost always means the LOCAL daemon PROCESS is
// dead, and forceReconnect only recycles the relay guest WebSocket — it can do
// nothing to a dead daemon. The single recovery affordance we show the user
// could not possibly fix the thing it appears to fix. So on desktop, restart
// the local daemon through the same `window.cyborg7Desktop` IPC bridge
// claim_desktop_daemon uses, then recycle the socket so it re-registers.
// In the browser there is no local daemon and no bridge, so the recycle IS the
// whole fix and the behaviour is unchanged.
export async function reconnectDaemon(): Promise<void> {
  const desktop = (
    window as unknown as {
      cyborg7Desktop?: {
        invoke: (command: string, args?: Record<string, unknown>) => Promise<unknown>;
      };
    }
  ).cyborg7Desktop;
  if (!desktop) {
    client.forceReconnect({ userInitiated: true });
    return;
  }
  // Don't SIGTERM a daemon that is perfectly alive. "Offline" on this pill is not
  // proof the process died: `daemon_status` fires only on connect/disconnect (see
  // refreshDaemonLiveness), so a client that misses the one-shot `online` edge —
  // a laptop waking from sleep, or this very function's forceReconnect() below
  // landing as the daemon re-registers — shows Offline over a healthy daemon.
  // Restarting then costs 10-45s of REAL downtime to fix a display bug, and burns
  // the shell's crash-loop budget (5 starts / 10 min) two clicks at a time. That
  // is exactly what happened on 2026-08-24 (#2470).
  //
  // So re-read the relay's rows first. That single refetch both answers "is it
  // actually down?" and, via the reconcile's lastSeenAt bound, clears a stale
  // override — so the common case now heals with no restart at all.
  const wsId = workspaceState.current?.id;
  const localId = (
    (await desktop.invoke("desktop_daemon_status").catch((err) => {
      // intentional: this probe only decides whether we can SKIP the restart. If
      // it fails we fall through and restart, which is the pre-existing
      // behaviour — never block the user's one recovery affordance on it.
      reportUiFailure("desktop.daemon_status_before_restart", err);
      return null;
    })) as { serverId?: string | null } | null
  )?.serverId;
  if (wsId && localId) {
    await refreshDaemonLiveness(wsId);
    if (daemonStatusState.get(localId) === "online") {
      toast.success("Daemon is connected");
      client.forceReconnect({ userInitiated: true });
      return;
    }
  }
  // restart_desktop_daemon stops, respawns and POLLS for readiness (up to ~30s),
  // so tell the user something is happening rather than leaving the button inert.
  const tid = toast.loading("Restarting the local daemon…");
  try {
    // Resolves with the daemon status even on failure (a foreign process owning
    // the home, a startup crash) — `error` carries the reason — and REJECTS on a
    // spawn failure. Surface both instead of silently reporting success.
    const status = (await desktop.invoke("restart_desktop_daemon")) as {
      error?: string | null;
      status?: string | null;
    } | null;
    if (status?.error) throw new Error(status.error);
    // An EXHAUSTED readiness poll resolves `{status:"stopped", error:null}` — the
    // error check above waves it through and we would report a green "Daemon
    // restarted" for a daemon that never came up. Trust the phase, not just the
    // absence of an error.
    if (status?.status && status.status !== "running") {
      throw new Error(`daemon did not come up (${status.status})`);
    }
    toast.success("Daemon restarted", { id: tid });
  } catch (err) {
    reportUiFailure("app.reconnect_daemon", err, { workspaceId: wsId });
    toast.error(
      `Couldn't restart the daemon: ${err instanceof Error ? err.message : String(err)}`,
      { id: tid },
    );
  }
  client.forceReconnect({ userInitiated: true });
}

// Tracks the workspace selection currently in flight. selectWorkspace awaits
// several fetches; if the user switches workspace mid-await, a slower previous
// selection must not clobber the newer one. Every post-await assignment checks
// this token.
let activeWorkspaceSelection: string | null = null;
// Bumped on every selection, so A→B→A tells a read issued in the first A from the second.
let workspaceSelections = 0;

// A selection is its workspace AND its turn: another account (or a later visit) selecting
// the same id is not this one, so a read of this one never lands there.
function selectionIsCurrent(workspaceId: string, selection: number): boolean {
  return activeWorkspaceSelection === workspaceId && workspaceSelections === selection;
}

// ─── Actions (override core to use CyborgClient) ─────────────────

export async function connectToServer(
  url: string,
  token: string,
  // CYBORG-87: the durable refresh token returned alongside the access token by
  // login/register/refresh. Persisted so a later 4003 (token expiry / relay
  // JWT-secret rotation) self-heals silently instead of a silent session death.
  refreshToken?: string,
): Promise<void> {
  authState.token = token;
  persistDesktopCredential(url, token, refreshToken);
  if (refreshToken) saveRefreshToken(refreshToken);
  let authResp: Awaited<ReturnType<typeof client.authenticate>>;
  try {
    await client.connect(url, token);
    authResp = await client.authenticate();
    // Authenticated — replay anything queued while the socket was down.
    flushSendOutbox();
    void registerMobilePush(client);
    warmMediaPass();
  } catch (e) {
    // Classify the failure so callers can react correctly: a bad/expired token
    // (AuthError) means log out; an unreachable relay (NetworkError) means keep
    // the session and reconnect. The relay's bad-token response is the literal
    // string "unauthorized"; everything else (socket closed, timeout, refused)
    // is a transient network failure.
    const msg = e instanceof Error ? e.message : String(e);
    if (/unauthor/i.test(msg)) throw new AuthError(msg);
    throw new NetworkError(msg);
  }
  // Claim this device's localStorage for the human who just signed in, removing
  // the previous account's entries when it is a DIFFERENT human. FIRST thing
  // after auth, before anything hydrates from storage — the drafts, queued
  // prompts, board answers, terminal pointers and (worst) the actionable
  // approval card and superadmin token below all live in keys that logout never
  // touched. Keyed on the EMAIL for the same reason cacheInit is; unlike
  // cacheInit this is NOT behind cacheOn(), because the leak is on every
  // platform and plain web is where a shared device is most likely.
  claimLocalStorage(typeof localStorage === "undefined" ? null : localStorage, authResp.user.email);
  // #2301: this server may have re-keyed me onto a DIFFERENT user id (same
  // human, same server — the mint flag flipping on). Carry my per-user
  // localStorage namespaces across NOW, before anything binds to the new id, and
  // remember the old id so rehydrateOutbox can adopt its queued sends. Returns
  // null (no-op) on every login where the id is unchanged, where the old id
  // isn't provably mine, or where this is simply a different server.
  rekeyedFromUserId = migrateRekeyedIdentity(url, authResp.user);
  coldStartTrace("auth", { userId: authResp.user.id, rekeyedFrom: rekeyedFromUserId });
  authState.user = authResp.user;
  authState.bindUser(authResp.user.id);
  userStatusState.bindUser(authResp.user.id);
  // Per-user session aliases are global (not per-workspace) — hydrate the local
  // mirror from the DB once on connect. Fire-and-forget; failure keeps any local
  // aliases visible (display-only).
  void sessionAlias.load(client);
  // A session's shared name rides its roster row; a live rename by its owner updates that row.
  sharedSessionNames.follow(client, {
    get: () => workspaceState.agents,
    set: (rows) => {
      workspaceState.agents = rows;
    },
  });
  // Per-user terminal aliases are global too — hydrate from the DB and subscribe
  // to the live cross-device change feed. Fire-and-forget; failure keeps any
  // local (localStorage-fallback) aliases visible (display-only).
  void terminalAlias.load(client);
  notificationState.bindUser(authResp.user.id);
  unreadFlagState.bindUser(authResp.user.id);
  dmActivityState.bindUser(authResp.user.id);
  dmHiddenState.bindUser(authResp.user.id);
  favoritesState.bindUser(authResp.user.id);
  // Opened-DMs set drives the DM sidebar filter. Bind AFTER dmActivityState so
  // we can seed each not-yet-tracked workspace from its existing DM activity —
  // conversations that predate this feature stay in the list on first load.
  openedDmsState.bindUser(authResp.user.id);
  openedDmsState.seedFromActivity(dmActivityState.snapshot());
  activityState.bindUser(authResp.user.id);
  // Client-only notification pref (DND). Per-user localStorage so a shared
  // device stays separate.
  notifyClientPrefsState.bindUser(authResp.user.id);
  // Once per device: a DND set before the relay persisted it reaches the push gate.
  if (notifyClientPrefsState.takeRelaySync()) client.setDnd(true);
  // Open the local message cache for this user. Wipes the cache if a different
  // human previously owned this device + runs the LRU sweep. Keyed on the EMAIL,
  // not user.id: the cloud relay and a daemon-direct/self-hosted login mint
  // DIFFERENT ids for the same person, so an id key wiped the cache on every
  // switch between them (see cacheInit).
  // Fire-and-forget — must not block login.
  if (cacheOn()) void cache.cacheInit(authResp.user.email);
  // Rehydrate the persistent send outbox (#502) — NOT iOS-gated: an offline send
  // must survive a reload everywhere. Rebuild the optimistic bubbles + replay
  // queue from IndexedDB, then flush them on this freshly-authed socket. The
  // early flushSendOutbox above only covered in-memory entries; this covers ones
  // persisted across a reload. Fire-and-forget — must not block login.
  void rehydrateOutbox(authResp.user.id).then(() => flushSendOutbox());
  // Desktop only: bind the embedded daemon to this user + the relay it should
  // connect to (derived from the server we just authed against), so it registers
  // with the cloud relay and shows online. Fire-and-forget — it restarts the
  // local daemon and must not block login. No-op in the browser.
  try {
    const desktop = (
      window as unknown as {
        cyborg7Desktop?: {
          invoke: (command: string, args?: Record<string, unknown>) => Promise<unknown>;
        };
      }
    ).cyborg7Desktop;
    if (desktop) {
      // The daemon relay endpoint is /relay at the ORIGIN. `url` here is the
      // guest WS URL (e.g. ws://host:9100/api/ws), so derive from host only —
      // appending /relay to the path would yield the wrong /api/ws/relay.
      const u = new URL(url);
      const wsProto = u.protocol === "https:" || u.protocol === "wss:" ? "wss" : "ws";
      const relayUrl = `${wsProto}://${u.host}/relay`;
      // Pass the guest token so the daemon enrolls a relay-signed token
      // (CYBORG-58) instead of self-minting with a shared secret.
      rememberDesktopClaim({ ownerId: authResp.user.id, relayUrl, userToken: token });
      void claimThisComputer()
        .then((r) => {
          coldStartTrace("claim", { status: r.status, error: !!r.error });
          reportDesktopClaimError(r);
          return undefined;
        })
        // Fire-and-forget: the daemon's own status heartbeat is the source of truth.
        .catch((err: unknown) => {
          coldStartTrace("claim-fail", { err: errorSlug(err) });
          reportUiFailure("desktop.claim_daemon", err);
        });
      // The Remote/Local session badge's "which machine am I at" signal
      // (resolveViewerDaemonId, daemon-select.ts). The UI never opens a WS
      // directly to a daemon (always <host>/api/ws, served by the relay, NOT
      // the daemon's own /ws) — `cyborg:fetch_daemon_info` therefore always
      // resolves null here and can't answer this. The desktop bridge CAN:
      // `desktop_daemon_status` (daemon-manager.ts) reads the EMBEDDED
      // daemon's own lock file and returns its real serverId directly, no WS
      // round-trip needed.
      //
      // POLLED, not one-shot: main.ts starts the embedded daemon in PARALLEL
      // with createWindow(), so on a cold relaunch this can run before
      // paseo.pid even exists — a single call would see serverId:"" and,
      // without a retry, connectedDaemonId would stay null for the WHOLE
      // session (falling back to pickOwnDaemon's list-order guess — the exact
      // coin-flip this fix exists to remove). pollForConnectedDaemonId keeps
      // trying until the daemon reports in or the budget runs out.
      // Fire-and-forget, same as claim_desktop_daemon above; a miss just
      // leaves connectedDaemonId null.
      void pollForConnectedDaemonId(() => desktop.invoke("desktop_daemon_status"), {
        intervalMs: 1000,
        maxAttempts: 15,
        sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
      }).then((serverId) => {
        coldStartTrace("local-daemon", { serverId });
        if (serverId) {
          daemonState.setConnectedDaemonId(serverId);
          client.setLocalDaemonId(serverId);
          client.setAppState(document.visibilityState === "hidden" ? "background" : "active");
        }
        return undefined;
      });
    }
  } catch {
    // intentional: desktop-only daemon claim is fire-and-forget and must not
    // block login; a clean no-op in the browser (no bridge present).
  }
  // Memberships PLUS the host workspaces this account may enter as a Cyborg7
  // Connect external. This is the `?server=&token=` login path, and it used to
  // map the payload by hand — dropping every external, so an accepted external
  // was bounced out of the host workspace by /workspace/[id]/+layout.svelte.
  workspaceState.list = workspaceListFromAuth(authResp);
  workspaceState.listLoaded = true;
  // After the list: the resent active view must know which workspaces are external seats.
  syncAppStateToRelay();
  // Hear live broadcasts from ALL the user's workspaces + seed their unread, so
  // cross-workspace DMs/messages badge live and the switcher badges are filled.
  subscribeAndSeedAllWorkspaces();
  try {
    localStorage.setItem("cyborg7-session", JSON.stringify({ mode: "direct", url, token }));
  } catch {
    // intentional: best-effort session persistence; storage-disabled just means
    // the user re-auths next launch.
  }
  // Best-effort: re-register the web push subscription if the user already
  // granted permission. Dynamic import avoids a circular module dependency.
  // intentional: opportunistic push re-register; failure just means no push until the next launch (settings page re-enables it).
  void import("$lib/push/web-push.js").then((m) => m.ensureWebPushSubscription()).catch(() => {});
}

// Upload a profile avatar to S3 and persist its URL in PG (users.image_url) so
// every workspace member sees it. Replaces the old localStorage data-URL flow.
// Writes the ACCOUNT-level users.image_url. RETAINED ONLY — deliberately NO caller
// in the UI any more, the exact twin of updateProfileName below. The profile menu's
// cropper used to land here ("change my photo everywhere"), which is why a photo set
// in one workspace showed up in the others; it now writes the per-workspace override
// via updateWorkspaceAvatar. The account photo survives purely as the server's
// COALESCE fallback, for workspace-less surfaces (login, the workspace switcher,
// invites, superadmin), and as the signup seed. Kept because the relay RPC + handler
// + tests still exist; do NOT re-wire it into a workspace-scoped editor.
export async function updateProfileImage(file: File): Promise<void> {
  // CYBORG-897 phase 3 — an ACCOUNT photo has no workspace by design (it renders on
  // login, in the workspace switcher and on invites), so the scope carries none.
  const { publicUrl } = await client.uploadAsset(file, "avatars", {
    scope: { scopeKind: "avatar" },
  });
  await client.setProfileImage(publicUrl);
  authState.setProfileImage(publicUrl);
}

// Writes the ACCOUNT-level users.name. Deliberately NO caller in the UI any more:
// the settings screen only edits the per-workspace profile (the account name/photo
// survive purely as the server's COALESCE fallback and for workspace-less surfaces
// — login, the workspace switcher, invites, superadmin). Kept because the relay RPC
// + handler + tests still exist and a workspace-less surface may need it; do not
// re-wire it into a workspace-scoped editor.
export async function updateProfileName(name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) return;
  await client.setProfileName(trimmed);
  authState.setProfileName(trimmed);
}

// Optimistically patch my own membership row (Svelte 5's $state proxy is deeply
// reactive, so mutating keys in place surgically updates just those cells). No-op when
// the target workspace isn't the active one — the relay's members_updated broadcast
// reconciles that roster when it loads.
function patchMyMembership(workspaceId: string, patch: (m: WorkspaceMember) => void): void {
  const myId = authState.user?.id;
  if (!myId || workspaceState.current?.id !== workspaceId) return;
  const me = workspaceState.members.find((m) => m.userId === myId);
  if (me) patch(me);
}

// My job title in one workspace ("Head of Sales") — `cyborg:set_functional_role`. The
// server trims it and refuses > 80 characters; "" clears it. Patched into my own roster
// row once the relay accepts it (members_updated reconciles every other client).
export async function setMyFunctionalRole(workspaceId: string, role: string): Promise<void> {
  const trimmed = role.trim();
  await client.setFunctionalRole(workspaceId, trimmed);
  patchMyMembership(workspaceId, (me) => {
    me.functionalRole = trimmed || null;
  });
}

// My areas of work in one workspace (memberships.work_areas) — `cyborg:set_work_areas`,
// self-only like the role. Shown at once (the chips re-order on each click), sent one
// at a time per workspace so a quick second click can never land before the first; a
// refused write re-reads the roster so the chips fall back to what was saved.
const workAreasWrites = new Map<string, Promise<unknown>>();
export async function setMyWorkAreas(workspaceId: string, areas: string[]): Promise<void> {
  const next = [...new Set(areas)];
  patchMyMembership(workspaceId, (me) => {
    me.workAreas = next;
  });
  const write = () => client.setupRpc("cyborg:set_work_areas", { workspaceId, areas: next }, 5_000);
  const prev = workAreasWrites.get(workspaceId) ?? Promise.resolve();
  const mine = prev.then(write, write); // after the previous write, whatever it did
  workAreasWrites.set(workspaceId, mine);
  try {
    await mine;
  } catch (e) {
    await refreshMembers(workspaceId);
    throw e;
  } finally {
    if (workAreasWrites.get(workspaceId) === mine) workAreasWrites.delete(workspaceId);
  }
}

// ── Per-workspace profile (0121) ─────────────────────────────────────────────
// A different photo per workspace (the name is account-wide). NULL = "use my
// global photo", so these only ever write the OVERRIDE — users.image_url is never
// touched here (that's updateProfileImage).

// Upload a new photo for THIS workspace only. Reuses the SAME avatar upload flow
// as the global profile picture (POST /api/assets/presign folder "avatars" → S3
// PUT); only the persisted destination differs (memberships.avatar_url).
export async function updateWorkspaceAvatar(file: File, workspaceId?: string): Promise<void> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) return;
  // CYBORG-897 phase 3 — the per-workspace override DOES know its workspace.
  const { publicUrl } = await client.uploadAsset(file, "avatars", {
    scope: { workspaceId: wsId, scopeKind: "avatar" },
  });
  await client.setWorkspaceProfile(wsId, { avatarUrl: publicUrl });
  patchMyMembership(wsId, (me) => {
    me.workspaceAvatarUrl = publicUrl;
    me.imageUrl = publicUrl;
  });
  if (workspaceState.current?.id === wsId) {
    authState.setSelfWorkspaceProfile({ image: publicUrl });
  }
}

// Clear my per-workspace photo override → fall back to my global profile picture.
export async function clearWorkspaceAvatar(workspaceId?: string): Promise<void> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) return;
  await client.setWorkspaceProfile(wsId, { avatarUrl: null });
  patchMyMembership(wsId, (me) => {
    me.workspaceAvatarUrl = null;
    me.imageUrl = authState.user?.imageUrl ?? null;
  });
  if (workspaceState.current?.id === wsId) {
    authState.setSelfWorkspaceProfile({ image: null });
  }
}

// Cold-start race: on app launch the local embedded daemon may still be
// connecting/subscribing to the relay when selectWorkspace's first listAgents
// runs, so the daemon-fan-out returns no agents even though the workspace has
// some — they only appear after a manual reload. If the initial fetch came back
// empty, retry a few times on a short backoff; stop as soon as it's non-empty or
// the user navigates away. Complements the daemon_status offline→online self-heal
// (which misses the "online but not yet workspace-subscribed" window).
//
// SINGLE-FLIGHT per (kind, workspace) — the request-storm fix (task_3c5914ed).
// These loops are spawned from several independent call sites (selectWorkspace,
// pane/route $effects, SessionResumePicker, post-restore refetches, the daemon
// offline→online flip), and a hung daemon keeps every fetch empty — so the old
// unguarded fixed-1.5s loops OVERLAPPED and hammered the relay with hundreds of
// list forwards at 1-2s cadence for minutes (observed live 2026-08-10). A call
// site that finds a loop already in flight for the same workspace+kind JOINS it
// (returns immediately) instead of spawning another; mirrors retryCybosOnEmpty's
// exponential 1s→5s-cap backoff so even a single loop paces itself down.
// SvelteSet (not a plain Set) so a reactive reader of the in-flight loops would invalidate.
const emptyListRetryInFlight = new SvelteSet<string>();

// The ONE definition of a trustworthy roster answer: the daemon connection is up and the
// fan-out produced a resolved answer. relistAgents uses it.
function isRosterAnswerTrusted(noDaemonAnswered: boolean): boolean {
  return daemonConnStatus() === "connected" && !noDaemonAnswered;
}

async function retryAgentsOnEmpty(workspaceId: string): Promise<void> {
  if (isOutsiderSeat(workspaceState.list, workspaceId)) return; // no roster to wait for
  const selection = workspaceSelections;
  const flightKey = `agents:${workspaceId}:${selection}`;
  if (emptyListRetryInFlight.has(flightKey)) return; // join the in-flight loop
  emptyListRetryInFlight.add(flightKey);
  try {
    for (let attempt = 0; attempt < 8; attempt++) {
      const delay = Math.min(1000 * 1.5 ** attempt, 5000);
      await new Promise((resolve) => setTimeout(resolve, delay));
      if (!selectionIsCurrent(workspaceId, selection) || workspaceState.agents.length > 0) return;
      try {
        const { agents, noDaemonAnswered } = rereadLateDaemons(
          workspaceId,
          await listRoster(workspaceId),
        );
        if (selectionIsCurrent(workspaceId, selection) && agents.length > 0) {
          workspaceState.agents = agents;
          // Daemon answered with a real roster — refresh the cold-start cache.
          saveRosterCache(
            rosterStore(),
            workspaceId,
            authState.user?.email,
            agents,
            daemonConnStatus() === "connected",
          );
          attentionState.seedFromAgents(agents); // #591: seed derived attention
          return;
        }
        // The relay says the fan-out reached daemons but NONE answered its
        // fallback window (hung/dead, not cold-starting) — retrying can't help,
        // it only re-storms the hung daemon. Stop; the daemon_status
        // offline→online self-heal refetches when it actually recovers.
        if (noDaemonAnswered) return;
      } catch {
        // intentional: one attempt in a cold-start backoff retry loop; the loop
        // tries again and the daemon_status self-heal is the backstop.
      }
    }
  } finally {
    emptyListRetryInFlight.delete(flightKey);
  }
}

// Refetch the agent roster for `workspaceId` on demand — the same agents-only
// refetch reconcileWorkspacePanels does, extracted so a caller can pull a fresh
// roster WITHOUT dragging in the channels/members/cybos/sessions reconcile it doesn't
// need. Guards on the active selection like its siblings so
// a stale result can't clobber a newer workspace switch. Best-effort.
export async function refreshAgentRoster(workspaceId: string): Promise<void> {
  try {
    const res = rereadLateDaemons(workspaceId, await listRoster(workspaceId));
    // Stale-switch guard — identical to reconcileWorkspacePanels/refreshDaemonLiveness.
    if (workspaceState.current?.id !== workspaceId) return;
    const daemonConnected = daemonConnStatus() === "connected";
    // Same partial-answer-safe merge as reconcileWorkspacePanels: keep the last-known
    // roster (greyed rows) when the proxied list comes back empty while a daemon is
    // reachable, and carry over rows for daemons that were reached but stayed silent
    // (pendingDaemons) rather than letting a short list wipe them — see nextAgentSnapshot.
    workspaceState.agents = nextAgentSnapshot(
      workspaceState.agents,
      res.agents,
      daemonConnected,
      res.pendingDaemons,
    );
    saveRosterCache(
      rosterStore(),
      workspaceId,
      authState.user?.email,
      workspaceState.agents,
      daemonConnected,
    );
    attentionState.seedFromAgents(res.agents); // #591: seed derived attention
  } catch {
    // intentional: best-effort on-mount roster refresh; a miss leaves the last-known
    // roster and is recovered by the next poll / the daemon_status self-heal.
  }
}

// localStorage for the cold-start agent roster cache, or null on an SSR/prerender
// pass (no storage) — loadRosterCache/saveRosterCache treat null as a no-op.
function rosterStore(): RosterStore {
  return typeof localStorage === "undefined" ? null : localStorage;
}

// The last workspace the user actually entered, so the next launch reopens straight
// into it instead of the workspace picker (see routes/workspace/+page.svelte). Written
// by selectWorkspace; null when never set / storage unavailable.
export function getLastWorkspace(): string | null {
  if (typeof localStorage === "undefined") return null; // SSR/prerender: no storage yet.
  try {
    return localStorage.getItem("cyborg7:lastWorkspace");
  } catch {
    // intentional: storage-disabled just means the picker shows next launch.
    return null;
  }
}

// Fetches the workspace roster for the Members settings tab (also used by
// selectWorkspace's post-paint hydration below). Fail CLOSED: a rejection sets
// membersLoadError so the tab can tell a relay hiccup apart from a genuinely
// empty workspace — mirrors fetchCybos/cyboState.loadError further down this
// file. Exported so the Members tab's Retry button can re-fire the exact same
// fetch.
export async function loadMembers(
  workspaceId: string,
  read?: ReturnType<typeof client.listMembers>,
): Promise<void> {
  const selection = workspaceSelections;
  workspaceState.membersLoading = true;
  workspaceState.membersLoadError = false;
  try {
    const res = await (read ?? client.listMembers(workspaceId));
    if (!selectionIsCurrent(workspaceId, selection)) return;
    workspaceState.members = res.members;
    workspaceState.external = res.external;
    authState.setMemberImagesFromMembers([...res.members, ...res.external]);
  } catch (err) {
    reportUiFailure("app.loadMembers", err, { workspaceId });
    if (selectionIsCurrent(workspaceId, selection)) {
      workspaceState.members = [];
      workspaceState.membersLoadError = true;
    }
  } finally {
    if (selectionIsCurrent(workspaceId, selection)) workspaceState.membersLoading = false;
  }
}

/** Re-read the roster in place: never the loading state, and a failed ask keeps what is shown. */
export async function refreshMembers(workspaceId: string): Promise<void> {
  const ticket = membersReadOrder.ask();
  try {
    const res = await client.listMembers(workspaceId);
    if (workspaceState.current?.id !== workspaceId) return;
    if (!membersReadOrder.mayApply(ticket)) return;
    workspaceState.members = res.members;
    workspaceState.external = res.external;
    workspaceState.membersLoadError = false;
    authState.setMemberImagesFromMembers([...res.members, ...res.external]);
  } catch (err) {
    reportUiFailure("app.refreshMembers", err, { workspaceId });
  }
}

// An external seat has no member panels to load: settle their loading states, keep its prefs.
function settleOutsiderPanels(prefs: NotifPrefsRead): void {
  workspaceState.tasksLoading = false;
  workspaceState.membersLoading = false;
  workspaceState.membersLoadError = false;
  workSessionsState.loading = false;
  workSessionsState.error = false;
  notifPrefsState.merge(prefs.prefs);
}

// Reads of a workspace open that need nothing from the channel list: sent with the gate, applied
// by the caller behind `selectionIsCurrent`. `committed` settles once the workspace is current.
function startOpenReads(wsId: string, legacy: boolean, committed: Promise<void>) {
  const send = <T>(start: () => Promise<T>): Promise<T> => {
    const read = (async () => start())(); // async: a throw while sending is a rejection, not a stranded switch
    // intentional: marks it handled while the gate is pending; the apply site awaits it and reports there.
    read.catch(() => {});
    return read;
  };
  return {
    license: send(() => startLicenseRead(wsId)),
    tasks: send(() => taskBoard.load(wsId, committed)),
    activity: send(() =>
      client.fetchWorkspaceActivity(wsId, 200, true).catch(() => ({ messages: [] })),
    ),
    workspaces: send(() => client.fetchWorkspaces().catch((): Workspace[] => [])),
    daemons: send(() =>
      Promise.all([
        client.listDaemons(wsId),
        client.fetchDaemonAccess(wsId).catch((err: unknown) => {
          // Unknown, not empty: an empty list would hide every card from a real drive holder.
          reportUiFailure("machines.access_load", err, { workspaceId: wsId });
          return { access: undefined };
        }),
        client.fetchDaemonInfo().catch(() => ({ daemonId: null, ownerId: null })),
      ]),
    ),
    daemonAccessRequests: send(() => client.fetchDaemonAccessRequests(wsId)),
    cyboTransferRequests: send(() => client.fetchCyboTransferRequests(wsId)),
    pageAccessRequests: send(() => client.fetchPageAccessRequests(wsId)),
    threadCounts: send(() => client.threadCounts(wsId)),
    saved: send(() => client.listSaved(wsId)),
    statuses: send(() => client.fetchUserStatuses(wsId)),
    members: legacy ? send(() => client.listMembers(wsId)) : undefined,
    workSessions: legacy ? send(() => client.listWorkSessions(wsId)) : undefined,
  };
}

// This legacy bootstrap coordinates the entire workspace handoff. Splitting it
// safely is separate work; cache-boundary additions must not force a behavioral
// refactor here.
// oxlint-disable-next-line complexity
export async function selectWorkspace(workspace: Workspace): Promise<void> {
  activeWorkspaceSelection = workspace.id;
  coldStartTrace("workspace-select", { ws: workspace.id, current: workspaceState.current?.id });
  void armMessageChanges(workspace.id);
  workspaceSelections += 1;
  const selection = workspaceSelections;
  // Loading indicator for the rail switch (cleared when the target's core data is
  // in, or on failure below). The shell renders a clear "switching" view instead
  // of a blank half-loaded one while this is true.
  workspaceState.switching = true;
  workspaceState.switchError = null;
  // The target's task board hasn't loaded yet — flag it so the work-items view
  // shows a load skeleton instead of the previous board or the "no tasks" empty
  // state until the deferred task fetch (post-paint hydration below) resolves.
  workspaceState.tasksLoading = true;
  // Clear any prior task-load error so a fresh switch starts clean (the board's
  // error branch only shows while this is non-null).
  workspaceState.tasksError = null;
  // Drop the previous workspace's tasks now that the task fetch is deferred to the
  // post-paint hydration below (CYBORG-778) — without this the new workspace would
  // briefly read the old board's rows (the cybo/member profile "tasks" stats + the
  // board's skeleton guard both read workspaceState.tasks) until the deferred fetch
  // lands.
  workspaceState.tasks = [];
  taskBoard.reset();
  // The target's agent roster hasn't answered yet — flag it so a surface can tell
  // a daemon that is still connecting from a workspace with no agents. Cleared
  // when the deferred roster fetch below SETTLES, either way.
  workspaceState.agentsLoading = true;
  channelState.clear();
  channelState.activeId = null;
  // Preserve workspace-owned streams across navigation. Clearing the global map
  // here erased optimistic prompts and their monotonic high-water while the
  // owning daemon was still running.
  agentStreamState.activateWorkspace(workspace.id);
  heldDmReload.start(); // a DM held when the tab closed: hidden until its agent's queue frame (bounded)
  void rehydrateAgentPrompts(workspace.id);
  // Provider catalogs reveal which runtimes are installed on a host. Their
  // daemon-keyed cache is only valid inside the workspace that authorized the
  // probe, so never carry it across a workspace boundary.
  providerState.clear();
  rowWork.clear();
  // Same boundary for what registered itself as workspace-scoped. A re-entry for the
  // workspace already open (a reconnect) keeps it: an install may still be running there.
  if (workspaceState.current?.id !== workspace.id) workspaceSync.reset();
  providerSubagentStore.activateWorkspace(workspace.id);
  attentionState.clearAll(); // #591: drop the previous workspace's attention badges
  // Drop the PREVIOUS workspace's cybos at once: a stale row invites a spawn the relay refuses.
  cyboState.list = [];
  startCyboWait(workspace.id);
  rosterRowsFromServer = false;
  cyboState.loading = true;
  // Reset any error flagged for the PREVIOUS workspace — it must not bleed
  // into this one's load (see the cybos fetch's own catch below + the
  // retryCybosOnEmpty backoff, which is the sole authority for re-setting it
  // for this workspace once its own attempts are exhausted).
  cyboState.loadError = false;

  // Ensure this socket receives the workspace's live broadcasts — covers
  // workspaces joined mid-session (e.g. via an invite) that weren't part of the
  // socket's original auth-time subscription. (cyborg7)
  const subscribed = client.subscribeWorkspace(workspace.id).catch((err: unknown) => {
    reportUiFailure("selectWorkspace.subscribe", err, { workspaceId: workspace.id });
  });
  // Joined after this socket authenticated: the relay's floor refuses every read below
  // ("not a member") until the subscribe lands, so this one switch waits for it.
  if (!client.holdsWorkspace(workspace.id)) await subscribed;

  // Only channels lacks a fallback; unread and drafts must land before the channel opens.
  let loadFailed = false;
  // Captured BEFORE the unread fetch below goes out — see reconcileWorkspaceUnread.
  const unreadSeededAt = Date.now();
  // CYBORG-989: one RPC covers channels + unread + drafts (+ prefs, merged below)
  // when the relay serves it. Fired FIRST so the three legacy calls it replaces are
  // never sent; on a null answer (older relay / flag off / external seat) they run
  // exactly as before. `boot` is also what carries the roster into
  // reconcileWorkspacePanels' place — see the members seed further down.
  // Belt to bootstrapWorkspace's braces. This is the FIRST await of the switch and
  // the only one outside the guarded Promise.all below, so a throw here escapes
  // selectWorkspace with `switching` still true — the permanent-spinner bug
  // workspace-switch-spinner.test.ts exists for. A workspace that cannot bootstrap
  // must fall back to the per-RPC path, never strand the shell.
  // An external seat reads only its seated channels and prefs: no bootstrap, unread, drafts or rosters.
  const outsider = isOutsiderSeat(workspaceState.list, workspace.id);
  const heldAgents = workspaceState.current?.id === workspace.id ? workspaceState.agents : loadRosterCache(rosterStore(), workspace.id, authState.user?.email); // prettier-ignore
  const agentsMirror = outsider || heldAgents.length > 0 ? undefined : mirrorFirst(() => client.listAgentsMirror(workspace.id), (err) => reportUiFailure("select_workspace.agent_mirror", err, { workspaceId: workspace.id })); // prettier-ignore
  const cybosMirror = outsider ? undefined : mirrorFirst(() => client.fetchCybosMirror(workspace.id), (err) => reportUiFailure("select_workspace.cybo_mirror", err, { workspaceId: workspace.id })); // prettier-ignore
  let commit = (): void => {};
  const committed = new Promise<void>((resolve) => {
    commit = resolve;
  });
  const legacyGate = () => ({
    channels: client.fetchChannels(workspace.id).catch(() => {
      loadFailed = true;
      return [];
    }),
    unread: outsider
      ? Promise.resolve(null)
      : client.fetchUnread(workspace.id).catch((err) => {
          reportUiFailure("selectWorkspace.fetchUnread", err, { workspaceId: workspace.id });
          return null;
        }),
    drafts: outsider
      ? Promise.resolve({ drafts: [], draftTombstones: undefined })
      : client.fetchDrafts(workspace.id).catch((err) => {
          reportUiFailure("selectWorkspace.fetchDrafts", err, { workspaceId: workspace.id });
          return { drafts: [], draftTombstones: [] };
        }),
    // intentional: best-effort pref seed; a miss leaves prefs as loaded and the next switch re-merges them.
    prefs: client.fetchNotificationPrefs(workspace.id).catch((): NotifPrefsRead => ({ prefs: {} })),
  });
  let reads: ReturnType<typeof startOpenReads> | undefined;
  const startReads = () =>
    outsider
      ? undefined // no member-only reads for a seat the relay refuses them for
      : (reads ??= startOpenReads(workspace.id, !client.bootstrapSupported, committed));
  // The gate request goes out first, the independent reads right behind it.
  const legacy = outsider || !client.bootstrapSupported ? legacyGate() : undefined;
  if (legacy) startReads();
  const boot = outsider
    ? null // null: the same "no bootstrap" answer bootstrapWorkspace gives
    : await bootstrapWorkspace(workspace.id, startReads).catch((err) => {
        reportUiFailure("select_workspace.bootstrap", err, { workspaceId: workspace.id });
        return null;
      });
  const gate = boot
    ? {
        channels: Promise.resolve(boot.channels),
        unread: Promise.resolve(boot.unread ?? null),
        drafts: Promise.resolve({
          drafts: boot.drafts ?? [],
          draftTombstones: boot.draftTombstones,
        }),
        prefs: Promise.resolve<NotifPrefsRead>({ prefs: {} }),
      }
    : (legacy ?? legacyGate());
  const early = startReads();
  // Daemon-forwarded (cold daemon: 7.8-27 s measured): both resolve post-paint, never in the gate.
  const agentsPromise = outsider
    ? Promise.resolve({ ok: false as const })
    : listRoster(workspace.id)
        .then((res) => ({ ok: true as const, res: rereadLateDaemons(workspace.id, res) }))
        .catch((err: unknown) => {
          reportUiFailure("select_workspace.agent_roster_fetch", err, {
            workspaceId: workspace.id,
          });
          return { ok: false as const };
        });
  const cybosPromise = outsider
    ? Promise.resolve<typeof cyboState.list>([])
    : client.fetchCybos(workspace.id).catch((err: unknown) => {
        reportUiFailure("select_workspace.cybo_roster_fetch", err, { workspaceId: workspace.id });
        return undefined;
      });
  const [channels, unread, serverDrafts, legacyPrefs] = await Promise.all([
    gate.channels,
    gate.unread,
    gate.drafts,
    gate.prefs,
  ]);
  // `committed` must settle on every exit, a throw included, or the task board hangs.
  try {
    // The bootstrap's prefs merge here (the legacy ones post-paint). A throw here strands the spinner.
    if (boot?.prefs) notifPrefsState.merge(boot.prefs);
    if (boot?.archivedChannelIds) archivedChannelsState.seed(boot.archivedChannelIds, workspace.id);
    if (legacyPrefs?.archivedChannelIds)
      archivedChannelsState.seed(legacyPrefs.archivedChannelIds, workspace.id);

    // Stale selection: leave `switching` to the newer selectWorkspace, which owns it.
    if (!selectionIsCurrent(workspace.id, selection)) return;

    if (loadFailed) {
      workspaceState.switchError =
        "Couldn't load that workspace. Check your connection and try again.";
      workspaceState.switching = false;
      // Failed switch: nothing else clears the task skeleton or the roster placeholder.
      workspaceState.tasksLoading = false;
      cyboState.loading = false;
      workspaceState.agentsLoading = false;
      dmHealPump.pump(workspace.id);
      drainBootOnlineDaemons();
      return;
    }

    workspaceState.channels = channels;
    // Cold paint: this workspace's cached roster now; the live answer merges over it post-paint.
    // The same workspace keeps its in-memory roster.
    if (workspaceState.current?.id !== workspace.id) {
      workspaceState.agents = loadRosterCache(rosterStore(), workspace.id, authState.user?.email);
    }
    // A different workspace: drop the previous members so none is read under the new `current`.
    if (workspaceState.current?.id !== workspace.id) {
      workspaceState.members = [];
      workspaceState.external = [];
    }
    workspaceState.current = workspace;
  } finally {
    commit();
  }
  coldStartTrace("workspace-committed", { ws: workspace.id, rows: workspaceState.agents.length });
  // A retained stream may have missed frames while this workspace was inactive
  // and the socket was disconnected. Reconcile it on re-entry: the ordinary
  // route loader intentionally skips an already server-hydrated stream, so
  // without this pull that replica could remain stale indefinitely. The helper
  // scopes reads through workspace ownership before issuing any RPC, preventing
  // retained streams from another workspace being queried under this id.
  void catchUpAgentTimelines(workspace.id);
  // Remember this as the last-entered workspace so the next launch reopens straight into
  // it (past the stale-selection bail + loadFailed guard above, so only genuine entries
  // are recorded). Best-effort.
  try {
    localStorage.setItem("cyborg7:lastWorkspace", workspace.id);
  } catch {
    // intentional: storage-disabled just means the picker shows next launch.
  }
  // Essential shell state (channels/agents/cybos/current) is committed — unblock the
  // shell NOW, BEFORE the non-essential seeders below. The render gate keys off
  // `switching`, and `current` is already this workspace, so if a seeder below threw
  // while this was still true the switch would hang forever on the "Switching…"
  // spinner with no error and no recovery (the permanent-spinner bug).
  workspaceState.switching = false;
  drainBootOnlineDaemons();

  // Non-essential post-load seeding that reads ONLY the awaited data
  // (channels/agents/cybos/unread/serverDrafts), extracted so a throw in ANY of it is
  // contained (the try/catch below) without stranding the switch — the shell already
  // renders. The fetches that used to feed this block but aren't first-paint-critical
  // (activity, notif prefs) now land in the post-paint hydration below, so their
  // seeders moved there too.
  const isCurrent = (): boolean => selectionIsCurrent(workspace.id, selection);
  const seedNonEssential = (): void => {
    // Server-authoritative license (trial bar + HARD-PAUSE gate). Best-effort —
    // an older relay without the billing routes just leaves the bar hidden.
    if (!outsider) void refreshLicense(workspace.id, early?.license, isCurrent);
    // Server-truth unread signals (survive reload + cross-device). BUG #2: the RED
    // badge seeds from channel MENTION counts + DM message counts; the BOLD flag
    // seeds from channels with any unread message. Item 12: DM badges (keyed by
    // peerId) merge into the SAME per-workspace map as channel mention counts; the
    // frozen-divider cursors seed into readCursorState separately. Seeded here
    // SYNCHRONOUSLY (from the awaited `unread`) so the initial channel restore below
    // opens ON the divider — a late post-paint seed raced the channel open and
    // dropped the divider (CYBORG-767 e2e).
    // Skipped wholesale on a null (failed) answer — see the fetch above.
    if (unread) {
      seedUnreadSignals(workspace.id, unread, unreadSeededAt);
      readCursorState.seed({ reads: unread.reads, dmReads: unread.dmReads });
    }
    // Seed the threads aggregate badge; the list loads lazily when the Threads
    // view is opened. (clear first so stale counts from the previous workspace
    // don't linger.)
    threadsState.clear();
    threadFeed.reset();
    if (!outsider) void seedThreadCounts(workspace.id, early?.threadCounts, isCurrent);
    // Saved messages are per-workspace; drop the previous workspace's list, then
    // seed it so the per-message bookmark glyph is correct everywhere (not just
    // after opening the Saved view). One cheap query, like seedThreadCounts (#609).
    savedState.clear();
    if (!outsider) void loadSavedMessages(workspace.id, early?.saved, isCurrent);

    // P2 Item 6: hydrate co-members' custom statuses so their emoji shows where
    // presence shows. Clear first so a previous workspace's statuses don't linger;
    // live changes arrive via the user_status_changed broadcast.
    workspaceUserStatusesState.clear();
    if (!outsider) void seedUserStatuses(workspace.id, early?.statuses, isCurrent);
    // Server-side draft sync (#610): seed this device's composer drafts from the
    // server and reconcile against the localStorage cache (newest updatedAt wins).
    // Runs SYNCHRONOUSLY here (from the awaited `serverDrafts`) so the store is
    // populated BEFORE the initial channel opens — the composer's one-shot untracked
    // hydration reads it on open and never re-runs on a later seed (a post-paint seed
    // showed a blank composer + let the debounced push overwrite the cross-device
    // draft). Runs after workspaceState.current is set (drafts key their localStorage
    // by the active workspace id) and LAST so a known-possible throw here can't skip
    // the seeders above.
    draftsState.seedFromServer(serverDrafts.drafts, serverDrafts.draftTombstones);
  };
  try {
    seedNonEssential();
  } catch (err) {
    // A non-essential seeder threw. The shell already rendered (switching cleared
    // above), so log and keep going — never let this strand the switch.
    reportUiFailure("select_workspace.seed_non_essential", err, { workspaceId: workspace.id });
  }

  agentsMirror?.whenReady((rows) => {
    if (!selectionIsCurrent(workspace.id, selection) || workspaceState.agents.length > 0) return;
    for (const agent of rows) agentStreamState.bindAgentWorkspace(agent.agentId, workspace.id);
    workspaceState.agents = rows;
    rosterRowsFromServer = true;
  });
  cybosMirror?.whenReady((rows) => {
    if (!selectionIsCurrent(workspace.id, selection) || cyboState.list.length > 0) return;
    cyboState.list = rows;
    noteCyboAnswer(rows);
  });
  // The agent roster, landing after the paint. Fired before the gate above; this is
  // every read of it that used to run inline, unchanged apart from where it sits.
  agentsPromise
    .then((read) => {
      if (!selectionIsCurrent(workspace.id, selection)) return;
      agentsMirror?.supersede();
      if (!read.ok) {
        if (workspaceState.agents.length === 0) void retryAgentsOnEmpty(workspace.id);
        return;
      }
      const agentsRes = read.res;
      const agents = agentsRes.agents;
      for (const agent of agents) agentStreamState.bindAgentWorkspace(agent.agentId, workspace.id);
      // The daemon may still have been connecting, so this proxied list can be empty:
      // keep the roster painted above (greyed rows) rather than blanking it. A SHORT
      // (partial) live list is handled too — `pendingDaemons` names the daemons the
      // fan-out reached that stayed silent, and their rows are carried over instead of
      // wiped. See nextAgentSnapshot.
      const daemonConnected = daemonConnStatus() === "connected";
      const snapshot = nextAgentSnapshot(
        workspaceState.agents,
        agents,
        daemonConnected,
        agentsRes.pendingDaemons,
      );
      if (snapshot !== workspaceState.agents) rosterRowsFromServer = true;
      workspaceState.agents = snapshot;
      coldStartTrace("roster-applied", { ws: workspace.id, rows: workspaceState.agents.length });
      saveRosterCache(
        rosterStore(),
        workspace.id,
        authState.user?.email,
        workspaceState.agents,
        daemonConnected,
      );
      // #591: seed derived finished/error attention badges from the initial roster
      // snapshot (so an agent that finished before this load already shows a badge).
      attentionState.seedFromAgents(agents);
      // Still empty — the local daemon was likely connecting; retry briefly so it
      // self-populates without a manual reload.
      if (workspaceState.agents.length === 0) void retryAgentsOnEmpty(workspace.id);
      return undefined;
    })
    .catch((err: unknown) => {
      reportUiFailure("select_workspace.agent_roster", err, { workspaceId: workspace.id });
    })
    .finally(() => {
      // Settles either way, so a surface can stop showing a pending roster even when
      // the fan-out found no live daemon.
      if (selectionIsCurrent(workspace.id, selection)) workspaceState.agentsLoading = false;
      dmHealPump.pump(workspace.id);
    });

  // The cybo roster, landing after the paint. cyboState.loading (set in the prologue)
  // is what keeps this window distinguishable from a workspace with no cybos.
  cybosPromise
    .then((answer) => {
      if (!selectionIsCurrent(workspace.id, selection)) return undefined;
      cybosMirror?.supersede();
      const cybos = answer ?? cyboState.list;
      cyboState.list = cybos;
      if (answer) {
        noteCyboAnswer(answer);
      }
      // Runs AFTER `current = workspace` above, so it wipes any stale true a PRIOR
      // workspace's retryCybosOnEmpty backoff set in the race window between the
      // pre-await reset and this line (that backoff guards on
      // `workspaceState.current?.id`, which stayed the OLD workspace for the whole
      // core load) — and that same guard now makes the prior retry a no-op going
      // forward. If THIS workspace's cybos fetch genuinely failed, the
      // retryCybosOnEmpty(workspace.id) call below re-flags it after its own backoff.
      cyboState.loadError = false;
      cyboState.loading = false;
      // Cold-start race: cybos are DAEMON_FORWARD, so the initial fetch can return []
      // before the embedded daemon connects, leaving cybo avatars/names blank until
      // reload. Retry until the daemon answers.
      if (cybos.length === 0 || !answer) void retryCybosOnEmpty(workspace.id);
      return undefined;
    })
    .catch((err: unknown) => {
      if (selectionIsCurrent(workspace.id, selection)) cyboState.loading = false;
      reportUiFailure("select_workspace.cybo_roster", err, { workspaceId: workspace.id });
    })
    .finally(() => dmHealPump.pump(workspace.id));

  if (!early) {
    settleOutsiderPanels(legacyPrefs);
  } else {
    // Tasks: workspaceState.tasks feeds the Tasks board + the cybo/member profile
    // "tasks" stats. Not needed to paint the landing, so it hydrates here; the Tasks
    // tab ALSO re-pulls on open (reloadTasks) and the live cyborg:tasks_changed
    // broadcast keeps an open board current. tasksLoading/tasksError drive the board's
    // skeleton/error states exactly as the old awaited path did (the .catch surfaces a
    // real failure so the board shows Try again, not a false "empty project" state).
    early.tasks
      .then(() => {
        if (!selectionIsCurrent(workspace.id, selection)) return undefined;
        workspaceState.tasksError = null;
        return undefined;
      })
      .catch((err: unknown) => {
        if (!selectionIsCurrent(workspace.id, selection)) return;
        workspaceState.tasksError =
          err instanceof Error ? err.message : "Couldn't load work items.";
        reportUiFailure("select_workspace.load_tasks", err, { workspaceId: workspace.id });
      })
      .finally(() => {
        if (selectionIsCurrent(workspace.id, selection)) workspaceState.tasksLoading = false;
      });

    // Activity preview + notification prefs + the settings-carrying workspace list.
    // Batched in ONE post-paint Promise.all. Unread and drafts are NOT here — they stay
    // awaited above because the first channel's divider (unread) and the composer's
    // one-shot hydration (drafts) both read their state before the channel opens.
    Promise.all([
      // light: the consumer (seedActivityFromServer's older-relay backfill) renders a
      // compact preview only, so drain the trimmed rows
      // (no attachments/reactions/unfurls/card) — the largest single workspace-open payload.
      early.activity,
      // CYBORG-989: bootstrap rows were merged above; the legacy rows were fetched in the
      // awaited gate (for the archived ids) and are only merged here, post-paint.
      Promise.resolve(legacyPrefs),
      // The auth-time workspace list hydrates settings as {} (the auth payload doesn't
      // carry them), so flags like settings.trialDismissed were lost on reload.
      // fetch_workspaces returns the real settings jsonb — merge it in.
      early.workspaces,
    ])
      .then(([activity, notifPrefs, allWorkspaces]) => {
        if (!selectionIsCurrent(workspace.id, selection)) return undefined;
        try {
          const fresh = allWorkspaces.find((w) => w.id === workspace.id);
          if (fresh?.settings) {
            workspace.settings = fresh.settings;
            const inList = workspaceState.list.find((w) => w.id === workspace.id);
            if (inList) inList.settings = fresh.settings;
          }
          // CYBORG-954: adopt a role the server has since resolved authoritatively (the auth
          // frame can carry null for a presence row). NON-NULL only — writing a null back would
          // discard the role the auth frame already established, which is the bug itself.
          if (fresh?.role != null && fresh.role !== workspace.role) {
            workspace.role = fresh.role;
            const inList = workspaceState.list.find((w) => w.id === workspace.id);
            if (inList) inList.role = fresh.role;
          }
          // MERGE (not seed/replace): switching to this workspace must NOT discard the
          // other workspaces' prefs that subscribeAndSeedAllWorkspaces loaded —
          // otherwise a cross-workspace muted/mentions-only channel would banner a plain
          // message again the moment you switch away from it. Scope ids are
          // globally-unique, so overlaying this workspace's fresh prefs keeps every
          // workspace's prefs correct.
          notifPrefsState.merge(notifPrefs.prefs);

          // P2 Item 9 (server-authoritative badge): seed the Activity feed AND the rail
          // unread baseline from the relay's cyborg:fetch_activity — the real
          // activity_events (channel mentions + thread replies), with a server-counted
          // `unread` total. This replaces the old ~200-message reconstruction. The live
          // path (incoming mention → addMention, activity_read_changed → markRead)
          // dedupes against the seeded items by source id, so it never double-counts.
          // Falls back to the legacy message backfill only if the server feed is
          // unavailable (older relay).
          const myId = authState.user?.id;
          void seedActivityFromServer(workspace.id, channels, activity.messages, myId);
        } catch (err) {
          reportUiFailure("select_workspace.post_paint_hydration", err, {
            workspaceId: workspace.id,
          });
        }
        return undefined;
      })
      // intentional: best-effort post-paint hydration; a miss leaves the activity feed/settings to the live push + next sync.
      .catch(() => {});

    // Members load AFTER core data (switching already cleared), so the sidebar is
    // visible with an empty DM roster during this gap — show skeletons (item 4).
    // CYBORG-989: the bootstrap answer already carried the roster (the same
    // getMembers + getExternalGuestParticipants pair list_members runs), so skip the
    // RPC entirely when we have it. This is the sixth call the one round trip covers.
    if (boot) {
      workspaceState.membersLoading = false;
      workspaceState.membersLoadError = false;
      workspaceState.members = boot.members;
      authState.setMemberImagesFromMembers([...boot.members, ...boot.external]);
    } else {
      void loadMembers(workspace.id, early.members);
    }

    // Cybo work sessions (CYBORG-791/794): the sidebar "Work Sessions" index. Cheap relay/PG
    // query (not daemon-forwarded), hydrated post-paint like members — the live
    // work_session_changed broadcast (applyWorkSession) keeps it current afterward.
    // hydrateWorkSessions REPLACES the store, matching its "fresh list on workspace
    // switch" contract; the byId map is workspace-tagged, so the sidebar reads only the
    // current workspace's rows either way. Workspace-guarded so a stale result for a
    // workspace the user already left is dropped.
    workSessionsState.loading = true;
    workSessionsState.error = false;
    const workSessionsSnapshotVersion = beginWorkSessionsSnapshot();
    // CYBORG-989: the bootstrap answer already carried this list, narrowed by the SAME
    // visibleWorkSessions rule the RPC applies (the relay runs it against the same
    // private-channel roster), so skip the RPC when we have one. This is the sixth and
    // last of the calls the single round trip covers — carrying the rows and then
    // asking for them again would be worse than not carrying them.
    if (boot) {
      hydrateWorkSessions(workspace.id, boot.workSessions, workSessionsSnapshotVersion);
      workSessionsState.error = false;
      workSessionsState.loading = false;
    } else
      (early.workSessions ?? client.listWorkSessions(workspace.id))
        .then((sessions) => {
          if (!selectionIsCurrent(workspace.id, selection)) return;
          hydrateWorkSessions(workspace.id, sessions, workSessionsSnapshotVersion);
          workSessionsState.error = false;
          return undefined;
        })
        .catch((err: unknown) => {
          if (!selectionIsCurrent(workspace.id, selection)) return;
          workSessionsState.error = true;
          reportUiFailure("select_workspace.work_sessions", err, { workspaceId: workspace.id });
        })
        .finally(() => {
          if (selectionIsCurrent(workspace.id, selection)) workSessionsState.loading = false;
        });

    // Load the full daemon list + per-user access grants into the global daemon
    // state (the sidebar + detail view read from here). Access fetch is best-effort
    // so a daemon list still renders if it fails.
    //
    // Also fetch WHICH daemon this client is actually connected to
    // (fetchDaemonInfo → the connected daemon's own serverId). This is a
    // FALLBACK source for the Remote/Local session badge's "which machine am I
    // at" signal (resolveViewerDaemonId, daemon-select.ts) — the PRIMARY source
    // is the desktop bridge's `desktop_daemon_status` (connectToServer, fired
    // once at login), because this UI only ever opens a WS to the relay's
    // /api/ws, never a daemon's own /ws — so fetchDaemonInfo resolves null in
    // every configuration this UI reaches TODAY, and would only ever return
    // something for a hypothetical direct-daemon client. Only ever SET
    // connectedDaemonId here, never clear it: a null result here must not
    // clobber a real serverId the desktop bridge already set.
    early.daemons
      .then(
        ([
          {
            daemons,
            defaultSlashDaemonId,
            slashCommandModel,
            workspaceSlashConfig,
            pendingWorkerInvites,
            workerBillingLive,
          },
          { access },
          { daemonId: connectedDaemonId },
        ]) => {
          if (!selectionIsCurrent(workspace.id, selection)) return;
          daemonStatusState.load(daemons);
          daemonState.setWorkerBillingLive(workerBillingLive);
          daemonState.load(daemons, access);
          // Worker tier: seed the invite rows that rode the same response (absent
          // on an older relay → empty). Live updates via worker_invite_changed.
          workerInvitesState.load((pendingWorkerInvites ?? []).map(normalizeWorkerInvite));
          // The authoritative daemon list just landed — evict any cold-start
          // roster row homed on a daemon that no longer exists (CYBORG-680 layer
          // 4: a deleted daemon lived on in localStorage and kept being queried
          // for providers until the key was cleared by hand). The write below
          // passes `true` deliberately: this list is relay/DB-served, so the
          // pruned roster IS authoritative regardless of daemon connectivity —
          // without it, `shouldPersistRoster` would skip an emptied write offline
          // and leave the ghost row on disk, which is the whole bug.
          // Cached rows only: this list is viewer-narrowed, and the relay already judged an answer.
          const prunedAgents = rosterRowsFromServer
            ? workspaceState.agents
            : pruneRosterForDaemons(
                workspaceState.agents,
                daemons.map((d) => d.id),
              );
          if (prunedAgents.length !== workspaceState.agents.length) {
            workspaceState.agents = prunedAgents;
            saveRosterCache(rosterStore(), workspace.id, authState.user?.email, prunedAgents, true);
          }
          daemonState.setDefaultSlashDaemon(defaultSlashDaemonId ?? null);
          // The workspace slash config is the admin-controlled SOURCE OF TRUTH (#opt-A);
          // the per-user `slashCommandModel` is deprecated/back-compat and can hold a
          // stale value (e.g. a legacy provider id), which made the run-target hint show
          // the wrong provider — "OpenCode" for a pi/opencode-go/glm-5.1 selection.
          // Prefer the workspace model (server-parsed provider/model); fall back to the
          // per-user field only when the workspace hasn't pinned one.
          daemonState.setSlashCommandModel(
            workspaceSlashConfig?.model ?? slashCommandModel ?? null,
          );
          if (connectedDaemonId) daemonState.setConnectedDaemonId(connectedDaemonId);
          return undefined;
        },
      )
      // intentional: background daemon-list/access hydration; a miss leaves the daemon pane empty until reconnect-reconcile.
      .catch(() => {});

    // #705: seed the daemon-access-request inbox (owner) + outbox (requester) so the
    // Activity "Permissions" tab + the request affordances render the pending set on
    // load. Best-effort: a miss (older relay) just leaves the inbox empty until the
    // live daemon_access_request_changed push arrives.
    early.daemonAccessRequests
      .then(({ requests }) => {
        if (!selectionIsCurrent(workspace.id, selection)) return;
        daemonAccessRequestsState.load(requests as DaemonAccessRequest[]);
        return undefined;
      })
      // intentional: background daemon-access-request hydration; a miss is recovered by the live push.
      .catch(() => {});
    early.cyboTransferRequests
      .then(({ requests }) => {
        if (!selectionIsCurrent(workspace.id, selection)) return;
        cyboTransferRequestsState.load(requests);
        return undefined;
      })
      // intentional: background agent-transfer hydration (an older relay lacks it); the live push recovers.
      .catch(() => {});

    // Structural clone of the daemon-access-request seed above: without this, an
    // owner who reloads (or was offline) before the live page_access_request_changed
    // push landed would never see the pending request at all — the ActivityPane
    // Approve/Deny card and PageCard's "Requested" pending-collapse both read off
    // pageAccessRequestsState, which is otherwise empty until a fresh push arrives.
    early.pageAccessRequests
      .then(({ requests }) => {
        if (!selectionIsCurrent(workspace.id, selection)) return;
        pageAccessRequestsState.load(requests as PageAccessRequest[]);
        return undefined;
      })
      // intentional: background page-access-request hydration; a miss is recovered by the live push.
      .catch(() => {});
  }

  if (channels.length > 0) {
    const initialChannelId = channels[0].id;
    channelState.activeId = initialChannelId;
    // Same unread gate as selectChannel's `frozen`, read before the clears below.
    const initialHasUnread = channelHasUnread(workspace.id, initialChannelId);
    // Cache-first cold render, the same decision selectChannel makes.
    const cachedInitial = coldPaintChannel(initialChannelId, initialHasUnread);
    // CYBORG-755 (Wave A): freeze the SAME last-read cursor selectChannel does and
    // issue the IDENTICAL call shape, so this bootstrap fetch and the channel
    // page's concurrent selectChannel(channels[0].id) produce the same read-
    // coalescing key and collapse to ONE wire request.
    //
    // Wave A originally shipped as the client-side read coalescer (28ea657d5,
    // #2128), which keys on `type + params`. CYBORG-767 (3bf08bf91, three hours
    // later) then switched selectChannel to the around-the-cursor fetch WITHOUT
    // updating this call site, so the keys diverged — `{channelId,limit:50,…}` here
    // vs `{aroundTs:N,channelId,…}` there — and the duplicate came straight back for
    // every channel the user had EVER read (frozen != null). Deriving `frozen`
    // identically here is what restores the match; the coalescer itself is correct
    // and untouched.
    //
    // Ordering is structural, not lucky, and rests on three links:
    //   1. The workspace layout renders `{@render children()}` — which mounts the
    //      channel page whose $effect calls selectChannel — only in a branch
    //      strictly AFTER the `{:else if workspaceState.switching}` spinner
    //      (+layout.svelte), so no channel-page effect can even exist while
    //      `switching` is true. That is what makes `switching = false` the start
    //      of the span.
    //   2. From that `switching = false` through this call selectWorkspace never
    //      yields (no await in the span), and a Svelte $effect is always
    //      scheduled, never synchronous.
    //   3. request() does its in-flight map.set synchronously, before
    //      sendRequest()'s first await.
    // So this cursor read and the coalescing entry both land BEFORE selectChannel
    // can run. Links 1 and 2 are locked by bootstrap-fetch-ordering.source.test.ts
    // — the behavioural test cannot catch a violation, because it drives the two
    // calls in a fixed order.
    //
    // ONE KNOWN EXCEPTION (deliberately not "fixed" here): selectChannel calls
    // flushLiveReadAdvance() BEFORE its own cursor read, and that advances the
    // cursor to now. selectWorkspace's prologue clears channelState/cyboState/
    // attentionState but NOT liveReadTimer, so on a same-workspace re-select
    // within LIVE_READ_ADVANCE_MS of a live message in the focused default
    // channel, selectChannel reads `now` while this read got the seeded value —
    // the keys diverge and we fall back to TWO requests, exactly as today. That
    // is a missed dedup in a ≤1.5s self-healing window, never a correctness loss.
    // Draining the timer earlier was considered and rejected: doing it in the
    // prologue (before readCursorState.seed) loses the advance to the seed and
    // resurrects CYBORG-767's phantom divider, and doing it here fires
    // flushLiveReadAdvance's markRead against the NEW workspace id with the
    // PREVIOUS conversation's channel id on a path where it never fired before.
    // Neither is worth that risk for a dedup ticket.
    //
    // CYBORG-988 rides on exactly the same requirement: the delta open changes the
    // call SHAPE, so this seam has to make the same decision from the same inputs
    // or the keys diverge again and the duplicate comes back for every WARM
    // channel. `planChannelOpen` reads the L1 window (both call sites just peeked
    // it) and the unread signals, and both read them before selectChannel's clears
    // — so both land on the same plan. The one exception is the same ≤1.5s
    // flushLiveReadAdvance window documented above, with the same verdict: a missed
    // dedup, never a correctness loss.
    const frozenInitial = initialHasUnread ? readCursorState.getChannel(initialChannelId) : null;
    const initialPlan = planChannelOpen(initialChannelId, cachedInitial);
    try {
      const { page, usedDelta } = await fetchChannelForPlan(
        workspace.id,
        initialChannelId,
        initialPlan,
        frozenInitial,
      );
      const { messages, hasMore, hasMoreNewer } = page;
      if (!selectionIsCurrent(workspace.id, selection)) return;
      // Guard against a stale response (prod incident: the channel page's OWN
      // effect calls selectChannel(urlChannelId) concurrently with this bootstrap
      // — e.g. a direct URL/hard-reload/new-tab open of a non-default channel —
      // and selectChannel's guarded fetch can resolve and correctly paint FIRST.
      // Without this check, THIS default-channel (channels[0]) fetch resolving
      // afterward unconditionally overwrote channelState.messages with the
      // default channel's history while activeId (and therefore the header/
      // member-count/composer) still correctly showed the real target channel —
      // silently, no console error. Mirrors the guard selectChannel already has.
      if (channelState.activeId === initialChannelId) {
        if (usedDelta) {
          // CYBORG-988: a delta is NOT a window — it is only what arrived after the
          // cached one, so it is unioned onto the cold paint above, never a replace.
          applyChannelDelta(workspace.id, initialChannelId, messages);
          channelState.hasMore = deltaHasMore(
            channelKey(workspace.id, initialChannelId),
            cachedInitial,
          );
          channelState.hasMoreNewer = false;
        } else {
          channelState.setMessages(hydrateMessages(messages));
          channelState.hasMore = hasMore;
          // CYBORG-767: an around-window open loaded read context + the unread head
          // but NOT the tail. This fetch can now BE that window, so the flag has to
          // travel with it — otherwise the pane looks "caught up at the tail" and
          // load-newer never arms. (setMessages does not touch unreadCursor, so the
          // divider selectChannel freezes is never clobbered by this write.)
          channelState.hasMoreNewer = hasMoreNewer ?? false;
        }
        channelState.loading = false;
        // Writeback on SUCCESS (Caveat #29). Fire-and-forget; no-op when the cache is off.
        cacheWriteChannel(workspace.id, initialChannelId, !usedDelta);
      }
    } catch (err) {
      // Don't let a failed default-channel fetch escape selectWorkspace: it used to
      // propagate out of the whole switch, where restoreSession's catch only handles
      // AuthError — so the user was stranded on the "Reconnecting…" gate with no
      // error and no signal anywhere off their machine. The shell has already
      // painted (switching === false above), so degrade to an empty channel the
      // route can retry, and beacon the failure to telemetry (observability rule).
      reportUiFailure("selectWorkspace.initialChannelFetch", err, { channelId: initialChannelId });
    } finally {
      // Only clear loading if this channel is still the active one — otherwise we'd
      // stomp a freshly-selected channel's own loading state.
      if (channelState.activeId === initialChannelId) channelState.loading = false;
    }
  }
}

// ─── Pull-to-refresh entry points (mobile) ────────────────────────
// Thin wrappers over the existing private resync helpers so UI components can
// trigger a refresh without reaching into module internals.

// Conversation refresh: if the socket dropped, kick a reconnect (its own loop
// drains the delta on auth); otherwise resync the open workspace/channel now.
export async function refreshActiveConversation(): Promise<void> {
  if (!client.connected) {
    client.forceReconnect();
    return;
  }
  await handleReconnect();
}

// Activity refresh: re-pull the server activity feed for the current workspace.
// channels/fallback args only feed the legacy backfill path, so [] is safe.
export async function refreshActivity(): Promise<void> {
  const wsId = workspaceState.current?.id;
  if (!wsId) return;
  if (!client.connected) {
    client.forceReconnect();
    return;
  }
  await seedActivityFromServer(wsId, [], [], authState.user?.id);
}

// On-demand task-board re-fetch — the guaranteed fallback to the live
// cyborg:tasks_changed broadcast. The board only loads at workspace-open/reconnect,
// so a task that arrived by a non-broadcasting path (external sync) or on an older
// shell stays invisible until an app restart; this re-pulls server truth on Tasks
// entry, on project switch, and via the header refresh button. Workspace-guarded
// (same as the reconnect catch-up ~:1042) so a slow/failed fetch can't clobber a
// since-switched workspace. Best-effort: the live broadcast/reducer path is the
// real source of truth, so a miss just leaves the last-good board in place.
// CYBORG-1176: joins the workspace-open load when it is still running, else asks only
// for what changed since the last answer.
export async function reloadTasks(wsId: string): Promise<void> {
  if (!wsId) return;
  try {
    await taskBoard.refresh(wsId);
  } catch {
    // best-effort; the live tasks_changed broadcast remains the source of truth
  }
}

// reloadTasks for a caller that shows its own failure (pull-to-refresh, a profile tab).
export function loadTasks(wsId: string): Promise<void> {
  return taskBoard.refresh(wsId);
}

// Billing / license (Stripe + IAP) RPC facades — extracted to ./billing.ts
// (knowledge/87 Phase 2). Re-exported here so importers keep hitting the app
// barrel. refreshLicense is also imported above for the one internal caller
// (selectWorkspace).
export {
  currentBillingPlatform,
  refreshLicense,
  reconcileIapLicense,
  startCheckout,
  openBillingPortal,
  fetchBillingDetails,
  handleBillingReturn,
  fetchLicensePool,
  allocateLicense,
  deallocateLicense,
} from "./billing.js";

// Cross-component UI state for the Agents pane, so other surfaces can pick its
// sub-tab before navigating there.
export const agentsPaneState = $state<{
  subTab: "cybos" | "agents" | "skills";
  // The Settings › Machines access-scope switch ("My daemons" vs "All"), lifted
  // out so other surfaces can preselect it before navigating — the Home
  // "Team machines → View All" jumps straight to the full roster by setting "all"
  // here. Defaults to "mine" (only the daemons you can access).
  daemonAccessFilter: "mine" | "all";
}>({
  // "cybos" = the workspace cybo roster (your cybos); shown first + default.
  subTab: "cybos",
  daemonAccessFilter: "mine",
});

/**
 * Tear the session down. Two DIFFERENT events land here and they are not the
 * same request:
 *   • the user pressing Sign out / Delete account — they asked to leave, so
 *     `clearQueuedSends: true` and the undelivered outbox goes with them;
 *   • a dead token (`auth_expired`, or a reconnect the relay answers
 *     "unauthorized" with no refresh token to heal it) — the user asked for
 *     NOTHING. Default (no opts) therefore KEEPS the persisted outbox: wiping
 *     it here silently destroyed messages composed offline, with no warning and
 *     no trace, on an event the user never triggered and often never saw.
 */
export function disconnectFromServer(opts?: { clearQueuedSends?: boolean }): void {
  clearTimeout(cyboWaitTimer);
  beginCyboWait();
  cancelActiveConversationBadgeClear();
  hasConnectedBefore = false;
  activeWorkspaceSelection = null; // null: signed out, so a selection in flight must not land
  clearSavedSession();
  // Native mobile push: hand the relay back this device's FCM token BEFORE the
  // socket closes (the RPC needs it live), then disconnect — same ordering the
  // native shell uses (PushManager.detach() → relay.disconnect(), AppModel.swift).
  // unregisterMobilePush also drops push.ts's in-process token cache
  // synchronously, so the NEXT sign-in always re-registers this device under the
  // NEW user. Without that, one phone with two accounts left the relay row
  // pointing at the previous user and pushed THEIR sender names and message text
  // to whoever signed in next. No-op off the native shell / on a dead socket, so
  // web and desktop reach client.disconnect() on the next microtask.
  void unregisterMobilePush(client).finally(() => client.disconnect());
  // Wipe the local message cache on logout so the next account can't read this
  // one's messages (also clears L1). No-op when the cache is off. Fire-and-forget.
  if (cacheOn()) void cache.cacheClear();
  // Drop the PERSISTED send outbox (#502) only when the user actually signed
  // out. The original "don't leak to the next account" rationale is already
  // covered without deleting anything: getOutboxForUser filters by userId
  // (cache/outbox.ts), so another account can neither see nor replay these rows.
  // That made the unconditional wipe pure data loss on the token-expiry path.
  if (opts?.clearQueuedSends) void cache.clearOutbox();
  clearSwitchStates();
  // The in-memory mirrors are always dropped — they're per-session view state,
  // and rehydrateOutbox() rebuilds both from IndexedDB on the next authenticate()
  // (which is what makes the kept rows send after re-auth).
  sendOutbox.length = 0;
  pendingOutboxBubbles.clear();
  authState.clearLocal();
  userStatusState.clearLocal();
  notificationState.clearLocal();
  // Explicitly clear the dock badge / web title now (don't rely on the reactive
  // effect firing before the socket + state tear down on logout).
  pushUnreadIndicators(0);
  unreadFlagState.clearLocal();
  dmActivityState.clearLocal();
  dmHiddenState.clearLocal();
  favoritesState.clearLocal();
  openedDmsState.clearLocal();
  // #607: drop the cached scheduled-message list so the next account doesn't
  // inherit the previous user's pending rows.
  scheduledMessagesState.clear();
  // #619: drop the cached recurring-schedule board for the same reason.
  schedulesState.clear();
  rowWork.clear();
  // Drop the cached built-in recipe installs so the next account/workspace doesn't
  // inherit the previous one's integrations state.
  recipesState.clear();
  // #602: drop the cached prompt-template list so the next account/workspace
  // doesn't inherit the previous workspace's templates in the composer menu.
  promptTemplatesState.clear();
  readCursorState.reset();
  activityState.clearLocal();
  notifyClientPrefsState.clearLocal();
  // Cross-workspace notification prefs are now merged (not replaced per switch),
  // so clear them explicitly on logout or the previous account's prefs leak.
  notifPrefsState.clear();
  archivedChannelsState.clear();
  // Drop in-memory composer drafts (#610) so the previous account's unfinished
  // messages don't leak into the next login; server truth re-seeds on load.
  draftsState.clearAllLocal();
  // Clear per-account live state too, or the previous account's presence dots,
  // open DM thread, and daemon online/offline indicators leak into the next login.
  presenceState.clear();
  dmState.clear();
  dmState.activePeerId = null;
  agentDmReadState.close();
  daemonStatusState.load([]);
  daemonState.clear();
  providerState.clear();
  daemonAccessRequestsState.clear();
  cyboTransferRequestsState.clear();
  workerInvitesState.clear();
  pageAccessRequestsState.clear();
  daemonHealthState.stop();
  workspaceState.current = null;
  workspaceState.list = [];
  workspaceState.listLoaded = false;
  workspaceState.channels = [];
  workspaceState.tasks = [];
  taskBoard.reset();
  // Back to the initial "nothing yet" state — the next workspace-open starts
  // loading again rather than briefly showing an empty board.
  workspaceState.tasksLoading = true;
  workspaceState.tasksError = null;
  workspaceState.agents = [];
  attentionState.clearAll(); // #591
  channelState.clear();
  sessionState.list = [];
  sessionState.fetched = false;
  connectionState.status = "disconnected";
  // Reset the "has connected at least once" gate too. The ConnectionStatus banner
  // shows whenever status !== "connected" AND hasConnectedOnce — so without this a
  // full teardown (logout, or an auth-rejected reconnect) would leave a stuck
  // "Disconnected — reconnecting…" banner over the /login screen even though the
  // socket is intentionally down. A fresh sign-in re-sets it on the next connect.
  connectionState.hasConnectedOnce = false;
  connectionState.reconnectAttempt = 0;
  connectionState.reconnectDelayMs = 0;
  // Wipe the warm projects cache so the next account's workspaces start cold
  // (no stale project headers from the previous session).
  projectsCache.clear();
  // Nothing the agent-sync stack learned under this account reaches the next (#3629):
  // its conversations with every read and run of theirs, then its daemons and rosters.
  agentStreamState.clearAll();
  forwardedTurnCards.reset();
  accountSync.reset();
}

// ─── Core actions (override to use CyborgClient) ─────────────────

export async function selectChannel(
  channelId: string,
  opts: SelectConversationOptions = NO_FOCUS,
): Promise<void> {
  const wsId = workspaceState.channelWorkspaceId(channelId);
  if (!wsId) return;
  const focusMessageId = opts.focusMessageId;
  // Selecting a real (joined) channel always exits any preview: the channel now
  // resolves from `channels`, so the read-only preview slot must be cleared or
  // `isPreviewing` would linger and keep the composer hidden.
  workspaceState.previewChannel = null;
  // Navigating away defuses any pending focus-driven badge clear so the DM being
  // left is never marked read, regardless of how long the route takes to settle.
  cancelActiveConversationBadgeClear();
  // CYBORG-767: flush a pending live-read-advance for the conversation being LEFT
  // (a message seen <1.5s ago) so its cursor advances past those seen messages —
  // otherwise re-opening freezes a stale cursor → a phantom "New messages" divider.
  flushLiveReadAdvance();
  // Leaving any open DM: clear the active peer so this channel is the sole
  // active conversation. Otherwise composer draftKey (which prefers dm:<peer>
  // over channel:<id>) and DM read-receipts keep pointing at the stale DM.
  dmState.activePeerId = null;
  channelState.activeId = channelId;
  // Read while the unread signals are intact; the open clears them below.
  const hasUnread = channelHasUnread(wsId, channelId);
  // Cache-first cold render; the read below revalidates (or waits, see coldPaintChannel).
  const cachedChannel = coldPaintChannel(channelId, hasUnread);
  // CYBORG-988: decide the READ SHAPE from the window we just painted, while the
  // unread signals are still intact — the clears a few lines below would make
  // every re-open look caught-up. `tail` is today's behaviour; `delta` asks only
  // for the rows after the cached window's anchor.
  const openPlan = planChannelOpen(channelId, cachedChannel);
  rememberLastChannel(wsId, channelId);
  // A hidden group DM comes back when it is opened (a no-op for every other channel).
  showDm(wsId, channelId);
  // Item 12: freeze the divider from the server read-cursor BEFORE this visit's
  // mark-read advances it, so the "New messages" line renders at the true
  // first-unread boundary and stays frozen for the whole visit. Only when the
  // channel HAS unreads (the selectDm rule, CYBORG-767): every read channel keeps a
  // cursor, and opening around it fetched ~25 rows of old context instead of the
  // tail page.
  const frozen = hasUnread ? readCursorState.getChannel(channelId) : null;
  channelState.setUnreadCursor(frozen);
  notificationState.clear(wsId, channelId);
  // BUG #2: opening the channel reads it — drop the bold unread flag too.
  unreadFlagState.clearUnread(wsId, channelId);
  // Persist read-state server-side (cross-device unread sync, P6). The relay
  // also marks this channel's Activity items read and broadcasts
  // activity_read_changed back (incl. this device); mark optimistically here so
  // the activity badge clears instantly. (P2 Item 9)
  // The same stamp goes on the wire and into the local map, so a reconnect seed
  // that is AHEAD of the local map can only mean another device read (#514).
  const readAt = Date.now();
  client.markRead(wsId, channelId, readAt);
  // Nothing was sent while disconnected: a local-only clear would hide rows the server counts.
  if (connectionState.ready) activityState.markReadByChannel(channelId);
  // Advance the LOCAL cursor to now so a later re-open snapshots the new value,
  // not the frozen copy this visit's divider holds (monotonic; Mattermost model).
  readCursorState.advanceChannel(channelId, readAt);
  try {
    // CYBORG-988 delta read, with the CYBORG-767 around-window read (read context
    // ABOVE the "New messages" divider, so the open lands ON it) and the plain tail
    // as the fallbacks. All three live in fetchChannelForPlan so this seam and
    // selectWorkspace's default-channel bootstrap cannot drift apart — see the note
    // there and CYBORG-755.
    const { page, usedDelta } = await fetchChannelForPlan(
      wsId,
      channelId,
      openPlan,
      frozen,
      focusMessageId,
    );
    const { messages, hasMore, hasMoreNewer } = page;
    // Guard against stale responses if the user switched channels mid-fetch.
    if (channelState.activeId === channelId) {
      reportMissingFocusTarget(focusMessageId, messages);
      if (usedDelta) {
        applyChannelDelta(wsId, channelId, messages);
        channelState.hasMore = deltaHasMore(channelKey(wsId, channelId), cachedChannel);
        // A delta read is anchored INSIDE the loaded window, so by construction it
        // ends at the tail — there is nothing newer left unloaded.
        channelState.hasMoreNewer = false;
      } else {
        channelState.setMessages(hydrateMessages(messages));
        channelState.hasMore = hasMore;
        channelState.hasMoreNewer = hasMoreNewer ?? false;
      }
      // #502: re-show any still-pending persisted-outbox bubble for this channel
      // AFTER the fetch (setMessages replaced the list). No-op when none pending.
      reapplyOutboxBubbles("channel", channelId);
      // Writeback the authoritative window + stamp cursors (Caveat #29: only on
      // SUCCESS). Fire-and-forget; no-op when the cache is off or the window was
      // read around an alert's target.
      cacheWriteChannel(wsId, channelId, !usedDelta, focusMessageId);
    }
  } catch (err) {
    // Leave activeId set so the channel can be retried (e.g. on reconnect).
    // console.error reaches NOTHING off the user's machine, and this is the open
    // path for EVERY channel — a silent failure here is an empty chat pane with no
    // record anywhere we can query. Beacon it too (observability rule), same shape
    // as loadMoreMessages.
    reportUiFailure("selectChannel", err, { workspaceId: wsId, channelId });
  } finally {
    if (channelState.activeId === channelId) channelState.loading = false;
  }
}

// Read-only preview of a PUBLIC channel the user has NOT joined. Mirrors the
// message-load half of selectChannel but deliberately skips EVERY joined-channel
// side effect: no mark-read, no badge/unread-flag clear, no read-cursor advance,
// no last-channel cache, no SQLite writeback. Seeding `previewChannel` is what
// makes `viewedChannel`/`isPreviewing` resolve so the header, empty-state and the
// "Join to participate" banner render for a non-member. The relay already lets any
// workspace member read a public channel's history (private channels are gated at
// fetch_messages), so no server change is needed.
export async function selectChannelPreview(channel: Channel): Promise<void> {
  if (!workspaceState.current) return;
  const channelId = channel.id;
  cancelActiveConversationBadgeClear();
  dmState.activePeerId = null;
  channelState.activeId = channelId;
  workspaceState.previewChannel = channel;
  // No unread divider in preview — a channel you haven't joined has no read
  // cursor to freeze from, and we must not advance one.
  channelState.setUnreadCursor(null);
  channelState.loading = true;
  channelState.messages = [];
  try {
    const { messages, hasMore } = await client.fetchMessages(workspaceState.current.id, channelId, {
      limit: 50,
    });
    // Guard against a stale response if the user navigated away mid-fetch.
    if (channelState.activeId === channelId) {
      channelState.setMessages(hydrateMessages(messages));
      channelState.hasMore = hasMore;
    }
  } catch (err) {
    reportUiFailure("chat.select_channel_preview", err, {
      workspaceId: workspaceState.current?.id,
      channelId,
    });
  } finally {
    if (channelState.activeId === channelId) channelState.loading = false;
  }
}

export async function loadMoreMessages(): Promise<void> {
  if (
    !workspaceState.current ||
    !channelState.activeId ||
    !channelState.hasMore ||
    channelState.loading
  )
    return;
  channelState.loading = true;
  const oldest = channelState.messages[0];
  // Capture the target before the await: channelState is a singleton, so if the
  // user switches channel/workspace while the fetch is in flight, prepending the
  // result would inject the old channel's history into the new channel, and a
  // blind `loading = false` would clear the new channel's own loading state.
  const workspaceId =
    workspaceState.channelWorkspaceId(channelState.activeId) ?? workspaceState.current.id;
  const channelId = channelState.activeId;
  try {
    const { messages, hasMore } = await client.fetchMessages(workspaceId, channelId, {
      before: oldest?.id,
      limit: 50,
    });
    if (
      workspaceState.channelWorkspaceId(channelId) === workspaceId &&
      channelState.activeId === channelId
    ) {
      channelState.prependMessages(hydrateMessages(messages), hasMore);
    }
  } catch (err) {
    // A failed page fetch must NOT wedge load-older. Without resetting `loading`
    // in finally, the scroll-up trigger (gated on !loading) dies for the rest of
    // the session, so older history can never load again and the chat looks
    // permanently "cut off". Surface the error; scrolling up retries. (Mirrors
    // loadMoreDmMessages, which already had this guard.)
    reportUiFailure("loadMoreMessages", err, { channelId });
  } finally {
    // Only clear loading if we're still on the channel we fetched for — otherwise
    // we'd stomp the freshly-selected channel's own loading state.
    if (channelState.activeId === channelId) {
      channelState.loading = false;
    }
  }
}

// b1 load-newer (CYBORG-768): page NEWER than the loaded tail. Fired when the
// user scrolls near the loaded bottom of a channel opened at its unread divider
// (around-window fetch → hasMoreNewer). The list is NON-virtualized, so appending
// rows below the viewport is position-stable — no bottom anchor needed (the
// scroll controller's follow-tail handles the glued-to-bottom case). Mirrors
// loadMoreMessages' stale-channel guards + telemetry reporting.
export async function loadNewerMessages(): Promise<void> {
  if (
    !workspaceState.current ||
    !channelState.activeId ||
    !channelState.hasMoreNewer ||
    channelState.loading
  )
    return;
  channelState.loading = true;
  // Cursor = the newest row a PAGE returned (twin of loadMoreMessages' `oldest`),
  // not the newest row in the window: a live broadcast lands at the end of the
  // window while the tail is unloaded, and "after" it the server has nothing.
  const after = newerPageCursor(channelState.fetchedTailId, channelState.messages);
  const workspaceId =
    workspaceState.channelWorkspaceId(channelState.activeId) ?? workspaceState.current.id;
  const channelId = channelState.activeId;
  try {
    const { messages, hasMoreNewer } = await client.fetchMessages(workspaceId, channelId, {
      after,
      limit: 50,
    });
    if (
      workspaceState.channelWorkspaceId(channelId) === workspaceId &&
      channelState.activeId === channelId
    ) {
      channelState.appendMessages(hydrateMessages(messages), hasMoreNewer ?? false);
    }
  } catch (err) {
    // Same reasoning as loadMoreMessages: a failed page must NOT wedge load-newer.
    // The finally resets `loading`, so scrolling down retries; surface the error.
    reportUiFailure("loadNewerMessages", err, { channelId });
  } finally {
    if (channelState.activeId === channelId) {
      channelState.loading = false;
    }
  }
}

// knowledge/117: replace the loaded window with the one AROUND `messageId` — the
// focus loop's one-shot fallback for a target that is not in the open window (a
// Threads / Saved jump into the open channel, a thread's "View in channel").
// Same stale-channel guards + telemetry reporting as the pagers above; no cache
// writeback (the window is not the tail).
export async function loadMessagesAround(messageId: string): Promise<void> {
  if (!workspaceState.current || !channelState.activeId || channelState.loading) return;
  channelState.loading = true;
  const workspaceId =
    workspaceState.channelWorkspaceId(channelState.activeId) ?? workspaceState.current.id;
  const channelId = channelState.activeId;
  try {
    const { messages, hasMore, hasMoreNewer } = await client.fetchMessages(workspaceId, channelId, {
      aroundId: messageId,
    });
    if (
      workspaceState.channelWorkspaceId(channelId) === workspaceId &&
      channelState.activeId === channelId
    ) {
      channelState.setMessages(hydrateMessages(messages));
      channelState.hasMore = hasMore;
      channelState.hasMoreNewer = hasMoreNewer ?? false;
      reapplyOutboxBubbles("channel", channelId);
    }
  } catch (err) {
    reportUiFailure("loadMessagesAround", err, { channelId });
  } finally {
    if (channelState.activeId === channelId) channelState.loading = false;
  }
}

// b1 "jump to recents" (CYBORG-768): re-fetch the TRUE tail window. When a
// channel was opened at its unread divider in a large unread block, the real
// latest messages were never loaded (hasMoreNewer). jumpToBottom alone would
// land on the loaded scrollHeight — the wrong row — so the toast handler calls
// this first to replace the window with the actual tail (no before/after/aroundTs
// = the classic newest-N read) before scrolling to the bottom.
export async function loadLatestMessages(): Promise<void> {
  // Same `loading` flag as the two pagers: an older page still in flight would
  // otherwise land on the replaced window and splice old history onto the new tail.
  if (!workspaceState.current || !channelState.activeId || channelState.loading) return;
  channelState.loading = true;
  const workspaceId =
    workspaceState.channelWorkspaceId(channelState.activeId) ?? workspaceState.current.id;
  const channelId = channelState.activeId;
  const seqAtRequest = channelState.lastSeq;
  try {
    const { messages, hasMore } = await client.fetchMessages(workspaceId, channelId, { limit: 50 });
    if (
      workspaceState.channelWorkspaceId(channelId) === workspaceId &&
      channelState.activeId === channelId
    ) {
      // Same carry-over as loadLatestDmMessages: a pending send or a live row that
      // landed while this read was in flight is not in the tail, and must not vanish.
      const tail = hydrateMessages(messages);
      const carried = rowsTheTailMissed(channelState.messages, tail, seqAtRequest);
      channelState.setMessages(tail);
      for (const m of carried) channelState.addMessage(m);
      channelState.hasMore = hasMore;
      channelState.hasMoreNewer = false;
    }
  } catch (err) {
    reportUiFailure("loadLatestMessages", err, { channelId });
  } finally {
    if (channelState.activeId === channelId) channelState.loading = false;
  }
}

// Back to pending (even after the watchdog failed it) and watched for budgetMs; no-op once echoed.
function holdParkedSend(clientMsgId: string, budgetMs: number): void {
  const localId = cache.localIdFor(clientMsgId);
  const hasRow = (s: SendSurface) => surfaceFor(s).messages.some((m) => m.id === localId);
  const watch = watchOf(localId);
  const queued = sendOutbox.find((e) => e.clientMsgId === clientMsgId);
  const surface = watch?.surface ?? queued?.surface ?? (["channel", "dm"] as const).find(hasRow);
  if (!surface) return;
  const targetId = watch?.targetId ?? queued?.targetId ?? openSendRow(surface, localId)?.targetId;
  if (!targetId) return;
  setBubbleStatus(surface, localId, "pending");
  keepParkedBubble(surface, localId);
  armSendWatchdog(localId, surface, targetId, budgetMs);
}

// My send landed (its surface open or not): no watchdog, no kept bubble, no replay left for it.
function settleOwnEcho(fromMe: boolean, clientMsgId: string | null | undefined): void {
  if (!fromMe || !clientMsgId) return;
  clearSendWatchdog(cache.localIdFor(clientMsgId));
  dequeueOutbox(clientMsgId);
}

// A send the relay refused (`stale_send`, `already_deleted`), lost (`send_lost`) or one that aged out of
// the replay queue: mark its bubble failed, stop its watchdog and drop the queued
// copy, so Retry is a human decision and nothing loops.
function failSendByClientMsgId(clientMsgId: string, reason?: string): void {
  const localId = cache.localIdFor(clientMsgId);
  const watchdog = watchOf(localId);
  clearSendWatchdog(localId);
  setBubbleStatus(watchdog?.surface, localId, "failed", reason);
  // The replay stops; the bubble is kept, so reopening its surface still shows X + Retry.
  dropOutboxReplay(clientMsgId);
}

// A unique client-generated id for optimistic reconciliation (#501). The relay
// echoes it on the broadcast so the sender settles the EXACT optimistic bubble —
// two identical consecutive sends / a retried send each match their own row.
export function newClientMsgId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // crypto unavailable (older SSR) — fall through to the timestamp+random id.
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function dispatchSend(
  localId: string,
  wsId: string,
  chId: string,
  text: string,
  mentions: string[] | undefined,
  attachments: Attachment[] | undefined,
  clientMsgId: string,
  // #602 — template-sourced send: ask the server to expand {channel}/{user}/{date}
  // on the live send. Not persisted on the offline outbox (a queued replay posts
  // the raw body), so it only affects the live path.
  expandTemplate?: boolean,
  // fix/pages-attach-not-send: a staged page's id, riding the SAME send call as
  // text/attachments (drafts, clearing, typing indicator, optimistic bubble,
  // retry/outbox reconciliation all follow the normal path now, instead of the
  // old bespoke direct client.sendMessage call). Like expandTemplate above, NOT
  // persisted to the offline outbox — a send that fails while the socket is
  // down replays as plain text on reconnect, dropping the page reference.
  // Known, narrow gap (same precedent as expandTemplate); closing it needs
  // OutboxRecord to carry pageRef too — flagged as a follow-up.
  pageRef?: string,
  // Ids picked from the @-autocomplete; persisted on the outbox so a replay keeps them.
  mentionPicks?: string[],
): void {
  // The kept copy too (Retry after reload); a fresh send's is made by keepParkedBubble below.
  setBubbleStatus("channel", localId, "pending");
  armSendWatchdog(localId, "channel", chId);
  keepParkedBubble("channel", localId);
  // The replay carries the SAME clientMsgId, so the echo reconciles this bubble (no dupe).
  sendThroughOutbox(
    {
      clientMsgId,
      userId: authState.user?.id ?? "unknown",
      kind: "channel",
      workspaceId: wsId,
      targetId: chId,
      text,
      mentions: mentions ?? null,
      mentionPicks: mentionPicks ?? null, // null: OutboxRecord field, nothing picked
      attachments: attachments ?? null,
      parentId: null,
      createdAt: Date.now(),
      bubbleAt: openSendRow("channel", localId)?.row.createdAt,
    },
    () =>
      client.sendMessage(
        wsId,
        chId,
        text,
        mentions,
        attachments,
        undefined,
        clientMsgId,
        expandTemplate,
        pageRef,
        Date.now(),
        mentionPicks,
      ),
  );
}

export function sendMessage(
  text: string,
  mentions?: string[],
  attachments?: Attachment[],
  // #602 — the text came from a composer prompt template; the server expands
  // {channel}/{user}/{date} on send. The optimistic bubble shows the raw body
  // until the server's expanded broadcast echo reconciles it (same as a slash
  // command's optimistic → server-result flow).
  expandTemplate?: boolean,
  // fix/pages-attach-not-send: a staged /pages selection, sent alongside text.
  // Client-constructed from the picker's cached title/icon (NOT a call into the
  // server card wire) so the optimistic bubble shows the page immediately —
  // mirrors how a pending attachment shows its local blob preview before the
  // real URL comes back. The server's broadcast echo carries the authoritative,
  // freshly-hydrated PageCard and reconciles this by clientMsgId (#501), same
  // as every other optimistic field.
  pageCard?: PageCard,
  // Ids picked from the @-autocomplete (vs `mentions`, which adds text matches).
  mentionPicks?: string[],
): void {
  const wsId = workspaceState.channelWorkspaceId(channelState.activeId);
  if (!wsId || !channelState.activeId) return;
  // Capture the target so a queued replay (after a deploy/reconnect) lands in
  // the right channel even if the user has since navigated away.
  const chId = channelState.activeId;
  const myId = authState.user?.id ?? "unknown";
  const myName = myDisplayName();

  const clientMsgId = newClientMsgId();
  const localId = `local-${clientMsgId}`;
  channelState.addMessage({
    id: localId,
    channelId: chId,
    fromId: myId,
    fromType: "human",
    fromName: myName,
    text,
    mentions: mentions ?? [],
    attachments: attachments ?? null,
    ...(pageCard ? { card: pageCard } : {}),
    sendStatus: "pending",
    clientMsgId,
    seq: 0,
    createdAt: bubbleTime(channelState.messages, Date.now()),
  });

  dispatchSend(
    localId,
    wsId,
    chId,
    text,
    mentions,
    attachments,
    clientMsgId,
    expandTemplate,
    pageCard?.pageId,
    mentionPicks,
  );
}

// #607: schedule a chat message to send later. Targets EXACTLY ONE of a channel
// or a DM peer (the composer passes whichever it's in). On success the created
// row is folded into the Scheduled-pane list (live if it's open) and returned so
// the composer can clear like a normal send; on failure the error is surfaced by
// the caller (the dialog). `sendAt` is epoch ms and must be in the future — the
// dialog gates this in the UI and the server re-validates.
export async function scheduleMessage(
  target: { channelId: string } | { toId: string },
  text: string,
  sendAt: number,
  mentions?: string[],
): Promise<{ ok: boolean; error?: string }> {
  if (!workspaceState.current) return { ok: false, error: "No workspace" };
  const wsId = workspaceState.current.id;
  try {
    const res = await client.scheduleMessageCreate({
      workspaceId: wsId,
      ...target,
      text,
      sendAt,
      ...(mentions && mentions.length > 0 ? { mentions } : {}),
    });
    if (res.ok && res.message) scheduledMessagesState.upsert(res.message);
    return { ok: res.ok, error: res.error };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Couldn't schedule message" };
  }
}

// Re-attempt a failed optimistic send. Reuses the SAME local row AND its original
// clientMsgId (#501), so the eventual echo collapses exactly this bubble — resets
// it to "pending" and re-arms the watchdog.
export function retrySendMessage(localId: string): void {
  if (!workspaceState.current) return;
  const msg = channelState.messages.find((m) => m.id === localId);
  if (!msg || !localId.startsWith("local-")) return;
  const chId = msg.channelId ?? channelState.activeId;
  const wsId = workspaceState.channelWorkspaceId(chId);
  if (!chId || !wsId) return;
  // Pre-#501 rows (or any without one) get a fresh id — still reconciles by
  // fromId+text via the fallback.
  const clientMsgId = msg.clientMsgId ?? localId.replace(/^local-/, "");
  followRetriedSend(localId);
  dispatchSend(
    localId,
    wsId,
    chId,
    msg.text,
    msg.mentions ?? undefined,
    msg.attachments ?? undefined,
    clientMsgId,
    undefined,
    // fix/pages-attach-not-send: the failed bubble's own optimistic card is the
    // only place the pageRef survives (dispatchSend doesn't persist it) — pull
    // it back out so a manual retry doesn't silently drop the page reference.
    msg.card?.kind === "page" ? msg.card.pageId : undefined,
  );
}

export function pinMessage(messageId: string, pinned: boolean): void {
  const chId = channelState.activeId;
  // A DM has no channel; the relay gates the pin on the persisted row and scopes its broadcast.
  if (!workspaceState.current || (!chId && !dmState.activePeerId)) return;
  const setPinned = (at: number | null, by: string | null) =>
    chId ? channelState.setPinned(messageId, at, by) : setDmPinned(messageId, at, by);
  const prev = (chId ? channelState.messages : dmState.messages).find((m) => m.id === messageId);
  const prevAt = prev?.pinnedAt ?? null; // null: wire null = not pinned
  const prevBy = prev?.pinnedBy ?? null; // null: wire null = not pinned
  // Optimistic: stamp/clear locally, reconcile from the broadcast.
  const ourAt = pinned ? Date.now() : null;
  const ourBy = pinned ? (authState.user?.id ?? null) : null; // null: wire null = not pinned
  setPinned(ourAt, ourBy);
  const seen = pinBroadcasts.get(messageId) ?? 0;
  client
    .pinMessage(
      workspaceState.channelWorkspaceId(chId) ?? workspaceState.current.id,
      chId,
      messageId,
      pinned,
    )
    .catch(() => {
      // intentional: the failure is shown by reverting the optimistic pin glyph, but only
      // if no pin_message broadcast for this message arrived meanwhile (it is newer).
      if ((pinBroadcasts.get(messageId) ?? 0) === seen) setPinned(prevAt, prevBy);
    });
}

// ─── Saved messages (#609 — personal bookmarks) ───────────────────
// Toggle a personal bookmark. Optimistic (the message row is in hand, so a save
// shows up in an already-loaded Saved view at once); the save_message broadcast
// reconciles across my devices and a failure reverts. The private analog of
// pinMessage, but works from ANY surface (channel, DM, thread, Saved view).
export function saveMessage(message: import("../core/types.js").Message, saved: boolean): void {
  if (!workspaceState.current) return;
  savedState.apply(message.id, saved, message);
  client.saveMessage(workspaceState.current.id, message.id, saved).catch(() => {
    // Revert the optimistic toggle on failure.
    savedState.apply(message.id, !saved, message);
  });
}

/**
 * Toggle a bookmark by message id ALONE — the agent-session path.
 *
 * saveMessage() above needs a full Message for its optimistic list insert; a
 * session turn has no such object. It does not need one: an agent turn's
 * `StreamEntry.messageId` IS the id of the persisted row (the relay stamps the
 * provider's message id onto it — verified against prod: 3415 of 3738 agent
 * rows carry a provider `msg_…` id, not a generated uuid). So the id is enough,
 * and the authoritative row comes back with the next Saved-list load rather
 * than from a synthesized stand-in that could disagree with it.
 *
 * The id set still updates optimistically so the glyph flips at once, and
 * reverts if the write fails.
 */
export function toggleSavedById(
  messageId: string,
  saved: boolean,
  snapshot?: { text: string; fromName?: string | null; agentId?: string | null },
): void {
  if (!workspaceState.current || !messageId) return;
  savedState.apply(messageId, saved);
  client
    .saveMessage(workspaceState.current.id, messageId, saved, snapshot)
    .then(() => {
      // apply() without a Message only flips the id set — `savedState.list` (what
      // the Saved view and the agent panel render) is untouched, so the bookmark
      // would not appear until something else refetched. Pull the authoritative
      // row instead of synthesizing one.
      return void loadSavedMessages();
    })
    .catch((err: unknown) => {
      savedState.apply(messageId, !saved);
      // Not silent: a bookmark that quietly fails to stick is indistinguishable
      // from one that saved and then vanished. Never the snapshot: it carries the text.
      reportUiFailure("chat.toggle_saved", err, {
        workspaceId: workspaceState.current?.id,
        messageId,
        saved,
      });
    });
}

// Load my saved messages for the current workspace (newest-saved first). Mirrors
// fetchThreads: one-shot on Saved-view open, guarded against a workspace switch
// racing the response.
export async function loadSavedMessages(
  wsId: string | undefined = workspaceState.current?.id,
  read?: ReturnType<typeof client.listSaved>,
  isCurrent: () => boolean = () => workspaceState.current?.id === wsId,
): Promise<void> {
  if (!wsId) return;
  savedState.loading = true;
  try {
    const messages = await (read ?? client.listSaved(wsId));
    if (isCurrent()) savedState.setList(messages);
  } catch {
    // Older relays may not support saved messages yet — leave the list empty.
  } finally {
    savedState.loading = false;
  }
}

// ─── /catchup digest (personal, ephemeral) ───────────────────────
// Drives the CatchupSheet overlay. The digest is NEVER persisted — it lives only
// here for the lifetime of the sheet. status: loading → ready (digest) | empty
// (all caught up) | error.
class CatchupState {
  open = $state(false);
  status = $state<"loading" | "ready" | "empty" | "error">("loading");
  channelId = $state<string | null>(null);
  channelName = $state("");
  digest = $state("");
  errorText = $state("");
  unreadCount = $state(0);

  // Open the sheet in its loading state for a freshly-dispatched /catchup.
  start(channelId: string, channelName: string): void {
    this.open = true;
    this.status = "loading";
    this.channelId = channelId;
    this.channelName = channelName;
    this.digest = "";
    this.errorText = "";
    this.unreadCount = 0;
  }

  // Apply an incoming catchup_result. Opens the sheet if it wasn't
  // already (the composer path doesn't pre-open). Ignores results for a different
  // channel than the one currently shown in the sheet (a stale digest from a prior run).
  resolve(p: {
    channelId: string;
    channelName: string;
    ok: boolean;
    text: string;
    unreadCount?: number;
  }): void {
    if (this.open && this.channelId && this.channelId !== p.channelId) return;
    this.open = true;
    this.channelId = p.channelId;
    this.channelName = p.channelName;
    this.unreadCount = p.unreadCount ?? 0;
    if (!p.ok && this.unreadCount === 0) {
      this.status = "empty";
      this.errorText = p.text;
      this.digest = "";
    } else if (!p.ok) {
      this.status = "error";
      this.errorText = p.text;
      this.digest = "";
    } else {
      this.status = "ready";
      this.digest = p.text;
      this.errorText = "";
    }
  }

  close(): void {
    this.open = false;
  }
}

export const catchupState = new CatchupState();

// /resume session picker (Phase 3/4). A CLIENT-only ephemeral sheet — like the
// /catchup digest — opened by intercepting /resume in the composer. It fans out
// to the existing restore_session (cloud archived restore, for everyone) and
// import_session (local transcript import, when a daemon serving the transcripts
// is reachable) RPCs; there is no server round-trip for /resume itself.
class ResumePickerState {
  open = $state(false);
  daemonId = $state<string | undefined>();
  // The channel /resume was run from. Anchors an imported agent's binding to that
  // channel (parity with create_agent), so a session resumed from a channel lands
  // back there. Null when opened outside a channel context.
  channelId = $state<string | null>(null);
  // The session /resume was typed in: only its provider's sessions resume there, and the
  // Local tab starts on its project.
  provider = $state<string | undefined>();
  cwd = $state<string | undefined>();

  start(
    channelId: string | null,
    daemonId?: string,
    session?: { provider?: string; cwd?: string | null },
  ): void {
    this.open = true;
    this.channelId = channelId;
    this.daemonId = daemonId;
    this.provider = session?.provider;
    this.cwd = session?.cwd ?? undefined;
  }

  close(): void {
    this.open = false;
  }
}

export const resumePickerState = new ResumePickerState();

// One page of the caller's resumable archived sessions (the /resume Cloud tab). The daemon
// applies the rule (resumable-sessions.ts): no agent runs, one row per session, the search.
export async function listResumableSessions(opts: {
  cursor?: string;
  query?: string;
  provider?: string;
}): Promise<{ sessions: ArchivedSession[]; nextCursor: string | null }> {
  const wsId = workspaceState.current?.id;
  if (!wsId) return { sessions: [], nextCursor: null };
  const page = await client.listArchivedSessions(wsId, {
    ...opts,
    limit: ARCHIVED_SESSIONS_PAGE_SIZE,
    resumable: true,
  });
  return { sessions: page.sessions, nextCursor: page.nextCursor };
}

// Dispatch /catchup for a channel and open the digest sheet (loading). Driven by
// the composer's /catchup submit (and the sheet's own re-run). Resolves the
// target daemon the same way the composer does; surfaces a no-daemon / dispatch
// error in the sheet itself (never as channel noise).
export async function runCatchup(channelId: string, channelName: string): Promise<void> {
  const wsId = workspaceState.current?.id;
  if (!wsId || !channelId) return;
  // In-flight guard: rapid repeated composer submits (or sheet re-runs) must not
  // fire two digests. A loading sheet for THIS channel
  // is already running; ignore. (Re-running from the error state is still fine —
  // status is "error" there, not "loading".)
  if (catchupState.status === "loading" && catchupState.channelId === channelId) return;
  catchupState.start(channelId, channelName);
  const def = daemonState.defaultSlashDaemonId;
  const daemonId = def && daemonState.isOnline(def) ? def : undefined;
  try {
    const resp = await client.slashCommand(wsId, channelId, "catchup", undefined, daemonId);
    if (resp.systemAlert) {
      catchupState.resolve({
        channelId,
        channelName,
        ok: false,
        text: resp.systemAlert,
        unreadCount: 1,
      });
      return;
    }
    if (!resp.ok) {
      catchupState.resolve({
        channelId,
        channelName,
        ok: false,
        text: resp.error ?? "/catchup failed",
        unreadCount: 1,
      });
    }
    // ok: the digest arrives asynchronously via the catchup_result event.
  } catch (err) {
    catchupState.resolve({
      channelId,
      channelName,
      ok: false,
      text: err instanceof Error ? err.message : "/catchup failed",
      unreadCount: 1,
    });
  }
}

// ─── Threads ─────────────────────────────────────────────────────
class ThreadState {
  parentId: string | null = $state(null);
  parent: import("../core/types.js").Message | null = $state(null);
  replies: import("../core/types.js").Message[] = $state([]);
  loading = $state(false);
  // The open's fetch_thread failed: the panel says so and offers a retry instead
  // of showing "0 replies" as if the thread were empty.
  loadFailed = $state(false);

  // Thread-typing (#11 thread-typing): people composing a reply in THIS thread.
  // Same 5s auto-expire + dedup-by-fromId machinery ChannelState uses; rendered as
  // a TypingIndicator at the bottom of ThreadPanel. Routed here by client.on("typing")
  // only when the event's parentId matches the open thread.
  readonly #typing = new TypingLines();
  get typing(): import("../core/types.js").TypingEvent[] {
    return this.#typing.typing;
  }

  receiveReply(msg: import("../core/types.js").Message): void {
    if (!this.parentId || this.parentId !== msg.parentId) return;
    if (this.replies.find((m) => m.id === msg.id)) return;
    // clientMsgId-first reconciliation (#501), fallback to fromId+text. Assigning
    // the echo verbatim is safe: CYBORG-801 bars an untagged echo from settling a
    // TAGGED reply, so messageKey can't jump on the local→server id swap.
    const localIdx = findOptimisticIndex(this.replies, msg);
    if (localIdx >= 0) {
      this.replies[localIdx] = msg;
      this.replies = this.replies.slice();
    } else {
      this.replies = [...this.replies, msg];
    }
  }

  addTyping(event: import("../core/types.js").TypingEvent): void {
    this.#typing.add(event);
  }

  endTyping(event: import("../core/types.js").TypingEvent): void {
    this.#typing.end(event);
  }

  endTypingAt(fromId: string, parentId: string): boolean {
    return this.#typing.endAt(fromId, parentId);
  }

  // The sender's reply arrived: drop their line now (#515).
  clearTypingFor(fromId: string): void {
    this.#typing.clearFor(fromId);
  }

  clearTyping(): void {
    this.#typing.clear();
  }

  close(): void {
    this.parentId = null;
    this.parent = null;
    this.replies = [];
    this.loading = false;
    this.loadFailed = false;
    this.clearTyping();
  }
}
export const threadState = new ThreadState();

// The stores a typing frame can land in (typing-route.ts).
function typingSurfaces(): TypingSurfaces {
  return {
    thread: threadState,
    channel: channelState,
    dm: dmState,
    dmSidebar: dmTypingState,
    workSession: workSessionTypingState,
  };
}

export async function openThread(parent: import("../core/types.js").Message): Promise<void> {
  // Mutual exclusivity: the thread is the panel the user just opened, so it wins
  // — close any open profile first, via the neutral coordinator (no reactive read/
  // write, so it can't form a close-loop). openThreadById routes through here, so
  // the deep-link path is covered too.
  closeOpenProfile();
  threadState.parentId = parent.id;
  threadState.parent = parent;
  threadState.replies = [];
  threadState.clearTyping();
  threadState.loading = true;
  threadState.loadFailed = false;
  if (!workspaceState.current) {
    threadState.loading = false;
    return;
  }
  try {
    const wsId = workspaceState.channelWorkspaceId(parent.channelId) ?? workspaceState.current.id;
    const res = await client.fetchThread(wsId, parent.id);
    if (threadState.parentId !== parent.id) return;
    // hydrateMessages, like every channel/DM history fetch: an older relay sends RAW
    // reaction rows, which un-grouped drew one count-less chip PER REACTOR.
    threadState.replies = hydrateMessages(res.messages);
    // Per-thread "New replies" divider (#7): freeze the divider cursor from the
    // server's last_viewed BEFORE we advance it. Captured once at open and kept
    // frozen (perThreadLastViewed snapshot), so marking the thread read here does
    // NOT make the divider vanish — it stays above the first reply newer than the
    // open-time cursor for the whole visit (same pattern as the channel divider).
    if (typeof res.lastViewed === "number") {
      threadsState.setPerThreadLastViewed(parent.id, res.lastViewed);
    }
    // Advance the server cursor + clear this thread's unread badge on open
    // (consistent with the existing mark_thread_read infra; divider stays frozen).
    // The Threads feed is the open workspace's; a host thread is not in it.
    if (wsId === workspaceState.current?.id) markThreadRead(parent.id);
    // Opening the thread reads its thread_reply alerts (a channel read no longer does).
    activityState.markReadByThread(threadState.replies.map((m) => m.id));
  } catch (err) {
    // Callers fire this from onclick without awaiting: an escaped rejection was an
    // unhandled error in the console and a panel stuck on "0 replies".
    if (threadState.parentId === parent.id) threadState.loadFailed = true;
    reportUiFailure("openThread", err, {
      workspaceId: workspaceState.current?.id,
      channelId: parent.channelId,
      messageId: parent.id,
    });
  } finally {
    // Only this open's thread: a slower earlier open must not clear the spinner
    // of the thread opened after it.
    if (threadState.parentId === parent.id) threadState.loading = false;
  }
}

export function closeThread(): void {
  threadState.close();
}

// Register the thread closer so opening a profile can close an open thread without
// profile-panel.svelte.ts importing this module (avoids the circular dependency).
registerThreadCloser(closeThread);

// Open the thread pane at a root resolved BY ID (CYBORG-794). openThread() needs the
// root Message object, but a deep-link (session "Open full thread", a shared
// `?thread=<rootId>` link) only carries the id. Resolve it from the loaded timeline
// when it is already there, else ask the server for that ONE message — a shared
// link's root is usually OLDER than the loaded page and nothing pages it in, which
// is why the old poll could only ever time out on exactly the links that matter.
// Idempotent + guard-safe: a no-op when that thread is already open or the id is empty.
export async function openThreadById(rootId: string): Promise<void> {
  if (!rootId || threadState.parentId === rootId) return;
  // A root lives in whichever timeline is loaded — channel OR DM (a DM thread is a
  // thread too: Shared Files / `?thread=` route DM roots here as well, CYBORG-1050).
  let root =
    channelState.messages.find((m) => m.id === rootId) ??
    dmState.messages.find((m) => m.id === rootId) ??
    null;
  if (!root && workspaceState.current) {
    // hydrateMessages for the same reason openThread hydrates its replies: the fetch
    // carries RAW reaction rows and the renderer wants grouped ones.
    const fetched = await client
      .fetchMessage(
        workspaceState.channelWorkspaceId(channelState.activeId) ?? workspaceState.current.id,
        rootId,
      )
      .catch(() => ({ message: null }));
    root = fetched.message ? (hydrateMessages([fetched.message])[0] ?? null) : null;
  }
  if (root) {
    // The root can belong to a conversation that is NOT the open one. The pane is
    // self-contained (it fetches its own replies by parentId, and sendThreadReply
    // routes off the root's own channelId / DM counterparty), so open it and leave
    // the timeline where the user is instead of moving them somewhere they didn't ask
    // to go.
    await openThread(root);
    return;
  }
  // Leave the `?m` scroll as-is — no empty pane, no crash — but never silently: the
  // same toast family as the other failed message actions below. Both halves of the
  // server's ONE not-found answer, since it deliberately does not say which.
  toast.error("Couldn't open that thread — that message no longer exists, or you can't see it.");
}

// The other party of a DM root, from the current user's perspective: if I sent
// the root, the peer is its toId; otherwise the peer is its sender. Through `isMe`
// and never `=== user.id` (the same call as the dm broadcast handler's peerId): a
// human holds TWO ids and the TRANSPORT picks which one a row carries, so a bare
// compare answers "someone else" for my own root and addresses the reply to me.
function dmCounterparty(root: import("../core/types.js").Message | null): string | null {
  if (!root) return null;
  return isMe(root.fromId, authState.user) ? (root.toId ?? null) : root.fromId;
}

// A REFUSED reply: nothing dispatched, nothing queued. `false` is the composer's
// cue to keep the typed text + the persisted draft (both composers clear only on
// a non-false return), and the toast reuses the agent-prompt path's failed-send
// copy rather than a second string for the same event.
// ponytail: one helper for every refusal reason — they all reduce to "nowhere to
// deliver this"; split the copy when a reason becomes individually actionable.
function refuseThreadReply(): false {
  toast.error("Couldn't send that message");
  return false;
}

// A refused DM send: the open peer's history is read again, so a viewer who may know she is erased gets the flag.
async function recheckPeerErased(peerId: string | undefined): Promise<void> {
  const wsId = workspaceState.current?.id;
  if (!wsId || !peerId) return;
  try {
    const page = await client.fetchDmMessages(wsId, peerId, { limit: 1 });
    if (dmState.activePeerId === peerId && workspaceState.current?.id === wsId)
      dmState.peerErased = page.peerErased === true;
  } catch (err) {
    reportUiFailure("app.recheckPeerErased", err); // best-effort: no ids; the composer stays as it was
  }
}

// True once the reply reached the transport OR the outbox (both "dispatched" —
// the outbox replays it after reconnect, so the draft is safe to clear).
export function sendThreadReply(
  text: string,
  mentions?: string[],
  attachments?: Attachment[],
  mentionPicks?: string[],
): boolean {
  const parentId = threadState.parentId;
  if (!workspaceState.current || !parentId) return refuseThreadReply();
  // A thread can be rooted in a channel OR a DM (human or agent). The parent's
  // channelId is the source of truth: null → DM thread, routed via cyborg:dm to
  // the conversation peer so the reply reaches both participants and threads.
  const parentChannelId = threadState.parent?.channelId ?? null;
  // For a DM thread the peer is the ROOT's own counterparty — the root is the only
  // authority for where its replies go (same precedence as sendThreadReplyTo). A
  // `?thread=` link can adopt a root from ANOTHER conversation, and activePeerId
  // then names a different person: it delivered the reply to them.
  const myId = authState.user?.id ?? "unknown";
  const dmPeerId = parentChannelId
    ? null
    : (dmCounterparty(threadState.parent) ?? dmState.activePeerId);
  // Reachable with a malformed root: no channelId AND no resolvable counterparty.
  if (!parentChannelId && (!dmPeerId || dmPeerId === ERASED_PEER)) return refuseThreadReply();
  // No channelState.activeId guard: parentChannelId IS "we have a channel to send
  // to", and requiring that channel be OPEN silently swallowed every reply to a
  // channel root adopted while a DM was on screen — no send, no outbox, no toast.
  const myName = myDisplayName();
  const clientMsgId = newClientMsgId();
  const localId = `local-${clientMsgId}`;
  threadState.replies = [
    ...threadState.replies,
    {
      id: localId,
      channelId: parentChannelId,
      fromId: myId,
      fromType: "human",
      fromName: myName,
      toId: parentChannelId ? null : dmPeerId,
      text,
      mentions: mentions ?? [],
      parentId,
      attachments: attachments ?? null,
      clientMsgId,
      seq: 0,
      createdAt: Date.now(),
    },
  ];
  // NOTE: do NOT optimistically bump the reply count here — the broadcast echo of
  // this very reply already bumps it (channel_message / dm handler), so bumping
  // here too double-counted the author's own replies (3 showed as 6).
  dispatchThreadReply({
    wsId: workspaceState.channelWorkspaceId(parentChannelId) ?? workspaceState.current.id,
    userId: myId,
    parentChannelId,
    dmPeerId,
    text,
    mentions,
    mentionPicks,
    attachments,
    parentId,
    clientMsgId,
  });
  return true;
}

// Root-parameterized reply for surfaces that render MANY threads at once (the
// Threads feed). Unlike sendThreadReply it does NOT read/write the global
// threadState singleton. The optimistic bubble is handed back via `onOptimistic`
// so the caller appends it to that card's own reply list. Transport reuses the same
// root-parameterized dispatchThreadReply (outbox fallback included).
export function sendThreadReplyTo(
  root: import("../core/types.js").Message,
  text: string,
  mentions: string[] | undefined,
  attachments: Attachment[] | undefined,
  onOptimistic: (msg: import("../core/types.js").Message) => void,
  mentionPicks?: string[],
): boolean {
  if (!workspaceState.current || !root) return refuseThreadReply();
  const parentChannelId = root.channelId ?? null;
  const myId = authState.user?.id ?? "unknown";
  // DM thread peer = the root's counterparty (feed has no active DM). The SAME
  // decision sendThreadReply makes — it was inlined here and drifted (bare id
  // compare), so both now share the one helper.
  const dmPeerId = parentChannelId ? null : dmCounterparty(root);
  if (!parentChannelId && (!dmPeerId || dmPeerId === ERASED_PEER)) return refuseThreadReply();
  const clientMsgId = newClientMsgId();
  onOptimistic({
    id: `local-${clientMsgId}`,
    channelId: parentChannelId,
    fromId: myId,
    fromType: "human",
    fromName: myDisplayName(),
    toId: parentChannelId ? null : dmPeerId,
    text,
    mentions: mentions ?? [],
    parentId: root.id,
    attachments: attachments ?? null,
    clientMsgId,
    seq: 0,
    createdAt: Date.now(),
  });
  dispatchThreadReply({
    wsId: workspaceState.current.id,
    userId: myId,
    parentChannelId,
    dmPeerId,
    text,
    mentions,
    mentionPicks,
    attachments,
    parentId: root.id,
    clientMsgId,
  });
  return true;
}

// One-shot fetch of a thread's full message set for an ARBITRARY root, without
// touching the global threadState singleton (openThread would clobber it + mark
// read). Used by the Threads feed to load each card's messages independently.
// `null` on failure — distinct from an empty thread, because the card must never
// mark read what it could not load.
// One message in full (a Threads-feed root that came as a preview, CYBORG-1198), or
// null when it cannot be read; the caller keeps what it had.
export async function fetchFullMessage(
  messageId: string,
): Promise<import("../core/types.js").Message | null> {
  if (!workspaceState.current) return null;
  try {
    const { message } = await client.fetchMessage(workspaceState.current.id, messageId);
    return message ? (hydrateMessages([message])[0] ?? null) : null;
  } catch (err) {
    reportUiFailure("threads.fetch_full_message", err, {
      workspaceId: workspaceState.current?.id,
      messageId,
    });
    return null;
  }
}

export async function fetchThreadMessages(
  parentId: string,
): Promise<import("../core/types.js").Message[] | null> {
  if (!workspaceState.current) return null;
  try {
    const res = await client.fetchThread(workspaceState.current.id, parentId);
    // Same hydration as openThread: raw reaction rows keyed by emoji would render as
    // duplicate `{#each}` keys the moment two people react alike.
    return hydrateMessages(res.messages);
  } catch {
    // Non-fatal: the card shows its root + composer even if replies fail to load.
    return null;
  }
}

// Send a thread reply over the wire, falling back to the persistent outbox
// (#502) when the socket is down OR open-but-unauthenticated (the post-reconnect
// auth race, e.g. the desktop app restarting for an auto-update). Previously the
// reply was DROPPED — the optimistic bubble was removed and it never replayed, so
// thread messages vanished on restart while channel/DM messages survived. Now,
// exactly like dispatchSend for channel sends, an unauthenticated socket is
// treated as a failure and the reply is queued (carrying parentId) so it replays
// after reconnect. The optimistic bubble is KEPT — the broadcast echo reconciles
// it by clientMsgId (#501), and the parentId echo branch dequeues the record.
// Split out of sendThreadReply to keep that function under the complexity budget.
function dispatchThreadReply(opts: {
  wsId: string;
  userId: string;
  parentChannelId: string | null;
  dmPeerId: string | null;
  text: string;
  mentions?: string[];
  mentionPicks?: string[];
  attachments?: Attachment[];
  parentId: string;
  clientMsgId: string;
}): void {
  sendThroughOutbox(
    {
      clientMsgId: opts.clientMsgId,
      userId: opts.userId,
      kind: opts.parentChannelId ? "channel" : "dm",
      workspaceId: opts.wsId,
      // sendThreadReply's guard guarantees exactly one of parentChannelId / dmPeerId is set.
      targetId: opts.parentChannelId ?? opts.dmPeerId!,
      text: opts.text,
      mentions: opts.mentions ?? null,
      mentionPicks: opts.mentionPicks ?? null, // null: OutboxRecord field, nothing picked
      attachments: opts.attachments ?? null,
      parentId: opts.parentId,
      createdAt: Date.now(),
    },
    () =>
      sendThreadReplyOverWire({
        wsId: opts.wsId,
        parentChannelId: opts.parentChannelId,
        dmPeerId: opts.dmPeerId,
        text: opts.text,
        mentions: opts.mentions,
        mentionPicks: opts.mentionPicks,
        attachments: opts.attachments,
        parentId: opts.parentId,
        clientMsgId: opts.clientMsgId,
      }),
  );
}

// Route a thread reply to the channel or DM transport. Split out of
// sendThreadReply to keep that function under the complexity budget.
function sendThreadReplyOverWire(opts: {
  wsId: string;
  parentChannelId: string | null;
  dmPeerId: string | null;
  text: string;
  mentions?: string[];
  mentionPicks?: string[];
  attachments?: Attachment[];
  parentId: string;
  clientMsgId: string;
}): void {
  if (opts.parentChannelId) {
    client.sendMessage(
      opts.wsId,
      opts.parentChannelId,
      opts.text,
      opts.mentions,
      opts.attachments,
      opts.parentId,
      opts.clientMsgId,
      undefined,
      undefined,
      Date.now(),
      opts.mentionPicks,
    );
  } else if (opts.dmPeerId) {
    client.sendDm(
      opts.wsId,
      opts.dmPeerId,
      opts.text,
      opts.attachments,
      opts.parentId,
      opts.clientMsgId,
      undefined,
      Date.now(),
    );
  }
}

// Peers whose DM window has actually been fetched into dmState this session.
// Deliberately a plain Set and NOT $state — selectDm reads it inside the route
// $effect, and a reactive read there would re-trigger the effect on every write.
// Only ever consulted together with `dmState.activePeerId === peerId`, so a
// stale entry for a peer you navigated away from can never short-circuit a
// genuine re-open.
const loadedDmPeers = new Set<string>();

// knowledge/117: an alert into the DM that is ALREADY open (the re-entry selectDm
// short-circuits), whose target is above the loaded window, still lands — re-read
// around it instead of walking pages back. No-op for any other open.
async function refocusOpenDm(peerId: string, focusMessageId: string | undefined): Promise<void> {
  if (!focusMessageId) return;
  if (dmState.activePeerId !== peerId || !loadedDmPeers.has(peerId)) return;
  if (dmState.messages.some((m) => m.id === focusMessageId)) return;
  await loadDmMessagesAround(focusMessageId);
}

// The teardown of selectDm (canon:PAIR-EVERY-SETUP), run by the surface that showed the DM
// when it goes away. A peer left as `activePeerId` reads as "on screen": its DMs never badge
// and the live read advance marks them read on the server.
const dmVisits = accountSync.own("dmVisits", new DmVisitCounter());
export function currentDmVisit(): number {
  return dmVisits.current;
}

export function leaveDm(peerId: string, visit: number = dmVisits.current): void {
  // The rows leave with the visit: a re-open drew them before selectDm ran, and they took
  // the open and its scroll (#4377).
  const wsId = workspaceState.current?.id;
  const rows = dmState.messages.some(
    (m) => !!wsId && cache.messageBelongsToDm(m, wsId, peerId, viewerIsMe),
  );
  if (visit === dmVisits.current && rows) dmState.messages = [];
  if (dmState.activePeerId !== peerId) return;
  cancelActiveConversationBadgeClear();
  flushLiveReadAdvance();
  dmState.activePeerId = null;
}

// markDmRead sends nothing while disconnected: a local-only clear would hide rows the server still
// counts. The relay's activity_read_changed clears them once it has the read.
function clearDmActivityLocally(peerId: string): void {
  if (connectionState.ready) activityState.markReadByDmPeer(peerId);
}

export async function selectDm(
  peerId: string,
  opts: SelectConversationOptions = NO_FOCUS,
): Promise<void> {
  const focusMessageId = opts.focusMessageId;
  // CYBORG-688: "new" is the compose-new-DM placeholder route segment, never a
  // real peer — bail before it becomes activePeerId (which the focus/remember-
  // last-chat paths would then persist as a peer). Defense in depth with the
  // write-side guards in pg.markDmRead / opened-dms. Self-DM is handled below.
  if (peerId === "new") return;
  // The relay's erased mask is not a conversation: it would pool every erased person in one pane.
  if (peerId === "__erased__") return;
  // No workspace selected yet (e.g. fresh login before the layout finishes
  // restoring) — bail WITHOUT stranding activePeerId. The DM route effect also
  // depends on workspaceState.current, so it re-runs and fetches once the
  // workspace is set. (Previously this set activePeerId then returned, and the
  // guard below blocked any retry → the DM stayed permanently empty after a
  // fresh login.)
  if (!workspaceState.current) return;
  // Synchronous, so the surface that called reads its own visit; past the bails that open nothing.
  dmVisits.bump();
  // Already selected this peer — don't refetch. (Must NOT also key this on
  // messages.length/loading: this runs inside the route $effect, which then
  // tracks those reads, so an empty or just-loaded DM would re-trigger the
  // effect and fetch again in a loop. The strand that needed a "refetch when
  // empty" escape hatch is already handled — we bail above before setting
  // activePeerId, and the effect re-runs when workspaceState.current is set.)
  // ...but only once it has actually LOADED. Keying purely on activePeerId
  // latched a FAILED first load permanently: selectDm set activePeerId, its 3
  // attempts failed, and every later call — including the route effect's own
  // re-runs — bailed here onto an empty pane, so the DM could only ever be
  // rescued by an unrelated later reconnect (see the `connection` handler).
  // `loadedDmPeers` is a plain Set, NOT $state, on purpose: this runs inside the
  // route $effect, so a reactive read here would re-trigger the effect and loop
  // (which is exactly why messages.length must not be read here either).
  await refocusOpenDm(peerId, focusMessageId);
  if (dmState.activePeerId === peerId && loadedDmPeers.has(peerId)) return;
  // Switching to a DIFFERENT DM defuses any pending focus-driven clear so the DM
  // being left isn't marked read (re-selecting the SAME peer above returns early,
  // so a pure refocus on the open DM still clears it). See scheduleActiveConversationBadgeClear.
  cancelActiveConversationBadgeClear();
  // CYBORG-767: flush a pending live-read-advance for the conversation being LEFT
  // (a message seen <1.5s ago) so its cursor advances past those seen messages —
  // otherwise re-opening freezes a stale cursor → a phantom "New messages" divider.
  flushLiveReadAdvance();
  dmState.activePeerId = peerId;
  dmState.peerErased = false; // the history read below says
  dmState.readPeerId = null; // and it is read again for this visit
  // CYBORG-698: opening a DM must NOT re-rank it. The DM list orders by MESSAGE
  // recency (inbound bumps in the DM handler, outbound in sendDmMessage), not by
  // "last viewed" — bumping activity here made every opened conversation jump to
  // the top, which is disorienting when you're just reading. Selecting a DM only
  // changes what's active + adds it to the opened set; it never moves it.
  // Opening a DM adds it to the sidebar's opened-conversations list (Slack-style).
  openedDmsState.add(workspaceState.current.id, peerId);
  // Opening a DM from anywhere (search, +, a name tag, a mention, a profile) un-hides it.
  showDm(workspaceState.current.id, peerId);
  // Remember this DM as the workspace's last open chat so return/reload restores
  // it (mirrors selectChannel's rememberLastChannel).
  rememberLastChat(workspaceState.current.id, { kind: "dm", id: peerId });
  // Cache-first cold render (every surface — additive/safe). HIT → paint cached
  // window synchronously, no skeleton; MISS → null, exact prior behavior (clear).
  // On a peer SWITCH always clear first so the previous peer's messages never
  // bleed into the new peer's view — mergeServer is additive (deduplicates by id
  // only), so without the clear, peer A's messages survive alongside peer B's on
  // a cache-HIT switch. Mirrors selectChannel which uses setMessages (full replace)
  // on its HIT path. Same-peer re-entry returns early above, so this is always a
  // different peer when we reach here.
  dmState.messages = [];
  dmState.fetchedTailId = undefined;
  // With unreads the cached window lacks the rows that made it unread: painting it handed the
  // open (and its scroll) to a window the around read then walked away from (#4377, as #4376).
  const hasUnread = notificationState.getCount(workspaceState.current.id, peerId) > 0;
  const cachedDm = hasUnread ? null : coldPeekDm(peerId); // null: coldPeekDm's "no window"
  if (cachedDm) dmState.mergeServer(cachedDm);
  if (!hasUnread) reapplyOutboxBubbles("dm", peerId); // else the read reapplies them
  // CYBORG-988: pick the read shape from the window just painted, BEFORE the
  // notification clear below zeroes the unread count this decision reads.
  const plan = planDmOpen(peerId, cachedDm);
  dmState.hasMore = false;
  // Drop the previous DM's in-conversation typing line on switch.
  dmState.clearTyping();
  channelState.activeId = null;
  // Item 12: freeze the DM divider from the server read-cursor BEFORE this
  // visit's mark-read advances it (mirrors selectChannel). selectDm already
  // bailed above when no workspace is set, so workspaceState.current is non-null.
  //
  // CYBORG-767 regression fix: use the frozen cursor (→ the "New messages" divider
  // AND the around-window fetch below) ONLY when this DM actually HAS unreads.
  // `readCursorState.getDm` is non-null for EVERY previously-read DM, so gating on
  // "frozen != null" opened every read DM AROUND a possibly-STALE cursor (read on
  // another device, own messages sent elsewhere, an un-hydrated local cursor) —
  // stranding it in old history behind a phantom divider, slow, sometimes flashing
  // empty. The unread COUNT (seeded from server dmCounts + live increments) is the
  // cross-device truth: 0 ⇒ null frozen ⇒ the fast tail read + open at the bottom,
  // exactly matching "no badge means I read it, take me to the latest message".
  const frozen = dmUnreadCursorAtOpen(workspaceState.current.id, peerId);
  dmState.setUnreadCursor(frozen);
  notificationState.clear(workspaceState.current.id, peerId);
  // Persist DM read-state server-side (cross-device unread sync + frozen-divider
  // basis, Item 12). The relay also clears this DM's Activity items and
  // broadcasts back; mark optimistically here so the activity badge clears now.
  const readAt = Date.now();
  client.markDmRead(workspaceState.current.id, peerId, readAt);
  clearDmActivityLocally(peerId);
  // Advance the LOCAL cursor to now so a later re-open snapshots the new value,
  // not the frozen copy this visit's divider holds (monotonic).
  readCursorState.advanceDm(peerId, readAt);
  // On a cache HIT we already painted — keep the spinner off so there's no flash.
  // On a MISS, show the spinner exactly as before.
  dmState.loading = !cachedDm;
  const wsIdForFetch = workspaceState.current.id;
  // Cold-start resilience (#20 — intermittent "empty DM"). client.request()
  // waits for the socket to be OPEN *and* authenticated before sending, so a
  // throw here means a real failure: on a cold start the auth handshake +
  // first relay round-trip can blow the request timeout. The fetch was caught
  // and the DM left permanently blank — and the FIRST connect does NOT run
  // handleReconnect's DM self-heal (it's gated on a prior connection), so
  // nothing recovered it. Retry a few times with backoff, keeping the spinner
  // up (never flash the "beginning of your history" empty state on a transient
  // failure), and bail if the user moved to another peer/workspace meanwhile.
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      // CYBORG-988 delta read, with the CYBORG-767 around-window read (so the DM
      // opens at the "New messages" divider) and the plain tail as the fallbacks —
      // mirrors selectChannel, see fetchDmForPlan.
      const { page, usedDelta } = await fetchDmForPlan(
        wsIdForFetch,
        peerId,
        plan,
        frozen,
        focusMessageId,
      );
      const { messages, hasMore, hasMoreNewer } = page;
      if (dmState.activePeerId === peerId) {
        dmState.peerErased = page.peerErased === true;
        dmState.readPeerId = peerId;
        // Loaded — the guard at the top may now short-circuit re-entry. Marked
        // ONLY here, so a failed load stays retryable.
        loadedDmPeers.add(peerId);
        reportMissingFocusTarget(focusMessageId, messages);
        // mergeServer is already a union by id (and filters to the active peer),
        // so the delta rows splice onto the painted cache with no extra guard.
        dmState.mergePage(hydrateMessages(messages), peerId);
        // A delta says nothing about older history and ends at the tail by
        // construction — see deltaHasMore for why a full cached window means more.
        if (usedDelta) {
          dmState.hasMore = deltaHasMore(dmKey(wsIdForFetch, peerId), cachedDm);
          dmState.hasMoreNewer = false;
        } else {
          dmState.hasMore = hasMore;
          dmState.hasMoreNewer = hasMoreNewer ?? false;
        }
        // #502: re-show any still-pending persisted-outbox bubble for this DM
        // after the fetch merge. No-op when none pending.
        reapplyOutboxBubbles("dm", peerId);
        // Writeback the authoritative window + stamp cursors (only on SUCCESS; not
        // a window read around an alert's target).
        cacheWriteDm(wsIdForFetch, peerId, !usedDelta, focusMessageId);
      }
      break;
    } catch (err) {
      // Stale retry guard: a fast switch A→B must not let A's retry clobber B.
      if (dmState.activePeerId !== peerId || workspaceState.current?.id !== wsIdForFetch) break;
      if (attempt === 2) {
        reportUiFailure("chat.select_dm_retries_exhausted", err, {
          workspaceId: wsIdForFetch,
          peerId,
          attempts: 3,
        });
        break;
      }
      await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
    }
  }
  // Only clear loading if we're still on this peer — a fast switch A→B must
  // not let A's resolution flip B's spinner off and flash the empty state.
  if (dmState.activePeerId === peerId) {
    dmState.loading = false;
    dmState.readPeerId = peerId; // answered, or given up on: the pane stops waiting either way
  }
}

export async function loadMoreDmMessages(): Promise<void> {
  if (!workspaceState.current || !dmState.activePeerId || !dmState.hasMore || dmState.loading)
    return;
  dmState.loading = true;
  const oldest = dmState.messages[0];
  // Captured before the await, like loadMoreMessages: a peer switch mid-fetch must
  // not let this page's `hasMore` or `loading` land on the peer now on screen.
  const peerId = dmState.activePeerId;
  try {
    const { messages, hasMore } = await client.fetchDmMessages(workspaceState.current.id, peerId, {
      before: oldest?.id,
      limit: 50,
    });
    if (dmState.activePeerId !== peerId) return;
    dmState.mergeServer(hydrateMessages(messages), peerId);
    dmState.hasMore = hasMore;
  } catch {
    // server may not support DM pagination yet
  } finally {
    if (dmState.activePeerId === peerId) dmState.loading = false;
  }
}

// b1 load-newer (CYBORG-768): the DM twin of loadNewerMessages. mergeServer
// dedups + sorts (its own peer guard mirrors appendMessages' channel guard), so
// the newer page slots in ascending; hasMoreNewer is set at the call site.
// knowledge/117: DM twin of loadMessagesAround — the focus loop's one-shot
// around read for a target outside the open DM's loaded window.
export async function loadDmMessagesAround(messageId: string): Promise<void> {
  if (!workspaceState.current || !dmState.activePeerId || dmState.loading) return;
  dmState.loading = true;
  const workspaceId = workspaceState.current.id;
  const peerId = dmState.activePeerId;
  try {
    const { messages, hasMore, hasMoreNewer } = await client.fetchDmMessages(workspaceId, peerId, {
      aroundId: messageId,
    });
    if (workspaceState.current?.id === workspaceId && dmState.activePeerId === peerId) {
      dmState.messages = [];
      dmState.mergePage(hydrateMessages(messages), peerId);
      dmState.hasMore = hasMore;
      dmState.hasMoreNewer = hasMoreNewer ?? false;
      reapplyOutboxBubbles("dm", peerId);
    }
  } catch (err) {
    reportUiFailure("loadDmMessagesAround", err, { peerId });
  } finally {
    if (dmState.activePeerId === peerId) dmState.loading = false;
  }
}

export async function loadNewerDmMessages(): Promise<void> {
  if (!workspaceState.current || !dmState.activePeerId || !dmState.hasMoreNewer || dmState.loading)
    return;
  dmState.loading = true;
  const after = newerPageCursor(dmState.fetchedTailId, dmState.messages);
  const workspaceId = workspaceState.current.id;
  const peerId = dmState.activePeerId;
  try {
    const { messages, hasMoreNewer } = await client.fetchDmMessages(workspaceId, peerId, {
      after,
      limit: 50,
    });
    // A tail replace (loadLatestDmMessages) that landed meanwhile moved the cursor: this
    // page now belongs above a gap, and the window already reaches the tail.
    const stillAtCursor = dmState.fetchedTailId === undefined || dmState.fetchedTailId === after;
    if (
      stillAtCursor &&
      workspaceState.current?.id === workspaceId &&
      dmState.activePeerId === peerId
    ) {
      dmState.mergePage(hydrateMessages(messages), peerId);
      dmState.hasMoreNewer = hasMoreNewer ?? false;
    }
  } catch (err) {
    reportUiFailure("loadNewerDmMessages", err, { peerId });
  } finally {
    if (dmState.activePeerId === peerId) dmState.loading = false;
  }
}

// b1 "jump to recents" (CYBORG-768): the DM twin of loadLatestMessages —
// re-fetch the real tail window (newest-N, no cursor) so jumpToBottom lands on
// the actual latest DM, not the loaded scrollHeight.
export async function loadLatestDmMessages(): Promise<void> {
  if (!workspaceState.current || !dmState.activePeerId) return;
  const workspaceId = workspaceState.current.id;
  const peerId = dmState.activePeerId;
  const seqAtRequest = dmState.lastSeq;
  try {
    const { messages, hasMore } = await client.fetchDmMessages(workspaceId, peerId, { limit: 50 });
    if (workspaceState.current?.id === workspaceId && dmState.activePeerId === peerId) {
      // Full replace, mirroring loadLatestMessages. setMessages is channel-only;
      // for DMs we clear then merge the authoritative tail so a stale higher-up
      // window can't linger above it. Rows the read could not have seen (the user's
      // own send, settled or not, and live arrivals) survive the replace in this same
      // synchronous update — see rowsTheTailMissed.
      const tail = hydrateMessages(messages);
      const carried = rowsTheTailMissed(dmState.messages, tail, seqAtRequest);
      dmState.messages = [];
      dmState.mergePage(tail, peerId);
      for (const m of carried) dmState.addMessage(m);
      dmState.hasMore = hasMore;
      dmState.hasMoreNewer = false;
    }
  } catch (err) {
    reportUiFailure("loadLatestDmMessages", err, { peerId });
  }
}

function dispatchDmSend(
  localId: string,
  wsId: string,
  peerId: string,
  text: string,
  attachments: Attachment[] | undefined,
  clientMsgId: string,
  // fix/pages-attach-not-send: see dispatchSend's pageRef param — same
  // not-persisted-to-the-offline-outbox caveat applies here.
  pageRef?: string,
): void {
  // The kept copy too (Retry after reload); a fresh send's is made by keepParkedBubble below.
  setBubbleStatus("dm", localId, "pending");
  armSendWatchdog(localId, "dm", peerId);
  keepParkedBubble("dm", localId);
  sendThroughOutbox(
    {
      clientMsgId,
      userId: authState.user?.id ?? "unknown",
      kind: "dm",
      workspaceId: wsId,
      targetId: peerId,
      text,
      mentions: null,
      attachments: attachments ?? null,
      parentId: null,
      createdAt: Date.now(),
      bubbleAt: openSendRow("dm", localId)?.row.createdAt,
    },
    () =>
      client.sendDm(wsId, peerId, text, attachments, undefined, clientMsgId, pageRef, Date.now()),
  );
}

export function sendDmMessage(
  text: string,
  attachments?: Attachment[],
  // fix/pages-attach-not-send: see sendMessage's pageCard param.
  pageCard?: PageCard,
): void {
  if (!workspaceState.current || !dmState.activePeerId) return;
  const wsId = workspaceState.current.id;
  const peerId = dmState.activePeerId;
  const myId = authState.user?.id ?? "unknown";
  const myName = myDisplayName();
  // #17 recency is NOT recorded here. The send's own echo comes back through the
  // `dm` handler carrying the relay's `createdAt`, which is the clock every other
  // writer uses; bumping here as well used the CLIENT clock, so a machine running
  // fast stamped the peer in the future and dmActivityState's monotonic guard then
  // discarded every genuinely newer inbound bump until wall-clock caught up.
  // Add the optimistic message FIRST, then send — so a failed send can roll it
  // back. (Previously the send ran first, so on failure the user saw nothing.)
  const clientMsgId = newClientMsgId();
  const localId = `local-${clientMsgId}`;
  // After a deep jump the window is an around-slice with newer rows unloaded: appending here would
  // land the bubble below a gap. Load the true tail (the Alerts cybo view does the same).
  const deepJump = dmState.hasMoreNewer;
  const optimistic: Parameters<typeof dmState.addMessage>[0] = {
    id: localId,
    channelId: null,
    fromId: myId,
    fromType: "human",
    fromName: myName,
    toId: peerId,
    text,
    attachments: attachments ?? null,
    ...(pageCard ? { card: pageCard } : {}),
    sendStatus: "pending",
    clientMsgId,
    seq: 0,
    createdAt: bubbleTime(dmState.messages, Date.now()),
  };
  // Decided before it is drawn: the cybo's live agent will hold it, so it goes to the queue bar
  // (the same hold its queued signal sets; its verdict releases it, a failure shows the row).
  // The session the DM routes to (the same pick the agent route makes), and only that one.
  const routed = defaultDmSessionForCybo(filterMine(workspaceState.agents, authState.user), peerId);
  const holder = routed && agentStreamState.sendWouldQueue(routed.agentId) ? routed : undefined;
  if (holder) {
    mentionQueueState.hold(`c:${clientMsgId}`, holder.agentId);
    heldDmReload.remember(clientMsgId, holder.agentId);
  }
  dmState.addMessage(optimistic);
  dispatchDmSend(localId, wsId, peerId, text, attachments, clientMsgId, pageCard?.pageId);
  if (deepJump) void loadLatestDmMessages();
}

// Re-attempt a failed optimistic DM send. Reuses the SAME local row AND its
// original clientMsgId (#501), resetting it to "pending" + re-arming the
// watchdog. Mirrors retrySendMessage for the channel surface.
export function retrySendDmMessage(localId: string): void {
  if (!workspaceState.current || !dmState.activePeerId) return;
  const msg = dmState.messages.find((m) => m.id === localId);
  if (!msg || !localId.startsWith("local-")) return;
  const wsId = workspaceState.current.id;
  const peerId = msg.toId ?? dmState.activePeerId;
  if (!peerId) return;
  const clientMsgId = msg.clientMsgId ?? localId.replace(/^local-/, "");
  followRetriedSend(localId);
  dispatchDmSend(
    localId,
    wsId,
    peerId,
    msg.text,
    msg.attachments ?? undefined,
    clientMsgId,
    msg.card?.kind === "page" ? msg.card.pageId : undefined,
  );
}

// parentId (#11 thread-typing): tag the event with the thread root so receivers
// route the indicator to the open thread panel. Rides along on channel OR DM typing.
export function sendTypingIndicator(parentId?: string, workSessionId?: string): void {
  const ws = workspaceState.current;
  if (!ws) return;
  // Session first: it can be open over a selected channel and has no DM peer.
  if (workSessionId) {
    client.sendTyping(
      ws.id,
      channelState.activeId ?? undefined,
      undefined,
      undefined,
      workSessionId,
    );
    return;
  }
  if (channelState.activeId) {
    client.sendTyping(
      workspaceState.channelWorkspaceId(channelState.activeId) ?? ws.id,
      channelState.activeId,
      undefined,
      parentId,
    );
    return;
  }
  // DM: route typing to the peer. Skip self-DM (never send typing to yourself).
  const peerId = dmState.activePeerId;
  if (peerId && peerId !== authState.user?.id) {
    client.sendTyping(ws.id, undefined, peerId, parentId);
  }
}

export async function toggleReaction(messageId: string, emoji: string): Promise<void> {
  if (!workspaceState.current) return;
  // A message that hasn't settled yet still carries this client's placeholder id, and
  // the relay has no row under it: it answers "message not found", and the rollback
  // below would toast an error at a user who did nothing wrong. The id flips to the
  // server's on the send ack, so the honest behaviour for that sliver is nothing at all.
  if (messageId.startsWith("local-")) return;
  const viewer = authState.user;
  if (!viewer?.id) return;
  const me = { id: viewer.id, kind: "user" as const, name: myDisplayName() };

  // The state to return to if the write is refused, read BEFORE the optimistic patch.
  const current =
    [
      threadState.parent,
      ...channelState.messages,
      ...dmState.messages,
      ...threadState.replies,
    ].find((m) => m?.id === messageId)?.reactions ?? rowReactions.get(messageId);
  const wasReacted = (normalizeReactions(current) ?? []).some(
    (r) => r.emoji === emoji && reactedByMe(r, viewer),
  );
  if (!wasReacted && !canAddReaction(current, emoji)) {
    toast.error(
      singleEmoji(emoji) === emoji
        ? "This message can't take any more reactions"
        : "A reaction must be a single emoji",
    );
    return;
  }

  // Absolute on MY entry only (never a snapshot revert): others' reactions survive.
  const applyMine = (on: boolean) =>
    patchReactions(messageId, (reactions) => setMyReaction(reactions, emoji, on, me, viewer));

  applyMine(!wasReacted);

  try {
    const action = await client.sendReaction(
      workspaceState.channelWorkspaceId(channelState.activeId) ?? workspaceState.current.id,
      messageId,
      emoji,
    );
    // Reconcile against the relay's post-write truth. `null` = the peer acked but
    // has no authoritative answer (local daemon) — keep the optimistic guess.
    if (action) applyMine(action === "added");
  } catch (err) {
    // Refused or never sent: put my entry back and say so.
    applyMine(wasReacted);
    reportUiFailure("app.toggleReaction", err);
    toast.error("Couldn't react to message");
  }
}

type ChatMsg = import("../core/types.js").Message;

/** The PRE-patch values of exactly the fields a patch rewrote — a field-scoped undo. */
function changedFieldsBefore(before: ChatMsg, after: ChatMsg): Partial<ChatMsg> {
  const fields: Partial<ChatMsg> = {};
  for (const key of Object.keys({ ...before, ...after }) as (keyof ChatMsg)[]) {
    if (before[key] !== after[key]) Object.assign(fields, { [key]: before[key] });
  }
  return fields;
}

/**
 * Patch ONE message in every store that renders it — the channel timeline, the
 * open DM, and the thread pane (ThreadPanel renders `threadState.replies`, not
 * the two stores above it; toggleReaction, editMessage and the delete_message /
 * reaction / unfurl broadcast handlers each had to learn that separately) — and
 * hand back the UNDO.
 *
 * `patch` returning a new object replaces the row (edit); returning null
 * removes it (delete). The undo puts back only the FIELDS that patch rewrote,
 * onto the row as it stands then; a removed row has none to write into, so the
 * delete path re-inserts the whole pre-patch copy at its sorted position through
 * the same `insertMessageSorted` the live-message path uses. Either way only the
 * stores that actually held it are touched, and only while they still render the
 * conversation they held it IN: `found` is a fact about the past, so a re-insert
 * 15s later has to re-check the channel / DM peer / thread root, or a #general
 * message materializes inside whatever #random the user switched to meanwhile.
 *
 * This is the rollback seam the two async message mutations below share.
 * pinMessage / saveMessage / toggleSavedById already revert on `.catch`; edit
 * and delete did not, so a rejected request left this screen — and only this
 * screen — believing something that never happened.
 */
function patchMessageOptimistically(
  messageId: string,
  patch: (m: ChatMsg) => ChatMsg | null,
): () => void {
  // ponytail: last-writer-wins PER FIELD. Two writes to the SAME field (two edits
  // of one message overlapping in flight) still let the loser's revert put the
  // pre-patch text over the winner's; the edit_message broadcast reconciles it.
  // Add a per-message revision if that race ever shows up for real.
  let snapshot: ChatMsg | undefined;
  // The pre-patch values of exactly the fields the patch rewrote — null when it
  // removed the row instead (delete), which has no live row to merge back into.
  let undoFields: Partial<ChatMsg> | null = null;
  const apply = (messages: ChatMsg[]): { next: ChatMsg[]; found: boolean } => {
    let found = false;
    const next: ChatMsg[] = [];
    for (const m of messages) {
      if (m.id !== messageId) {
        next.push(m);
        continue;
      }
      found = true;
      const patched = patch(m);
      if (snapshot === undefined) {
        snapshot = m;
        undoFields = patched ? changedFieldsBefore(m, patched) : null;
      }
      if (patched) next.push(patched);
    }
    return { next, found };
  };

  const channel = apply(channelState.messages);
  const dm = apply(dmState.messages);
  const thread = apply(threadState.replies);
  channelState.messages = channel.next;
  dmState.messages = dm.next;
  threadState.replies = thread.next;
  // WHICH conversation each store was rendering when the patch landed. `found` says
  // the row lived there THEN; the revert runs whenever the request settles, by which
  // time the user may have switched channel, DM or thread underneath it.
  const patchedChannelId = channelState.activeId;
  const patchedDmPeerId = dmState.activePeerId;
  const patchedThreadRootId = threadState.parentId;

  return () => {
    const original = snapshot;
    if (!original) return;
    // Field-scoped: put back what THIS patch rewrote, onto the row as it stands now.
    // A reaction, an unfurl or a re-rendered card can land on the same row while the
    // request is in flight (those handlers patch other fields in place) — restoring
    // the whole pre-patch snapshot would silently drop them, the way setMyReaction's
    // rollback deliberately doesn't.
    const undo = undoFields;
    const back = (m: ChatMsg): ChatMsg => (undo ? { ...m, ...undo } : original);
    const restore = (messages: ChatMsg[]): ChatMsg[] => {
      // Gone from a store that held it: only a delete re-inserts. An edit whose row
      // was removed mid-flight must not resurrect it.
      if (!messages.some((m) => m.id === messageId)) {
        return undo ? messages : insertMessageSorted(messages, original);
      }
      return messages.map((m) => (m.id === messageId ? back(m) : m));
    };
    if (channel.found && channelState.activeId === patchedChannelId) {
      channelState.messages = restore(channelState.messages);
    }
    if (dm.found && dmState.activePeerId === patchedDmPeerId) {
      dmState.messages = restore(dmState.messages);
    }
    if (thread.found && threadState.parentId === patchedThreadRootId) {
      threadState.replies = restore(threadState.replies);
    }
  };
}

// `mentionPicks`: ids newly picked from the @-autocomplete in the edit (added server-side).
export async function editMessage(
  messageId: string,
  newText: string,
  mentionPicks?: string[],
): Promise<void> {
  if (!workspaceState.current) return;

  const revert = patchMessageOptimistically(messageId, (m) => ({
    ...m,
    text: newText,
    updatedAt: Date.now(),
  }));

  try {
    await client.editMessage(
      workspaceState.channelWorkspaceId(channelState.activeId) ?? workspaceState.current.id,
      messageId,
      newText,
      mentionPicks,
    );
  } catch (err) {
    // The server rejected the edit, so everyone else still sees the original
    // text. Put the original back here too — a screen that keeps showing the
    // new wording is the one lying — and say what happened.
    revert();
    reportUiFailure("app.editMessage", err);
    // A refusal of the text itself names its reason: retrying the same edit can't help.
    const reason = err instanceof Error ? contentRefusalReason(errorCodeOf(err), err.message) : undefined; // prettier-ignore
    toast.error(`Couldn't save your edit — ${reason ?? "the message is unchanged. Try again."}`);
  }
}

export async function deleteMessage(messageId: string): Promise<void> {
  if (!workspaceState.current) return;

  const revert = patchMessageOptimistically(messageId, () => null);

  try {
    await client.deleteMessage(
      workspaceState.channelWorkspaceId(channelState.activeId) ?? workspaceState.current.id,
      messageId,
    );
  } catch (err) {
    // The server rejected the delete, so the message is still live for every
    // other member. Bring it back instead of leaving this screen the only one
    // that thinks it is gone.
    revert();
    reportUiFailure("app.deleteMessage", err);
    toast.error("Couldn't delete that message — it's still there. Try again.");
  }
}

export async function updateWorkspace(updates: {
  name?: string;
  avatarUrl?: string | null;
  settings?: import("../core/types.js").WorkspaceSettings;
}): Promise<void> {
  // The workspace asked about, not whichever is current when the answer lands: a switch
  // mid-request must not paint one workspace's settings onto another.
  const id = workspaceState.current?.id;
  if (!id) throw new Error("No workspace selected");
  await client.updateWorkspace(id, updates);
  patchWorkspaceRow(id, updates);
}

// The active workspace and its list copy can be different objects (refreshWorkspaceList
// replaces the list): patch both, or the sidebar and Settings disagree.
function patchWorkspaceRow(
  id: string,
  updates: {
    name?: string;
    avatarUrl?: string | null;
    settings?: import("../core/types.js").WorkspaceSettings;
  },
): void {
  const current = workspaceState.current?.id === id ? workspaceState.current : null; // null: switched away
  const listed = workspaceState.list.find((x) => x.id === id);
  const oldName = (current ?? listed)?.name;
  // The relay renamed the default department with it; the cache follows.
  if (updates.name && oldName) renameDefaultProjectInCache(id, oldName, updates.name);
  for (const w of [current, listed]) {
    if (!w) continue;
    if (updates.name) w.name = updates.name;
    if (updates.avatarUrl !== undefined) w.avatarUrl = updates.avatarUrl;
    if (updates.settings !== undefined) w.settings = updates.settings;
  }
}

client.on("workspace_updated", ({ workspaceId, name, avatarUrl, settings }) => {
  patchWorkspaceRow(workspaceId, { name, avatarUrl, settings });
});

export async function deleteWorkspace(workspaceId: string): Promise<void> {
  await client.deleteWorkspace(workspaceId);
  workspaceState.list = workspaceState.list.filter((w) => w.id !== workspaceId);
  if (workspaceState.current?.id === workspaceId) {
    const next = workspaceState.list[0];
    if (next) {
      await selectWorkspace(next);
    } else {
      workspaceState.current = null;
    }
  }
}

// App-Store Guideline 5.1.1(v): permanently delete the signed-in user's
// account. On success we tear down the saved session + all local state (via
// disconnectFromServer, which clears the saved session) and route to /login so
// the now-deleted session can't continue. Throws on failure so the UI can show
// an error and leave the user signed in.
export async function deleteAccount(
  handoffs?: WorkspaceHandoffs[],
  agents: HandoffAgent[] = [],
): Promise<void> {
  const failed = await client.deleteAccount(handoffs);
  if (failed.length > 0) {
    toast.warning(`Account deleted. ${failedMoveMessage(failed, agents)}`);
  }
  // clearQueuedSends: the account no longer exists, so a queued send can never
  // be delivered under it — keeping the rows would only leave undeliverable
  // work behind. This is a user-initiated, confirmed destructive action.
  disconnectFromServer({ clearQueuedSends: true });
  releaseLocalStorage(typeof localStorage === "undefined" ? null : localStorage); // null: SSR has no storage
  void goto("/login");
}

/** Explicit sign-out: end this device's session on the relay, then clear locally. */
export function signOut(): void {
  signOutOrdered({
    hasToken: () => !!authState.token,
    revoke: () => client.revokeCurrentDeviceSession(),
    clear: () => disconnectFromServer({ clearQueuedSends: true }),
    report: (err) => reportUiFailure("auth.sign_out_revoke", err),
  });
}

// Workspace member + invitation RPC facades — extracted to ./invitations.ts
// (knowledge/87 Phase 1). Re-exported here so importers keep hitting the app
// barrel. handleLicenseError/handleUpgradeError (above) are exported for the
// extracted inviteMember; the resulting app.svelte.ts ↔ invitations.ts cycle is
// eval-safe (both are runtime-only hoisted functions).
export {
  inviteMember,
  getOpenInvite,
  upsertOpenInvite,
  removeMember,
  updateMemberRole,
  listPendingInvitations,
  resendInvitation,
  cancelInvitation,
  getInvitePreview,
  askForNewInvite,
  acceptInvitation,
} from "./invitations.js";
export type { PendingInvitation } from "./invitations.js";
export type { PublicInvitePreview, InviteeArrival, InviteeLanding } from "../core/client.js";

// Corporate-email domain onboarding facades (CYBORG onboarding). Same barrel
// pattern as the invitation facades above — re-exported so the joiner flow
// (login/+page) and the admin Members panel import from the app state module.
export {
  requestWorkspaceJoin,
  listWorkspaceDomains,
  claimWorkspaceDomain,
  unclaimWorkspaceDomain,
  setWorkspaceJoinPolicy,
  listJoinRequests,
  approveJoinRequest,
  rejectJoinRequest,
} from "./domain-onboarding.js";
export type {
  JoinPolicy,
  WorkspaceEmailDomainView,
  WorkspaceJoinRequestView,
} from "./domain-onboarding.js";
// Live pending join-request store — seeded by the Members panel, kept fresh by
// the join_request_changed push handler below. Drives the admin approvals list
// + the Settings-tab pending badge.
export { joinRequestsState } from "./join-requests.svelte.js";

// ─── Agent actions ────────────────────────────────────────────────

// An in-flight fetch can predate the caller's change (a sign-in, an install), so a
// `fresh` caller waits for it and asks once more. Others keep the plain de-dupe.

/** Registers this daemon's fetch as in flight; the returned call settles it. */
function trackProviderFetch(daemonId: string): () => void {
  let settle: () => void = () => {};
  const mine = new Promise<void>((resolve) => (settle = resolve));
  const inFlight = providerState.fetchInFlight;
  inFlight.set(daemonId, mine);
  return () => {
    if (inFlight.get(daemonId) === mine) inFlight.delete(daemonId);
    settle();
  };
}

/** One shared re-fetch after the in-flight one, or undefined when none is in flight. */
function freshProviderFollowUp(daemonId: string, workspaceId?: string): Promise<void> | undefined {
  const inFlight = providerState.fetchInFlight.get(daemonId);
  if (!inFlight) return undefined;
  const followUps = providerState.freshFollowUp;
  let next = followUps.get(daemonId);
  if (!next) {
    next = inFlight.then(() => {
      followUps.delete(daemonId);
      return fetchProviders(daemonId, workspaceId);
    });
    followUps.set(daemonId, next);
  }
  return next;
}

/**
 * `workspaceId` overrides the VIEWED workspace for callers that do not have one
 * yet. Onboarding is the only such caller: it prefetches this catalog from the
 * install step, which runs BEFORE selectWorkspace() has set workspaceState.current,
 * so the default below would send no workspaceId at all. Omitting it is the exact
 * latent bug forward-workspace-target.ts documents (CYBORG-682) — an explicit id
 * keeps the relay's daemon-in-workspace resolution deterministic. Every other call
 * site passes one argument and is unchanged.
 */
export async function fetchProviders(
  daemonId?: string,
  workspaceId?: string,
  opts: { fresh?: boolean } = {},
): Promise<void> {
  const followUp =
    opts.fresh && daemonId ? freshProviderFollowUp(daemonId, workspaceId) : undefined;
  if (followUp) return followUp;
  const providerGeneration = providerState.generation;
  const selectedWorkspaceAtRequest = workspaceState.current?.id;
  const requestedWorkspaceId = workspaceId ?? selectedWorkspaceAtRequest;
  let settleInFlight: (() => void) | undefined;
  const requestStillRelevant = (): boolean => {
    if (providerState.generation !== providerGeneration) return false;
    if (!authState.user) return false;
    const currentWorkspaceId = workspaceState.current?.id;
    if (selectedWorkspaceAtRequest && currentWorkspaceId !== selectedWorkspaceAtRequest)
      return false;
    if (currentWorkspaceId && requestedWorkspaceId && currentWorkspaceId !== requestedWorkspaceId)
      return false;
    return true;
  };
  if (daemonId) {
    if (providerState.loadingDaemons[daemonId]) return; // already in flight for this daemon
    providerState.loadingDaemons = { ...providerState.loadingDaemons, [daemonId]: true };
    settleInFlight = trackProviderFetch(daemonId);
    // Stale-while-revalidate: serve the cached catalog for this daemon
    // immediately (instant provider rows on revisit) while the fetch below
    // refreshes it in the background.
    const cached = providerState.byDaemon[daemonId];
    if (cached) providerState.list = cached;
  }
  providerState.loading = true;
  try {
    // CYBORG-682: scope the fetch to the VIEWED workspace so the relay targets a
    // daemon in it (not the first-subscribed workspace). Undefined → relay keeps
    // its first-subscribed fallback (the key is dropped off the JSON frame).
    const list = await client.listProviders({
      daemonId,
      workspaceId: requestedWorkspaceId,
    });
    // A workspace switch/logout can complete while this daemon RPC is in flight.
    // Never let the old response repopulate the new account/workspace cache.
    if (!requestStillRelevant()) return;
    providerState.list = list;
    providerState.loadError = false;
    // Keep the per-daemon catalog so daemon-aware surfaces (agent session model
    // menu) don't read a catalog that an arbitrary daemon answered.
    if (daemonId) {
      providerState.byDaemon = { ...providerState.byDaemon, [daemonId]: list };
      // …and clear the per-daemon error alongside it, so this machine's answer
      // is never held hostage by another machine's failed probe.
      providerState.loadErrorDaemons = { ...providerState.loadErrorDaemons, [daemonId]: false };
    }
  } catch (err) {
    // Don't swallow: a failed list_providers is why the model picker collapses to
    // a disabled label. Logged so the failure is visible (observability rule) and
    // so it's clear the empty catalog is a fetch error, not an empty daemon.
    // console.error reaches NOTHING off the user's machine. This is the ONLY record
    // that a provider probe failed, and its invisibility is why the last incident took
    // days to surface — the model picker collapsed to a disabled label with no signal
    // anywhere we can query. Beaconed to /api/cyborg/client-log → logError("ui.client")
    // → telemetry. Through reportUiFailure, so the relay's code decides the kind: an
    // offline or silent machine (daemon_offline, daemon_timeout) and the client's own
    // timeout or dropped socket are transient, not_a_member a refusal, the rest a fault.
    reportUiFailure("app.fetchProviders", err, { daemonId });
    if (!requestStillRelevant()) return;
    providerState.list = [];
    // CYBORG-686 §7: mark it as "couldn't check", so the empty list never
    // masquerades as "this machine has no providers".
    providerState.loadError = true;
    // Scope the failure to the daemon it was aimed at. The global flag above is
    // shared by every machine, so consumers asking about ONE daemon must read
    // this map instead — a rejected probe for daemon B says nothing about A.
    // An UNtargeted fetch names no daemon and so marks none.
    if (daemonId) {
      providerState.loadErrorDaemons = { ...providerState.loadErrorDaemons, [daemonId]: true };
    }
  } finally {
    // clear() belongs to a newer workspace/account. Its new in-flight flags must
    // not be overwritten by this older request's cleanup.
    if (providerState.generation === providerGeneration) {
      providerState.loading = false;
      if (daemonId) {
        providerState.loadingDaemons = { ...providerState.loadingDaemons, [daemonId]: false };
      }
    }
    settleInFlight?.();
  }
}

export async function fetchRecentCwds(
  daemonId?: string,
): Promise<{ home: string; recent: string[] }> {
  return client.listRecentCwds(daemonId ? { daemonId } : undefined);
}

export async function createAgent(
  provider: string,
  cwd: string,
  opts?: {
    model?: string;
    systemPrompt?: string;
    channelId?: string;
    title?: string;
    daemonId?: string;
  },
): Promise<Agent> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  let agent: Agent;
  try {
    agent = await client.createAgent(workspaceState.current.id, provider, cwd, opts);
  } catch (err) {
    if (!handleLicenseError(err)) handleUpgradeError(err);
    throw err;
  }
  noteSessionCreatedHere(agent.agentId);
  // The create answer names no initiator; this client's viewer started it, so every "mine"
  // check (the Try a session tick) counts it now, not after the next list_agents.
  const me = authState.user;
  const row: Agent = me
    ? {
        ...agent,
        initiatedBy: agent.initiatedBy ?? me.id,
        initiatedByEmail: agent.initiatedByEmail ?? me.email,
      }
    : agent;
  workspaceState.agents = [...workspaceState.agents, row];
  return row;
}

// One roster load per workspace at a time: every screen that mounts calls fetchCybos, and
// each used to send its own read and start its own 8-try retry (CYBORG-1211).
interface CybosLoad {
  wsId: string;
  selection: number;
  promise: Promise<CybosLoadOutcome>;
  // A heal-only read leaves recovery to the healer's own retry: one retry owner.
  retryOnEmpty: boolean;
  // The heal reports the read it started (dm.surface_heal), and never joins one (its
  // pump waits while a read is out): one report owner per read.
  readonly startedByHeal: boolean;
}
let cybosLoad: CybosLoad | null = null;
let cybosRetryFor: string | null = null;
// Every cybo read takes a ticket; an answer lands only if no LATER read already has, so
// a slow, older snapshot can't remove a row a newer read put in.
let cybosReadsIssued = 0;
let cybosReadLanded = 0;

// fetchCybos stays fire-and-forget; the DM surface heal needs the failure and staleness.
type CybosLoadOutcome =
  | { status: "applied" }
  | { status: "stale" }
  | { status: "failed"; error: unknown };

export function fetchCybos(): Promise<void> {
  return loadCybosOnce().then(() => undefined);
}

function loadCybosOnce(owner: "shared" | "heal" = "shared"): Promise<CybosLoadOutcome> {
  if (!workspaceState.current) return Promise.resolve({ status: "stale" });
  const wsId = workspaceState.current.id;
  const selection = workspaceSelections;
  if (cybosLoad?.wsId === wsId && cybosLoad.selection === selection) {
    if (owner === "shared") cybosLoad.retryOnEmpty = true;
    return cybosLoad.promise;
  }
  const load: CybosLoad = {
    wsId,
    selection,
    promise: Promise.resolve({ status: "stale" }),
    retryOnEmpty: owner === "shared",
    startedByHeal: owner === "heal",
  };
  const promise = loadCybos(load).finally(() => {
    if (cybosLoad === load) cybosLoad = null;
    dmHealPump.pump(wsId);
  });
  load.promise = promise;
  cybosLoad = load;
  return promise;
}

// The relistAgents rule: a read of A landing after a switch to B neither replaces B's
// roster nor ends B's loading placeholder.
function cybosReadIsCurrent(wsId: string, selection: number): boolean {
  return (
    selection === workspaceSelections &&
    workspaceState.current?.id === wsId &&
    (activeWorkspaceSelection ?? wsId) === wsId
  );
}

function claimCybosAnswer(wsId: string, selection: number, ticket: number): boolean {
  if (!cybosReadIsCurrent(wsId, selection) || ticket < cybosReadLanded) return false;
  cybosReadLanded = ticket;
  return true;
}

async function loadCybos(load: CybosLoad): Promise<CybosLoadOutcome> {
  const { wsId, selection } = load;
  const ticket = ++cybosReadsIssued;
  cyboState.loading = true;
  let failed = false;
  let outcome: CybosLoadOutcome = { status: "applied" };
  try {
    const cybos = await client.fetchCybos(wsId);
    if (claimCybosAnswer(wsId, selection, ticket)) {
      cyboState.list = cybos;
      noteCyboAnswer(cybos);
      cyboState.loadError = false;
    } else {
      outcome = { status: "stale" };
    }
  } catch (error) {
    // intentional: a failed read keeps the roster shown; the retry below owns recovery.
    failed = true;
    outcome = { status: "failed", error };
    if (!load.startedByHeal) reportUiFailure("app.fetchCybos", error, { workspaceId: wsId });
    // Fail CLOSED: never blank the roster on a rejected fetch. A warm roster keeps showing what it
    // has; loadError is retryCybosOnEmpty's call, and only for the genuinely-empty case.
  } finally {
    if (cybosReadIsCurrent(wsId, selection)) cyboState.loading = false;
  }
  if (!cybosReadIsCurrent(wsId, selection)) return outcome;
  // Retry only a FAILED read of an empty roster: the relay answers a missing daemon with
  // the workspace's stored cybos, so an empty answer is the answer. One loop per workspace.
  if (!load.retryOnEmpty) return outcome;
  if (failed && cyboState.list.length === 0 && cybosRetryFor !== wsId) {
    cybosRetryFor = wsId;
    void retryCybosOnEmpty(wsId).finally(() => {
      if (cybosRetryFor === wsId) cybosRetryFor = null;
    });
  }
  return outcome;
}

const CYBOS_RETRY_ATTEMPTS = 8;

let rosterRowsFromServer = false;

// After this a missing cybo reads Deleted, as on trunk; one last read may correct it.
const CYBO_WAIT_MS = 30_000;
let cyboWaitTimer: ReturnType<typeof setTimeout> | undefined;
function startCyboWait(wsId: string): void {
  clearTimeout(cyboWaitTimer);
  beginCyboWait();
  const selection = workspaceSelections;
  cyboWaitTimer = setTimeout(() => {
    endCyboWait();
    if (cyboRosterMirror.mirrorOnly && cybosReadIsCurrent(wsId, selection)) healCyboRoster(wsId);
  }, CYBO_WAIT_MS);
}

// Silent self-heal: sets no loading, which would flash. Overlapping heals may run; the ticket keeps the newest.
function healCyboRoster(wsId: string): void {
  if ((cyboState.list.length > 0 && !cyboRosterMirror.mirrorOnly) || cyboState.loading) return;
  const selection = workspaceSelections;
  const ticket = ++cybosReadsIssued;
  void client
    .fetchCybos(wsId)
    .then((cybos) => {
      if (!claimCybosAnswer(wsId, selection, ticket)) return undefined;
      cyboState.loadError = false;
      if (cybos.length > 0) cyboState.list = cybos;
      noteCyboAnswer(cybos);
      return undefined;
    })
    .catch((err: unknown) => {
      reportUiFailure("app.healCybos", err, { workspaceId: wsId });
    });
}

async function retryCybosOnEmpty(workspaceId: string): Promise<void> {
  if (isOutsiderSeat(workspaceState.list, workspaceId)) return; // no roster to wait for
  // The single authority for cyboState.loadError on an empty roster: only a LAST rejected attempt flags it.
  const selection = workspaceSelections;
  let lastAttemptFailed = false;
  let lastError: unknown;
  for (let attempt = 0; attempt < CYBOS_RETRY_ATTEMPTS; attempt++) {
    const delay = Math.min(1000 * 1.5 ** attempt, 5000);
    await new Promise((resolve) => setTimeout(resolve, delay));
    if (!cybosReadIsCurrent(workspaceId, selection)) return;
    if (cyboState.list.length > 0 && !cyboRosterMirror.provisional) return;
    const ticket = ++cybosReadsIssued;
    try {
      const cybos = await client.fetchCybos(workspaceId);
      lastAttemptFailed = false;
      if (!claimCybosAnswer(workspaceId, selection, ticket)) return;
      cyboState.loadError = false;
      cyboState.list = cybos;
      noteCyboAnswer(cybos);
      return;
    } catch (err) {
      // intentional: one attempt in a cold-start backoff retry loop; the loop
      // tries again and the daemon_status self-heal is the backstop. Surfacing
      // loadError is deferred to the end of the loop (below) so a blip that
      // self-recovers on the NEXT attempt never flashes an error.
      lastAttemptFailed = true;
      lastError = err;
    }
  }
  // Backoff exhausted with the roster still empty. Only flag it as an error
  // if the LAST attempt actually rejected — every attempt resolving (even
  // empty) means the workspace is genuinely empty, not erroring.
  if (!cybosReadIsCurrent(workspaceId, selection)) return;
  if (
    lastAttemptFailed &&
    workspaceState.current?.id === workspaceId &&
    cyboState.list.length === 0
  ) {
    cyboState.loadError = true;
    // The retries are over and the person sees an error: no longer a transient blip.
    reportUiFailure(
      "app.fetchCybos.retries_exhausted",
      lastError,
      { workspaceId, attempts: CYBOS_RETRY_ATTEMPTS },
      isRefusalCode(errorCodeOf(lastError)) ? {} : { failureKind: "fault" },
    );
  }
}

// CYBORG-686 §1: the relay's create_cybo_response carries `readiness` (can
// ANYTHING in this workspace run the new cybo? — cybo-readiness.ts). Returning
// only the id threw that verdict away, so the wizard could never warn "created,
// but nothing can run it". A NAMED type, not an inline one, so the return
// annotation stays free of braces (the source-scan regression locks in
// cybo-load-error.source.test.ts brace-match the signature).
export interface CreatedCybo {
  id: string;
  // Undefined against an older relay that doesn't compute readiness.
  readiness?: CyboReadiness;
}

export async function createCybo(opts: {
  slug: string;
  name: string;
  soul: string;
  provider: string;
  model?: string;
  description?: string;
  avatar?: string;
  role?: string;
  llmAuthMode?: string;
  behaviorMode?: string;
  homeDaemonId?: string | null;
  autonomyLevel?: string | null;
  monthlySpendCap?: number | null;
  platformPermissions?: string[];
  toolGrants?: Record<string, unknown> | null;
  mcpServers?: Record<string, unknown> | null;
  bypassPermissions?: boolean;
  isDefault?: boolean;
}): Promise<CreatedCybo> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  let created: CreatedCybo;
  try {
    created = await client.createCybo(workspaceState.current.id, opts);
  } catch (err) {
    if (!handleLicenseError(err)) handleUpgradeError(err);
    throw err;
  }
  const fresh = await client.fetchCybos(workspaceState.current.id);
  cyboState.list = fresh;
  noteCyboAnswer(fresh);
  cyboState.loadError = false;
  return { id: created.id, readiness: created.readiness };
}

export async function updateCybo(
  cyboId: string,
  fields: {
    name?: string;
    description?: string | null;
    avatar?: string | null;
    role?: string | null;
    soul?: string;
    provider?: string;
    model?: string | null;
    llmAuthMode?: string;
    behaviorMode?: string;
    homeDaemonId?: string | null;
    autonomyLevel?: string | null;
    monthlySpendCap?: number | null;
    platformPermissions?: string[];
    toolGrants?: Record<string, unknown> | null;
    mcpServers?: Record<string, unknown> | null;
    bypassPermissions?: boolean;
    isDefault?: boolean;
  },
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  try {
    await client.updateCybo(workspaceState.current.id, cyboId, fields);
  } catch (err) {
    if (!handleLicenseError(err)) handleUpgradeError(err);
    throw err;
  }
  const fresh = await client.fetchCybos(workspaceState.current.id);
  cyboState.list = fresh;
  noteCyboAnswer(fresh);
  cyboState.loadError = false;
  // The actor doesn't receive its own cybo_updated broadcast, so re-stamp the
  // denormalized name/avatar the session rows carry here too — otherwise the
  // editor's own sidebar / DM list / roster keep showing the pre-edit photo
  // (resolveCyboChatIdentity prefers the row denorm) until a cold reload. Use
  // the freshly-fetched roster row as the canonical source.
  const updated = cyboState.list.find((c) => c.id === cyboId);
  if (updated) {
    const denorm = { name: updated.name, avatar: updated.avatar ?? null };
    applyCyboDenormToSessions(workspaceState.agents, cyboId, denorm);
    applyCyboDenormToSessions(sessionState.list, cyboId, denorm);
  }
}

export async function deleteCybo(cyboId: string): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.deleteCybo(workspaceState.current.id, cyboId);
  cyboState.list = cyboState.list.filter((c) => c.id !== cyboId);
  // The actor does not receive its OWN cybo_deleted broadcast, so drop the
  // cybo's agent bindings here too — same prune, same reason as the handler
  // above (the DM row / chat / profile all read a binding, not the roster).
  removeCyboSessions(workspaceState.agents, cyboId);
  // A resulting empty roster here is a genuine "you deleted your last cybo",
  // never an error — never leave a stale loadError from before this delete.
  cyboState.loadError = false;
}

// ─── Provider credentials (api-key cybos — knowledge/40 + 43) ────────
//
// Thin wrappers over the daemon-forwarded credential RPCs. The key is write-only:
// `listProviderAuth` returns set/not-set METADATA only (no secret ever reaches the
// client). `daemonId` is the daemon that holds the credential (each daemon has its
// own per-machine store).

export async function setCyboCredential(
  daemonId: string,
  providerId: string,
  credential: ProviderCredentialPayload,
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.setCyboCredential(workspaceState.current.id, daemonId, providerId, credential);
}

export async function removeCyboCredential(daemonId: string, providerId: string): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.removeCyboCredential(workspaceState.current.id, daemonId, providerId);
}

export async function listProviderAuth(daemonId: string): Promise<ProviderAuthMeta[]> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.listProviderAuth(workspaceState.current.id, daemonId);
}

// Single cybo WITH soul — used by the editor/detail view to lazy-load the
// personality (the list omits it).
export async function fetchCybo(
  cyboId: string,
): Promise<Awaited<ReturnType<typeof client.fetchCybo>>> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.fetchCybo(workspaceState.current.id, cyboId);
}

// Import a local (disk) cybo into the workspace DB, then refresh the list.
export async function importCybo(slug: string): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.importCybo(workspaceState.current.id, slug);
  const fresh = await client.fetchCybos(workspaceState.current.id);
  cyboState.list = fresh;
  noteCyboAnswer(fresh);
  cyboState.loadError = false;
}

// ─── Composio connect (OAuth) + connected-state ────────────────────────────
// Start a hosted-OAuth connect for one toolkit + binding. Returns the redirect URL
// the caller opens (system browser on desktop). Daemon-forwarded to the cybo's home
// daemon (where the Composio key lives) when a daemonId is given.
export async function composioConnect(input: {
  toolkit: string;
  binding: "caller" | "service";
  daemonId?: string;
  /** Where the sign-in window lands afterwards (Tasks/onboarding: the /welcome/app page). */
  callbackUrl?: string;
}): Promise<{ redirectUrl: string; transport: "platform" | "router"; daemonId?: string | null }> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.composioConnect({ workspaceId: workspaceState.current.id, ...input });
}

// END one toolkit's connection — the counterpart of composioConnect, and the ONLY
// way a person can end one connection without wiping the daemon's whole Composio key.
// `cyborg:composio_disconnect` has been fully implemented and allowlisted end to end
// for some time; nothing in the UI reached it, so "Disconnect" meant the key-removal
// path and took every other toolkit down with it.
//
// `disconnected: false` means there was nothing to end — the same end state, not a
// failure. `upstreamRevoked` says whether the account was also ended AT COMPOSIO, and
// is reported rather than assumed: a connection that never finished its OAuth has no
// account to revoke, and nothing may tell a person their access was withdrawn when it
// was not.
export async function composioDisconnect(input: {
  toolkit: string;
  binding: "caller" | "service";
  daemonId?: string;
}): Promise<{ disconnected: boolean; upstreamRevoked: boolean; daemonId?: string | null }> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.composioDisconnect({ workspaceId: workspaceState.current.id, ...input });
}

// Per-toolkit connected-state for the cybo editor's Tools section.
export async function composioConnectionStatus(input: {
  toolkits: Array<{ toolkit: string; binding: "caller" | "service" }>;
  daemonId?: string;
}): Promise<{
  configured: boolean;
  daemonId?: string | null;
  accountEmail?: string | null;
  statuses: Array<{ toolkit: string; binding: "caller" | "service"; connected: boolean }>;
}> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.composioConnectionStatus({ workspaceId: workspaceState.current.id, ...input });
}

// The provider's toolkit catalog for the integrations picker. `available:false` ⇒ the
// caller keeps its curated fallback catalog.
export async function composioToolkits(input: { daemonId?: string } = {}): Promise<{
  available: boolean;
  daemonId?: string | null;
  toolkits: Array<{
    slug: string;
    name: string;
    description?: string | null;
    logo?: string | null;
    categories?: string[];
    // Absent on an older daemon, and absent means UNKNOWN — never "cannot connect".
    authSchemes?: string[] | null;
    composioManagedAuthSchemes?: string[] | null;
    noAuth?: boolean | null;
    projectAuthConfigured?: boolean | null;
  }>;
}> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.composioToolkits({ workspaceId: workspaceState.current.id, ...input });
}

// One toolkit's action catalog for the per-action picker. `available:false` ⇒ the
// caller hides the picker and leaves the grant on its (empty ⇒ default) action set.
export async function composioToolkitActions(input: {
  toolkit: string;
  daemonId?: string;
}): Promise<{
  available: boolean;
  daemonId?: string | null;
  toolkit: string;
  defaultActions: Array<{ slug: string; label: string }>;
  droppedByCap: Array<{ slug: string; label: string }>;
  excludedCount: number;
}> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.composioToolkitActions({ workspaceId: workspaceState.current.id, ...input });
}

// Set the daemon's Composio key from the cybo editor (owner-only). Daemon-forwarded to
// the cybo's home daemon, where the encrypted vault lives; the secret never reaches PG.
export async function composioSetKey(input: {
  daemonId?: string;
  consumerKey?: string;
  apiKey?: string;
}): Promise<{ ok: boolean; configured: boolean; daemonId?: string | null }> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.composioSetKey({ workspaceId: workspaceState.current.id, ...input });
}

// Remove the daemon's in-app Composio key (owner-only).
export async function composioRemoveKey(input: {
  daemonId?: string;
}): Promise<{ ok: boolean; daemonId?: string | null }> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.composioRemoveKey({ workspaceId: workspaceState.current.id, ...input });
}

// Start a browser-based Composio sign-in on the target daemon (the cybo
// editor's "Connect Composio" banner). Returns the session to poll + the URL
// the caller opens in the system browser.
export async function composioLoginStart(input: {
  daemonId?: string;
}): Promise<{ sessionId: string; loginUrl: string; expiresAt?: number; daemonId?: string | null }> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.composioLoginStart(workspaceState.current.id, input.daemonId);
}

// Poll a composioLoginStart session until the daemon reports it linked.
// Linked sessions are consumed one-shot server-side — never poll past "linked".
export async function composioLoginPoll(input: { daemonId?: string; sessionId: string }): Promise<{
  status: "pending" | "linked";
  accountEmail?: string | null;
  configured?: boolean;
  daemonId?: string | null;
}> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.composioLoginPoll(workspaceState.current.id, input.daemonId, input.sessionId);
}

export async function spawnCybo(
  cyboIdOrSlug: string,
  cwd: string | undefined,
  // `workSessionId` + `prompt` (fix/worksession-one-agent) cold-resume a work session:
  // spawn its cybo against the session and deliver `prompt` to it. Routed through THIS
  // state wrapper (not client.spawnCybo directly) precisely so the roster refetch below
  // runs — the just-spawned agent then resolves as the session's LIVE agent, so the
  // composer flips to its full/warm variant and the NEXT send reuses this one agent
  // instead of cold-spawning a fresh one per message (the fragmentation this fixes).
  opts?: {
    channelId?: string;
    daemonId?: string;
    ephemeral?: boolean;
    workSessionId?: string;
    prompt?: string;
    // COLD work-session composer permission mode — forwarded verbatim to
    // client.spawnCybo (see its opts doc + dispatcher.ts's resolveSpawnBypass).
    mode?: string;
    // The client's own id for `prompt` — see client.spawnCybo.
    promptId?: string;
    // Called with the agent id the moment the spawn answers, BEFORE the roster re-read
    // below. A cold work session's agent starts streaming right away (its first frame is
    // the user's own prompt), and the session body can only mirror frames of an agent it
    // knows about — waiting for the re-read is how that first frame was dropped.
    onSpawned?: (agentId: string) => void;
  },
): Promise<string> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  const wsId = workspaceState.current.id;
  const { onSpawned, ...spawnOpts } = opts ?? {};
  let result: { agentId: string };
  try {
    result = await client.spawnCybo(wsId, cyboIdOrSlug, cwd, spawnOpts);
  } catch (err) {
    if (!handleLicenseError(err)) handleUpgradeError(err);
    throw err;
  }
  onSpawned?.(result.agentId);
  await relistAfterMutation(wsId, "spawn");
  return result.agentId;
}

// The relay answers list_agents with whatever daemons replied within its window, so a
// slow daemon's rows can be missing from one answer and present in the next.
// Resolves true only when the answer was AUTHORITATIVE (see relistAgents); existing callers ignore it.
export async function refreshWorkspaceAgents(): Promise<boolean> {
  if (!workspaceState.current) return false;
  return relistAgents(workspaceState.current.id);
}

// Every roster re-read after a user action takes the reconnect's rule: a daemon too slow
// for that answer keeps its rows (pendingDaemons) instead of vanishing from an open chat.
const relistIssued = accountSync.collection("relistIssued", new Map<string, number>());

// The mutation already succeeded: failing it here would make a retry duplicate it.
async function relistAfterMutation(wsId: string | undefined, mutation: string): Promise<void> {
  if (!wsId) {
    // Signed out or left the workspace while it ran: nothing to re-read, still a success.
    const kind = { failureKind: "expected", code: "no_workspace" } as const;
    reportUiFailure("agents.relist_after_mutation", "no workspace selected", { mutation }, kind);
    return;
  }
  try {
    await relistAgents(wsId);
  } catch (err) {
    reportUiFailure("agents.relist_after_mutation", err, { workspaceId: wsId, mutation });
  }
}

// Resolves true when the read was applied AND authoritative: trusted per isRosterAnswerTrusted
// with no silent (pending) daemon. A superseded or stale read resolves false.
async function relistAgents(wsId: string): Promise<boolean> {
  // Only the latest read of the workspace still on screen, in the same selection, writes.
  if (workspaceState.current?.id !== wsId) return false;
  const selection = workspaceSelections;
  const issued = (relistIssued.get(wsId) ?? 0) + 1;
  relistIssued.set(wsId, issued);
  const res = rereadLateDaemons(wsId, await listRoster(wsId));
  const current =
    relistIssued.get(wsId) === issued &&
    selection === workspaceSelections &&
    workspaceState.current?.id === wsId;
  if (!current) return false;
  workspaceState.agents = nextAgentSnapshot(
    workspaceState.agents,
    res.agents,
    daemonConnStatus() === "connected",
    res.pendingDaemons,
  );
  return isRosterAnswerTrusted(res.noDaemonAnswered) && res.pendingDaemons.length === 0;
}

// Start a standalone WORK SESSION with a cybo (CYBORG-794): unlike spawnCybo above
// (which continues/mints a DM), this always asks the relay for a fresh standalone
// session, then navigates to that session's work timeline. The relay mints the
// session and returns its `workSessionId` on the spawn response.
export async function startWorkSession(
  cyboId: string,
  // The WORKING DIRECTORY the owner picked for this session (StartWorkSessionDialog's
  // DirectoryBrowser). It is what makes the agent's world the owner's real project
  // instead of the daemon's `~/.cybo/run/<id>` scratch dir — the measured cause of an
  // agent refusing a path "outside this workspace". `undefined` ⇒ the scratch dir, the
  // pre-existing behavior and still the fail-closed default for every other spawn.
  cwd?: string,
): Promise<{ workSessionId: string; agentId: string }> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  const wsId = workspaceState.current.id;
  let result: { agentId: string; workSessionId?: string };
  try {
    result = await client.spawnCybo(wsId, cyboId, cwd, { createStandaloneSession: true });
  } catch (err) {
    if (!handleLicenseError(err)) handleUpgradeError(err);
    throw err;
  }
  if (!result.workSessionId) throw new Error("Work session was not created");
  await relistAfterMutation(wsId, "work_session");
  void goto(`/workspace/${wsId}/session/${result.workSessionId}`);
  return { workSessionId: result.workSessionId, agentId: result.agentId };
}

// List importable local provider sessions (on-disk Claude/Codex/… sessions the
// daemon has not yet imported) for the session-import picker. Thin pass-through
// to the client; the picker drives the actual import via importSession() below.
export async function fetchRecentProviderSessions(opts?: {
  cwd?: string;
  limit?: number;
  daemonId?: string;
  query?: string;
  providers?: string[];
}): Promise<RecentSession[]> {
  // Scope the scan to the viewed workspace and, when known, the session's host.
  return client.fetchRecentProviderSessions({
    ...opts,
    workspaceId: workspaceState.current?.id,
  });
}

export async function importSession(opts: {
  providerId?: string;
  providerHandleId?: string;
  sessionId?: string;
  cwd?: string;
}): Promise<string> {
  const { agentId } = await client.importSession(opts);
  await relistAfterMutation(workspaceState.current?.id, "import");
  return agentId;
}

// #4166 M1: the relay wrote the message into the DM thread; take it back from the channel session.
// Only a person still looking at that session is moved: a replayed send must not yank them.
function followPromptRedirect(
  workspaceId: string,
  fromAgentId: string,
  promptId: string | undefined,
  redirect: { agentId: string; cyboId?: string },
): void {
  if (promptId) {
    agentStreamState.retractOptimisticPrompt(fromAgentId, promptId);
    void cache.deleteAgentPromptOutbox(promptId);
  }
  if (activeAgentIdFromUrl() !== fromAgentId) return;
  const cyboId =
    redirect.cyboId ?? workspaceState.agents.find((a) => a.agentId === fromAgentId)?.cyboId;
  void goto(`/workspace/${workspaceId}/agent/${dmAddress({ agentId: redirect.agentId, cyboId })}`);
}

// Fires the WS round-trip for a prompt entry that already exists in the
// transcript (a fresh send or a Retry of a failed one) and reflects the
// outcome on THAT bubble's own status — "pending" while in flight, a
// transient "sent" check on success (markSent), "failed" (+ Retry) if it
// rejects. Shared by sendAgentPrompt (new send) and retryAgentPrompt (same
// entryId, no new bubble) — mirrors the channel message triad
// (dispatchSend/sendMessage/retrySendMessage) above.
async function dispatchAgentPrompt(
  sessionRevision: number,
  workspaceId: string,
  agentId: string,
  // null for a SILENT control prompt (CYBORG-884): there is no bubble to drive.
  entryId: string | null,
  prompt: string,
  attachments?: Attachment[],
  pageRef?: string,
  // Active-turn steering (CYBORG-823): "steer" joins the live turn, "interrupt"
  // cancels + restarts it; undefined: a busy daemon holds it behind the turn (CYBORG-1560).
  activeTurnBehavior?: "interrupt" | "steer",
  // Work session (CYBORG-794): when set, the relay keeps the prompt + reply inside
  // the session instead of posting to the anchor channel/thread.
  workSessionId?: string,
  // CYBORG-884 — see sendAgentPrompt's own promptId doc.
  promptId?: string,
  silent?: boolean,
  // CYBORG-1016 — see sendAgentPrompt's own persistOnVerdict doc.
  persistOnVerdict?: boolean,
): Promise<void> {
  if (authState.sessionRevision !== sessionRevision) {
    throw new PromptNotSentError(new Error("Account changed before the message was sent"));
  }
  agentStreamState.bindAgentWorkspace(agentId, workspaceId);
  if (entryId) agentStreamState.setSendStatus(agentId, entryId, "pending");
  try {
    const ack = await client.sendAgentPrompt(
      workspaceId,
      agentId,
      prompt,
      attachments,
      pageRef,
      workSessionId,
      activeTurnBehavior,
      promptId,
      silent,
      persistOnVerdict,
    );
    if (authState.sessionRevision !== sessionRevision) return;
    // #4166 M1: delivered to the sender's DM, nothing left to confirm here.
    if (ack?.redirectedTo) {
      followPromptRedirect(workspaceId, agentId, promptId, ack.redirectedTo);
      return;
    }
    // P03: keep the relay's minted attempt with the durable row, so a reload can ask
    // the owning daemon what became of this prompt instead of inferring from silence.
    if (promptId && ack?.promptAttemptId) {
      latestPromptAttempt.set(promptId, ack.promptAttemptId);
      void cache.stampAgentPromptAttempt(promptId, ack.promptAttemptId);
    }
    if (entryId) agentStreamState.markSent(agentId, entryId);
    // The relay wrote this prompt as a DM row: the bubble can be reacted to from now on.
    if (entryId && ack?.messageId) {
      agentStreamState.setPromptSourceMessageId(agentId, entryId, ack.messageId);
      rowReactions.track(ack.messageId);
    }
    // Ack ≠ turn: the relay resolved this send as forwarded, but a prompt aimed
    // at an offline daemon then produces NOTHING — no turn_started, no error.
    // Arm the bounded pre-turn wait (PROMPT_PENDING_WATCHDOG_MS) so that gap
    // ends in an unconfirmed-delivery state instead of silence forever.
    if (entryId) agentStreamState.notePromptDispatched(agentId, entryId);
  } catch (err) {
    if (authState.sessionRevision !== sessionRevision) throw err;
    // Proven undelivered (a refusal, not a lost answer) is a KNOWN outcome: the
    // bubble offers Retry, and the outbox row goes — it has nothing left to
    // reconcile against and would otherwise return as an unconfirmed ghost on
    // every reload. Same disposal the "unavailable" verdict already does.
    const notSent = isPromptNotSent(err);
    if (notSent && promptId) void cache.deleteAgentPromptOutbox(promptId);
    if (entryId) settleRefusedSend(agentId, entryId, notSent, err);
    if (!handleLicenseError(err)) handleUpgradeError(err);
    // RPC failure is not a provider turn failure. Do not stop an unrelated turn
    // or drain its queued prompts when the response to this send is lost.
    throw err;
  }
}

// CYBORG-1416: read one session's goal / background tasks / retry back from the daemon (a reload
// has no push to wait for). A daemon that cannot answer leaves what the pushes already set.
export async function refreshAgentActivity(agentId: string): Promise<boolean> {
  return (await refreshAgentLiveState(agentId)).activity;
}

// CYBORG-1516: a device that never received the live frame reads the pending requests from the
// hosting daemon. true: the stream holds exactly its list; false: the daemon could not say.
export async function refreshPendingPermissions(agentId: string): Promise<boolean> {
  return (await refreshAgentLiveState(agentId)).permissions;
}

interface LiveStateRead {
  activity: boolean;
  permissions: boolean;
}
const nothingRead = (): LiveStateRead => ({ activity: false, permissions: false });
// One fetch_agent_state per agent at a time: the session page and the alert pane share it.
const liveStateReads = conversationSync.collection(
  "agentLiveStateReads",
  new Map<string, Promise<LiveStateRead>>(),
);

function refreshAgentLiveState(agentId: string): Promise<LiveStateRead> {
  const workspaceId = workspaceState.current?.id;
  if (!workspaceId) return Promise.resolve(nothingRead());
  const key = `${workspaceId}\t${agentId}`;
  const inFlight = liveStateReads.get(key);
  if (inFlight) return inFlight;
  const read = readAgentLiveState(workspaceId, agentId).finally(() => liveStateReads.delete(key));
  liveStateReads.set(key, read);
  return read;
}

async function readAgentLiveState(workspaceId: string, agentId: string): Promise<LiveStateRead> {
  const since = agentActivityStore.stamp(agentId);
  const permissionsSince = agentStreamState.permissionStamp(agentId);
  try {
    const state = await client.fetchAgentLiveState(workspaceId, agentId);
    if (workspaceState.current?.id !== workspaceId) return nothingRead();
    return {
      // A push that landed while the read was in flight is newer than it: keep the push.
      activity:
        !!state.activity && agentActivityStore.replaceIfUnchanged(since, agentId, state.activity),
      permissions:
        Array.isArray(state.pendingPermissions) &&
        agentStreamState.replacePendingPermissions(
          agentId,
          permissionsSince,
          state.pendingPermissions.filter((r) => typeof r?.id === "string"),
        ),
    };
  } catch (err) {
    reportUiFailure("agent_activity.fetch", err, { agentId, workspaceId });
    return nothingRead();
  }
}

// CYBORG-1416: the tray's Stop. true: the daemon ended the task (or it had already ended).
export async function stopBackgroundTask(agentId: string, taskId: string): Promise<boolean> {
  if (!workspaceState.current) return false;
  const workspaceId = workspaceState.current.id;
  try {
    await client.stopBackgroundTask(workspaceId, agentId, taskId);
    return true;
  } catch (err) {
    reportUiFailure("agent_activity.stop_background_task", err, { agentId, workspaceId, taskId });
    toast.error(err instanceof Error && err.message ? err.message : "Couldn't stop the task");
    return false;
  }
}

// The process modal opened one process's log: subscribe first, then read the backlog (contract
// order, so no line falls between the two). The returned cleanup unsubscribes and drops the log.
export function followAgentProcess(agentId: string, processId: string): () => void {
  const workspaceId = workspaceState.current?.id;
  if (!workspaceId) return () => {};
  const attachId = `proc_${crypto.randomUUID()}`;
  const k = `${agentId}\t${processId}`;
  followedProcesses.set(k, { workspaceId, attachId });
  client.subscribeAgentProcess(workspaceId, agentId, processId, attachId);
  void readProcessBacklog(workspaceId, agentId, processId);
  return () => {
    followedProcesses.delete(k);
    client.unsubscribeAgentProcess(workspaceId, agentId, processId, attachId);
    agentProcessOutputStore.forget(agentId, processId);
  };
}

// Pages of one from-start read, joined into the whole log it replaces.
function joinPages(whole: AgentProcessTail | undefined, tail: AgentProcessTail): AgentProcessTail {
  if (!whole) return tail;
  const lines = [...whole.lines, ...tail.lines];
  return { ...tail, firstSeq: whole.firstSeq, lines, droppedBefore: whole.droppedBefore };
}
// The followed process ended: read its whole output again from the start and replace the log
// (Codex reports a process's first block only once it ends).
export function rereadAgentProcess(agentId: string, processId: string): void {
  const followed = followedProcesses.get(`${agentId}\t${processId}`);
  if (followed) void readProcessBacklog(followed.workspaceId, agentId, processId, true);
}

// Pages through the backlog from the log's last line (2,000 lines a page on the daemon);
// `fromStart` reads from seq 0 and replaces the log. A re-read asked during a read runs after it.
async function readProcessBacklog(
  workspaceId: string,
  agentId: string,
  processId: string,
  fromStart = false,
): Promise<void> {
  const k = `${agentId}\t${processId}`;
  if (readingProcesses.has(k)) {
    if (fromStart) rereadPending.add(k);
    return;
  }
  readingProcesses.add(k);
  try {
    // A from-start read gathers every page and replaces the log once: no shrink-then-refill.
    let whole: AgentProcessTail | undefined;
    for (;;) {
      const lastSeq = fromStart
        ? whole?.lastSeq
        : agentProcessOutputStore.get(agentId, processId)?.lastSeq;
      const tail = await client.tailAgentProcess(
        workspaceId,
        agentId,
        processId,
        lastSeq ? lastSeq + 1 : undefined,
      );
      if (!followedProcesses.has(k)) return; // the modal closed meanwhile
      if (!fromStart) agentProcessOutputStore.applyTail(agentId, processId, tail);
      else whole = joinPages(whole, tail);
      if (tail.more) continue;
      if (whole) agentProcessOutputStore.replaceTail(agentId, processId, whole);
      return;
    }
  } catch (err) {
    if (!followedProcesses.has(k)) return;
    const code = (err as { code?: unknown } | null)?.code;
    // A refusal is an answer the log shows ("only the session's owner"), not a failure to report.
    if (code === "forbidden" || code === "not_found" || code === "invalid") {
      agentProcessOutputStore.fail(agentId, processId, code);
      return;
    }
    reportUiFailure("agent_activity.tail_process", err, { agentId, workspaceId, processId });
    agentProcessOutputStore.fail(agentId, processId, "failed");
  } finally {
    readingProcesses.delete(k);
    if (rereadPending.delete(k)) void readProcessBacklog(workspaceId, agentId, processId, true);
  }
}

// CYBORG-1410: run a typed agent command (/goal, /compact, ...) on the caller's own session.
// The words are drawn at once as the user's own bubble (keyed by the client id the daemon's
// echo settles onto); a refusal takes the bubble back and rethrows with the daemon's reason,
// so a refused command never lingers as a failed prompt whose Retry would send it as prose.
export async function sendAgentCommand(
  agentId: string,
  cmd: AgentCommandInput,
  opts?: { workSessionId?: string; promptId?: string; silent?: boolean; origin?: "pill" },
): Promise<void> {
  if (!workspaceState.current) throw new PromptNotSentError(new Error("No workspace selected"));
  const sessionRevision = authState.sessionRevision;
  const workspaceId = workspaceState.current.id;
  agentStreamState.bindAgentWorkspace(agentId, workspaceId);
  const clientMessageId = opts?.promptId ?? globalThis.crypto.randomUUID();
  rememberTypedCommand(clientMessageId, cmd);
  const entryId = opts?.silent
    ? undefined
    : agentStreamState.addUserMessage(
        agentId,
        agentCommandText(cmd),
        undefined,
        "pending",
        undefined,
        clientMessageId,
        opts?.workSessionId,
      );
  try {
    await client.sendAgentCommand(
      workspaceId,
      agentId,
      cmd.name,
      cmd.args,
      clientMessageId,
      undefined,
      opts?.origin,
    );
    if (authState.sessionRevision !== sessionRevision) return;
    if (entryId) agentStreamState.markSent(agentId, entryId);
  } catch (err) {
    if (entryId) agentStreamState.retractOptimisticPrompt(agentId, clientMessageId);
    throw err;
  }
}

// CYBORG-1410: a command typed in the owner's DM with a cybo. It is drawn as the human's own
// DM row (the relay or daemon writes the one durable row under the same client id), and goes to
// the cybo's default session as a typed command. A refusal takes the row back and rethrows.
export async function sendDmCommand(
  peerId: string,
  agentId: string,
  cmd: AgentCommandInput,
): Promise<void> {
  if (!workspaceState.current || dmState.activePeerId !== peerId) return;
  const workspaceId = workspaceState.current.id;
  const clientMsgId = newClientMsgId();
  const localId = `local-${clientMsgId}`;
  dmState.addMessage({
    id: localId,
    channelId: null,
    fromId: authState.user?.id ?? "unknown",
    fromType: "human",
    fromName: myDisplayName(),
    toId: peerId,
    text: agentCommandText(cmd),
    attachments: null,
    sendStatus: "pending",
    clientMsgId,
    seq: 0,
    createdAt: Date.now(),
  });
  try {
    // A current daemon queues a busy agent's command itself; an older one refuses before anything is
    // written, so the row stays pending and the send is retried until the turn ends.
    await sendRetryingWhileBusy(() =>
      client.sendAgentCommand(workspaceId, agentId, cmd.name, cmd.args, clientMsgId, peerId),
    );
    setBubbleStatus("dm", localId, "sent");
  } catch (err) {
    dmState.removeMessage(localId);
    throw err;
  }
}

export async function sendAgentPrompt(
  agentId: string,
  prompt: string,
  attachments?: Attachment[],
  // /pages parity (CYBORG-309): pageRef is the wire id sent to the relay (which
  // resolves it to a PageCard + injects the body); pageCard renders the staged
  // page as a card on the optimistic transcript echo.
  pageRef?: string,
  pageCard?: PageCard,
  // Active-turn steering (CYBORG-823): threaded down to client.sendAgentPrompt.
  // "steer" joins the live turn, "interrupt" restarts it; undefined = queue path.
  activeTurnBehavior?: "interrupt" | "steer",
  // Work session (CYBORG-794): threaded down so a send from a session stays in it.
  workSessionId?: string,
  // CYBORG-884: the client-generated id the owning daemon echoes back on
  // cyborg:agent_prompt_verdict, which is how a JOIN learns whether it landed. Set by
  // the send-now join and the soft stop; absent everywhere else.
  promptId?: string,
  // CYBORG-884: draw and echo nothing before the outcome is known — no transcript bubble
  // here and no DM echo at the relay. Set by the soft stop AND by the send-now join.
  silent?: boolean,
  // CYBORG-1016: the send-now's other half — the human DID write this, so the owning
  // daemon persists it as their own message once its verdict is "accepted". Without it a
  // silent prompt reads as a control and no record of it survives a reload anywhere.
  persistOnVerdict?: boolean,
): Promise<void> {
  if (!workspaceState.current) throw new PromptNotSentError(new Error("No workspace selected"));
  const sessionRevision = authState.sessionRevision;
  const workspaceId = workspaceState.current.id;
  agentStreamState.bindAgentWorkspace(agentId, workspaceId);
  if (silent) {
    if (persistOnVerdict) {
      promptId ??= globalThis.crypto.randomUUID();
      await persistAgentPrompt(
        workspaceId,
        agentId,
        promptId,
        prompt,
        attachments,
        pageCard,
        workSessionId,
      );
    }
    await dispatchAgentPrompt(
      sessionRevision,
      workspaceId,
      agentId,
      null,
      prompt,
      attachments,
      pageRef,
      activeTurnBehavior,
      workSessionId,
      promptId,
      true,
      persistOnVerdict,
    );
    return;
  }
  const clientMessageId = promptId ?? globalThis.crypto.randomUUID();
  const entryId = agentStreamState.addUserMessage(
    agentId,
    prompt,
    pageCard,
    "pending",
    attachments,
    clientMessageId,
    workSessionId,
  );
  await persistAgentPrompt(
    workspaceId,
    agentId,
    clientMessageId,
    prompt,
    attachments,
    pageCard,
    workSessionId,
  );
  await dispatchAgentPrompt(
    sessionRevision,
    workspaceId,
    agentId,
    entryId,
    prompt,
    attachments,
    pageRef,
    activeTurnBehavior,
    workSessionId,
    clientMessageId,
    undefined,
    // A join typed while the agent answers is drawn at once, and the daemon writes its row
    // only when asked. Dropped here, that row never existed and every reload re-showed the
    // prompt as "Delivery unconfirmed".
    persistOnVerdict,
  );
}

// Stop tracking a prompt whose delivery never got confirmed. Resends nothing and
// deletes nothing on the server — it drops the local tracking, including the outbox
// row that would otherwise repaint the same unconfirmed ghost on every reload.
export function dismissAgentPrompt(viewKey: string, viewEntryId: string): void {
  // A work session read through its pages shows a copy; the action is the original's.
  const { agentId, entryId } = agentStreamState.originalOfSessionCopy(viewKey, viewEntryId) ?? {
    agentId: viewKey,
    entryId: viewEntryId,
  };
  const clientMessageId = agentStreamState.dismissUnknownPrompt(agentId, entryId);
  if (clientMessageId) void cache.deleteAgentPromptOutbox(clientMessageId);
}

// Re-attempt a FAILED or unconfirmed optimistic agent prompt (fix/agent-composer-send-ux —
// composer parity with retrySendMessage): reuses the SAME transcript entry/id,
// so this never duplicates the bubble, just re-runs the original send. The
// page reference (if any) is recovered from the bubble's own card, same as
// retrySendMessage recovers it from the message's card.
export function retryAgentPrompt(viewKey: string, viewEntryId: string): void {
  const { agentId, entryId } = agentStreamState.originalOfSessionCopy(viewKey, viewEntryId) ?? {
    agentId: viewKey,
    entryId: viewEntryId,
  };
  // The guard (re-enqueue instead of dispatching while ANOTHER turn is
  // running — see AgentStreamState.retryPrompt's doc comment) lives in the
  // state layer; this just supplies the actual WS dispatch as a callback,
  // invoked ONLY when it's safe to fire.
  agentStreamState.retryPrompt(
    agentId,
    entryId,
    (text, attachments, pageRef, clientMessageId, workSessionId, command) => {
      const sessionRevision = authState.sessionRevision;
      const workspaceId = agentStreamState.workspaceForAgent(agentId) ?? workspaceState.current?.id;
      if (!workspaceId) return;
      agentStreamState.setSendStatus(agentId, entryId, "pending");
      if (command && clientMessageId) {
        // The bubble is already there: resend the COMMAND under the same id (never its text as prose).
        void sendRetryingWhileBusy(() =>
          client.sendAgentCommand(
            workspaceId,
            agentId,
            command.name,
            command.args,
            clientMessageId,
          ),
        )
          .then(() => agentStreamState.markSent(agentId, entryId))
          .catch((err) => {
            agentStreamState.setSendStatus(agentId, entryId, "failed");
            reportUiFailure("agents.retry_command", err, { workspaceId, agentId, entryId });
          });
        return;
      }
      void (async () => {
        if (clientMessageId) {
          await persistAgentPrompt(
            workspaceId,
            agentId,
            clientMessageId,
            text,
            attachments,
            undefined,
            workSessionId,
          );
        }
        await dispatchAgentPrompt(
          sessionRevision,
          workspaceId,
          agentId,
          entryId,
          text,
          attachments,
          pageRef,
          undefined,
          workSessionId,
          clientMessageId,
        );
      })().catch((err) => {
        reportUiFailure("agents.retry_prompt", err, {
          workspaceId,
          agentId,
          entryId,
          clientMessageId,
        });
      });
    },
  );
}

// Provider-subagent permission routes (#1612). A surfaced subagent permission is
// stored in agentStreamState under the SUBAGENT id (so the shared card renders),
// but its resolution must route to the PARENT agent's provider permission channel
// via cyborg:subagent_permission_response. Keyed by (subagentId, permissionRequestId)
// — exactly the (agentId, requestId) pair the generic card answers with.
const subagentPermissionRoutes = conversationSync.collection(
  "subagentPermissionRoutes",
  new Map<string, { parentAgentId: string; subagentId: string }>(),
);

function subagentPermissionRouteKey(subagentId: string, permissionRequestId: string): string {
  return `${subagentId}::${permissionRequestId}`;
}

export function respondToPermission(
  agentId: string,
  permissionRequestId: string,
  response: AgentPermissionResponse,
): void {
  // The agent's OWN workspace: an answer given while another workspace is on screen must
  // still reach the agent it was for.
  const wsId = workspaceOfAgent(agentId);
  if (!wsId) return;
  // A surfaced provider-subagent permission answers on the PARENT's channel, so
  // route it as cyborg:subagent_permission_response and clear the card locally
  // (the server's permission_resolved echo lands on the parent id, not here).
  const key = subagentPermissionRouteKey(agentId, permissionRequestId);
  const route = subagentPermissionRoutes.get(key);
  if (route) {
    // The card clears ONLY on the daemon's REAL permission_resolved (via the parent link), not
    // the relay's `delivered` ack — that means "forwarded", and getConnectedDaemons is even
    // local-only so it false-negatives a remote daemon (Codex S2). So ignore the ack; the card
    // stays (truthfully pending) until real resolution, and a HARD failure throws to the catch.
    const workspaceId = wsId;
    markAnswered(agentId, permissionRequestId);
    void (async () => {
      try {
        await client.respondToSubagentPermission({
          workspaceId,
          parentAgentId: route.parentAgentId,
          subagentId: route.subagentId,
          permissionRequestId,
          response,
        });
      } catch (err) {
        answeredPermissions.delete(answeredKey(agentId, permissionRequestId));
        reportUiFailure("agents.subagent_permission_response", err, {
          workspaceId,
          agentId: route.parentAgentId,
          subagentId: route.subagentId,
          permissionRequestId,
        });
        toast.error(
          err instanceof Error && err.message
            ? err.message
            : "Couldn't send the permission decision — please try again.",
        );
      }
    })();
    return;
  }
  client.respondToPermission(wsId, agentId, permissionRequestId, response);
  markAnswered(agentId, permissionRequestId);
}

// Resolves when the relay confirms the stop reached the owning daemon; REJECTS
// with the relay's own reason when it did not (daemon unreachable, denied,
// socket down) so the caller can tell the user. The local queue rescue below
// still runs SYNCHRONOUSLY, before that answer arrives — it is a client-side
// repair of stale state and must not wait on (or be skipped by) the round trip.
export function cancelAgent(agentId: string): Promise<void> {
  if (!workspaceState.current) return Promise.resolve();
  const delivered = client.cancelAgent(workspaceState.current.id, agentId);
  // The relay's ack only proves the stop REACHED the owning daemon; when the turn
  // is ALREADY over the daemon then answers with nothing at all
  // (agentManager.cancelAgentRun returns {status:"not_running"}, which
  // message-router discards and never broadcasts).
  // So the user's ONE visible escape hatch was a guaranteed silent no-op in
  // exactly the state they need it most: composer wedged in Queue mode, Cancel
  // sitting right there, clicking it forever and nothing happening.
  //
  // A LIVE turn is untouched here — the daemon's real turn_canceled does that
  // work — so only act when this client has no running turn of its own, which
  // is the "nothing to cancel, the flag is just stale" case: clear the stale
  // roster lifecycle. What waits is the daemon's, and its Stop pauses it.
  if (agentStreamState.getTurnStatus(agentId) !== "running") {
    workspaceState.noteAgentLifecycle(agentId, "idle");
  }
  return delivered;
}

// Refresh the provider-subagent snapshot for a parent agent (list.request). Safe
// to call unconditionally: a rejection is reported and leaves the track empty.
// Returns whether the snapshot was actually refreshed (CYBORG-884). Every caller that
// only PAINTS the track can keep ignoring it, but the Cancel confirm must be able to
// tell "no subagents are running" apart from "we could not find out" — a swallowed
// failure rendered as 0 is how Stop would kill children with no warning.
export async function refreshProviderSubagents(parentAgentId: string): Promise<boolean> {
  if (!workspaceState.current) return false;
  const workspaceId = workspaceState.current.id;
  try {
    const subagents = await client.listProviderSubagents(workspaceId, parentAgentId);
    if (workspaceState.current?.id !== workspaceId) return false;
    providerSubagentStore.bindParentWorkspace(parentAgentId, workspaceId);
    for (const subagent of subagents) {
      agentStreamState.bindAgentWorkspace(subagent.id, workspaceId);
    }
    providerSubagentStore.replaceList(parentAgentId, subagents);
    return true;
  } catch (err) {
    // The code decides refusal/transient/fault. Once per agent per 10 min, whatever the kind: the page
    // and the session fan-out both read it. No toast.
    if (shouldReport(`subagents.list:${parentAgentId}`, 600_000)) {
      reportUiFailure("subagents.list", err, { workspaceId, agentId: parentAgentId });
    }
    return false;
  }
}

// CYBORG-1418: load ONE subagent by id even when it is archived (auto-archive makes that the
// common case), so its deep link still opens after a reload.
export async function refreshProviderSubagent(
  parentAgentId: string,
  subagentId: string,
): Promise<boolean> {
  if (!workspaceState.current) return false;
  const workspaceId = workspaceState.current.id;
  try {
    const found = await client.getProviderSubagent(workspaceId, parentAgentId, subagentId);
    if (!found || workspaceState.current?.id !== workspaceId) return false;
    providerSubagentStore.bindParentWorkspace(parentAgentId, workspaceId);
    agentStreamState.bindAgentWorkspace(found.id, workspaceId);
    providerSubagentStore.applyUpdate({ kind: "upsert", subagent: found });
    return true;
  } catch (err) {
    reportUiFailure("subagents.lookup", err, { workspaceId, agentId: parentAgentId, subagentId });
    return false;
  }
}

// (parentAgentId::subagentId) pairs this page has already asked the daemon about. See
// the guard inside refreshProviderSubagentTimeline for why it exists.
const attemptedSubagentTranscriptFetches = conversationSync.collection(
  "attemptedSubagentTranscriptFetches",
  new Set<string>(),
);

// Fetch a subagent's whole transcript and merge it under the live rows by seq. The viewer
// forces it on every open; the fan-out group asks once per page, only for empty rows.
export async function refreshProviderSubagentTimeline(
  parentAgentId: string,
  subagentId: string,
  opts?: { force?: boolean },
): Promise<void> {
  if (!workspaceState.current) return;
  const force = opts?.force === true;
  if (!force && !shouldBackfillSubagentTranscript(agentStreamState.getEntries(subagentId))) return;
  const wsAtStart = workspaceState.current.id;
  // ponytail: the group asks once per page load; opening the viewer is the retry.
  const attemptKey = `${parentAgentId}::${subagentId}`;
  if (!force && attemptedSubagentTranscriptFetches.has(attemptKey)) return;
  attemptedSubagentTranscriptFetches.add(attemptKey);
  try {
    const { provider, rows, hasOlder } = await fetchWholeSubagentTranscript((before) =>
      client.fetchProviderSubagentTimeline(wsAtStart, parentAgentId, subagentId, before),
    );
    console.info("[subagent-transcript] fetched", {
      parentAgentId,
      subagentId,
      rows: rows.length,
      provider,
      hasOlder,
    });
    if (workspaceState.current?.id !== wsAtStart) return;
    providerSubagentStore.noteTranscriptTruncated(subagentId, hasOlder);
    const resolved =
      provider ?? providerSubagentStore.descriptorFor(parentAgentId, subagentId)?.provider;
    if (!resolved) return;
    providerSubagentStore.mergeTranscript(parentAgentId, subagentId, resolved, rows);
  } catch (err) {
    // Offline daemon or relay: the panel keeps its empty state, the failure is reported.
    reportUiFailure("agents.subagent_transcript", err, {
      workspaceId: wsAtStart,
      agentId: parentAgentId,
      subagentId,
    });
  }
}

// What the subagent viewer calls on open: the whole transcript. Live rows keep it current
// after that, so a viewer re-mounted moments later (seen live on the full page, every ~2 s)
// does not re-read it.
// ponytail: 15 s per (parent, child) window; a mount-cause fix makes this unnecessary.
const recentTranscriptLoads = conversationSync.collection(
  "recentTranscriptLoads",
  new Map<string, number>(),
);
export function loadSubagentTranscript(parentAgentId: string, subagentId: string): Promise<void> {
  const key = `${parentAgentId}::${subagentId}`;
  const last = recentTranscriptLoads.get(key);
  if (last !== undefined && Date.now() - last < 15_000) return Promise.resolve();
  recentTranscriptLoads.set(key, Date.now());
  return refreshProviderSubagentTimeline(parentAgentId, subagentId, { force: true });
}

// User-initiated reload (the Retry button). `force` re-issues the fetch even with a visible
// row and replaces the stream — the empty-only path could not recover a partial transcript
// (Codex audit, knowledge/113 §13.2). One deliberate click, not the fan-out the guard throttles.
export async function retrySubagentTranscript(
  parentAgentId: string,
  subagentId: string,
): Promise<void> {
  await refreshProviderSubagentTimeline(parentAgentId, subagentId, { force: true });
}

// Relay capability flags for the UI (knowledge/113 E11 / §13.2). FAIL-CLOSED: an action
// stays hidden until the relay confirms it can serve it, so an old relay (which can't answer
// the capability query) never shows a control it would only reject.
export { relayCapabilities };

// Re-derive for the CURRENT connection, fail-closed and stale-safe: a result from a superseded
// generation is dropped (Codex S3). Called on mount AND on every connection change.
// Called fire-and-forget on every connection change: a failure stays fail-closed and is
// reported here, never left as an unhandled rejection.
export async function refreshRelayCapabilities(): Promise<void> {
  const gen = relayCapabilities.beginRefresh();
  try {
    const caps = await client.getRelayCapabilities();
    relayCapabilities.applyIfCurrent(
      gen,
      caps.detachSubagent,
      caps.legacyDesktopUpdate,
      caps.deviceSessions === true,
      caps.pairing === true,
      caps.externalAgents === true,
    );
  } catch (err) {
    if (relayCapabilities.generation === gen) relayCapabilities.ready = true;
    reportUiFailure("refreshRelayCapabilities", err);
  }
}

// Connection changed: reset fail-closed under a fresh generation, then re-derive (Codex S3).
export function resetRelayCapabilitiesForConnection(): void {
  relayCapabilities.resetForConnection();
  void refreshRelayCapabilities();
}

// Detach a MANAGED subagent (a real Agent row with parentAgentId) so it continues as a
// standalone agent — the daemon clears its parent and it regroups top-level over the
// normal status broadcast. Only managed children are detachable (a provider subagent has
// no binding, knowledge/113 E1/E2); live over the cloud since knowledge/113 §11 item 3.
export async function detachSubagent(subagentId: string): Promise<void> {
  if (!workspaceState.current) return;
  const workspaceId = workspaceState.current.id;
  const { status } = await client.detachSubagent(workspaceId, subagentId);
  // Paseo parity (track.tsx): a confirmed detach promotes the child to a root agent and
  // OPENS it (its own agent id is the root view), so the user lands on what they detached.
  if (status === "ok") await goto(`/workspace/${workspaceId}/agent/${subagentId}`);
}

// Persist an archive of finished provider subagents (W3): the in-memory store hides them
// instantly, this makes it durable so a reload/other machine keeps them hidden. `subagentId`
// archives one; omitting it archives every finished child of the parent.
export async function archiveProviderSubagents(
  parentAgentId: string,
  subagentId?: string,
  trigger: "auto" | "manual" = "manual",
  cancelStalled = false,
): Promise<void> {
  if (!workspaceState.current) return;
  await client.archiveProviderSubagents({
    workspaceId: workspaceState.current.id,
    parentAgentId,
    subagentId,
    trigger,
    cancelStalled,
  });
}

export async function archiveAgent(agentId: string): Promise<string> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  const wsId = workspaceState.current.id;
  // Off the roster before the round trip (seconds with a turn running): End keeps you in the
  // agent's DM, and a message typed meanwhile must start the fresh session, not go to the ending one.
  const ending = workspaceState.agents.find((a) => a.agentId === agentId);
  forgetEndedDmWindow(wsId, ending?.cyboId);
  workspaceState.agents = workspaceState.agents.filter((a) => a.agentId !== agentId);
  let sessionId: string;
  try {
    sessionId = await client.archiveAgent(wsId, agentId, (notice) => toast.info(notice));
  } catch (err) {
    // Not ended: the row comes back where nothing has replaced it.
    if (ending && !workspaceState.agents.some((a) => a.agentId === agentId)) {
      workspaceState.agents = [...workspaceState.agents, ending];
    }
    throw err;
  }
  agentStreamState.clear(agentId);
  dropEndedAgentRow(wsId, agentId, true);
  await fetchSessions();
  return sessionId;
}

// An ended (archived/cleared) session leaves the roster, here or on another device.
function dropEndedAgentRow(workspaceId: string, agentId: string, persist = false): void {
  const row = workspaceState.agents.find((a) => a.agentId === agentId);
  if (!persist && !row) return;
  forgetEndedDmWindow(workspaceId, row?.cyboId);
  workspaceState.agents = workspaceState.agents.filter((a) => a.agentId !== agentId);
  if (workspaceId !== workspaceState.current?.id) return;
  // The archive is authoritative even when it empties the roster (shouldPersistRoster skips an
  // empty list as "daemon unreachable"): else the next cold start repaints the ended run as live,
  // over its purged timeline, and the person's own messages vanish from the conversation.
  saveRosterCache(rosterStore(), workspaceId, authState.user?.email, workspaceState.agents, true);
}

// The DM thread an ended session falls back to: its cached window took the agent's replies live,
// never the person's prompts to that session, so its next open reads the tail, not a delta on it.
function forgetEndedDmWindow(workspaceId: string, cyboId: string | null | undefined): void {
  if (cyboId) cache.forgetScopeSynced(dmKey(workspaceId, cyboId));
}

// Clone a session (record + provider transcript + binding + timeline) into a new
// one that diverges from the original. The daemon owns all four copies; here we
// just refresh the agent list so the clone shows up in the sidebar.
export async function cloneAgent(
  agentId: string,
  title?: string | null,
): Promise<CloneAgentResult> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  const wsId = workspaceState.current.id;
  const result = await client.cloneAgent(wsId, agentId, title ?? null);
  await relistAfterMutation(wsId, "clone");
  return result;
}

// Reload/restart a wedged session in place (#592): recover a hung/desynced agent
// keeping its agentId and identity. rehydrateFromDisk re-streams the provider
// history; the fresh stream arrives via the existing agent_stream subscription,
// so no local timeline surgery is needed here.
export async function reloadSession(
  agentId: string,
  opts?: { rehydrateFromDisk?: boolean; cwd?: string },
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  const result = await client.reloadSession(workspaceState.current.id, agentId, opts);
  if (result.cwd !== undefined) {
    workspaceState.agents = workspaceState.agents.map((agent) =>
      agent.agentId === agentId ? { ...agent, cwd: result.cwd } : agent,
    );
  }
  // A rehydrate wipes the daemon's in-memory timeline, mints a new epoch and
  // re-streams provider history. Drop the stale local stream so it rebuilds
  // from the fresh server timeline instead of the re-stream piling ON TOP of
  // the old items (thread_started doesn't reset the timeline) — same model as
  // the workspace-switch clear. Non-rehydrate reloads preserve the timeline, so
  // only clear when rehydrating.
  if (opts?.rehydrateFromDisk) agentStreamState.clear(agentId);
}

export async function restoreSession(
  sessionId: string,
  overrides?: ResumeOverrides,
): Promise<string> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  // Carry the archived session's owning daemon (stamped by the relay) so the
  // restore scopes + routes to the daemon that holds it (#593). Absent in
  // single-daemon flows, where the relay resolves the sole online daemon.
  const daemonId = sessionState.list.find((s) => s.id === sessionId)?.daemonId ?? undefined;
  // Only forward override fields the caller actually set, so an empty/absent
  // object resumes exactly as before.
  const hasOverrides =
    !!overrides &&
    (overrides.model !== undefined ||
      overrides.modeId !== undefined ||
      overrides.thinkingOptionId !== undefined);
  const wsId = workspaceState.current.id;
  const agentId = await client.restoreSession(wsId, sessionId, {
    daemonId,
    overrides: hasOverrides ? overrides : undefined,
  });
  await relistAfterMutation(wsId, "restore");
  await fetchSessions();
  return agentId;
}

// Import a LOCAL provider transcript into the active workspace via the new Cyborg
// path and refresh the roster and archives. Pin the session's host when known;
// otherwise the relay resolves the sole online workspace daemon.
export async function importProviderSession(opts: {
  provider: string;
  providerHandleId: string;
  cwd?: string;
  channelId?: string;
  daemonId?: string;
}): Promise<string> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  const wsId = workspaceState.current.id;
  const { agentId } = await client.importSessionCyborg({
    workspaceId: wsId,
    provider: opts.provider,
    providerHandleId: opts.providerHandleId,
    ...(opts.daemonId ? { daemonId: opts.daemonId } : {}),
    ...(opts.cwd ? { cwd: opts.cwd } : {}),
    ...(opts.channelId ? { channelId: opts.channelId } : {}),
  });
  await relistAfterMutation(wsId, "import_provider_session");
  await fetchSessions();
  return agentId;
}

// daemonId routes the control RPC straight to the agent's owning daemon (#843
// Gap B), avoiding the daemon_agents binding race on a freshly-created session.
export async function setAgentModel(
  agentId: string,
  modelId: string | null,
  daemonId?: string,
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  const resp = await client.setAgentModel(workspaceState.current.id, agentId, modelId, daemonId);
  if (resp?.status === "superseded") return;
  // Optimistically reflect the switch — the server's model_changed event doesn't
  // reliably reach cloud guests, so the selector would otherwise stay stale.
  agentStreamState.setModel(agentId, modelId);
}

export async function setAgentMode(
  agentId: string,
  modeId: string,
  daemonId?: string,
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.setAgentMode(workspaceState.current.id, agentId, modeId, daemonId);
  // Optimistically reflect the switch (same as setAgentModel) so the mode pill —
  // and its bypass danger styling — update immediately on every provider, even
  // when the server's mode_changed event doesn't reach cloud guests.
  agentStreamState.setModeId(agentId, modeId);
}

export async function setAgentThinking(
  agentId: string,
  thinkingOptionId: string | null,
  daemonId?: string,
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.setAgentThinking(workspaceState.current.id, agentId, thinkingOptionId, daemonId);
  // Optimistically reflect the switch — the thinking_option_changed event shares
  // model_changed's cloud-guest unreliability, so the selector would otherwise
  // keep showing the previous value after a change.
  agentStreamState.setThinkingOption(agentId, thinkingOptionId);
}

// 'Rewind to here' (#649): expose Paseo's session rewind for cybos. The server
// truncates the provider session to before `messageId` (a Paseo timeline
// user-message id) and re-broadcasts the truncated timeline. We then re-fetch +
// re-hydrate so the stream view reflects the truncation immediately — the
// broadcast alone only appends, it can't shrink the local entry list.
export async function rewindAgent(agentId: string, messageId: string): Promise<void> {
  // The flow (invalidate first, stale-guarded re-read, rewind hydrate) is rewindConversation.
  await rewindConversation(client, agentId, messageId);
}

export async function fetchAgentCommands(agentId: string): Promise<AgentSlashCommand[]> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.listAgentCommands(workspaceState.current.id, agentId);
}

// #581: @-file/dir autocomplete in the agent composer. Returns file/directory
// entries from the agent's workspace cwd matching the text typed after `@`.
export async function fetchAgentFileSuggestions(
  agentId: string,
  query: string,
): Promise<{ path: string; kind: "file" | "directory" }[]> {
  if (!workspaceState.current) return [];
  return client.getAgentDirectorySuggestions(workspaceState.current.id, agentId, query);
}

export async function fetchSessions(): Promise<void> {
  if (!workspaceState.current) return;
  const wsId = workspaceState.current.id;
  sessionState.loading = true;
  // Reset paging state for the new fetch so a stale "load more" in flight can't
  // leave the affordance stuck.
  sessionState.loadingMore = false;
  // COLD-START PAINT. listArchivedSessions is DAEMON_FORWARD and answers [] while the
  // embedded daemon is still attaching, so the panes render the "No archived sessions"
  // EMPTY STATE (not a spinner — two of the three surfaces have no loading branch, and
  // `loading` initializes to false) for up to ~28s of retrySessionsOnEmpty backoff
  // below. Paint the last-known page NOW and let the live answer replace it — the same
  // cold-start treatment the agent roster already gets in selectWorkspace.
  // Only when memory is empty: a same-workspace refetch must never rewind a fresher
  // live list back to the cached page.
  let paintedFromCache = false;
  if (sessionState.list.length === 0) {
    const cached = loadSessionListCache(rosterStore(), wsId, authState.user?.email);
    if (cached.length > 0) {
      sessionState.list = cached;
      // The cached rows ARE the render, so drop the skeleton or AgentsPane shows it
      // INSTEAD of them. `fetched` deliberately stays false — it means "the server
      // answered", and it hasn't.
      paintedFromCache = true;
      sessionState.loading = false;
    }
  }
  // True once the daemon has actually answered with rows — the thing the retry loop
  // below is waiting for. Tracked separately from `sessionState.list` because a cache
  // paint makes the list non-empty without the daemon having answered at all.
  let daemonAnswered = false;
  try {
    // Page 1 only — older history loads on demand via loadMoreSessions("Show more")
    // so the panes never render the full unbounded archive at once.
    const page = await client.listArchivedSessions(wsId, { limit: ARCHIVED_SESSIONS_PAGE_SIZE });
    // Workspace-switch race: a switch mid-fetch must not overwrite the new
    // workspace's list with this (now stale) response.
    if (workspaceState.current?.id !== wsId) return;
    daemonAnswered = page.sessions.length > 0;
    // Sampled HERE, not before the await: an answer's authority depends on whether a
    // daemon was reachable when it ARRIVED, not when it was asked for.
    const daemonConnected = daemonConnStatus() === "connected";
    // A live answer is authoritative EXCEPT an empty one that came back with no daemon
    // reachable — that is "couldn't reach the daemon", never "no archived sessions".
    // Same rule, same function, as the agent roster (nextAgentSnapshot with no
    // silentDaemonIds reduces to exactly that guard). With no cache in play `current`
    // is [] anyway, so this is a no-op on the pre-existing path.
    sessionState.list = nextAgentSnapshot(sessionState.list, page.sessions, daemonConnected);
    // A partial first page (fewer rows than the page size) is the LAST page — even
    // when the server echoes a cursor. Guard so "Load more" never offers a dead
    // click (the "showing 1 + Load more that loads nothing" bug).
    sessionState.nextCursor =
      page.sessions.length < ARCHIVED_SESSIONS_PAGE_SIZE ? null : page.nextCursor;
    sessionState.fetched = true;
    // Persist the SERVER's page, never sessionState.list (which may still be the
    // cache) — re-persisting our own cache would pin a stale page forever. The
    // offline-empty skip lives in saveSessionListCache.
    saveSessionListCache(
      rosterStore(),
      wsId,
      authState.user?.email,
      page.sessions,
      daemonConnected,
    );
  } catch {
    if (workspaceState.current?.id !== wsId) return;
    // Fail CLOSED: never blank the list on a rejected fetch — that made a flaked
    // request look identical to a genuinely empty archive and DESTROYED a loaded
    // list (the relay-redeploy report: for the 8-40s the daemon takes to
    // re-register, list_archived_sessions rejects and the sidebar emptied itself).
    // A warm list that fails a refetch just keeps showing what it has; recovery
    // comes from the next successful fetch or live event, exactly like fetchCybos
    // above. `fetched` is deliberately NOT set here: a failure is not a load, and
    // latching it would let a future reader treat this as "already fetched" and
    // skip the retry below. The genuinely-empty case is unchanged — the list stays
    // empty, so retrySessionsOnEmpty still spawns.
    // A cache paint is likewise preserved: nothing is blanked here, so a
    // cold-start paint survives a thrown refetch instead of snapping back to
    // "No archived sessions".
  } finally {
    sessionState.loading = false;
  }
  // Cold-start race: archived sessions are answered by the embedded daemon
  // (DAEMON_FORWARD); on launch it may not be connected to the relay yet, so the
  // first fetch returns []. The sidebar fetch is one-shot, so without a retry the
  // empty list sticks until a manual reload. Retry on a short backoff until the
  // daemon answers (mirrors retryAgentsOnEmpty).
  // Keyed on whether the DAEMON answered, not on the list being empty: identical to
  // the old condition on the uncached path (there `list` just IS `page.sessions`), but
  // a cache paint must not be mistaken for a loaded list — nor, once the daemon has
  // really answered, keep the loop polling for rows we already have.
  if (!daemonAnswered) void retrySessionsOnEmpty(wsId, paintedFromCache);
}

// Append the next (older) page of archived sessions. No-op when already loading
// or when there is no further page (nextCursor === null). Errors are swallowed to
// a stable list (the button simply stays — a transient relay hiccup retries on the
// next click) since this is an additive load, not the primary fetch.
export async function loadMoreSessions(): Promise<void> {
  if (!workspaceState.current) return;
  const wsId = workspaceState.current.id;
  const cursor = sessionState.nextCursor;
  if (!cursor || sessionState.loadingMore) return;
  sessionState.loadingMore = true;
  try {
    const page = await client.listArchivedSessions(wsId, {
      limit: ARCHIVED_SESSIONS_PAGE_SIZE,
      cursor,
    });
    if (workspaceState.current?.id !== wsId) return;
    // Guard against duplicates if a concurrent refetch already merged some ids.
    const seen = new Set(sessionState.list.map((s) => s.id));
    const fresh = page.sessions.filter((s) => !seen.has(s.id));
    sessionState.list = [...sessionState.list, ...fresh];
    // End of the archive when the page came back PARTIAL or brought nothing NEW —
    // clear the cursor so "Load more" stops offering a dead click even if the
    // server keeps echoing one.
    sessionState.nextCursor =
      fresh.length > 0 && page.sessions.length >= ARCHIVED_SESSIONS_PAGE_SIZE
        ? page.nextCursor
        : null;
  } catch {
    // Keep the current list + cursor so the user can retry the "Show more" click.
  } finally {
    // Only clear loadingMore if we're still on the workspace we started on — a
    // workspace switch mid-load owns its own loadingMore lifecycle now.
    if (workspaceState.current?.id === wsId) sessionState.loadingMore = false;
  }
}

// Same single-flight + exponential-backoff contract as retryAgentsOnEmpty (see
// the emptyListRetryInFlight comment there — this loop is spawned from every
// fetchSessions call site, so unguarded overlapping loops were the other half
// of the task_3c5914ed request storm).
async function retrySessionsOnEmpty(workspaceId: string, paintedFromCache = false): Promise<void> {
  const flightKey = `sessions:${workspaceId}`;
  if (emptyListRetryInFlight.has(flightKey)) return; // join the in-flight loop
  emptyListRetryInFlight.add(flightKey);
  try {
    for (let attempt = 0; attempt < 8; attempt++) {
      const delay = Math.min(1000 * 1.5 ** attempt, 5000);
      await new Promise((resolve) => setTimeout(resolve, delay));
      if (workspaceState.current?.id !== workspaceId) return;
      // A non-empty list normally means another path already loaded it — stop.
      // EXCEPT when the rows on screen came from the cold-start cache: those are
      // last-known, not a daemon answer, so bailing here would strand the cached page
      // on screen and never replace it (the retry loop IS the thing that waits for the
      // daemon to attach). Bounded either way: ≤8 attempts, single-flighted per
      // workspace, and the success branch below returns the moment real rows land.
      if (!paintedFromCache && sessionState.list.length > 0) return;
      try {
        const page = await client.listArchivedSessions(workspaceId, {
          limit: ARCHIVED_SESSIONS_PAGE_SIZE,
        });
        if (workspaceState.current?.id === workspaceId && page.sessions.length > 0) {
          sessionState.list = page.sessions;
          sessionState.nextCursor =
            page.sessions.length < ARCHIVED_SESSIONS_PAGE_SIZE ? null : page.nextCursor;
          // The daemon finally answered — this is the cold-start path that actually
          // produces the page the NEXT launch paints from, so persist it here too.
          saveSessionListCache(
            rosterStore(),
            workspaceId,
            authState.user?.email,
            page.sessions,
            daemonConnStatus() === "connected",
          );
          return;
        }
        // Relay flag: daemons were reached but none answered (hung, not
        // cold-starting) — stop retrying; recovery comes from the reconnect
        // panel reconcile (reconcileWorkspacePanels → fetchSessions) or the
        // next pane open, once the daemon is actually back.
        if (page.noDaemonAnswered) return;
      } catch {
        // intentional: one attempt in a cold-start backoff retry loop; the loop
        // tries again and the daemon_status self-heal is the backstop.
      }
    }
  } finally {
    emptyListRetryInFlight.delete(flightKey);
  }
}

// Workspace / projects / channels CRUD — extracted to ./workspace-crud.svelte.ts
// (knowledge/87 Phase 3). Re-exported here so importers keep hitting the app
// barrel. projectsCache is also imported above for the one internal caller (the
// logout/account-switch cache wipe). leaveChannel stays below — it calls
// selectChannelPreview (the navigation spine) so it can't move without cycling.
export {
  createWorkspace,
  projectsCache,
  fetchProjects,
  createProject,
  updateProject,
  deleteProject,
  setChannelProject,
  createChannel,
  createGroupDm,
  updateChannel,
  deleteChannel,
  archiveChannel,
  getChannelRole,
  getChannelFileCount,
  fetchChannelMembers,
  addChannelMember,
  removeChannelMember,
  fetchChannelCybos,
  fetchChannelCybosWithIdentity,
  addChannelCybo,
  removeChannelCybo,
  fetchProjectRoles,
  setProjectRole,
  setProjectPm,
  fetchProjectBrief,
  setProjectBrief,
  fetchProjectAutonomy,
  setProjectAutonomy,
  setProjectAgentChannel,
  createProjectAgentChannel,
  fetchProjectActivity,
  fetchChannelFiles,
  fetchDmFiles,
  listChannels,
  searchMessages,
  searchTasks,
  searchPages,
  joinChannel,
} from "./workspace-crud.svelte.js";
export type { ProjectsCacheEntry } from "./workspace-crud.svelte.js";

export async function leaveChannel(channelId: string): Promise<void> {
  const wsId = workspaceState.channelWorkspaceId(channelId);
  if (!wsId || !authState.user) return;
  // Capture the channel before we drop it from the list — we need it to decide
  // the post-leave view (preview vs. nothing).
  const left = workspaceState.channels.find((c) => c.id === channelId);
  await client.removeChannelMember(wsId, channelId, authState.user.id);
  // Slack model: the sidebar shows only channels you're a member of, so leaving
  // any channel (public or private) removes it from your sidebar.
  workspaceState.channels = workspaceState.channels.filter((c) => c.id !== channelId);
  dropExternalChannel(wsId, channelId);
  notificationState.clear(wsId, channelId);
  unreadFlagState.clearUnread(wsId, channelId);
  if (channelState.activeId === channelId) {
    // We were viewing the channel we just left. If it's public, drop straight into
    // the read-only preview (with a Join CTA to rejoin) rather than a dead-end
    // "Select a channel". Private channels can't be previewed, so clear selection.
    if (left && !left.isPrivate) {
      selectChannelPreview(left);
    } else {
      channelState.activeId = null;
    }
  }
}

// ─── Badge sync (notification state → rail + dock + web title) ──────────────

// Push the global unread total to (a) the Electron dock/taskbar badge and (b)
// the web tab title. Imperative so it can be called both reactively (the effect
// below) AND directly on connect/seed/logout — the effect alone proved flaky
// (it was starved by the rail effect's $state writes, so a freshly-seeded count
// never reached the dock and the badge showed nothing). This function reads
// nothing reactive itself; callers pass the total.
const WEB_TITLE_BASE = "Cyborg";
export function pushUnreadIndicators(total: number): void {
  const n = Math.max(0, Math.floor(total) || 0);
  if (typeof window === "undefined") return;
  // Push decisions E-H: one badge, the Alerts count. No "•" for unread channels/DMs on top.
  const label = n > 0 ? String(n) : "";
  // (a) Electron dock / taskbar badge. The desktop bridge is `cyborg7Desktop`;
  // absent in a plain browser, so this is a clean no-op there. Prefer the
  // string-aware setBadgeText (dot + count); fall back to the numeric
  // setBadgeCount on an older shell that predates it.
  const w = window as Window & {
    cyborg7Desktop?: {
      setBadgeText?: (text: string) => void;
      setBadgeCount?: (n: number) => void;
    };
  };
  try {
    if (w.cyborg7Desktop?.setBadgeText) {
      w.cyborg7Desktop.setBadgeText(label);
    } else {
      w.cyborg7Desktop?.setBadgeCount?.(n);
    }
  } catch {
    // intentional: desktop dock-badge update is best-effort; a no-op in the browser.
  }
  // (b) Web/PWA fallback: reflect the badge into the tab title — "(3) Cyborg"
  // for a count, "(•) Cyborg" for activity-only. Only when NOT running inside
  // the desktop shell (which has the real dock badge). Mirrors v1's ClientShell
  // title indicator.
  if (!w.cyborg7Desktop && typeof document !== "undefined") {
    try {
      document.title = label ? `(${label}) ${WEB_TITLE_BASE}` : WEB_TITLE_BASE;
    } catch {
      // intentional: tab-title unread indicator is cosmetic best-effort.
    }
  }
}

$effect.root(() => {
  // Rail badge (chat). Kept in its own effect; it WRITES $state
  // (shellConfig.config.railItems), which is why the dock push must NOT live
  // here — those writes could abort the flush before the dock line ran, leaving
  // the badge stale/empty (the historical bug).
  $effect(() => {
    const wsId = workspaceState.current?.id;
    const chatTotal = wsId ? workspaceBadgeTotal(wsId) : 0;
    // Chat badge = per-workspace mention/DM total. Alerts (the "activity" rail
    // item) badge = the global unread-activity count, so the rail shows how many
    // notifications are still unread. Reading activityState.unreadCount here makes
    // this effect re-run as alerts arrive/clear. (The old top-bar Activity bell
    // that used to carry this count was removed when everything moved to the rail.)
    shellConfig.updateRailBadge("chat", chatTotal);
    shellConfig.updateRailBadge("activity", activityState.badgeCount);
  });

  // Dock badge + web title in a DEDICATED effect that performs NO $state writes,
  // so it can't be starved by the rail effect above.
  // Push decisions E-H: the dock/taskbar number IS the Alerts unread count
  // (activityState.unreadCount), so it matches the bell and the OS push badge.
  // pushUnreadIndicators ALSO reads unreadFlagState.hasAnyUnread() for the
  // Slack-parity "dot" (any unread channel, no mention) — that reactive read
  // happens inside the synchronous call below, so this effect re-runs when
  // EITHER the count or the bold-flag set changes.
  $effect(() => {
    pushUnreadIndicators(activityState.badgeCount);
  });
});

// A resolved permission closes its alert row: a re-used request id (the daemon numbers hold cards per
// session, so `peer-hold-card-1` comes back) must raise a NEW row, never silently update the settled one.
// A hold that dropped by expiry reads "Expired — not delivered".
function settlePermissionAlert(
  agentId: string,
  event: { requestId: string; resolution: { behavior: string; message?: string } },
): void {
  const request = agentStreamState
    .getPendingPermissions(agentId)
    .find((r) => r.id === event.requestId);
  const expired =
    request?.name === "peer_message_hold" &&
    event.resolution.behavior === "deny" &&
    /expired/i.test(event.resolution.message ?? "");
  for (const row of activityState.items) {
    if (row.eventType !== "permission_request" || row.actorId !== agentId || row.permissionSettled)
      continue;
    if (row.permissionRequestId !== event.requestId) continue;
    row.permissionSettled = true;
    if (expired) {
      row.permissionOutcome = PEER_HOLD_EXPIRED_TEXT;
      row.preview = PEER_HOLD_EXPIRED_TEXT;
    }
  }
}

// The alert preview for a permission request: a held peer message names its sender, never the raw peer name.
function permissionAlertTitle(request: AgentPermissionRequest): string {
  const title =
    request.name === "peer_message_hold"
      ? (request.title ?? request.name)
      : permissionRequestTitle(request);
  // Resolved here, not via peer-name-resolver: that module imports this one (an import cycle
  // deadlocks every test that mocks app.svelte.js with importOriginal).
  const resolve = (raw: string): string | undefined =>
    cyboNameOf({ cyboId: raw }, cyboState.list) ??
    peerDisplayName(raw, workspaceState.agents, cyboState.list, () => undefined);
  return request.name === "peer_message_hold" ? peerHoldTitle(title, resolve) : title;
}
